import React, { createContext, useContext, useState, useEffect, useMemo, useCallback, useRef } from 'react';
import confetti from 'canvas-confetti';
import {
  User,
  UserRole,
  Account,
  AccountAllocation,
  AllocationHistory,
  FollowUp,
  PTPRecord,
  RecoveryRecord,
  FieldVisit,
  PhotoRecord,
  VoiceNoteRecord,
  DocumentRecord,
  ActivityLog,
  NotificationItem,
  CommissionRecord,
  CommissionSettings,
  Branch,
  Area,
  Zone,
  Bank,
  RecoveryDepartment,
  CommissionRule,
  UserCommissionAssignment,
  CommissionAuditLog,
  CommissionCalculationResult,
  TimelineEvent,
  PTPDigestResult,
  AgentNote,
  QDType,
  RecoveryStage,
  OTSHistoryRecord,
  WhatsAppOfferLog,
  StaffMessage,
} from '../types';
import {
  evaluateOTSSlab,
  calculateActualConcession,
  OTS_VALID_TILL,
  setLiveOTSConfig,
  isOTSExpired,
} from '../utils/otsScheme';
import { resolveCommissionRate, commissionFor } from '../utils/commissionRate';
import { getLinkedAccounts } from '../utils/linkedAccounts';
import {
  isSystemNote,
  userNotesOf,
  MAX_USER_NOTES_PER_ACCOUNT,
  recalcAccountMoney,
  originalOutstandingOf,
  preSettlementOriginalOf,
  otsWaiverOf,
  applyOtsSettlementState,
} from '../utils/collections';
import { todayIso } from '../utils/accountFilters';
import {
  isViewOnly,
  writeRefusal,
  canApproveOTS,
  scopeBranchesOf,
  scopeBanksOf,
  norm,
  isPlaceholderName,
  isKnownBankName,
  planStatusOf,
  canSendMessages,
  canDeleteMessages,
  messageRecipientsFor,
} from '../utils/hierarchy';

/**
 * Which accounts are in memory right now.
 *
 * 'none'  nothing loaded -- an administrator who has not chosen a scope yet
 * 'agent' one agent's allotment (live)
 * 'branch' / 'branches' / 'bank'  a chosen slice
 * 'all'   the whole book, loaded deliberately by Allocation or Bulk Delete
 */
export interface AccountsScope {
  /**
   * 'zone' is what a recovery department or branch manager gets when no
   * branches have been allotted to them. It used to fall through to 'bank',
   * which loaded every zone in the bank -- see the scope decision in the
   * subscription effect.
   */
  kind: 'none' | 'agent' | 'branch' | 'branches' | 'zone' | 'bank' | 'all';
  label: string;
}
import {
  placeholderUser,
  initialAccounts,
  initialAccountAllocations,
  initialAllocationHistories,
  initialFollowUps,
  initialPTPs,
  initialRecoveries,
  initialVisits,
  initialPhotos,
  initialVoiceNotes,
  initialDocuments,
  initialActivityLogs,
  initialNotifications,
  initialCommissions,
  initialBranches,
  initialAreas,
  initialZones,
  initialBanks,
  initialRecoveryDepartments,
  initialCommissionRules,
  initialUserCommissionAssignments,
  initialCommissionAuditLogs,
  initialCommissionSettings,
} from '../mockData/initialData';
import { createSRMSWatermarkedPhoto, WatermarkOptions } from '../utils/watermark';
import {
  isMatchingAccountNumber,
  exportAllBackendDataToExcel,
  exportFullBackupToExcel,
} from '../utils/excelAccountImporter';
import { firebaseFirestoreService, FirestoreSpeedStats } from '../services/firebaseFirestoreService';
import {
  firebaseAuthService,
  normaliseLoginId,
  buildInternalEmail,
} from '../services/firebaseAuthService';
import { firebaseStorageService } from '../services/firebaseStorageService';
import { auth, clearLocalDataCache } from '../firebase';

export interface SheetQueueStatus {
  pendingCount: number;
  isFlushing: boolean;
  lastError: string | null;
  lastSuccessAt: string | null;
}
import {
  hashPasswordSync,
  generateSalt,
  generateOTP,
  maskEmail,
  maskMobile,
  generateToken,
} from '../utils/security';
import { calculateCommissionWithPriority, CalculationParams } from '../utils/commissionEngine';
import { fetchDynamicRules } from '../services/dynamicCommissionService';
import {
  DynamicCommissionRule,
  EvaluationResult,
  RecoveryEntryInput,
  evaluateRecoveryEntry,
} from '../utils/dynamicCommission/dynamicCommissionEngine';

import { todayLocal, nowLocalStamp } from '../utils/dates';
export interface AuthResult {
  success: boolean;
  error?: string;
  code?: string;
  user?: User;
}

export interface OTPRequestResult {
  success: boolean;
  message: string;
  maskedEmail?: string;
  maskedMobile?: string;
  requestId?: string;
  error?: string;
}

interface SRMSContextType {
  currentUser: User;
  users: User[];
  isAuthenticated: boolean;
  /** False until Firebase Auth has reported who (if anyone) is signed in. */
  authResolved: boolean;
  deviceMode: 'web' | 'android';
  setDeviceMode: (mode: 'web' | 'android') => void;
  switchUser: (userId: string) => void;
  loginWithCredentials: (
    username: string,
    pin: string,
    targetMode?: 'web' | 'android'
  ) => Promise<AuthResult>;
  logout: () => void;

  // Admin Forgot Password with OTP Flow
  requestAdminOTP: (identifier: string) => Promise<OTPRequestResult>;
  verifyAdminOTP: (
    requestId: string,
    otp: string
  ) => Promise<{ success: boolean; resetToken?: string; error?: string }>;
  resetAdminPasswordWithToken: (
    resetToken: string,
    newPassword: string
  ) => Promise<{ success: boolean; message?: string; error?: string }>;
  
  // Data
  accounts: Account[];
  allocations: AccountAllocation[];
  allocationHistories: AllocationHistory[];
  followups: FollowUp[];
  ptps: PTPRecord[];
  recoveries: RecoveryRecord[];
  visits: FieldVisit[];
  photos: PhotoRecord[];
  /** Delete a geo-tagged photo. Admins may delete any; agents only their own. */
  deleteGeoTaggedPhoto: (
    photoId: string,
    reason?: string
  ) => Promise<{ success: boolean; message: string }>;
  /** Fill in or correct a photo's note after capture. Allowed once. */
  updatePhotoNote: (
    photoId: string,
    note: string
  ) => Promise<{ success: boolean; message: string }>;
  documents: DocumentRecord[];
  voiceNotes: VoiceNoteRecord[];
  activityLogs: ActivityLog[];
  notifications: NotificationItem[];
  commissions: CommissionRecord[];
  commissionSettings: CommissionSettings;
  branches: Branch[];
  areas: Area[];
  zones: Zone[];
  banks: Bank[];
  recoveryDepartments: RecoveryDepartment[];
  commissionRules: CommissionRule[];
  userCommissionAssignments: UserCommissionAssignment[];
  commissionAuditLogs: CommissionAuditLog[];
  activeVisit: FieldVisit | null;

  // Scoped views for role-based hierarchy enforcement
  scopedAccounts: Account[];
  scopedRecoveries: RecoveryRecord[];
  scopedPtps: PTPRecord[];
  scopedVisits: FieldVisit[];
  scopedCommissions: CommissionRecord[];
  scopedUsers: User[];
  scopedRules: CommissionRule[];
  scopedAssignments: UserCommissionAssignment[];
  archivedAccounts: Account[];

  // Actions
  //
  // The three `add` calls below RETURN what they created. They used to return
  // nothing, which is why a "+ Create Bank" button could not tick the bank it
  // had just made -- the caller had no way of knowing its name or id, and
  // guessing from the list meant picking whatever sorted first.
  addBank: (bank: Partial<Bank>) => Bank;
  updateBank: (bankId: string, updates: Partial<Bank>) => void;
  /**
   * Hierarchy deletes. Each refuses, with counts, while anything still
   * depends on the record -- accounts, staff or child records.
   */
  deleteBank: (bankId: string) => { success: boolean; message: string };
  deleteZone: (zoneId: string) => { success: boolean; message: string };
  deleteBranch: (branchId: string) => { success: boolean; message: string };
  deleteRecoveryDepartment: (deptId: string) => { success: boolean; message: string };
  /** Create a Zonal Office under one bank. There was no way to add one at all. */
  addZone: (zone: Partial<Zone>) => Zone;
  updateZone: (zoneId: string, updates: Partial<Zone>) => void;
  addRecoveryDepartment: (dept: Partial<RecoveryDepartment>) => RecoveryDepartment;
  updateRecoveryDepartment: (deptId: string, updates: Partial<RecoveryDepartment>) => void;
  addBranch: (branch: Partial<Branch>) => Branch;
  updateBranch: (branchId: string, updates: Partial<Branch>) => void;
  /** Fold duplicate branch records into one and repoint their accounts. */
  /** Set when a remembered whole-book scope is too large to reload silently. */
  pendingScopeReload: { label: string; count: number } | null;
  resumeLastScope: () => Promise<{ success: boolean; count: number; message: string }>;
  correctAccountOriginalBalance: (
    accountId: string,
    newOriginal: number
  ) => Promise<{ success: boolean; message: string }>;
  previewOrphanedCommissions: () => {
    orphans: Array<{
      id: string;
      agentName: string;
      accountId: string;
      recoveryId: string;
      commissionAmount: number;
      recoveryAmount: number;
      date: string;
      reason: string;
    }>;
    totalNow: number;
    totalAfter: number;
  };
  cleanOrphanedCommissions: () => Promise<{ removed: number; message: string }>;
  previewBalanceRepair: () => Array<{
    accountId: string;
    customerName: string;
    original: number;
    collected: number;
    waiver: number;
    storedOutstanding: number;
    correctOutstanding: number;
    difference: number;
    /** The red figure on the accounts list. Checked as well as the balance. */
    storedOverdue: number;
    correctOverdue: number;
    overdueDifference: number;
    /** The balance is right and only the overdue is stale. */
    overdueOnly: boolean;
    reopening: boolean;
  }>;
  applyBalanceRepair: (auto?: boolean) => Promise<{ repaired: number; message: string }>;
  backfillAccountBanks: () => {
    success: boolean;
    updated: number;
    skipped: number;
    message: string;
  };
  mergeBranches: (
    keepBranchId: string,
    mergeBranchIds: string[]
  ) => { success: boolean; message: string };
  addCommissionRule: (rule: Partial<CommissionRule>) => { success: boolean; message: string; rule?: CommissionRule; conflictDetected?: boolean };
  updateCommissionRule: (id: string, updates: Partial<CommissionRule>) => { success: boolean; message: string };
  deactivateCommissionRule: (id: string) => { success: boolean; message: string };
  addUserCommissionAssignment: (assignment: Partial<UserCommissionAssignment>) => { success: boolean; message: string; assignment?: UserCommissionAssignment; conflictDetected?: boolean };
  updateUserCommissionAssignment: (id: string, updates: Partial<UserCommissionAssignment>) => { success: boolean; message: string };
  deactivateUserCommissionAssignment: (id: string) => { success: boolean; message: string };
  calculateCommission: (params: CalculationParams) => CommissionCalculationResult;
  liveDynamicCommission: (args: { acc?: Account; agentId: string; amount: number; date: string }) => { result: EvaluationResult; ruleName: string } | null;
  getFilteredRecoveryDepartments: (bankName?: string, zoneName?: string) => RecoveryDepartment[];
  getFilteredBranches: (bankName?: string, zoneName?: string, departmentName?: string) => Branch[];
  isAuthorizedForAccount: (account: Account, user?: User) => boolean;
  isAuthorizedForRecovery: (recovery: RecoveryRecord, user?: User) => boolean;
  addAccount: (account: Partial<Account>) => void;
  /** What is in `accounts` right now, and whether more is still coming. */
  accountsScope: AccountsScope;
  accountsLoading: boolean;
  /** Read only the accounts in a chosen scope. Nothing loads until this runs. */
  loadAccountsForScope: (scope: {
    agentId?: string;
    agentName?: string;
    branch?: string;
    branches?: string[];
    bank?: string;
  }) => Promise<{ success: boolean; count: number; message: string }>;
  /** The whole book, on purpose — Allocation and Bulk Delete need it. */
  loadAllAccounts: () => Promise<{ success: boolean; count: number; message: string }>;
  /** How many accounts match, for one read instead of one per record. */
  /**
   * How many records one agent has in a log collection, without reading them.
   * Used by the "choose an agent first" gate on the admin log screens.
   */
  countCollectionFor: (collectionName: string, agentId: string) => Promise<number>;
  /**
   * Save the OTS scheme settings (slabs, valid-till dates, per-bank rules) so
   * every device sees what the administrator set. Admin only.
   */
  persistOTSSettings: (config: any) => Promise<{ success: boolean; message: string }>;
  /**
   * Permanently delete ONE agent's records in ONE menu. Administrator only.
   * Deleting their accounts correctly leaves their logs behind; this is how
   * those are cleared when they are costing storage.
   */
  deleteAgentRecordsIn: (
    collectionName: 'recoveries' | 'followups' | 'ptps' | 'visits' | 'photos' | 'voicenotes',
    agentId: string,
    agentName?: string
  ) => Promise<{ success: boolean; count: number; message: string }>;
  countAccountsFor: (scope?: {
    agentId?: string;
    branch?: string;
    bank?: string;
  }) => Promise<number>;
  updateAccount: (
    accountId: string,
    updates: Partial<Account>,
    /** Only the OTS approval path sets this; see mutateAccounts. */
    options?: { allowViewOnly?: boolean }
  ) => void;
  updateCustomerStarRating: (accountId: string, rating: number) => void;
  closeAccount: (accountId: string, closureType: 'Closed as per OTS' | 'Regular Close' | 'Administrative Deletion', remarks?: string) => Promise<{ success: boolean; message: string }>;
  deleteAccount: (accountId: string, closureType?: 'Closed as per OTS' | 'Regular Close' | 'Administrative Deletion', remarks?: string) => Promise<{ success: boolean; message: string }>;
  deleteAllAccountsFromStorage: () => Promise<{ success: boolean; count: number; message: string }> | { success: boolean; count: number; message: string };
  deleteBulkAccounts: (options: {
    scope: 'all' | 'agent' | 'branch' | 'ids';
    agentId?: string;
    agentName?: string;
    branchName?: string;
    accountIds?: string[];
    permanent?: boolean;
  }) => Promise<{ success: boolean; count: number; message: string }>;
  bulkAddAccounts: (accountsList: Partial<Account>[]) => void;
  allocateAccount: (accountId: string, agentId: string, remarks?: string) => void;
  bulkAllocateAccounts: (accountIds: string[], agentId: string, remarks?: string) => void;
  allocateBulkAccounts: (accountIds: string[], agentId: string, agentName?: string, agentCode?: string) => void;
  toggleUserActive: (userId: string) => void;
  addUser: (userData: Partial<User>) => Promise<{ success: boolean; uid: string; loginId: string }>;
  updateUser: (userId: string, updates: Partial<User>) => void;
  deleteUser: (userId: string) => Promise<{ success: boolean; reassignedCount: number; message: string }>;
  changeUserPassword: (userId: string, newPassword: string, newPin?: string) => Promise<{ success: boolean; error?: string }>;
  changeUserLoginId: (
    userId: string,
    newLoginId: string
  ) => Promise<{ success: boolean; error?: string; loginId?: string }>;
  exportAllBackendData: (filename?: string) => void;
  /** Full backup: the work, without the accounts nothing has happened on. */
  downloadFullBackup: () => {
    accountsIncluded: number;
    accountsSkipped: number;
    filename: string;
  };
  logFollowUp: (data: {
    accountId: string;
    status: FollowUp['status'];
    customerResponse: string;
    discussionDetails: string;
    agentRemarks: string;
    nextFollowUpDate?: string;
    nextFollowUpTime?: string;
  }) => void;
  createPTP: (data: {
    accountId: string;
    amount: number;
    ptpDate: string;
    ptpMode: PTPRecord['ptpMode'];
    customerCommitment: string;
    remarks: string;
  }) => void;
  updatePTPStatus: (ptpId: string, status: PTPRecord['status']) => void;
  recordRecovery: (data: {
    accountId: string;
    amount: number;
    paymentMode: RecoveryRecord['paymentMode'];
    referenceNumber: string;
    remarks: string;
    recoveryDate?: string;
    proofDriveFileId?: string;
    isOTS?: boolean;
    otsStage?: '10_percent_token' | 'full_settlement';
    otsAgreedAmount?: number;
    agentId?: string;
    agentName?: string;
    /** Bank interest and charges collected on top of the loan balance. */
    excessChargesAmount?: number;
  }) => void;
  editRecovery: (
    recoveryId: string,
    updatedData: {
      amount?: number;
      paymentMode?: RecoveryRecord['paymentMode'];
      referenceNumber?: string;
      remarks?: string;
      recoveryDate?: string;
      isOTS?: boolean;
      otsStage?: '10_percent_token' | 'full_settlement';
      otsAgreedAmount?: number;
    }
  ) => Promise<{ success: boolean; message: string }>;
  deleteRecovery: (recoveryId: string) => Promise<{ success: boolean; message: string }>;
  reverseRecovery: (recoveryId: string, reason: string) => Promise<{ success: boolean; message: string }>;
  markAccountForDeletion: (accountId: string, isMarked?: boolean) => void;
  startFieldVisit: (accountId: string, coords?: { lat: number; lng: number }) => Promise<FieldVisit>;
  completeFieldVisit: (
    visitId: string,
    status: FieldVisit['visitStatus'],
    remarks: string,
    customerInteraction: string,
    photoIds?: string[],
    voiceIds?: string[]
  ) => void;
  captureWatermarkedPhoto: (options: Omit<WatermarkOptions, 'agentId' | 'agentName'>) => Promise<PhotoRecord>;
  recordVoiceNote: (data: {
    accountId: string;
    durationSeconds: number;
    title: string;
    transcription?: string;
    audioBlobUrl?: string;
    visitId?: string;
  }) => VoiceNoteRecord;
  uploadDocument: (data: {
    accountId: string;
    documentType: DocumentRecord['documentType'];
    fileName: string;
    fileSizeBytes: number;
    file?: File | Blob;
  }) => Promise<DocumentRecord> | DocumentRecord;
  attachGoogleDriveDocument?: (
    accountId: string,
    file: any,
    documentType?: DocumentRecord['documentType']
  ) => DocumentRecord;
  updateCommissionSettings: (newRate: number) => void;
  updateCommissionRate: (newRate: number) => void;
  /** Admin-only SARFAESI commission rate. */
  updateSarfaesiRate: (newRate: number) => { success: boolean; message: string };
  /** Agent marks which of their accounts are SARFAESI recoveries. */
  markAccountsSarfaesi: (
    accountIds: string[],
    isSarfaesi: boolean
  ) => { success: boolean; message: string };
  updateCommissionStatus: (commissionId: string, status: CommissionRecord['status']) => void;
  addAgentNote: (
    accountId: string,
    note: string,
    category?: string
  ) => { success: boolean; message: string };
  deleteAgentNote: (accountId: string, noteId: string) => { success: boolean; message: string };
  markNotificationAsRead: (id: string) => void;
  messages: StaffMessage[];
  myMessages: StaffMessage[];
  unreadMessageCount: number;
  messageRecipients: User[];
  sendStaffMessage: (
    recipientIds: string[],
    subject: string,
    body: string
  ) => Promise<{ success: boolean; message: string; sentTo: number }>;
  markMessageRead: (messageId: string) => void;
  deleteStaffMessages: (
    ids: string[],
    month?: string
  ) => Promise<{ success: boolean; removed: number; message: string }>;
  deleteVoiceNote: (voiceNoteId: string) => Promise<{ success: boolean; message: string }>;
  deleteFieldVisit: (visitId: string) => Promise<{ success: boolean; message: string }>;
  setAccountCategoryBulk: (accountIds: string[], category: string) => number;
  deleteFollowUpLog: (followUpId: string) => Promise<{ success: boolean; message: string }>;
  noticeBanner: {
    text: string;
    colour: 'red' | 'amber' | 'green';
    bank?: string;
    zone?: string;
    /** Agent ids this notice is aimed at. Empty means nobody is singled out. */
    agents?: string[];
    updatedAt?: string;
    updatedBy?: string;
  };
  /** The notice THIS person should see, after bank, zone and agent targeting. */
  visibleNotice: {
    text: string;
    colour: 'red' | 'amber' | 'green';
    bank?: string;
    zone?: string;
    agents?: string[];
  } | null;
  saveNoticeBanner: (
    text: string,
    colour: 'red' | 'amber' | 'green',
    scope?: { bank?: string; zone?: string; agents?: string[] }
  ) => Promise<{ success: boolean; message: string }>;
  markNotificationRead: (id: string) => void;
  clearAllNotifications: () => void;
  acknowledgeReminder: (reminderId: string) => void;
  ackedReminders: string[];
  
  // Computed & Aggregates
  getAccountTimeline: (accountId: string) => TimelineEvent[];
  /** Loads one account's allocation records, for the timeline drawer. */
  ensureAllocationsForAccount: (accountId: string) => Promise<void>;
  /** Server-side count of the allocations collection (one read). */
  countAllocations: () => Promise<number>;
  getGoogleSheetsData?: () => any[];
  getGoogleDriveData?: () => any;
  syncToCloudStorage: () => Promise<{ success: boolean; message: string; timestamp: string }>;
  syncWithGoogleSheets?: () => Promise<{ success: boolean; message: string; timestamp: string }>;
  pullFromGoogleSheets?: () => Promise<{ success: boolean; message: string; updatedCount: number }>;
  runPTPDigest: (referenceDate?: string) => Promise<PTPDigestResult>;
  deletePTP: (ptpId: string, reason?: string) => Promise<{ success: boolean; message: string }>;
  getExpiredPTPPreview: (referenceDate?: string) => {
    expiredCount: number;
    expiredAmount: number;
    expiredPTPs: PTPRecord[];
    dueTodayCount: number;
    dueTodayPTPs: PTPRecord[];
    upcomingCount: number;
    totalPending: number;
  };
  analyzeAccountAI: (accountId: string) => Promise<any>;
  /** When a backup file was last downloaded on this computer. */
  lastBackupTime: string | null;
  isSyncing: boolean;
  autoSyncEnabled: boolean;
  setAutoSyncEnabled: (enabled: boolean) => void;
  lastAutoSyncTime: string | null;

  // OTS Scheme & WhatsApp Offer Module
  otsAuthorizedRoles: UserRole[];
  updateOTSAuthorizedRoles: (roles: UserRole[]) => void;
  canUserEditOTS: (user?: User) => boolean;
  /** Approving a settlement is a separate right from calculating one. */
  canUserApproveOTS: (user?: User) => boolean;
  /** Banks the signed-in user covers, and which one they are viewing. */
  userBanks: string[];
  activeBank: string;
  setActiveBank: (bank: string) => void;
  whatsAppLogs: WhatsAppOfferLog[];
  logWhatsAppOffer: (offerData: Omit<WhatsAppOfferLog, 'id' | 'timestamp'>) => void;
  applyOTS: (accountId: string, options?: { userEnteredAmount?: number; notes?: string }) => { success: boolean; message: string; slabDefined: boolean };
  modifyOTS: (accountId: string, userEnteredAmount: number, notes?: string) => { success: boolean; message: string };
  confirmOTS: (accountId: string, notes?: string) => { success: boolean; message: string };
  cancelOTS: (accountId: string, reason?: string) => { success: boolean; message: string };

  // Shared Central Master Database Across All Logins
  pullSharedMasterData: () => Promise<{ success: boolean; message: string }>;
  pushSharedMasterData: (overrides?: Record<string, any>) => Promise<{ success: boolean; message: string }>;
  batchSaveAccountsToServer: (newAccounts: Account[], replaceAll?: boolean) => Promise<{ success: boolean; message: string; totalAccounts?: number }>;
  fetchServerStats: () => Promise<any>;
  serverStats: any;

  // Permanent Demo Data Purge
  purgeAllDemoData: () => Promise<{ success: boolean; message: string }>;

  // Storage Architecture (Firebase Firestore Primary Route)
  firestoreSpeedStats: FirestoreSpeedStats;
  sheetQueueStatus: SheetQueueStatus;
  isFirebasePrimaryActive: boolean;
  flushSheetSyncNow: () => Promise<void>;
}

const isDemoUser = (u: any): boolean => {
  if (!u) return false;
  // Primary Admin Ashish Kharad is never purged
  if (u.id === 'USR-ADMIN-1' || u.email?.toLowerCase() === 'ashish.kharad2@gmail.com') return false;
  const demoIds = [
    'USR-BM-001', 'USR-BM-1', 'USR-COORD-1', 'USR-AGT-0009', 'USR-AGT-0045',
    'USR-AGT-0089', 'USR-AGENT-1', 'USR-AGENT-2', 'USR-DEPT-001', 'USR-RD-001', 'BM-001'
  ];
  const demoUsernames = [
    'branch_manager', 'agent_rahul', 'agent_priya', 'ra0009', 'ra0045', 'ra0089',
    'coordinator', 'recovery_dept', 'dept_officer'
  ];
  const demoEmails = [
    'bm001@srms.in', 'bm001@srms-recovery.in', 'dept001@srms.in', 'coord01@srms.in',
    'rahul@srms.in', 'priya@srms.in', 'ag0009@srms.in', 'ag0045@srms.in', 'ag0089@srms.in'
  ];
  if (demoIds.includes(u.id)) return true;
  if (u.username && demoUsernames.includes(u.username.toLowerCase())) return true;
  if (u.email && demoEmails.includes(u.email.toLowerCase())) return true;
  if (u.email && (u.email.endsWith('@srms-recovery.in') || u.email.endsWith('@srms.in'))) return true;

  /**
   * NO MATCHING ON NAMES. REMOVED DELIBERATELY.
   *
   * This used to return true for anyone called Rahul Sharma, Priya Patel,
   * Amit Verma, Vikram Deshmukh or Sanjay Kulkarni (BM) -- the names the demo
   * users were given. Those are among the most common names in India.
   *
   * `isDemoUser` decides who `purgeAllDemoData` DELETES. So the day a real
   * agent named Rahul Sharma was added, he would have been treated as demo
   * data: his login removed, and with it his place in the lists -- for no
   * reason he or anyone else could see. A real recovery agent erased because
   * he shares a name with a test record.
   *
   * The check was never needed either. Every demo user is already caught
   * above by its fixed id, its fixed username, or its @srms.in /
   * @srms-recovery.in address. The name added nothing except that risk.
   *
   * A real person must never be identified by their name alone.
   */
  return false;
};

/**
 * A suffix that cannot repeat within a session.
 *
 * `Date.now()` alone gives two records created in the same millisecond the
 * same id, and four random digits collide roughly once in 9,000. Either way
 * the second record silently overwrote the first rather than appearing, which
 * made duplicate-entry bugs behave differently every time they were chased.
 */
let idCounter = 0;
/**
 * Deletes currently in flight, by recovery id.
 *
 * Nothing stopped the delete button being pressed twice. Each press ran the
 * whole routine, and each one adjusted the account balance. A double-click was
 * enough to add a collection's amount to the outstanding balance twice.
 */
const recoveryDeletesInFlight = new Set<string>();

function uniqueSuffix(): string {
  idCounter += 1;
  return `${Date.now().toString(36)}${idCounter.toString(36)}${Math.floor(
    Math.random() * 1296
  )
    .toString(36)
    .padStart(2, '0')}`.toUpperCase();
}

const SRMSContext = createContext<SRMSContextType | undefined>(undefined);

const getStoredSessionItem = (key: string): string | null => {
  try {
    return localStorage.getItem(key) || sessionStorage.getItem(key);
  } catch {
    return null;
  }
};

/**
 * The session flag lives in sessionStorage ONLY.
 *
 * It used to be written to localStorage as well, which belongs to the whole
 * browser and outlives closing it -- so the app told itself somebody was
 * signed in long after Firebase had any right to agree. Both halves now end
 * with the tab, which is what closing the browser should mean.
 */
const setStoredSessionItem = (key: string, value: string) => {
  if (key === SESSION_MARKER_KEY) memorySessionMarker = value;
  try {
    sessionStorage.setItem(key, value);
    // Any copy left over from the old behaviour is cleared, so an old flag
    // cannot outlive the sign-in it refers to.
    localStorage.removeItem(key);
  } catch {
    /* the in-page marker above is the fallback */
  }
};

/**
 * THE MARKER THAT SAYS "THIS BROWSER SESSION SIGNED IN".
 *
 * sessionStorage is destroyed by the browser when the last tab closes, and
 * nothing the app or Firebase does can make it outlive that. It therefore
 * answers exactly the question the sign-in rule needs answering -- is this
 * the same browser session that signed in, or a new one? -- which neither
 * Firebase's own persistence nor localStorage could, because both survive
 * closing the browser.
 */
const SESSION_MARKER_KEY = 'srms_auth_session';

/**
 * THE FALLBACK FOR A BROWSER THAT REFUSES STORAGE.
 *
 * Private browsing, and some locked-down corporate profiles, make
 * sessionStorage throw on read AND write. Without this, the marker could
 * never be written, the sign-in check would find nothing, and the person
 * would be signed out the instant they signed in -- unable to get into the
 * app at all, with a message about their profile that explained nothing.
 *
 * This variable lives in the page, so it does the same job for as long as
 * the page does. The behaviour is slightly weaker -- a REFRESH loses it and
 * signs them out, where a normal browser would keep them in -- but weaker in
 * the safe direction, and they can sign in, which is the point.
 */
let memorySessionMarker: string | null = null;

export const getSessionMarker = (): string | null => {
  try {
    const stored = sessionStorage.getItem(SESSION_MARKER_KEY);
    if (stored) return stored;
  } catch {
    /* storage refused -- fall through to the in-page marker */
  }
  return memorySessionMarker;
};

const removeStoredSessionItem = (key: string) => {
  if (key === SESSION_MARKER_KEY) memorySessionMarker = null;
  try {
    localStorage.removeItem(key);
    sessionStorage.removeItem(key);
  } catch {}
};

export const SRMSProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  // Authentication & Session State powered strictly by Firebase Authentication
  // Firebase restores the signed-in user asynchronously. Until that
  // finishes we must not render the login screen, or a simple page refresh
  // looks exactly like being logged out.
  const [authResolved, setAuthResolved] = useState<boolean>(false);

  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(() => {
    return !!auth.currentUser;
  });

  // Batch 105: the saved dynamic commission rules. One read per sign-in.
  // If the read fails, the list stays empty and every bank keeps its current commission.
  const [dynamicRules, setDynamicRules] = useState<DynamicCommissionRule[]>([]);
  useEffect(() => {
    if (!isAuthenticated) return;
    let cancelled = false;
    fetchDynamicRules()
      .then((list) => {
        if (!cancelled) setDynamicRules(list);
      })
      .catch(() => {
        /* keep the empty list */
      });
    return () => {
      cancelled = true;
    };
  }, [isAuthenticated]);

  // Users come from Firestore. Nothing is seeded locally, so a stale demo
  // row can never appear in the directory.
  const [users, setUsers] = useState<User[]>([]);
  const [deletedUsers, setDeletedUsers] = useState<User[]>([]);

  // Credential-free placeholder until Firebase Auth resolves the real
  // profile. It holds no password, hash or identity of its own.
  const [currentUser, setCurrentUser] = useState<User>(placeholderUser);

  const [deviceMode, setDeviceModeState] = useState<'web' | 'android'>('web');

  const setDeviceMode = (mode: 'web' | 'android') => {
    setDeviceModeState(mode);
    try {
      setStoredSessionItem('srms_device_mode', mode);
    } catch {}
  };

  const [accounts, setAccounts] = useState<Account[]>([]);

  // ------------------------------------------------------------------
  // ACCOUNT PERSISTENCE
  //
  // Account changes used to live only in React state. A recovery would
  // save, but the customer's new balance would not -- so after a refresh
  // the payment existed and the balance was back to its old value.
  //
  // `mutateAccounts` replaces a plain setAccounts for any change a user
  // makes: it updates the screen AND writes the rows that actually changed
  // to Firestore. Only rows whose object identity changed are written, so
  // editing one account does not rewrite all 412.
  //
  // Use setAccounts directly ONLY for data arriving FROM Firestore (the
  // live listeners) -- writing those back would cause an endless loop.
  // ------------------------------------------------------------------
  const accountsRef = useRef<Account[]>([]);

  /**
   * The signed-in user, readable from inside a callback without making that
   * callback depend on it.
   *
   * `mutateAccounts` is memoised with an empty dependency list on purpose --
   * it is the single funnel every account change passes through, and adding
   * `currentUser` to its dependencies would recreate it on every profile
   * refresh and re-run every effect that holds it. The ref gives it the
   * current role without that.
   */
  /**
   * What is currently loaded into `accounts`, and whether it is still coming.
   *
   * `accounts` used to be "everything, always". Now it is "whatever was asked
   * for", and every screen that shows a total needs to know which, or it will
   * report one branch's figures as the whole book's.
   */
  const [accountsScope, setAccountsScope] = useState<AccountsScope>({
    kind: 'none',
    label: '',
  });
  const [accountsLoading, setAccountsLoading] = useState(false);

  const currentUserRef = useRef<User>(currentUser);
  useEffect(() => {
    currentUserRef.current = currentUser;
  }, [currentUser]);

  useEffect(() => {
    accountsRef.current = accounts;
  }, [accounts]);

  /**
   * The receipts, readable from any callback without re-creating it.
   *
   * The import runs inside a state updater, where the `recoveries` captured
   * by the render may be several beats stale. Money must be recalculated
   * against the ledger as it is NOW, not as it was when the screen drew.
   */
  const recoveriesRef = useRef<RecoveryRecord[]>([]);
  /**
   * Has the collections listener answered yet, this session?
   *
   * The automatic balance repair refuses to run until it has. An empty
   * ledger and a ledger that has not arrived look identical from here, and
   * acting on the second would rewrite every account as though nothing had
   * ever been collected.
   */
  const recoveriesDeliveredRef = useRef(false);
  /** The automatic repair runs once per sign-in, not on every render. */
  const autoRepairDoneRef = useRef(false);

  /**
   * The photos, readable from a callback without re-creating it.
   *
   * The two-per-account cap runs immediately after a photo is saved and needs
   * to know what that account ALREADY holds. The `photos` captured by the
   * render may be several beats stale by then -- a photo taken a moment
   * earlier, or one the live listener has just delivered, would be missed and
   * the account would quietly keep three.
   */
  const photosRef = useRef<PhotoRecord[]>([]);

  /**
   * The last scope this person loaded, remembered between sign-ins.
   *
   * Reading nothing until a scope is chosen is what keeps the Firestore bill
   * down, but it also meant an administrator signed in to a dashboard of
   * zeros with nothing on screen explaining why -- which reads as "my data is
   * gone", not as "choose a scope". Remembering the last choice and loading it
   * again costs the same reads as opening the same screen by hand, and no
   * more.
   *
   * A whole-book scope is remembered but NOT reloaded automatically when the
   * book is large: four hundred accounts on sign-in is nothing, eighty
   * thousand on every sign-in for ten users is the bill this was written to
   * avoid. Above the threshold the screen offers the reload as a button
   * instead of doing it silently.
   */
  const SCOPE_MEMORY_PREFIX = 'srms.lastAccountsScope.';
  const AUTO_RELOAD_ALL_LIMIT = 2000;

  /**
   * A SMALL BOOK LOADS ITSELF.
   *
   * Choose-then-read was written for eighty thousand accounts across ten
   * users. Applied to a book of four hundred it costs almost nothing and
   * takes away something that matters much more: an administrator who has
   * just uploaded a spreadsheet, or just pressed refresh, expects to see
   * their accounts. They kept not seeing them.
   *
   * So the size is asked for first -- a count query, billed as ONE read
   * however many accounts it counts -- and if the whole book is under this
   * limit it is simply loaded. Above it, nothing is read until a scope is
   * chosen, exactly as before. At four hundred accounts this is 401 reads a
   * sign-in; at eighty thousand it is one read and a prompt.
   */
  const AUTO_LOAD_WHOLE_BOOK_LIMIT = 5000;

  type RememberedScope = {
    agentId?: string;
    agentName?: string;
    branch?: string;
    branches?: string[];
    bank?: string;
    all?: boolean;
    count?: number;
  };

  const rememberScope = (userId: string, scope: RememberedScope) => {
    if (!userId) return;
    try {
      window.localStorage.setItem(SCOPE_MEMORY_PREFIX + userId, JSON.stringify(scope));
    } catch {
      /* private browsing, or storage full -- remembering is a convenience */
    }
  };

  const recallScope = (userId: string): RememberedScope | null => {
    if (!userId) return null;
    try {
      const raw = window.localStorage.getItem(SCOPE_MEMORY_PREFIX + userId);
      return raw ? (JSON.parse(raw) as RememberedScope) : null;
    } catch {
      return null;
    }
  };

  /**
   * Read the accounts in a chosen scope, and nothing else.
   *
   * This is what an administrator calls after picking a bank, a branch or an
   * agent. Before this existed, opening Accounts read every account in the
   * database whether you were looking at one branch or all of them.
   */
  const loadAccountsForScope = async (scope: {
    agentId?: string;
    agentName?: string;
    branch?: string;
    branches?: string[];
    bank?: string;
  }): Promise<{ success: boolean; count: number; message: string }> => {
    setAccountsLoading(true);
    try {
      const list = await firebaseFirestoreService.fetchAccountsScoped(scope);
      accountsRef.current = list;
      setAccounts(list);

      const kind: AccountsScope['kind'] = scope.agentId
        ? 'agent'
        : scope.branches && scope.branches.length > 0
        ? 'branches'
        : scope.branch
        ? 'branch'
        : 'bank';
      const label =
        scope.agentName ||
        scope.agentId ||
        (scope.branches && scope.branches.length > 0
          ? scope.branches.join(', ')
          : scope.branch) ||
        scope.bank ||
        '—';
      setAccountsScope({ kind, label });
      rememberScope(currentUserRef.current?.id || '', { ...scope, count: list.length });

      return {
        success: true,
        count: list.length,
        message: `Loaded ${list.length} account${list.length === 1 ? '' : 's'} for ${label}.`,
      };
    } catch (err: any) {
      return {
        success: false,
        count: 0,
        message: `Could not load accounts: ${err?.message || 'unknown error'}`,
      };
    } finally {
      setAccountsLoading(false);
    }
  };

  /**
   * The whole book, read deliberately.
   *
   * Allocation and Bulk Delete genuinely need every account, and there is no
   * way around that. What changed is that it no longer happens by accident on
   * every sign-in -- something has to ask for it, and the screen says what it
   * is about to do first.
   */
  const loadAllAccounts = async (): Promise<{ success: boolean; count: number; message: string }> => {
    setAccountsLoading(true);
    try {
      const list = await firebaseFirestoreService.fetchAccountsScoped({});
      accountsRef.current = list;
      setAccounts(list);
      setAccountsScope({ kind: 'all', label: 'Every account' });
      rememberScope(currentUserRef.current?.id || '', { all: true, count: list.length });
      return { success: true, count: list.length, message: `Loaded all ${list.length} accounts.` };
    } catch (err: any) {
      return {
        success: false,
        count: 0,
        message: `Could not load accounts: ${err?.message || 'unknown error'}`,
      };
    } finally {
      setAccountsLoading(false);
    }
  };

  /**
   * Put the administrator back where they were.
   *
   * Runs once per sign-in, only when nothing is loaded yet, so it never fights
   * the screen's own choices and never reloads a second time. A remembered
   * whole-book scope that is too large to reload silently is offered to the
   * screen instead, through `pendingScopeReload`.
   */
  /**
   * Scope restore is off: an administrator now loads the whole book live on
   * sign-in, so there is nothing to restore and no offer to make.
   */
  const SCOPE_RESTORE_ENABLED = false;

  const [pendingScopeReload, setPendingScopeReload] = useState<{
    label: string;
    count: number;
  } | null>(null);
  const scopeRestoredForRef = useRef<string>('');
  const scopeRestoreInFlightRef = useRef(false);
  /** Bumped to try the restore again after a failure; three attempts, then stop. */
  const [scopeRetryTick, setScopeRetryTick] = useState(0);
  const scopeRetriesRef = useRef(0);
  /** The scope behind the offer on screen, so the button replays the right one. */
  const offeredScopeRef = useRef<RememberedScope | null>(null);

  const labelOfScope = (s: RememberedScope): string =>
    s.all
      ? 'Every account'
      : s.agentName ||
        s.agentId ||
        (s.branches && s.branches.length > 0 ? s.branches.join(', ') : s.branch) ||
        s.bank ||
        'your last view';

  useEffect(() => {
    const actor = currentUser;
    const uid = actor?.id || '';
    if (!uid || !isAuthenticated) return;

    /**
     * Wait for Firebase to finish signing in.
     *
     * The session flag comes back from storage the instant the page loads,
     * while Firebase takes a moment longer to restore its own sign-in. Asking
     * Firestore for accounts in that gap is refused, and the first version of
     * this marked the restore "done" before it knew whether it had worked --
     * so a refresh came back to an empty screen and never tried again.
     */
    if (!authResolved) return;

    /**
     * TURNED OFF. An administrator now loads the whole book live on sign-in
     * (see the accounts listener), so there is no scope to restore, nothing
     * to offer and no button to press. The machinery below is left in place
     * because "Load all accounts" and the allocation screen still use
     * `loadAllAccounts` and `loadAccountsForScope` directly.
     */
    if (!SCOPE_RESTORE_ENABLED) return;
    if (actor.role !== 'admin' && actor.role !== 'management') return;
    if (scopeRestoredForRef.current === uid) return;
    if (scopeRestoreInFlightRef.current) return;
    if (accountsRef.current.length > 0) return;

    const saved = recallScope(uid);

    scopeRestoreInFlightRef.current = true;
    // So the screen says "loading" rather than flashing "nothing loaded yet"
    // for the second this takes.
    setAccountsLoading(true);

    /**
     * How big is the book? One read answers it.
     *
     * Asking first is what lets a small book load itself and a large one stay
     * behind a choice, without either being guessed at. If the count itself
     * fails -- which on a refresh usually means sign-in has not finished --
     * the whole attempt is retried below rather than written off.
     */
    const attempt = (async (): Promise<{
      success: boolean;
      count: number;
      message: string;
    }> => {
      const total = await firebaseFirestoreService.countAccounts();

      // -1 means the count itself was refused -- almost always because
      // sign-in had not finished. That is a retry, not an answer.
      if (total < 0) {
        return { success: false, count: 0, message: 'Could not read the account count yet.' };
      }

      if (total <= AUTO_LOAD_WHOLE_BOOK_LIMIT) {
        return loadAllAccounts();
      }

      // Too big to open on sight. Go back to what was last looked at.
      if (!saved) {
        offeredScopeRef.current = { all: true, count: total };
        setPendingScopeReload({ label: 'Every account', count: total });
        return { success: true, count: 0, message: 'Waiting for a scope to be chosen.' };
      }

      if (saved.all && (saved.count || total) > AUTO_RELOAD_ALL_LIMIT) {
        offeredScopeRef.current = saved;
        setPendingScopeReload({ label: 'Every account', count: saved.count || total });
        return { success: true, count: 0, message: 'Offered the last whole-book view.' };
      }

      return saved.all
        ? loadAllAccounts()
        : loadAccountsForScope({
            agentId: saved.agentId,
            agentName: saved.agentName,
            branch: saved.branch,
            branches: saved.branches,
            bank: saved.bank,
          });
    })();

    void attempt
      .then((res) => {
        if (res?.success) {
          scopeRestoredForRef.current = uid;
          return;
        }
        console.warn('Scope restore did not succeed:', res?.message);
        if (scopeRetriesRef.current < 3) {
          scopeRetriesRef.current += 1;
          window.setTimeout(() => setScopeRetryTick((t) => t + 1), 1500);
        } else {
          // Out of attempts: stop trying and let the screen offer the button.
          scopeRestoredForRef.current = uid;
          const fallback = saved || { all: true, count: 0 };
          offeredScopeRef.current = fallback;
          setPendingScopeReload({
            label: labelOfScope(fallback),
            count: fallback.count || 0,
          });
        }
      })
      .catch((err) => console.warn('Scope restore notice:', err?.message || err))
      .finally(() => {
        scopeRestoreInFlightRef.current = false;
        setAccountsLoading(false);
      });
  }, [currentUser, isAuthenticated, authResolved, scopeRetryTick]);

  /** The screen calls this when the administrator accepts the offer above. */
  const resumeLastScope = async (): Promise<{ success: boolean; count: number; message: string }> => {
    const saved = offeredScopeRef.current;
    setPendingScopeReload(null);
    scopeRetriesRef.current = 0;
    if (!saved || saved.all) return loadAllAccounts();
    return loadAccountsForScope({
      agentId: saved.agentId,
      agentName: saved.agentName,
      branch: saved.branch,
      branches: saved.branches,
      bank: saved.bank,
    });
  };

  /**
   * How many accounts match, for one read.
   *
   * A count query is billed as a single read however many records it counts.
   * This is what makes "RA-0048: 1,240 accounts" cost one read instead of
   * 1,240 -- which is the whole of your point 22.
   */
  const countAccountsFor = (scope?: {
    agentId?: string;
    branch?: string;
    bank?: string;
  }): Promise<number> => firebaseFirestoreService.countAccounts(scope);

  const countCollectionFor = (collectionName: string, agentId: string): Promise<number> =>
    firebaseFirestoreService.countCollectionForAgent(collectionName, agentId);

  /**
   * Menu-wise permanent delete of one agent's records -- your line 57.
   *
   * Deleting an agent's ACCOUNTS deliberately leaves their calling logs, PTPs,
   * visits, photos and voice notes in place, because that history is often the
   * reason the accounts were deleted. This is how they are cleared afterwards,
   * one menu at a time, when they are costing storage.
   *
   * Administrator only, permanent, and batched -- one call per 450 records
   * rather than one per record, the same fix bulk account delete needed.
   */
  const deleteAgentRecordsIn = async (
    collectionName: 'recoveries' | 'followups' | 'ptps' | 'visits' | 'photos' | 'voicenotes',
    agentId: string,
    agentName?: string
  ): Promise<{ success: boolean; count: number; message: string }> => {
    if (currentUser.role !== 'admin') {
      return {
        success: false,
        count: 0,
        message: 'Only an administrator can delete records. This cannot be undone.',
      };
    }
    if (!agentId) {
      return { success: false, count: 0, message: 'No agent chosen.' };
    }

    const pools: Record<string, any[]> = {
      recoveries,
      followups,
      ptps,
      visits,
      photos,
      voicenotes: voiceNotes,
    };
    const idLower = agentId.trim().toLowerCase();
    const nameLower = (agentName || '').trim().toLowerCase();

    /** Reported back to the screen so a bulk delete says what it really did. */
    let bulkDeleteSideEffects: {
      commissionsRemoved: number;
      accountsAffected: number;
      accountsUpdated: number;
    } | null = null;

    const doomed = (pools[collectionName] || []).filter((r: any) => {
      const rid = String(r?.agentId || '').trim().toLowerCase();
      const rname = String(r?.agentName || '').trim().toLowerCase();
      // Matched by id OR name, because older records carry only the name.
      return (!!rid && rid === idLower) || (!!nameLower && rname === nameLower);
    });

    if (doomed.length === 0) {
      return { success: true, count: 0, message: 'Nothing to delete for that agent here.' };
    }

    // Every key a record could be filed under. Firestore treats deleting a key
    // that does not exist as success, which is what makes sending them all
    // together safe -- and is also why a delete that hit the wrong key used to
    // look like it had worked.
    const keys: string[] = [];
    doomed.forEach((r: any) => {
      [r.__docId, r.id, r.recoveryId, r.visitId, r.voiceNoteId, r.photoId].forEach((k) => {
        if (typeof k === 'string' && k) keys.push(k);
      });
    });

    try {
      await firebaseFirestoreService.batchDeleteDocs(collectionName, keys);
    } catch (err: any) {
      console.error(`[Firestore] ${collectionName} bulk delete FAILED:`, err);
      return {
        success: false,
        count: 0,
        message:
          err?.code === 'permission-denied'
            ? 'The database refused the delete. Nothing was removed.'
            : err?.message || 'Could not delete these records. Nothing was removed.',
      };
    }

    // Drop them locally too. The listeners are authoritative now, so they would
    // catch up anyway -- this just avoids the rows sitting there until they do.
    const doomedKeys = new Set(
      doomed.map((r: any) => r.__docId || r.id).filter(Boolean)
    );
    const survives = (r: any) => !doomedKeys.has(r.__docId || r.id);

    /**
     * DELETING COLLECTIONS IN BULK MUST DO EVERYTHING DELETING ONE DOES.
     *
     * THE BUG THIS FIXES -- and it is the one that has been costing money.
     *
     * This routine removed the collection documents and then stopped. It did
     * not remove the commissions earned on them, and it did not put the
     * balances back. `deleteRecovery` -- the one-at-a-time delete -- has done
     * both for a long time; this path never did, and it is the path an
     * administrator actually uses, because the log screens offer a bulk
     * delete per agent and the administrator's portal has no per-record
     * delete at all.
     *
     * So every symptom fitted together: the collections vanished, the
     * commission ledger kept 21 records for collections that no longer
     * existed and went on overstating the payout, and the loan balances never
     * came back. One routine, three consequences, and nothing on screen said
     * a word about any of it.
     *
     * Both halves are done here now, in the same order and by the same rules
     * as the single delete: commissions first, then every affected account
     * recalculated from the receipts that REMAIN -- never adjusted, always
     * recalculated, so running it twice cannot double anything.
     */
    if (collectionName === 'recoveries') {
      // ---- the commissions earned on the deleted collections ----
      const goneRecoveryKeys = new Set<string>();
      doomed.forEach((r: any) => {
        [r.__docId, r.id, r.recoveryId].forEach((k) => {
          if (typeof k === 'string' && k) goneRecoveryKeys.add(k);
        });
      });

      const doomedCommissions = commissions.filter((c) =>
        goneRecoveryKeys.has((c.recoveryId || '').trim())
      );

      for (const c of doomedCommissions) {
        try {
          await firebaseFirestoreService.deleteCommission((c as any).__docId || c.id);
        } catch (err) {
          console.warn('[Bulk delete] Commission removal notice:', err);
        }
      }

      if (doomedCommissions.length > 0) {
        const goneCommissionIds = new Set(
          doomedCommissions.map((c) => (c as any).__docId || c.id)
        );
        setCommissions((prev) =>
          prev.filter((c) => !goneCommissionIds.has((c as any).__docId || c.id))
        );
      }

      // ---- the balances ----
      const affectedAccountIds = Array.from(
        new Set(doomed.map((r: any) => r.accountId).filter(Boolean))
      );

      // What is left in the ledger once these are gone.
      const remaining = recoveriesRef.current.filter(survives);

      mutateAccounts((prev) =>
        prev.map((a) => {
          if (!affectedAccountIds.includes(a.accountId)) return a;

          const mine = remaining.filter(
            (r) => r.accountId === a.accountId && !(r as any).isReversed
          );
          const money = recalcAccountMoney(a, mine, otsWaiverOf(a));

          // Same rule as the single delete: a closure justified by money
          // cannot survive the money being gone, but the agreed settlement
          // does survive.
          const paymentBackedClosure =
            a.accountStatus === 'Closed as per OTS' ||
            a.accountStatus === 'Regular Close' ||
            a.otsStatus === 'OTS Settled';

          if (paymentBackedClosure && mine.length === 0) {
            return {
              ...a,
              ...money,
              accountStatus: 'Active' as const,
              otsStatus:
                a.otsStatus === 'OTS Settled' ||
                a.otsStatus === 'OTS Settled – Customer Account Closed'
                  ? 'OTS Confirmed'
                  : a.otsStatus,
              otsPaidAmount: 0,
              closureType: undefined,
              closureDate: undefined,
            };
          }

          return { ...a, ...money };
        })
      );

      // How many of the affected accounts were actually in memory to update.
      // An administrator can delete from the log screens without having
      // loaded the accounts, and saying so is better than a silent no-op.
      const loadedCount = affectedAccountIds.filter((id) =>
        accountsRef.current.some((a) => a.accountId === id)
      ).length;

      bulkDeleteSideEffects = {
        commissionsRemoved: doomedCommissions.length,
        accountsAffected: affectedAccountIds.length,
        accountsUpdated: loadedCount,
      };
    }

    if (collectionName === 'recoveries') setRecoveries((prev) => prev.filter(survives));
    if (collectionName === 'followups') setFollowups((prev) => prev.filter(survives));
    if (collectionName === 'ptps') setPtps((prev) => prev.filter(survives));
    if (collectionName === 'visits') setVisits((prev) => prev.filter(survives));
    if (collectionName === 'photos') setPhotos((prev) => prev.filter(survives));
    if (collectionName === 'voicenotes') setVoiceNotes((prev) => prev.filter(survives));

    logActivity(
      currentUser,
      'SETTINGS_UPDATED',
      `Permanently deleted ${doomed.length} ${collectionName} record(s) for agent ${agentName || agentId}.`
    );

    /**
     * SAY WHAT ELSE WAS DONE.
     *
     * "Permanently deleted 14 record(s)" was the whole message, so a delete
     * that also removed commissions and restored balances looked exactly like
     * one that quietly did neither -- which is what it used to be.
     */
    if (bulkDeleteSideEffects) {
      const { commissionsRemoved, accountsAffected, accountsUpdated } = bulkDeleteSideEffects;

      const parts = [`Permanently deleted ${doomed.length} collection(s).`];

      parts.push(
        commissionsRemoved > 0
          ? `${commissionsRemoved} commission record(s) removed with them.`
          : 'No commissions were attached to them.'
      );

      if (accountsUpdated > 0) {
        parts.push(`${accountsUpdated} account balance(s) recalculated.`);
      }

      if (accountsUpdated < accountsAffected) {
        parts.push(
          `\n\n${accountsAffected - accountsUpdated} of the ${accountsAffected} affected ` +
            `account(s) are not loaded in this session, so their balances were NOT updated. ` +
            `Open Loan Accounts, load them, then run Settings → Check and repair account ` +
            `balances.`
        );
      }

      return { success: true, count: doomed.length, message: parts.join(' ') };
    }

    return {
      success: true,
      count: doomed.length,
      message: `Permanently deleted ${doomed.length} record(s).`,
    };
  };

  const mutateAccounts = useCallback(
    (
      updaterOrValue: Account[] | ((prev: Account[]) => Account[]),
      options?: { persist?: boolean; allowViewOnly?: boolean }
    ) => {
      // VIEW-ONLY roles stop here.
      //
      // Branch Manager and Recovery Department are view only. Every account
      // change in the app funnels through this one function, so refusing here
      // covers every screen at once -- including any screen added later that
      // forgets to check. Hiding buttons is not a restriction; this is.
      //
      // The one thing a Branch Manager may still do is approve an OTS offer.
      // That right was given deliberately, because an agent must not approve
      // their own offer and somebody at the branch has to. OTS approval does
      // not come through here; `confirmOTS` carries its own check.
      const actor = currentUserRef.current;
      if (options?.persist !== false && !options?.allowViewOnly && isViewOnly(actor)) {
        const why = writeRefusal(actor, 'change account data');
        console.warn('Write refused:', why);
        try {
          window.alert(why || 'Your role is view only.');
        } catch {
          /* no window in a test environment */
        }
        return;
      }

      const prev = accountsRef.current;
      const next =
        typeof updaterOrValue === 'function'
          ? (updaterOrValue as (p: Account[]) => Account[])(prev)
          : updaterOrValue;

      // Keep the ref in step immediately so several mutations in one event
      // handler build on each other instead of on stale data.
      accountsRef.current = next;
      setAccounts(next);

      if (options?.persist === false) return;

      const before = new Map(prev.map((a) => [a.accountId || a.id, a]));
      const changed = next.filter((a) => before.get(a.accountId || a.id) !== a);

      if (changed.length === 0) return;

      void (async () => {
        try {
          if (changed.length === 1) {
            await firebaseFirestoreService.saveAccount(changed[0]);
          } else {
            await firebaseFirestoreService.batchSaveAccounts(changed);
          }
        } catch (err) {
          console.error(
            `[Firestore] Failed to save ${changed.length} account change(s):`,
            err
          );
        }
      })();
    },
    []
  );

  const [allocations, setAllocations] = useState<AccountAllocation[]>([]);
  const [allocationHistories, setAllocationHistories] = useState<AllocationHistory[]>([]);
  const [followups, setFollowups] = useState<FollowUp[]>([]);
  const [ptps, setPtps] = useState<PTPRecord[]>([]);
  const [recoveries, setRecoveries] = useState<RecoveryRecord[]>([]);
  useEffect(() => {
    recoveriesRef.current = recoveries;
  }, [recoveries]);
  const [visits, setVisits] = useState<FieldVisit[]>([]);
  const [photos, setPhotos] = useState<PhotoRecord[]>([]);
  useEffect(() => {
    photosRef.current = photos;
  }, [photos]);
  const [voiceNotes, setVoiceNotes] = useState<VoiceNoteRecord[]>([]);
  const [documents, setDocuments] = useState<DocumentRecord[]>([]);
  const [activityLogs, setActivityLogs] = useState<ActivityLog[]>(initialActivityLogs);
  const [notifications, setNotifications] = useState<NotificationItem[]>(initialNotifications);
  const [commissions, setCommissions] = useState<CommissionRecord[]>([]);
  const [messages, setMessages] = useState<StaffMessage[]>([]);
  const [commissionSettings, setCommissionSettings] = useState<CommissionSettings>(() => {
    // Start from whatever was last saved on this machine, so the rates are
    // correct on the first render instead of briefly showing "not set".
    try {
      const raw = localStorage.getItem('srms_commission_settings');
      if (raw) return { ...initialCommissionSettings, ...JSON.parse(raw) };
    } catch {
      /* ignore */
    }
    return initialCommissionSettings;
  });
  const commissionSettingsRef = useRef<CommissionSettings>(commissionSettings);
  useEffect(() => {
    commissionSettingsRef.current = commissionSettings;
  }, [commissionSettings]);
  const [branches, setBranches] = useState<Branch[]>([]);
  const [areas, setAreas] = useState<Area[]>([]);
  const [zones, setZones] = useState<Zone[]>([]);
  const [banks, setBanks] = useState<Bank[]>([]);
  const [recoveryDepartments, setRecoveryDepartments] = useState<RecoveryDepartment[]>([]);
  const [commissionRules, setCommissionRules] = useState<CommissionRule[]>(initialCommissionRules);
  const [userCommissionAssignments, setUserCommissionAssignments] = useState<UserCommissionAssignment[]>([]);
  const [commissionAuditLogs, setCommissionAuditLogs] = useState<CommissionAuditLog[]>(initialCommissionAuditLogs);
  const [archivedAccounts, setArchivedAccounts] = useState<Account[]>([]);
  const [serverStats, setServerStats] = useState<any>(null);

  // OTS Scheme Authorized Roles & WhatsApp Offer Logs
  const defaultOTSAuthorizedRoles: UserRole[] = [
    'admin',
    'recovery_department',
    'branch_manager',
    'management',
    'coordinator',
    'agent',
  ];
  const [otsAuthorizedRoles, setOtsAuthorizedRoles] = useState<UserRole[]>(defaultOTSAuthorizedRoles);
  const [whatsAppLogs, setWhatsAppLogs] = useState<WhatsAppOfferLog[]>([]);

  /**
   * Which of the signed-in person's banks they are currently looking at.
   * Empty means all of them. Only meaningful for a user covering more than
   * one bank; the selector is hidden otherwise.
   */
  const [activeBank, setActiveBank] = useState<string>('');

  // Firebase Authentication State & User Profile Listener
  useEffect(() => {
    let profileUnsub: (() => void) | null = null;

    const authUnsub = firebaseAuthService.onAuthStateChanged(async (fbUser) => {
      if (profileUnsub) {
        profileUnsub();
        profileUnsub = null;
      }

      if (!fbUser) {
        setIsAuthenticated(false);
        setAuthResolved(true);
        return;
      }

      /**
       * CLOSING THE BROWSER MUST SIGN YOU OUT. THIS IS WHAT ENFORCES IT.
       *
       * Firebase was already told to keep sign-ins for the life of the TAB
       * rather than the browser profile. That setting only governs sign-ins
       * made AFTER it runs, and it cannot reach a credential Firebase saved
       * earlier under the old behaviour. Those older credentials sit in the
       * browser's own storage and are restored on every launch, for as long
       * as they remain valid -- which is why the app kept coming back
       * already signed in however many times it was reported fixed.
       *
       * So the browser is no longer asked. The app keeps its own marker in
       * sessionStorage, which the browser itself destroys when the last tab
       * closes, and nothing can outlive. The two cases then separate
       * cleanly:
       *
       *   REFRESH, or a new tab in the same session -- the marker is still
       *   there, the person stays signed in. This is what you asked for.
       *
       *   BROWSER CLOSED AND REOPENED -- the marker is gone. Whatever
       *   Firebase restored is rejected and the person is signed out,
       *   whether that credential came from today or from before any of
       *   this was changed.
       *
       * This is the security half of the local cache: a field agent on a
       * shared phone, and the next person to open the browser.
       */
      const hasLiveSession = !!getSessionMarker();
      if (!hasLiveSession) {
        console.info('[Auth] Restored sign-in has no session marker — signing out.');
        try {
          await firebaseAuthService.logout();
        } catch {
          /* the state below is what the screen reads either way */
        }
        setIsAuthenticated(false);
        setAuthResolved(true);
        return;
      }

      // Show the app as soon as Firebase confirms a signed-in user. The
      // profile below fills in the details; waiting for it before marking
      // auth resolved is what made a refresh look like a logout.
      setIsAuthenticated(true);
      setAuthResolved(true);

      // Refresh the ID token so custom role claims are present before the
      // first Firestore read; otherwise the rules evaluate a stale role.
      try {
        await fbUser.getIdToken(true);
      } catch {
        /* non-fatal */
      }

      // A failed read must NEVER be treated as "this user has no profile".
      // Doing so signed people out every time they refreshed the page.
      const profileRes = await firebaseAuthService.fetchUserProfileSafe(fbUser.uid);

      if (!profileRes.ok) {
        console.warn(
          '[Auth] Could not read profile on refresh; keeping the session open.',
          profileRes.error
        );
        // Stay signed in. The live profile listener below and the normal
        // Firestore retries will fill this in once the database responds.
        setIsAuthenticated(true);
        return;
      }

      const userProfile = profileRes.user;
      if (userProfile) {
        if (userProfile.active === false || userProfile.isDeleted || userProfile.status === 'DELETED') {
          console.warn('[Firebase Auth] User profile is deactivated or deleted. Logging out.');
          await firebaseAuthService.logout();
          setIsAuthenticated(false);
          return;
        }

        /**
         * An expired plan closes a session that is already open, too.
         *
         * Refusing it only at the sign-in screen would mean a browser left
         * signed in kept working indefinitely past the end date -- the one
         * case where enforcing it actually matters, since nobody signs in
         * again to be refused.
         */
        const restoredPlan = planStatusOf(userProfile);
        if (restoredPlan.status === 'expired' && userProfile.role !== 'admin') {
          console.warn('[Firebase Auth] Plan expired. Signing out.');
          await firebaseAuthService.logout();
          setIsAuthenticated(false);
          return;
        }

        setCurrentUser(userProfile);
        setIsAuthenticated(true);
        // Agents open the full web portal by default. They can still switch
        // to the Android layout from the header.
        setDeviceMode('web');

        // Live Profile Listener for immediate deactivation enforcement
        profileUnsub = firebaseAuthService.subscribeUserProfile(
          userProfile.id || fbUser.uid,
          (updated) => {
            if (updated) setCurrentUser(updated);
          },
          () => {
            setIsAuthenticated(false);
          }
        );
      } else {
        // No profile document. This is only legitimate for the bootstrap
        // administrator on a fresh project. A staff account reaching here
        // means its profile is missing, so refuse rather than silently
        // minting an unmanaged agent record.
        const isRootAdmin = fbUser.email?.toLowerCase() === 'ashish.kharad2@gmail.com';

        if (!isRootAdmin) {
          console.warn('[Auth] Signed-in user has no profile document. Signing out.');
          await firebaseAuthService.logout();
          setIsAuthenticated(false);
          return;
        }

        const fallbackProfile: User = {
          id: fbUser.uid,
          username: 'admin',
          loginId: 'admin',
          name: fbUser.displayName || 'Ashish Kharad',
          email: fbUser.email || '',
          role: 'admin',
          branch: 'Head Office',
          area: 'Corporate',
          zone: 'Central',
          agentId: 'ADM-001',
          mobile: '',
          joiningDate: todayLocal(),
          tokenVersion: 1,
          active: true,
          status: 'ACTIVE',
        } as User;

        try {
          await firebaseFirestoreService.saveUser(fallbackProfile);
        } catch (e) {
          console.warn('[Auth] Could not persist bootstrap admin profile:', e);
        }
        setCurrentUser(fallbackProfile);
        setIsAuthenticated(true);
      }
    });

    return () => {
      authUnsub();
      if (profileUnsub) profileUnsub();
    };
  }, []);

  const [activeVisit, setActiveVisit] = useState<FieldVisit | null>(null);
  const [isSyncingState, setIsSyncingState] = useState(false);
  const [autoSyncEnabled, setAutoSyncEnabled] = useState<boolean>(() => {
    try {
      const saved = localStorage.getItem('srms_auto_sync_enabled');
      return saved !== null ? saved === 'true' : true; // Auto-sync enabled by default for real-time live connection
    } catch {
      return true;
    }
  });
  const [lastAutoSyncTime, setLastAutoSyncTime] = useState<string | null>(null);
  
  // ⚡ Cloud Firestore Service Optimization:
  // Sub-50ms speed route for active data & resilient real-time snapshot synchronization
  const [firestoreSpeedStats, setFirestoreSpeedStats] = useState<FirestoreSpeedStats>(() => firebaseFirestoreService.getStats());
  const [sheetQueueStatus] = useState<SheetQueueStatus>({
    pendingCount: 0,
    isFlushing: false,
    lastError: null,
    lastSuccessAt: new Date().toISOString(),
  });

  useEffect(() => {
    const unsubStats = firebaseFirestoreService.subscribeStats(setFirestoreSpeedStats);
    return () => {
      unsubStats();
    };
  }, []);

  /**
   * WHO the listeners below are for.
   *
   * THE BUG THIS FIXES: the effect that sets up every live listener ran once,
   * when the page first loaded -- which is BEFORE anybody has signed in. At
   * that moment there is no agent, so the branch that subscribes an agent to
   * their own allotment was skipped, and because the effect never ran again it
   * was never set up at all. An agent who signed in saw zero accounts, for
   * ever, while the administrator's screen correctly showed those same
   * accounts as allocated to them. Refreshing the page sometimes fixed it --
   * which is exactly what you would expect, because on a refresh the signed-in
   * agent is already known when the page loads.
   *
   * The listeners are now rebuilt whenever the person they are FOR changes.
   * This is a narrow key rather than the whole user record on purpose: a
   * profile refresh that changes a phone number must not tear down and rebuild
   * seventeen database listeners.
   */
  const actorSubscriptionKey = [
    isAuthenticated ? '1' : '0',
    currentUser?.role || '',
    currentUser?.agentId || '',
    currentUser?.id || '',
    (currentUser?.name || '').trim().toLowerCase(),
  ].join('|');

  // Real-time Firestore Live Routing Listeners
  useEffect(() => {
    let isSubscribed = true;

    // WHO READS WHAT.
    //
    // This used to be one listener over the whole `accounts` collection, for
    // every signed-in user. At 400 accounts it is free; at 80,000 it is 80,000
    // reads every time anybody signs in, on a database that bills reads. An
    // agent has never needed anything but their own allotment.
    //
    //   agent / coordinator  -> a live listener scoped to their allotment
    //   branch manager       -> their branch, fetched once (they are view only)
    //   recovery department  -> their bank/zone, fetched once
    //   admin / management   -> NOTHING until a scope is chosen on screen
    //
    // The administrator case is your point 1: choose the user or branch first,
    // then read. Allocation and Bulk Delete still need the whole book, and say
    // so before they load it.
    const actorRole = currentUser?.role;
    const actorIsAgentLike = actorRole === 'agent' || actorRole === 'coordinator';

    const applyAccountSnapshot = (fsAccounts: Account[]) => {
        setAccounts((prev) => {
          // The database decides which accounts exist.
          //
          // This used to start from the local list and only ADD to it, so an
          // account deleted here -- or by somebody on another device -- never
          // disappeared from the screen. It looked as though the delete had
          // failed and the record had "come back".
          //
          // Now the snapshot is the list. Local-only fields are still kept by
          // merging the local copy underneath the database copy.
          const localById = new Map<string, Account>(
            prev.map((a) => [a.accountId, a])
          );
          // OTS state is worked out from the amounts on every load (no reads).
          return applyOtsSettlementState(
            fsAccounts.map((a) => {
              const existing = localById.get(a.accountId);
              return existing ? Object.assign({}, existing, a) : a;
            })
          );
        });
    };

    let unsubAccounts: () => void = () => {};

    if (actorIsAgentLike) {
      const idKeys = [currentUser.agentId || '', currentUser.id || ''].filter(Boolean);
      const names = [currentUser.name || ''].filter(Boolean);

      /**
       * NEVER ask for "the accounts belonging to nobody".
       *
       * These listeners are rebuilt whenever the signed-in person changes,
       * which is what finally got an agent their accounts. But a profile that
       * is still loading can appear for an instant with no ID and no name --
       * and asking for an agent with no identity returns nothing, which would
       * wipe the list the agent is looking at and leave it empty until
       * something else happened to refresh it.
       *
       * With no identity there is nothing to ask, so nothing is asked. The
       * previous answer stays on screen, and the moment the profile arrives
       * this runs again properly.
       */
      if (idKeys.length === 0 && names.length === 0) {
        console.warn(
          '[Accounts] Agent profile has no ID or name yet — keeping the current list until it loads.'
        );
      } else {
        unsubAccounts = firebaseFirestoreService.subscribeAccountsForAgent(
          idKeys,
          names,
          (fsAccounts) => {
            if (!isSubscribed) return;
            if (fsAccounts) {
              applyAccountSnapshot(fsAccounts);
              setAccountsScope({ kind: 'agent', label: currentUser.name || 'My accounts' });
            }
          }
        );
      }
    } else if (actorRole === 'branch_manager' || actorRole === 'recovery_department') {
      const branchList = scopeBranchesOf(currentUser);
      const bankList = scopeBanksOf(currentUser);
      const ownZone = (currentUser.zone || '').trim();

      /**
       * NARROWEST FIRST -- WHICH IS BOTH THE CORRECT SCOPE AND THE CHEAPEST.
       *
       * THE BUG THIS FIXES. These two roles are defined by a bank AND a zone,
       * with branches optional. The old code asked only two questions: are
       * there branches, or is there a bank? A department user with no
       * branches set therefore loaded EVERY account in their bank -- every
       * zone, every branch, roughly a thousand accounts -- on every single
       * sign-in. It showed them accounts that are not theirs to see, and it
       * was the largest read in the application.
       *
       * The zone was recorded on their profile the whole time. Nothing could
       * ask for it, so nothing did.
       *
       * The order below is deliberate: each step is both a tighter scope and
       * a smaller read, so the most correct answer is also the cheapest one.
       * The bank remains only as the last resort, for a profile that somehow
       * has neither branches nor a zone -- and it is a scope they should not
       * be left in, so it says so on screen.
       */
      /**
       * The bank goes with every one of these, not only the last.
       *
       * Branch and zone names are not unique across banks -- Bidkin belongs
       * to Bank of Maharashtra AND to Central Bank of India. Asking for
       * "branch Bidkin" with no bank returned both banks' accounts, so a
       * branch manager at one saw the other's borrowers. The bank is now
       * carried alongside the narrower scope and used to drop what is not
       * theirs.
       */
      const scopeRequest = branchList.length > 0
        ? { branches: branchList, bank: bankList[0] }
        : ownZone
        ? { zone: ownZone, bank: bankList[0] }
        : { bank: bankList[0] };

      const scopeKind: AccountsScope['kind'] =
        branchList.length > 0 ? 'branches' : ownZone ? 'zone' : 'bank';

      const scopeLabel =
        branchList.length > 0
          ? branchList.join(', ')
          : ownZone
          ? `${ownZone} zone`
          : bankList[0] || '—';

      setAccountsLoading(true);
      void firebaseFirestoreService
        .fetchAccountsScoped(scopeRequest)
        .then((list) => {
          if (!isSubscribed) return;
          applyAccountSnapshot(list);
          setAccountsScope({ kind: scopeKind, label: scopeLabel });
        })
        .catch((err) => console.warn('Scoped account load notice:', err?.message || err))
        .finally(() => {
          if (isSubscribed) setAccountsLoading(false);
        });
    }
    else {
      /**
       * AN ADMINISTRATOR READS THE WHOLE BOOK, LIVE, ON SIGN-IN.
       *
       * WHAT THIS REVERSES, AND WHY. Accounts were read a scope at a time --
       * choose a bank, a branch or an agent, and only those accounts were
       * fetched -- to keep the Firestore bill down. It was a one-off fetch,
       * so what was on screen was a snapshot taken at the moment of loading,
       * not the live record.
       *
       * Ashish: "I want Firestore data, not browser data ... I don't want a
       * button, why a button if I want real-time data. Without account data
       * our website is nothing, then no need for selection."
       *
       * He is right, and the cost argument turned out to be weaker than it
       * looked. A listener backed by the persistent cache does not re-read
       * the book on every refresh: it resumes from the cache and is charged
       * only for the documents that have CHANGED since. The 5,897 accounts
       * are paid for once, not once per sign-in, and his own counter shows
       * it -- 60 billed against 166 served free.
       *
       * So: a live listener on the whole collection, the same one every
       * other role already has on its own slice. No scope to choose, no
       * banner, no button, and a figure on screen that changes when an agent
       * records a collection rather than when somebody remembers to reload.
       */
      setAccountsLoading(true);
      unsubAccounts = firebaseFirestoreService.subscribeAccounts((fsAccounts) => {
        if (!isSubscribed) return;
        if (fsAccounts) {
          applyAccountSnapshot(fsAccounts);
          accountsRef.current = fsAccounts;
          setAccountsScope({ kind: 'all', label: 'Every account' });
          setAccountsLoading(false);
        }
      });
    }

    /**
     * Recoveries, PTPs, follow-ups, visits, photos and voice notes.
     *
     * For an AGENT these are now scoped to that agent. All six collections
     * used to be read in full by everybody: an agent with 40 accounts was
     * reading every recovery, every PTP and every photo in the business, and
     * throwing all of it away in the browser a moment later. Firestore bills
     * the read either way.
     *
     * Administrators and management still read the whole collection, because
     * they legitimately need cross-agent totals and these collections are
     * proportional to activity rather than to the size of the loan book.
     */
    /**
     * THE DATABASE DECIDES WHAT EXISTS.
     *
     * Each of these listeners used to MERGE its snapshot into what was already
     * on screen: it could add a record and it could update one, but it could
     * never remove one. So a recovery deleted here -- or by an agent on their
     * own device -- disappeared for a moment and came straight back on the next
     * sync, because the merge had no way of noticing it was gone.
     *
     * That is your point 20. The delete was working and reaching Firestore all
     * along; the admin's screen simply could not represent a deletion. It is
     * the same fault the account list had before it was made authoritative.
     *
     * The overlay keeps any local field not yet written back (an optimistic
     * edit made a moment ago), but ONLY for records the database still has.
     */
    const authoritative = <T,>(
      incoming: T[],
      previous: T[],
      keyOf: (item: T) => string
    ): T[] => {
      const localByKey = new Map<string, T>();
      previous.forEach((item) => {
        const k = keyOf(item);
        if (k) localByKey.set(k, item);
      });
      return incoming.map((item) => {
        const existing = localByKey.get(keyOf(item));
        return existing ? Object.assign({}, existing, item) : item;
      });
    };

    const logScopeKeys = [currentUser.agentId || '', currentUser.id || ''].filter(Boolean);
    const logScopeNames = [currentUser.name || ''].filter(Boolean);

    const subscribeLog = <T,>(
      collectionName: string,
      whole: (cb: (items: T[]) => void) => () => void,
      cb: (items: T[]) => void
    ): (() => void) =>
      actorIsAgentLike
        ? firebaseFirestoreService.subscribeCollectionForAgent<T>(
            collectionName,
            logScopeKeys,
            logScopeNames,
            cb
          )
        : whole(cb);

    const unsubRecoveries = subscribeLog<RecoveryRecord>('recoveries', (cb) => firebaseFirestoreService.subscribeRecoveries(cb), (fsRecs) => {
      if (!isSubscribed) return;
      if (fsRecs) {
        // The automatic repair below will not run until this has happened at
        // least once. Repairing an account against a collection ledger that
        // has not arrived yet would hand every account its original balance
        // back as though nothing had ever been collected.
        recoveriesDeliveredRef.current = true;
        setRecoveries((prev) =>
          authoritative(fsRecs, prev, (r) => r.id || r.recoveryId)
        );
      }
    });

    const unsubPtps = subscribeLog<PTPRecord>('ptps', (cb) => firebaseFirestoreService.subscribePTPs(cb), (fsPtps) => {
      if (!isSubscribed) return;
      if (fsPtps) {
        setPtps((prev) => authoritative(fsPtps, prev, (p) => p.id));
      }
    });

    const unsubFollowups = subscribeLog<FollowUp>('followups', (cb) => firebaseFirestoreService.subscribeFollowUps(cb), (fsFlws) => {
      if (!isSubscribed) return;
      if (fsFlws) {
        setFollowups((prev) => authoritative(fsFlws, prev, (f) => f.id));
      }
    });

    const unsubVisits = subscribeLog<FieldVisit>('visits', (cb) => firebaseFirestoreService.subscribeVisits(cb), (fsVisits) => {
      if (!isSubscribed) return;
      if (fsVisits) {
        setVisits((prev) =>
          authoritative(fsVisits, prev, (v) => v.id || v.visitId || '')
        );
      }
    });

    const unsubPhotos = subscribeLog<PhotoRecord>('photos', (cb) => firebaseFirestoreService.subscribePhotos(cb), (fsPhotos) => {
      if (!isSubscribed) return;
      if (fsPhotos) {
        // Firestore is the source of truth for photos. Merging with the
        // previous list kept deleted photos on screen, because a merge can
        // only add and update -- it can never remove.
        setPhotos(fsPhotos);
      }
    });

    const unsubVoiceNotes = subscribeLog<VoiceNoteRecord>('voicenotes', (cb) => firebaseFirestoreService.subscribeVoiceNotes(cb), (fsVns) => {
      if (!isSubscribed) return;
      if (fsVns) {
        setVoiceNotes((prev) =>
          authoritative(fsVns, prev, (vn) => vn.id || vn.voiceNoteId || '')
        );
      }
    });

    const unsubDocs = firebaseFirestoreService.subscribeDocuments((fsDocs) => {
      if (!isSubscribed) return;
      if (fsDocs) {
        setDocuments((prev) => {
          const map = new Map<string, DocumentRecord>(prev.map((d) => [d.id || d.documentId, d]));
          fsDocs.forEach((d) => {
            const key = d.id || d.documentId;
            const existing = map.get(key);
            map.set(key, existing ? Object.assign({}, existing, d) : d);
          });
          return Array.from(map.values());
        });
      }
    });

    const unsubUsers = firebaseFirestoreService.subscribeUsers((fsUsers) => {
      if (!isSubscribed) return;
      if (fsUsers) {
        // Deleted users are kept in Firestore as a soft-delete so the audit
        // trail survives, but they must never come back into the directory.
        // Without this filter a deleted user vanishes for a moment and then
        // reappears as soon as the listener re-syncs.
        const live = fsUsers.filter(
          (u: any) => !u.isDeleted && u.status !== 'DELETED'
        );
        setUsers(live);
        setDeletedUsers(
          fsUsers.filter((u: any) => u.isDeleted || u.status === 'DELETED')
        );

        // The signed-in user's OWN copy is refreshed from the same listener.
        //
        // An administrator renaming a user, changing their role, or adding a
        // branch saved the change correctly -- but the person it was done to
        // carried the profile they had signed in with, so their screen kept
        // the old name and the old scope until they logged out and back in.
        // Nothing told them to, so they reported the rename as not saved.
        //
        // Only the fields an administrator can change are taken. The session
        // fields (token, password material) stay as they are.
        setCurrentUser((prev) => {
          if (!prev || !prev.id) return prev;
          const fresh: any = live.find(
            (u: any) =>
              u.id === prev.id ||
              (prev.loginId && u.loginId && u.loginId === prev.loginId)
          );
          if (!fresh) return prev;

          /**
           * ONLY take a field the database actually has.
           *
           * THE BUG THIS FIXES: this used to assign `bank: fresh.bank`,
           * `banks: fresh.banks`, `branch: fresh.branch`, `branches:
           * fresh.branches` and `name: fresh.name` unconditionally. A user
           * document created before those fields existed does not carry them,
           * so `fresh.banks` is undefined -- and a second after signing in,
           * the agent's own profile had its bank, branch and branch list
           * wiped.
           *
           * `scopedAccounts` is recomputed from `currentUser` every time this
           * runs, and `bankInScope` uses the bank list. So the agent's whole
           * allotment could disappear from their screen a moment after login,
           * or a moment after any action that made the user document re-sync.
           * Nothing was deleted; the screen simply stopped believing the
           * accounts belonged to them.
           *
           * A value is taken only when it is present, and a non-empty list is
           * never replaced by an empty one.
           */
          const take = (nextVal: any, prevVal: any) => {
            if (nextVal === undefined || nextVal === null) return prevVal;
            if (typeof nextVal === 'string' && nextVal.trim() === '' && prevVal) {
              return prevVal;
            }
            if (
              Array.isArray(nextVal) &&
              nextVal.length === 0 &&
              Array.isArray(prevVal) &&
              prevVal.length > 0
            ) {
              return prevVal;
            }
            return nextVal;
          };

          const merged = {
            ...prev,
            name: take(fresh.name, prev.name),
            agencyName: take(fresh.agencyName, prev.agencyName),
            role: take(fresh.role, prev.role),
            bank: take(fresh.bank, prev.bank),
            banks: take(fresh.banks, prev.banks),
            branch: take(fresh.branch, prev.branch),
            branches: take(fresh.branches, prev.branches),
            zone: take(fresh.zone, prev.zone),
            zoneId: take(fresh.zoneId, prev.zoneId),
            zonalOffice: take(fresh.zonalOffice, prev.zonalOffice),
            regionalOffice: take(fresh.regionalOffice, prev.regionalOffice),
            mobile: take(fresh.mobile, prev.mobile),
            commissionPercent: take(fresh.commissionPercent, prev.commissionPercent),
            sarfaesiCommissionPercent: take(
              fresh.sarfaesiCommissionPercent,
              prev.sarfaesiCommissionPercent
            ),
            // The agent's OWN identity is never taken from here. An account is
            // matched to an agent by these, so refreshing them is what would
            // orphan an allotment.
            agentId: prev.agentId,
            id: prev.id,
            loginId: prev.loginId,
            active: fresh.active === undefined ? prev.active : fresh.active,
          };

          // Nothing changed -- return the SAME object so `scopedAccounts` and
          // every other memo keyed on currentUser do not recompute.
          const same =
            merged.name === prev.name &&
            merged.role === prev.role &&
            merged.bank === prev.bank &&
            merged.branch === prev.branch &&
            merged.zone === prev.zone &&
            merged.active === prev.active &&
            merged.commissionPercent === prev.commissionPercent &&
            JSON.stringify(merged.banks || []) === JSON.stringify(prev.banks || []) &&
            JSON.stringify(merged.branches || []) === JSON.stringify(prev.branches || []);

          return same ? prev : merged;
        });
      }
    });

    /**
     * WHAT EACH PERSON ACTUALLY NEEDS.
     *
     * These four used to be read IN FULL by everybody, agents included: every
     * commission in the business, every allocation record, every allocation
     * history row and every WhatsApp log, on every sign-in, for a person who
     * can only ever see their own. Firestore charges for each of those
     * records whether or not the screen uses them.
     *
     * An agent now reads only their own commissions and their own WhatsApp
     * logs, and does not read allocations or allocation histories at all --
     * those are an administrator's audit of who holds what, and an agent has
     * no screen that shows them.
     */
    const unsubCommissions = subscribeLog<CommissionRecord>(
      'commissions',
      (cb) => firebaseFirestoreService.subscribeCommissions(cb),
      (fsComms) => {
        if (!isSubscribed) return;
        if (fsComms) setCommissions(fsComms);
      }
    );

    /**
     * ALLOCATIONS ARE NO LONGER READ ON SIGN-IN BY ANYBODY.
     *
     * An agent already did not read them. An administrator read all 412 --
     * one record per account -- on every sign-in and every refresh, and the
     * whole collection has two consumers: a count on the Backend Storage
     * screen, and the "Account Allocated" rows on ONE customer's timeline.
     *
     * The count is now asked for as a count (one read, when that screen is
     * opened). The timeline asks for the one account it is showing, through
     * `ensureAllocationsForAccount` below. Records this session creates
     * itself -- an Excel import, a reallocation -- are still put into state
     * as they always were, so they appear immediately.
     *
     * Nothing visible changes. This is the single largest saving in the app.
     */
    const unsubAllocations = () => {};

    /**
     * AUDIT TRAILS WAIT THEIR TURN.
     *
     * Eighteen live listeners all opened at once the instant somebody signed
     * in, and the screen could not settle until the slowest had answered.
     * Allocation histories and activity logs are the slowest by far -- they
     * are the two collections nothing ever deletes from -- and neither is on
     * any screen a person sees first.
     *
     * They start a few seconds later instead. Nothing is lost: they arrive
     * while the dashboard is already being read, and the audit screens fill
     * in long before anybody navigates to them. This is why signing in felt
     * slow, and the 300-record cap on each is the other half of it.
     */
    let unsubHistories: () => void = () => {};
    let unsubLogs: () => void = () => {};

    /**
     * The shared OTS scheme, read once per sign-in.
     *
     * One document, so one read. Until this existed the settings never left
     * the administrator's own browser and no agent ever saw them.
     */
    void firebaseFirestoreService
      .loadSettings<any>('ots')
      .then((cfg) => {
        if (isSubscribed && cfg) setLiveOTSConfig(cfg);
      })
      .catch((err) => console.warn('OTS settings load notice:', err?.message || err));

    void firebaseFirestoreService
      .loadSettings<any>('notice')
      .then((n) => {
        if (isSubscribed && n) {
          setNoticeBanner({
            text: n.text || '',
            colour: n.colour || 'green',
            bank: n.bank || '',
            zone: n.zone || '',
            // Agent targeting. Absent on every notice saved before this
            // existed, which correctly reads as "nobody singled out".
            agents: Array.isArray(n.agents) ? n.agents : [],
            updatedAt: n.updatedAt,
            updatedBy: n.updatedBy,
          });
        }
      })
      .catch((err) => console.warn('Notice load notice:', err?.message || err));

    const deferredTimer = window.setTimeout(() => {
      if (!isSubscribed) return;
      // An agent has no screen that shows either of these, so they are not
      // read for one at all.
      if (actorIsAgentLike) return;
      unsubHistories = firebaseFirestoreService.subscribeAllocationHistories((fsHist) => {
        if (!isSubscribed) return;
        if (fsHist) {
          setAllocationHistories(fsHist);
        }
      });

      unsubLogs = firebaseFirestoreService.subscribeActivityLogs((fsLogs) => {
        if (!isSubscribed) return;
        if (fsLogs) {
          setActivityLogs(fsLogs);
        }
      });
    }, 3500);

    const unsubNotifs = firebaseFirestoreService.subscribeNotifications((fsNotifs) => {
      if (!isSubscribed) return;
      if (fsNotifs) {
        setNotifications(fsNotifs);
      }
    });

    const unsubBanks = firebaseFirestoreService.subscribeBanks((fsBanks) => {
      if (!isSubscribed) return;
      if (fsBanks) {
        setBanks(fsBanks);
      }
    });

    const unsubBranches = firebaseFirestoreService.subscribeBranches((fsBranches) => {
      if (!isSubscribed) return;
      if (fsBranches) {
        setBranches(fsBranches);
      }
    });

    // The OTS scheme, read from the database rather than from this browser.
    //
    // `getOTSSchemeConfig` reads localStorage, so the settings an
    // administrator saved existed only on the machine they saved them on.
    // Every other device fell back to the built-in default, whose valid-till
    // date is the hard-coded string "30 September 2026" -- which is the date
    // that went out on agents' WhatsApp offers.
    void firebaseFirestoreService
      .loadSettings<any>('ots')
      .then((saved) => {
        if (!isSubscribed || !saved) return;
        try {
          localStorage.setItem('srms_ots_scheme_config_v2', JSON.stringify(saved));
          window.dispatchEvent(
            new CustomEvent('srms_ots_config_changed', { detail: saved })
          );
        } catch {
          /* storage blocked in a private window -- defaults still apply */
        }
      })
      .catch((err) => console.warn('OTS settings load notice:', err));

    // Commission rates, read back at sign-in. Without this the rates an
    // administrator set were correct on screen and gone after a logout.
    void firebaseFirestoreService
      .loadSettings<Partial<CommissionSettings>>('commission')
      .then((saved) => {
        if (!isSubscribed || !saved) return;
        setCommissionSettings((prev) => {
          const merged = { ...prev, ...saved };
          commissionSettingsRef.current = merged;
          try {
            localStorage.setItem('srms_commission_settings', JSON.stringify(merged));
          } catch {
            /* ignore */
          }
          return merged;
        });
      })
      .catch((err) => console.warn('Commission settings load notice:', err));

    const unsubZones = firebaseFirestoreService.subscribeZones((fsZones) => {
      if (!isSubscribed) return;
      if (fsZones) {
        setZones(fsZones);
      }
    });

    const unsubDepts = firebaseFirestoreService.subscribeDepartments((fsDepts) => {
      if (!isSubscribed) return;
      if (fsDepts) {
        setRecoveryDepartments(fsDepts);
      }
    });

    /**
     * Messages.
     *
     * An agent reads only the copies addressed to them -- one small query
     * that stays small however much traffic the business generates. Everybody
     * else reads the recent 500, newest first, because a message centre must
     * not make signing in slower every week.
     */
    const unsubMessages = actorIsAgentLike
      ? firebaseFirestoreService.subscribeMessagesForRecipient(
          [currentUser.agentId || '', currentUser.id || ''].filter(Boolean),
          (rows) => {
            if (!isSubscribed) return;
            if (rows) setMessages(rows);
          }
        )
      : firebaseFirestoreService.subscribeAllMessages((rows) => {
          if (!isSubscribed) return;
          if (rows) setMessages(rows);
        });

    const unsubWa = subscribeLog<WhatsAppOfferLog>(
      'whatsapp_logs',
      (cb) => firebaseFirestoreService.subscribeWhatsAppLogs(cb),
      (fsWa) => {
        if (!isSubscribed) return;
        if (fsWa) setWhatsAppLogs(fsWa);
      }
    );

    return () => {
      isSubscribed = false;
      unsubAccounts();
      unsubRecoveries();
      unsubPtps();
      unsubFollowups();
      unsubVisits();
      unsubPhotos();
      unsubVoiceNotes();
      unsubDocs();
      unsubUsers();
      unsubCommissions();
      unsubAllocations();
      unsubHistories();
      window.clearTimeout(deferredTimer);
      unsubLogs();
      unsubNotifs();
      unsubBanks();
      unsubBranches();
      unsubZones();
      unsubDepts();
      unsubMessages();
      unsubWa();
    };
  }, [actorSubscriptionKey]);

  const flushSheetSyncNow = useCallback(async () => {
    await firebaseFirestoreService.testConnection().catch(() => {});
  }, []);
  const isInitialMount = useRef(true);
  const autoPushTimerRef = useRef<NodeJS.Timeout | null>(null);
  const latestStateSnapshotRef = useRef<any>({});

  // Local fallback OTP storage for offline / standalone resilience
  const [localOTPRecords, setLocalOTPRecords] = useState<
    Record<
      string,
      {
        otp: string;
        identifier: string;
        userId: string;
        expiresAt: number;
        attempts: number;
        isUsed: boolean;
      }
    >
  >({});
  const [localResetTokens, setLocalResetTokens] = useState<
    Record<string, { userId: string; expiresAt: number; isUsed: boolean }>
  >({});

  // Keep a fresh reference to state for background network pulls/pushes without re-triggering hooks
  latestStateSnapshotRef.current = {
    accounts,
    allocations,
    allocationHistories,
    followups,
    ptps,
    recoveries,
    visits,
    photos,
    documents,
    voiceNotes,
    activityLogs,
    notifications,
    commissions,
    branches,
    areas,
    zones,
    banks,
    recoveryDepartments,
    commissionRules,
    userCommissionAssignments,
    commissionSettings,
    otsAuthorizedRoles,
    whatsAppLogs,
  };

  // ==========================================
  // SHARED MASTER DATA STORAGE ACROSS ALL LOGINS
  // ==========================================
  const pullSharedMasterData = useCallback(async (): Promise<{ success: boolean; message: string }> => {
    try {
      const res = await fetch('/api/master-data/get');
      if (!res.ok) return { success: false, message: 'Server responded with error' };
      const data = await res.json();
      if (data.success && data.hasData && data.masterData) {
        const m = data.masterData;

        // Authoritative server-first sync
        if (Array.isArray(m.accounts)) {
          setAccounts(m.accounts);
          try {
            if (m.accounts.length <= 1500) {
              localStorage.setItem('srms_persisted_accounts', JSON.stringify(m.accounts));
            }
          } catch {}
        }
        if (Array.isArray(m.allocations)) {
          setAllocations(m.allocations);
        }
        if (Array.isArray(m.allocationHistories)) {
          setAllocationHistories(m.allocationHistories);
        }
        if (Array.isArray(m.followups)) setFollowups(m.followups);
        if (Array.isArray(m.ptps)) setPtps(m.ptps);
        if (Array.isArray(m.recoveries)) setRecoveries(m.recoveries);
        if (Array.isArray(m.visits)) setVisits(m.visits);
        if (Array.isArray(m.photos)) setPhotos(m.photos);
        if (Array.isArray(m.documents)) setDocuments(m.documents);
        if (Array.isArray(m.voiceNotes)) setVoiceNotes(m.voiceNotes);
        if (Array.isArray(m.activityLogs)) setActivityLogs(m.activityLogs);
        if (Array.isArray(m.notifications)) setNotifications(m.notifications);
        if (Array.isArray(m.commissions)) setCommissions(m.commissions);
        if (Array.isArray(m.branches)) setBranches(m.branches);
        if (Array.isArray(m.areas)) setAreas(m.areas);
        if (Array.isArray(m.zones)) setZones(m.zones);
        if (Array.isArray(m.banks)) setBanks(m.banks);
        if (Array.isArray(m.recoveryDepartments)) setRecoveryDepartments(m.recoveryDepartments);
        if (Array.isArray(m.commissionRules)) setCommissionRules(m.commissionRules);
        if (Array.isArray(m.userCommissionAssignments)) setUserCommissionAssignments(m.userCommissionAssignments);
        if (Array.isArray(m.otsAuthorizedRoles)) setOtsAuthorizedRoles(m.otsAuthorizedRoles);
        if (Array.isArray(m.whatsAppLogs)) setWhatsAppLogs(m.whatsAppLogs);
        if (m.commissionSettings) setCommissionSettings(m.commissionSettings);
        if (Array.isArray(m.users) && m.users.length > 0) {
          setUsers(m.users);
          try {
            localStorage.setItem('srms_users', JSON.stringify(m.users));
          } catch {}
        }
        setLastAutoSyncTime(new Date().toLocaleTimeString());
        return { success: true, message: 'Master data synchronized across all logins.' };
      } else if (data.success && !data.hasData) {
        // First time initialization: push current initial data to server
        const initialPayload = latestStateSnapshotRef.current || {};
        await fetch('/api/master-data/save', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ data: initialPayload }),
        });
        return { success: true, message: 'Master data initialized on central storage.' };
      }
      return { success: false, message: 'No master data available on server' };
    } catch (err: any) {
      console.warn('[SRMS MASTER DATA] Pull error:', err);
      return { success: false, message: err?.message || 'Sync failed' };
    }
  }, []);

  const pushSharedMasterData = useCallback(async (overrides?: Record<string, any>): Promise<{ success: boolean; message: string }> => {
    try {
      // Safety guard: if local client has 0 accounts and not forced, preserve server database
      if (accounts.length === 0 && !overrides?.forceClearAccounts) {
        console.log('[SRMS MASTER DATA] Guard: Preserving central server database from empty push.');
        return { success: true, message: 'Preserved central server database.' };
      }

      const payload = {
        accounts,
        allocations,
        allocationHistories,
        followups,
        ptps,
        recoveries,
        visits,
        photos,
        deleteGeoTaggedPhoto,
        documents,
        voiceNotes,
        activityLogs,
        notifications,
        commissions,
        branches,
        areas,
        zones,
        banks,
        recoveryDepartments,
        commissionRules,
        userCommissionAssignments,
        commissionSettings,
        otsAuthorizedRoles,
        whatsAppLogs,
        users,
        ...overrides,
      };
      const res = await fetch('/api/master-data/save', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ data: payload }),
      });
      const data = await res.json();
      if (res.ok && data.success) {
        setLastAutoSyncTime(new Date().toLocaleTimeString());
        return { success: true, message: 'Master data successfully saved to central storage.' };
      }
      return { success: false, message: data.error || 'Failed to save to central storage' };
    } catch (err: any) {
      console.warn('[SRMS MASTER DATA] Push error:', err);
      return { success: false, message: err?.message || 'Save failed' };
    }
  }, [
    accounts,
    allocations,
    allocationHistories,
    followups,
    ptps,
    recoveries,
    visits,
    photos,
    documents,
    voiceNotes,
    activityLogs,
    notifications,
    commissions,
    branches,
    areas,
    zones,
    banks,
    recoveryDepartments,
    commissionRules,
    userCommissionAssignments,
    commissionSettings,
    otsAuthorizedRoles,
    whatsAppLogs,
  ]);

  const batchSaveAccountsToServer = useCallback(async (newAccounts: Account[], replaceAll = false): Promise<{ success: boolean; message: string; totalAccounts?: number }> => {
    try {
      const res = await fetch('/api/accounts/batch-save', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ accounts: newAccounts, replaceAll }),
      });
      const data = await res.json();
      if (res.ok && data.success) {
        if (replaceAll) {
          setAccounts(newAccounts);
        } else {
          setAccounts((prev) => {
            const map = new Map<string, Account>();
            prev.forEach((a) => map.set(a.accountId || a.id, a));
            newAccounts.forEach((a) => map.set(a.accountId || a.id, a));
            return Array.from(map.values());
          });
        }
        return { success: true, message: data.message, totalAccounts: data.totalAccounts };
      }
      return { success: false, message: data.error || 'Failed to save accounts to server.' };
    } catch (e: any) {
      return { success: false, message: e?.message || 'Network error saving accounts.' };
    }
  }, []);

  const fetchServerStats = useCallback(async () => {
    try {
      const res = await fetch('/api/master-data/stats');
      if (res.ok) {
        const data = await res.json();
        if (data.success && data.stats) {
          setServerStats(data.stats);
          return data.stats;
        }
      }
    } catch (e) {
      console.warn('Failed to fetch server stats:', e);
    }
    return null;
  }, []);

  // Firestore is the primary shared data source.
// The real-time Firestore listeners above handle synchronization.
useEffect(() => {
  return () => {};
}, []);

  // Firestore is the primary shared database.
// Legacy API master-data push is disabled.
useEffect(() => {
  return () => {};
}, []);

  // Firestore real-time listeners handle multi-device synchronization.
// No periodic API master-data pull is required.
useEffect(() => {
  return () => {};
}, []);

  // Periodic session security verification
  useEffect(() => {
    if (!isAuthenticated) return;

    const interval = setInterval(async () => {
      try {
        // 1. Immediate client-side check: if current user has been marked inactive, terminate session
        if (currentUser && currentUser.active === false) {
          console.warn('[SRMS AUTH] Current user account deactivated locally, terminating session.');
          logout();
          return;
        }

        const token = getStoredSessionItem('srms_session_token');
        if (!token) return;

        // 2. Query backend for explicit administrative deactivation or session revocation
        const res = await fetch('/api/auth/verify-session', {
          headers: { Authorization: `Bearer ${token}` },
        }).catch(() => null);

        if (!res) {
          // Network interruption / offline / Firebase hosting
          // Maintain authenticated state gracefully
          return;
        }

        // Only terminate on explicit security signals:
        // - 403 ACCOUNT_DEACTIVATED: User was explicitly deactivated by administrator
        // - 401 SESSION_REVOKED: Password was changed and prior tokens were revoked
        if (res.status === 403) {
          const data = await res.json().catch(() => ({}));
          if (data.code === 'ACCOUNT_DEACTIVATED') {
            console.warn('[SRMS AUTH] Session terminated by server: Account deactivated');
            alert(data.error || 'Your account has been deactivated. You have been logged out.');
            logout();
          }
        } else if (res.status === 401) {
          const data = await res.json().catch(() => ({}));
          if (data.code === 'SESSION_REVOKED') {
            console.warn('[SRMS AUTH] Session terminated by server: Password changed');
            alert(data.error || 'Your password was changed. Please log in again with your new password.');
            logout();
          }
          // Note: Standard 401 without SESSION_REVOKED (e.g. server restart, in-memory cache clear, or client fallback token)
          // will NOT force logout, preventing continuous unwanted logouts.
        }
      } catch {}
    }, 30000);

    return () => clearInterval(interval);
  }, [isAuthenticated, currentUser]);

  // ==========================================
  // STRICT HIERARCHICAL ACCESS ENFORCEMENT
  // Bank -> Zone -> Recovery Department -> Branch -> Branch Manager -> Recovery Agent -> QD -> Recovery -> Commission
  // ==========================================

  /**
   * The banks a person covers. An empty list means "not restricted by bank".
   *
   * `banks` is the list; `bank` remains the primary and is folded in, so a
   * user set up before multi-bank existed behaves exactly as before.
   */
  const banksOf = (user: User): string[] => {
    const list = Array.isArray(user?.banks) ? user.banks : [];
    const all = [...list, user?.bank].filter(
      (b): b is string => typeof b === 'string' && b.trim() !== '' && b !== 'All Banks'
    );
    return Array.from(new Set(all.map((b) => b.trim())));
  };

  /**
   * Does this record belong to a bank the person covers, and to the bank
   * they are currently looking at?
   *
   * `activeBank` is the selector at the top of the screen: it narrows a
   * multi-bank user to one bank at a time without changing what they are
   * entitled to see.
   */
  const bankInScope = (recordBank: string | undefined, targetUser: User): boolean => {
    const bank = (recordBank || '').trim();

    // An account whose bank is blank or a placeholder ("Partner Bank",
    // "N/A", "[NULL]") is NOT out of scope -- it is unclassified. Filtering
    // those out hid every such account from the user it was allotted to: the
    // allocation showed as done and the agent's list came up empty.
    const PLACEHOLDERS = [
      '', 'all banks', 'partner bank', 'n/a', 'na', 'null', '[null]', '-', 'none', 'bank',
    ];
    if (PLACEHOLDERS.includes(bank.toLowerCase())) return true;

    const covered = banksOf(targetUser);
    if (covered.length > 0 && !covered.some((b) => b.toLowerCase() === bank.toLowerCase())) {
      return false;
    }
    if (
      activeBank &&
      targetUser.id === currentUser.id &&
      activeBank.toLowerCase() !== bank.toLowerCase()
    ) {
      return false;
    }
    return true;
  };

  const isAuthorizedForAccount = (acc: Account, targetUser: User = currentUser): boolean => {
    if (!acc) return false;
    if (targetUser.role === 'admin') return true;

    if (!bankInScope(acc.bank, targetUser)) return false;

    if (targetUser.role === 'recovery_department') {
      const bankMatch = bankInScope(acc.bank, targetUser);
      const zoneMatch = !targetUser.zone || !acc.zone || targetUser.zone === acc.zone;
      const deptMatch = !targetUser.department || !acc.department || targetUser.department === acc.department;
      return bankMatch && zoneMatch && deptMatch;
    }

    if (targetUser.role === 'branch_manager') {
      const branchMatch = !targetUser.branch || !acc.branch || targetUser.branch === acc.branch;
      const branchIdMatch = !targetUser.branchId || !acc.branchId || targetUser.branchId === acc.branchId;
      return branchMatch || branchIdMatch;
    }

    if (targetUser.role === 'agent') {
      const agentId = targetUser.agentId || targetUser.id;
      /**
       * Matched on the agent's ID *or* their document ID *or* their name,
       * compared case-insensitively and trimmed.
       *
       * This was `acc.assignedAgentName === targetUser.name` -- an exact,
       * case-sensitive comparison. So an agent renamed by an administrator
       * stopped matching every account allocated to them under the old name,
       * and "Anand Deshmukh " with a trailing space matched nothing at all.
       * The admin screen still showed the accounts as allocated, because it
       * reads the allocation; only the agent's own view went empty.
       */
      const norm = (v?: string) => (v || '').trim().toLowerCase();
      const assignedId = norm(acc.assignedAgentId);
      return (
        (!!assignedId &&
          (assignedId === norm(agentId) ||
            assignedId === norm(targetUser.agentId) ||
            assignedId === norm(targetUser.id))) ||
        (!!norm(acc.assignedAgentName) && norm(acc.assignedAgentName) === norm(targetUser.name))
      );
    }

    return true;
  };

  const isAuthorizedForRecovery = (rec: RecoveryRecord, targetUser: User = currentUser): boolean => {
    if (!rec) return false;
    if (targetUser.role === 'admin') return true;

    if (!bankInScope(rec.bank, targetUser)) return false;

    if (targetUser.role === 'recovery_department') {
      const bankMatch = bankInScope(rec.bank, targetUser);
      const zoneMatch = !targetUser.zone || !rec.zone || targetUser.zone === rec.zone;
      const deptMatch = !targetUser.department || !rec.department || targetUser.department === rec.department;
      return bankMatch && zoneMatch && deptMatch;
    }

    if (targetUser.role === 'branch_manager') {
      return !targetUser.branch || !rec.branch || targetUser.branch === rec.branch;
    }

    if (targetUser.role === 'agent') {
      const agentId = targetUser.agentId || targetUser.id;
      return rec.agentId === agentId;
    }

    return true;
  };

  // Scoped views for role-based views
  const scopedAccounts = useMemo(() => {
    return accounts.filter((a) => isAuthorizedForAccount(a, currentUser));
  }, [accounts, currentUser]);

  const scopedRecoveries = useMemo(() => {
    return recoveries.filter((r) => isAuthorizedForRecovery(r, currentUser));
  }, [recoveries, currentUser]);

  const scopedPtps = useMemo(() => {
    if (currentUser.role === 'admin') return ptps;
    const scopedAccountIds = new Set(scopedAccounts.map((a) => a.accountId));
    return ptps.filter((p) => scopedAccountIds.has(p.accountId) || p.agentId === currentUser.agentId || p.agentId === currentUser.id);
  }, [ptps, scopedAccounts, currentUser]);

  const scopedVisits = useMemo(() => {
    if (currentUser.role === 'admin') return visits;
    const scopedAccountIds = new Set(scopedAccounts.map((a) => a.accountId));
    return visits.filter((v) => scopedAccountIds.has(v.accountId) || v.agentId === currentUser.agentId || v.agentId === currentUser.id);
  }, [visits, scopedAccounts, currentUser]);

  const scopedCommissions = useMemo(() => {
    if (currentUser.role === 'admin') return commissions;
    if (currentUser.role === 'agent') {
      const agentId = currentUser.agentId || currentUser.id;
      return commissions.filter((c) => c.agentId === agentId);
    }
    const scopedRecIds = new Set(scopedRecoveries.map((r) => r.recoveryId));
    return commissions.filter((c) => scopedRecIds.has(c.recoveryId));
  }, [commissions, scopedRecoveries, currentUser]);

  const scopedUsers = useMemo(() => {
    if (currentUser.role === 'admin') return users;
    if (currentUser.role === 'recovery_department') {
      return users.filter(
        (u) =>
          u.id === currentUser.id ||
          (u.bank === currentUser.bank && u.zone === currentUser.zone)
      );
    }
    if (currentUser.role === 'branch_manager') {
      return users.filter(
        (u) => u.id === currentUser.id || (u.branch === currentUser.branch && u.role === 'agent')
      );
    }
    return users.filter((u) => u.id === currentUser.id);
  }, [users, currentUser]);

  const scopedRules = useMemo(() => {
    if (currentUser.role === 'admin') return commissionRules;
    if (currentUser.role === 'recovery_department') {
      return commissionRules.filter(
        (r) => !r.bankName || r.bankName === 'All Banks' || r.bankName === currentUser.bank
      );
    }
    if (currentUser.role === 'branch_manager') {
      return commissionRules.filter(
        (r) =>
          !r.branchName ||
          r.branchName === 'All Branches' ||
          r.branchName === currentUser.branch
      );
    }
    return commissionRules;
  }, [commissionRules, currentUser]);

  const scopedAssignments = useMemo(() => {
    if (currentUser.role === 'admin') return userCommissionAssignments;
    if (currentUser.role === 'recovery_department') {
      return userCommissionAssignments.filter(
        (a) => !a.bankName || a.bankName === currentUser.bank
      );
    }
    if (currentUser.role === 'branch_manager') {
      return userCommissionAssignments.filter(
        (a) => !a.branchName || a.branchName === currentUser.branch
      );
    }
    const agentId = currentUser.agentId || currentUser.id;
    return userCommissionAssignments.filter((a) => a.agentId === agentId);
  }, [userCommissionAssignments, currentUser]);

  // ==========================================
  // HIERARCHY CRUD & FILTER HELPERS
  // ==========================================

  const addBank = (bankData: Partial<Bank>) => {
    const newBank: Bank = {
      id: `BNK-${Date.now().toString().slice(-4)}`,
      name: bankData.name?.trim() || 'New Bank',
      code: bankData.code?.trim().toUpperCase() || 'BNK',
      active: bankData.active !== undefined ? bankData.active : true,
      // Saved only when a rate is given. A bank may have several commission types, so no 10% is forced on it.
      ...(bankData.defaultCommissionRate && bankData.defaultCommissionRate > 0
        ? { defaultCommissionRate: bankData.defaultCommissionRate }
        : {}),
      description: bankData.description || '',
    };
    setBanks((prev) => [newBank, ...prev]);
    // Banks, zones, branches and departments were only ever held in memory.
    // Nothing wrote them to the database, so every one of them vanished on
    // logout -- and a commission rate set on a bank went with it, which is
    // why a bank put on 2% was back at the 10% default after a reload.
    void firebaseFirestoreService
      .batchSaveBanks([newBank])
      .catch((err) => console.warn('Bank save notice:', err));
    logActivity(currentUser, 'HIERARCHY_UPDATED', `Added new Bank: ${newBank.name} (${newBank.code})`);
    return newBank;
  };

  /**
   * Create a Zonal Office.
   *
   * There was no way to create one. The Hierarchy screen had an "Add" button
   * for zones that was wired to nothing, so every zone in the app came from
   * demo data or from an Excel import -- which is why the zone dropdown could
   * not offer the zone an administrator actually needed.
   *
   * A zone is created UNDER a bank. Without that link no zone list can be
   * filtered by bank, and a branch can end up under a zone of another bank.
   */
  const addZone = (zoneData: Partial<Zone>): Zone => {
    const bankName = (zoneData.bankName || '').trim();
    const parentBank = bankName
      ? banks.find((b) => (b.name || '').trim().toLowerCase() === bankName.toLowerCase())
      : undefined;

    const newZone: Zone = {
      id: `ZON-${uniqueSuffix()}`,
      name: zoneData.name?.trim() || 'New Zonal Office',
      code: zoneData.code?.trim().toUpperCase() || 'ZON',
      headName: zoneData.headName?.trim() || 'Zonal Head',
      // Resolved from the bank NAME rather than defaulted. A hard-coded
      // fallback id is what filed branches of one bank under another.
      bankId: zoneData.bankId || parentBank?.id || '',
      bankName: parentBank?.name || bankName,
      active: zoneData.active !== undefined ? zoneData.active : true,
    };

    setZones((prev) => [newZone, ...prev.filter((z) => z.id !== newZone.id)]);
    void firebaseFirestoreService
      .batchSaveZones([newZone])
      .catch((err) => console.warn('Zone save notice:', err));
    logActivity(
      currentUser,
      'HIERARCHY_UPDATED',
      `Added Zonal Office: ${newZone.name}${newZone.bankName ? ` (${newZone.bankName})` : ''}`
    );
    return newZone;
  };

  const updateZone = (zoneId: string, updates: Partial<Zone>) => {
    let saved: Zone | null = null;
    setZones((prev) =>
      prev.map((z) => {
        if (z.id !== zoneId) return z;
        const next = { ...z, ...updates };
        saved = next;
        return next;
      })
    );
    if (saved) {
      void firebaseFirestoreService
        .batchSaveZones([saved])
        .catch((err) => console.warn('Zone save notice:', err));
    }
    logActivity(currentUser, 'HIERARCHY_UPDATED', `Updated Zonal Office ${zoneId}`);
  };

  const updateBank = (bankId: string, updates: Partial<Bank>) => {
    let saved: Bank | null = null;
    setBanks((prev) =>
      prev.map((b) => {
        if (b.id !== bankId) return b;
        saved = { ...b, ...updates };
        return saved;
      })
    );
    if (saved) {
      void firebaseFirestoreService
        .batchSaveBanks([saved])
        .catch((err) => console.warn('Bank save notice:', err));
    }
    logActivity(currentUser, 'HIERARCHY_UPDATED', `Updated Bank details for ${bankId}`);
  };

  /* ------------------------------------------------------------------ *
   * DELETING A LEVEL OF THE HIERARCHY
   *
   * There was no delete at all. `deleteBank` existed but nothing in the app
   * ever called it, and there was no way to remove a zone, a branch or a
   * department once created -- so a branch typed under the wrong bank stayed
   * there for ever.
   *
   * THE RULE: NOTHING IS DELETED WHILE SOMETHING STILL DEPENDS ON IT.
   *
   * A delete here cannot be undone, and the damage is not the record that
   * goes -- it is everything left pointing at a parent that no longer
   * exists. Delete a branch with 38 accounts on it and those accounts are
   * still in the database, still carrying the branch's name, and no longer
   * reachable from any hierarchy screen.
   *
   * So each of these COUNTS what hangs off the record first and refuses with
   * the figures if anything does. The administrator clears it in order --
   * move the accounts, reassign the staff, delete the children -- and then
   * the delete goes through. No cascade, because a cascade is exactly how a
   * single click removes four hundred accounts.
   *
   * MATCHED ON BANK AND NAME TOGETHER. Two banks can have a branch of the
   * same name -- Bidkin belongs to both Bank of Maharashtra and Central Bank
   * of India. Counting by name alone would block a delete because of another
   * bank's accounts. Where a record carries no bank at all it is counted
   * anyway: an unparented record is a reason to stop, not to proceed.
   * ------------------------------------------------------------------ */

  const norm = (v?: string | null) => (v || '').trim().toLowerCase();

  /** Does this account/user sit under the given bank? Blank counts as yes. */
  const underBank = (theirBank: string | undefined, bankName: string) => {
    const t = norm(theirBank);
    return t === '' || t === norm(bankName);
  };

  const describeBlockers = (
    what: string,
    blockers: Array<{ count: number; label: string }>
  ): string => {
    const real = blockers.filter((b) => b.count > 0);
    if (real.length === 0) return '';
    return (
      `${what} cannot be deleted yet.\n\nStill attached:\n` +
      real.map((b) => `  • ${b.count} ${b.label}`).join('\n') +
      `\n\nMove or remove those first, then delete this.`
    );
  };

  const deleteBank = (bankId: string): { success: boolean; message: string } => {
    const bank = banks.find((b) => b.id === bankId);
    if (!bank) return { success: false, message: 'That bank was not found.' };

    const name = bank.name || '';
    const childZones = zones.filter(
      (z) => z.bankId === bankId || norm(z.bankName) === norm(name)
    );
    const childBranches = branches.filter((b) => norm(b.bankName) === norm(name));
    const childDepts = recoveryDepartments.filter(
      (d) => d.bankId === bankId || norm(d.bankName) === norm(name)
    );
    const boundAccounts = accounts.filter(
      (a) => norm(a.bank) === norm(name) || norm((a as any).bankName) === norm(name)
    );
    const boundUsers = users.filter(
      (u) => norm(u.bank) === norm(name) || (u.banks || []).some((b) => norm(b) === norm(name))
    );

    const blocked = describeBlockers(`Bank "${name}"`, [
      { count: boundAccounts.length, label: 'loan account(s)' },
      { count: boundUsers.length, label: 'user(s)' },
      { count: childBranches.length, label: 'branch(es)' },
      { count: childZones.length, label: 'zonal office(s)' },
      { count: childDepts.length, label: 'recovery department(s)' },
    ]);
    if (blocked) return { success: false, message: blocked };

    setBanks((prev) => prev.filter((b) => b.id !== bankId));
    void firebaseFirestoreService
      .deleteBank(bankId)
      .catch((err) => console.error('[Firestore] Bank delete FAILED:', err));

    logActivity(currentUser, 'HIERARCHY_UPDATED', `Deleted Bank: ${name} (${bankId})`);
    return { success: true, message: `Bank "${name}" deleted.` };
  };

  const deleteZone = (zoneId: string): { success: boolean; message: string } => {
    const zone = zones.find((z) => z.id === zoneId);
    if (!zone) return { success: false, message: 'That zonal office was not found.' };

    const name = zone.name || '';
    const bankName = zone.bankName || '';

    const childBranches = branches.filter(
      (b) =>
        (norm(b.zoneName) === norm(name) || norm((b as any).zone) === norm(name)) &&
        underBank(b.bankName, bankName)
    );
    const boundAccounts = accounts.filter(
      (a) =>
        (norm(a.zone) === norm(name) || norm((a as any).zonalOffice) === norm(name)) &&
        underBank(a.bank, bankName)
    );
    const boundUsers = users.filter(
      (u) =>
        (norm(u.zone) === norm(name) || norm(u.zonalOffice) === norm(name)) &&
        underBank(u.bank, bankName)
    );

    const blocked = describeBlockers(`Zonal office "${name}"`, [
      { count: boundAccounts.length, label: 'loan account(s)' },
      { count: boundUsers.length, label: 'user(s)' },
      { count: childBranches.length, label: 'branch(es) under it' },
    ]);
    if (blocked) return { success: false, message: blocked };

    setZones((prev) => prev.filter((z) => z.id !== zoneId));
    void firebaseFirestoreService
      .deleteZone(zoneId)
      .catch((err) => console.error('[Firestore] Zone delete FAILED:', err));

    logActivity(
      currentUser,
      'HIERARCHY_UPDATED',
      `Deleted Zonal Office: ${name}${bankName ? ` (${bankName})` : ''} (${zoneId})`
    );
    return { success: true, message: `Zonal office "${name}" deleted.` };
  };

  const deleteBranch = (branchId: string): { success: boolean; message: string } => {
    const branch = branches.find((b) => b.id === branchId);
    if (!branch) return { success: false, message: 'That branch was not found.' };

    const name = branch.name || '';
    const bankName = branch.bankName || '';

    const boundAccounts = accounts.filter(
      (a) => norm(a.branch) === norm(name) && underBank(a.bank, bankName)
    );
    const boundUsers = users.filter(
      (u) =>
        (norm(u.branch) === norm(name) ||
          (u.branches || []).some((b) => norm(b) === norm(name))) &&
        underBank(u.bank, bankName)
    );

    const blocked = describeBlockers(
      `Branch "${name}"${bankName ? ` (${bankName})` : ''}`,
      [
        { count: boundAccounts.length, label: 'loan account(s)' },
        { count: boundUsers.length, label: 'user(s) posted there' },
      ]
    );
    if (blocked) return { success: false, message: blocked };

    setBranches((prev) => prev.filter((b) => b.id !== branchId));
    void firebaseFirestoreService
      .deleteBranch(branchId)
      .catch((err) => console.error('[Firestore] Branch delete FAILED:', err));

    logActivity(
      currentUser,
      'HIERARCHY_UPDATED',
      `Deleted Branch: ${name}${bankName ? ` (${bankName})` : ''} (${branchId})`
    );
    return { success: true, message: `Branch "${name}" deleted.` };
  };

  const deleteRecoveryDepartment = (
    deptId: string
  ): { success: boolean; message: string } => {
    const dept = recoveryDepartments.find((d) => d.id === deptId);
    if (!dept) return { success: false, message: 'That recovery department was not found.' };

    const name = dept.name || '';
    const boundUsers = users.filter(
      (u) => norm(u.department) === norm(name) || u.departmentId === deptId
    );

    const blocked = describeBlockers(`Recovery department "${name}"`, [
      { count: boundUsers.length, label: 'user(s) in it' },
    ]);
    if (blocked) return { success: false, message: blocked };

    setRecoveryDepartments((prev) => prev.filter((d) => d.id !== deptId));
    void firebaseFirestoreService
      .deleteDepartment(deptId)
      .catch((err) => console.error('[Firestore] Department delete FAILED:', err));

    logActivity(currentUser, 'HIERARCHY_UPDATED', `Deleted Recovery Department: ${name} (${deptId})`);
    return { success: true, message: `Recovery department "${name}" deleted.` };
  };

  const addRecoveryDepartment = (deptData: Partial<RecoveryDepartment>): RecoveryDepartment => {
    // Same hard-coded-id problem as branches: the name said one bank, the id
    // said State Bank of India.
    const bankName = (deptData.bankName || '').trim();
    const zoneName = (deptData.zoneName || '').trim();
    const parentBank = bankName
      ? banks.find((b) => (b.name || '').trim().toLowerCase() === bankName.toLowerCase())
      : undefined;
    const parentZone = zoneName
      ? zones.find((z) => (z.name || '').trim().toLowerCase() === zoneName.toLowerCase())
      : undefined;

    const newDept: RecoveryDepartment = {
      id: `DEP-${uniqueSuffix()}`,
      name: deptData.name?.trim() || 'Recovery Department',
      code: deptData.code?.trim().toUpperCase() || 'RD',
      bankId: deptData.bankId || parentBank?.id || '',
      bankName: parentBank?.name || bankName,
      zoneId: deptData.zoneId || parentZone?.id || '',
      zoneName: parentZone?.name || zoneName,
      headName: deptData.headName || 'Department Head',
      active: deptData.active !== undefined ? deptData.active : true,
      description: deptData.description || '',
    };
    setRecoveryDepartments((prev) => [newDept, ...prev]);
    void firebaseFirestoreService
      .batchSaveDepartments([newDept])
      .catch((err) => console.warn('Department save notice:', err));
    logActivity(currentUser, 'HIERARCHY_UPDATED', `Added Recovery Department: ${newDept.name} (${newDept.bankName})`);
    return newDept;
  };

  const updateRecoveryDepartment = (deptId: string, updates: Partial<RecoveryDepartment>) => {
    let saved: RecoveryDepartment | null = null;
    setRecoveryDepartments((prev) =>
      prev.map((d) => {
        if (d.id !== deptId) return d;
        saved = { ...d, ...updates };
        return saved;
      })
    );
    if (saved) {
      void firebaseFirestoreService
        .batchSaveDepartments([saved])
        .catch((err) => console.warn('Department save notice:', err));
    }
    logActivity(currentUser, 'HIERARCHY_UPDATED', `Updated Recovery Department ${deptId}`);
  };

  const addBranch = (branchData: Partial<Branch>): Branch => {
    // The ids used to be hard-coded fallbacks: a branch created under
    // "Bank of Maharashtra / Pune Zone" was saved with bankId 'BNK-01' and
    // zoneId 'ZON-01' -- State Bank of India's ids. The NAME said one bank
    // and the ID said another, and every screen that trusted the id put the
    // branch under the wrong bank. Ids are now resolved from the names given,
    // and left blank when there is nothing to resolve to.
    const bankName = (branchData.bankName || '').trim();
    const zoneName = (branchData.zoneName || branchData.zone || '').trim();
    const parentBank = bankName
      ? banks.find((b) => (b.name || '').trim().toLowerCase() === bankName.toLowerCase())
      : undefined;
    const parentZone = zoneName
      ? zones.find((z) => {
          if ((z.name || '').trim().toLowerCase() !== zoneName.toLowerCase()) return false;
          if (!bankName || !z.bankName) return true;
          return (z.bankName || '').trim().toLowerCase() === bankName.toLowerCase();
        })
      : undefined;
    const parentDept = branchData.departmentName
      ? recoveryDepartments.find(
          (d) =>
            (d.name || '').trim().toLowerCase() ===
            (branchData.departmentName || '').trim().toLowerCase()
        )
      : undefined;

    const newBranch: Branch = {
      id: `BR-${uniqueSuffix()}`,
      name: branchData.name?.trim() || 'New Branch',
      code: branchData.code?.trim().toUpperCase() || 'BR',
      area: branchData.area || '',
      zone: zoneName,
      zoneId: branchData.zoneId || parentZone?.id || '',
      zoneName: parentZone?.name || zoneName,
      bankId: branchData.bankId || parentBank?.id || '',
      bankName: parentBank?.name || bankName,
      departmentId: branchData.departmentId || parentDept?.id || '',
      departmentName: parentDept?.name || branchData.departmentName || '',
      // No invented manager. Every branch created without one was stored
      // carrying the words "Branch Manager", and the hierarchy table then
      // printed that in the manager column for every row -- a job title
      // standing in for a person, on a screen whose whole purpose is to say
      // who is where.
      managerName: (branchData.managerName || '').trim(),
      targetAmount: branchData.targetAmount || 0,
      // A branch was never given an active flag, so the table read the
      // undefined value as false and showed EVERY branch as INACTIVE.
      active: branchData.active !== false,
    };
    setBranches((prev) => [newBranch, ...prev]);
    void firebaseFirestoreService
      .batchSaveBranches([newBranch])
      .catch((err) => console.warn('Branch save notice:', err));
    logActivity(
      currentUser,
      'HIERARCHY_UPDATED',
      `Added Branch: ${newBranch.name} (${newBranch.bankName || 'no bank'} / ${newBranch.zoneName || 'no zone'})`
    );
    return newBranch;
  };

  const updateBranch = (branchId: string, updates: Partial<Branch>) => {
    let saved: Branch | null = null;
    setBranches((prev) =>
      prev.map((b) => {
        if (b.id !== branchId) return b;
        saved = { ...b, ...updates };
        return saved;
      })
    );
    if (saved) {
      void firebaseFirestoreService
        .batchSaveBranches([saved])
        .catch((err) => console.warn('Branch save notice:', err));
    }
    logActivity(currentUser, 'HIERARCHY_UPDATED', `Updated Branch ${branchId}`);
  };

  /**
   * Fold several branch records into one, and repoint every account.
   *
   * Imports spell the same branch differently -- trailing spaces, capitals,
   * an abbreviation -- and each spelling became its own branch. Merging
   * renames the accounts onto the surviving branch and deletes the rest, so
   * a branch manager's list and a branch-wise bill cover the whole branch
   * rather than one spelling of it.
   */
  const mergeBranches = (
    keepBranchId: string,
    mergeBranchIds: string[]
  ): { success: boolean; message: string } => {
    const keep = branches.find((b) => b.id === keepBranchId);
    if (!keep) return { success: false, message: 'Branch to keep was not found.' };

    const toMerge = branches.filter(
      (b) => mergeBranchIds.includes(b.id) && b.id !== keepBranchId
    );
    if (toMerge.length === 0) {
      return { success: false, message: 'Select at least one branch to merge in.' };
    }

    /**
     * A MERGE MUST NOT REACH INTO ANOTHER BANK.
     *
     * THE DAMAGE THIS PREVENTS. Accounts were matched by branch NAME alone.
     * Bank of Maharashtra has a Bidkin branch and Central Bank of India has a
     * Bidkin branch, so merging one Bidkin would have re-stamped BOTH banks'
     * Bidkin accounts onto the branch being kept -- hundreds of accounts
     * moved to the wrong bank's branch, in one press, with no undo.
     *
     * The bank of the branch being merged in now has to agree as well. An
     * account carrying no bank at all is still moved: it belongs to neither,
     * and leaving it pointing at a branch that has just been deleted is
     * worse.
     */
    const bankOf = (v?: string | null) => (v || '').trim().toLowerCase();
    const oldKeys = new Set(
      toMerge.map((b) => `${bankOf(b.bankName)}|${(b.name || '').trim().toLowerCase()}`)
    );
    const oldNamesNoBank = new Set(
      toMerge
        .filter((b) => !bankOf(b.bankName))
        .map((b) => (b.name || '').trim().toLowerCase())
    );

    let movedAccounts = 0;
    mutateAccounts((prev) =>
      prev.map((a) => {
        const theirName = (a.branch || '').trim().toLowerCase();
        const theirBank = bankOf((a as any).bank || (a as any).bankName);
        const matches =
          oldKeys.has(`${theirBank}|${theirName}`) ||
          // An account with no bank recorded, under a branch name being merged.
          (!theirBank && [...oldKeys].some((k) => k.endsWith(`|${theirName}`))) ||
          // A branch with no bank recorded takes its name's accounts with it.
          oldNamesNoBank.has(theirName);
        if (!matches) return a;
        movedAccounts += 1;
        return { ...a, branch: keep.name, branchCode: keep.code || a.branchCode };
      })
    );

    const removedIds = new Set(toMerge.map((b) => b.id));
    setBranches((prev) => prev.filter((b) => !removedIds.has(b.id)));

    void (async () => {
      for (const b of toMerge) {
        try {
          await firebaseFirestoreService.deleteBranch(b.id);
        } catch (err) {
          console.warn('Branch delete notice:', err);
        }
      }
    })();

    logActivity(
      currentUser,
      'HIERARCHY_UPDATED',
      `Merged ${toMerge.length} branch record(s) (${toMerge
        .map((b) => b.name)
        .join(', ')}) into "${keep.name}". ${movedAccounts} account(s) moved.`
    );

    return {
      success: true,
      message: `Merged into "${keep.name}". ${movedAccounts} account(s) moved.`,
    };
  };

  /**
   * Fill in the bank on accounts that have none.
   *
   * The Excel importer never wrote a bank field -- it wrote branch, branch
   * code and zone, and nothing else. Every account uploaded before the bank
   * selector was added therefore has a blank bank, and filtering by bank
   * anywhere in the app returned nothing for them. The data, not the filter,
   * is what is wrong, so this repairs the data: for each bankless account the
   * branch it names is looked up in the branch records, and that branch's bank
   * is copied onto the account. The zone is filled in the same way when it is
   * also blank, because it comes from the same record.
   *
   * Nothing is guessed. An account whose branch cannot be found, or whose
   * branch record has no bank of its own, is left exactly as it is and
   * counted as skipped -- so the result says honestly how much was fixed and
   * how much still needs a branch to be parented to a bank first.
   *
   * This works on the accounts currently loaded. Administrators read accounts
   * a scope at a time, so "Load all accounts" first if every account is to be
   * repaired in one pass.
   */
  /**
   * FINDING AND REPAIRING BALANCES THAT ARE ALREADY WRONG.
   *
   * Fixing the code stops NEW damage. It does nothing for accounts whose
   * balance was already miscalculated and written to the database -- those
   * stay wrong until something recalculates them.
   *
   * This recalculates every loaded account from first principles: its own
   * original outstanding, the collections that actually exist for it, and
   * the waiver DERIVED from the settlement agreed on it. That is the same
   * arithmetic used everywhere else, so an account that is already correct
   * comes out unchanged.
   *
   * IT CHANGES NOTHING BY ITSELF. It returns what it WOULD change, so the
   * difference can be read before anything is written. `applyBalanceRepair`
   * is the half that writes.
   */
  const previewBalanceRepair = (): Array<{
    accountId: string;
    customerName: string;
    original: number;
    collected: number;
    waiver: number;
    storedOutstanding: number;
    correctOutstanding: number;
    difference: number;
    storedOverdue: number;
    correctOverdue: number;
    overdueDifference: number;
    overdueOnly: boolean;
    reopening: boolean;
    storedOriginal: number;
    correctOriginal: number;
    originalWrong: boolean;
  }> => {
    const out: Array<any> = [];

    accounts.forEach((a) => {
      // Matched on every identifier the account can be known by, for the
      // same reason as the delete: a collection filed under a different one
      // would otherwise look like no collection at all, and the account would
      // be "repaired" to a balance that ignores money actually collected.
      const keys = [a.accountId, a.id, (a as any).loanNumber, (a as any).__docId]
        .filter((k): k is string => typeof k === 'string' && k.trim() !== '')
        .map((k) => k.trim().toLowerCase());
      const mine = recoveriesRef.current.filter(
        (r) =>
          keys.includes(String(r.accountId || '').trim().toLowerCase()) &&
          !(r as any).isReversed
      );

      /**
       * AN ORIGINAL BALANCE THAT WAS OVERWRITTEN BY ITS OWN SETTLEMENT.
       *
       * THE BLIND SPOT THIS CLOSES. This check recalculates an account from
       * its original and its receipts. That cannot catch an account whose
       * ORIGINAL is the wrong figure -- the arithmetic agrees with itself
       * perfectly, and the account is waved through as healthy.
       *
       * M/S. LUCKY P O P AND CEMENT (ACC-60305066298) is the case: its
       * original was overwritten with its Rs 4,00,000 settlement, so a
       * Rs 6,02,366 write-off sat on the books and this function reported
       * nothing wrong, because 4,00,000 - 0 really is 4,00,000.
       *
       * `preSettlementOriginalOf` reads the true figure back out of the OTS
       * history, which the overwrite never touched. When it finds one, the
       * account is rebuilt on it before anything else is compared -- so the
       * balance and overdue reported below are measured against the real
       * original rather than the corrupted one.
       *
       * NOTE FOR A LIVE SETTLEMENT. Correcting the original does NOT change
       * what the borrower owes while the settlement stands: the waiver is
       * derived from the original, so a bigger original means a bigger
       * concession and the same agreed balance. It is the day the settlement
       * is cancelled that the right figure has to be there.
       */
      const storedOriginal = originalOutstandingOf(a);
      const correctOriginal = preSettlementOriginalOf(a);
      const originalWrong = Math.abs(correctOriginal - storedOriginal) >= 1;

      const basis = originalWrong
        ? { ...a, originalOutstandingAmount: correctOriginal }
        : a;

      const waiver = otsWaiverOf(basis);
      const money = recalcAccountMoney(basis, mine, waiver);

      const stored = Number(a.outstandingAmount) || 0;
      const correct = Number(money.outstandingAmount) || 0;

      /**
       * THE OVERDUE IS CHECKED TOO, AND THIS IS WHY THE CHECK KEPT SAYING
       * EVERYTHING WAS FINE.
       *
       * Only the balance was compared. The balance was generally right --
       * deleting a collection restored it correctly. What did NOT come back
       * was the overdue, which could only ever fall (see
       * recalcAccountMoney). So an account could carry an overdue tens of
       * lakhs below its balance and still be waved through as healthy, while
       * the accounts list printed that stale overdue in red as the headline
       * figure.
       *
       * An account is now reported when EITHER figure disagrees.
       */
      const storedOverdue = Number(a.overdueAmount) || 0;
      const correctOverdue = Number(money.overdueAmount) || 0;

      // A settled account with nothing behind it is wrong even when the
      // number happens to agree.
      const paymentBackedClosure =
        a.accountStatus === 'Closed as per OTS' ||
        a.accountStatus === 'Regular Close' ||
        a.otsStatus === 'OTS Settled';
      const reopening = paymentBackedClosure && mine.length === 0;

      // A rupee of rounding is not worth reporting.
      const balanceWrong = Math.abs(stored - correct) >= 1;
      const overdueWrong = Math.abs(storedOverdue - correctOverdue) >= 1;
      if (!balanceWrong && !overdueWrong && !reopening && !originalWrong) return;

      out.push({
        accountId: a.accountId,
        customerName: a.customerName,
        original: correctOriginal,
        collected: money.totalRecovered,
        waiver,
        storedOutstanding: stored,
        correctOutstanding: correct,
        difference: correct - stored,
        storedOverdue,
        correctOverdue,
        overdueDifference: correctOverdue - storedOverdue,
        overdueOnly: overdueWrong && !balanceWrong && !originalWrong,
        reopening,
        storedOriginal,
        correctOriginal,
        originalWrong,
      });
    });

    // Worst first, counting whichever figure is furthest out -- otherwise an
    // overdue-only or original-only row sorts to the bottom with a balance
    // difference of zero, which is exactly the fault being hunted.
    const weight = (r: any) =>
      Math.max(
        Math.abs(r.difference || 0),
        Math.abs(r.overdueDifference || 0),
        Math.abs((r.correctOriginal || 0) - (r.storedOriginal || 0))
      );
    return out.sort((x, y) => weight(y) - weight(x));
  };

  /**
   * Write the repair. Only ever called after the preview has been shown.
   *
   * Each account is rebuilt from its own ledger -- nothing is copied from the
   * preview, so a stale preview cannot write a stale figure.
   */
  const applyBalanceRepair = async (
    auto = false
  ): Promise<{ repaired: number; message: string }> => {
    const targets = previewBalanceRepair();
    if (targets.length === 0) {
      return { repaired: 0, message: 'Every loaded account already agrees with its collections.' };
    }

    const ids = new Set(targets.map((t) => t.accountId));

    mutateAccounts((prev) =>
      prev.map((a) => {
        if (!ids.has(a.accountId)) return a;

        const keys = [a.accountId, a.id, (a as any).loanNumber, (a as any).__docId]
          .filter((k): k is string => typeof k === 'string' && k.trim() !== '')
          .map((k) => k.trim().toLowerCase());
        const mine = recoveriesRef.current.filter(
          (r) =>
            keys.includes(String(r.accountId || '').trim().toLowerCase()) &&
            !(r as any).isReversed
        );
        // Rebuilt on the true original first, exactly as the preview measured
        // it. Writing the derived balance while leaving a corrupted original
        // in place would "repair" the account into agreeing with the wrong
        // figure -- which is the state LUCKY was already in.
        const trueOriginal = preSettlementOriginalOf(a);
        const basis = { ...a, originalOutstandingAmount: trueOriginal };
        const money = recalcAccountMoney(basis, mine, otsWaiverOf(basis));

        const paymentBackedClosure =
          a.accountStatus === 'Closed as per OTS' ||
          a.accountStatus === 'Regular Close' ||
          a.otsStatus === 'OTS Settled';

        if (paymentBackedClosure && mine.length === 0) {
          // Same rule as deleting the last collection: the closure goes, the
          // agreed settlement stays.
          return {
            ...a,
            originalOutstandingAmount: trueOriginal,
            ...money,
            accountStatus: 'Active' as const,
            otsStatus:
              a.otsStatus === 'OTS Settled' ||
              a.otsStatus === 'OTS Settled – Customer Account Closed'
                ? 'OTS Confirmed'
                : a.otsStatus,
            otsPaidAmount: 0,
            closureType: undefined,
            closureDate: undefined,
          };
        }

        // Derived, for the same reason as the delete path: a stored
        // otsPaidAmount left behind makes the account refuse new collections.
        return {
          ...a,
          originalOutstandingAmount: trueOriginal,
          ...money,
          otsPaidAmount: Number(money.totalRecovered) || 0,
        };
      })
    );

    /**
     * WHAT WAS CORRECTED, ACCOUNT BY ACCOUNT.
     *
     * The repair now runs on its own, so the administrator is not standing
     * over it watching a preview before pressing a button. A single summary
     * line would mean corrections happening with no record of which accounts
     * moved or by how much -- which is not acceptable for money, even when
     * every correction is arithmetic the screens were already doing.
     *
     * One line per account, worst first, so the whole list can be read back
     * from the activity log afterwards from any device. Capped so that a
     * large first pass cannot flood the log; the count is always exact.
     */
    const detail = targets
      .slice(0, 25)
      .map((t) => {
        const parts: string[] = [];
        if (t.originalWrong) {
          parts.push(
            `original ${Math.round(t.storedOriginal).toLocaleString('en-IN')} to ${Math.round(
              t.correctOriginal
            ).toLocaleString('en-IN')}`
          );
        }
        if (Math.abs(t.difference) >= 1) {
          parts.push(
            `balance ${Math.round(t.storedOutstanding).toLocaleString('en-IN')} to ${Math.round(
              t.correctOutstanding
            ).toLocaleString('en-IN')}`
          );
        }
        if (Math.abs(t.overdueDifference) >= 1) {
          parts.push(
            `overdue ${Math.round(t.storedOverdue).toLocaleString('en-IN')} to ${Math.round(
              t.correctOverdue
            ).toLocaleString('en-IN')}`
          );
        }
        if (t.reopening) parts.push('reopened (closed with nothing collected)');
        return `${t.customerName || t.accountId} [${t.accountId}]: ${parts.join(', ') || 'recalculated'}`;
      })
      .join(' | ');

    logActivity(
      currentUser,
      'SETTINGS_UPDATED',
      `${auto ? 'Automatic balance check' : 'Balance repair'}: recalculated ${targets.length} account(s) ` +
        `from their collection ledger. ${detail}` +
        (targets.length > 25 ? ` | …and ${targets.length - 25} more.` : '')
    );

    const overdueOnlyCount = targets.filter((t) => t.overdueOnly).length;

    return {
      repaired: targets.length,
      message:
        `${targets.length} account(s) recalculated and saved.` +
        (overdueOnlyCount > 0
          ? ` ${overdueOnlyCount} of them had a correct balance but a stale overdue -- the red figure on the accounts list -- left behind by a deleted collection.`
          : ''),
    };
  };

  /**
   * THE CHECK RUNS ITSELF.
   *
   * WHY IT CHANGED. The repair only ever ran when an administrator pressed a
   * button in Settings, which meant he had to sit at the desk watching for
   * the banner -- and until he pressed it, those accounts showed a wrong
   * figure to whoever opened them.
   *
   * IT IS SAFE TO RUN UNATTENDED, and this is the specific reason: the
   * repair makes no judgement and invents no figure. It recalculates the
   * balance from the collections -- the same arithmetic every screen already
   * performs when it draws. It is bringing a stale stored number back into
   * line with receipts that are already the truth. There is nothing for a
   * person to approve because there is nothing being decided.
   *
   * FOUR GATES, AND THE SECOND ONE IS THE IMPORTANT ONE.
   *
   *   1. Administrators only. Everybody else reads a scoped slice of the
   *      collections, and a slice is not a ledger.
   *   2. The collections listener must have ANSWERED. An empty ledger and a
   *      ledger that has not arrived yet are indistinguishable from here, and
   *      repairing against the second would hand every account its original
   *      balance back as though nothing had ever been collected. This is the
   *      one way automatic repair could destroy money, so it is gated on the
   *      listener having delivered AND on there being collections in hand.
   *   3. Accounts must be loaded and settled -- a repair mid-load would
   *      measure half a book.
   *   4. Once per sign-in. The manual button in Settings is still there for
   *      a second pass after loading another scope.
   *
   * Every correction is written to the activity log, account by account, so
   * the administrator can read back exactly what moved -- from any device,
   * afterwards, without having waited for it.
   */
  useEffect(() => {
    const isAdminActor =
      currentUser?.role === 'admin' || (currentUser?.role as string) === 'super_admin';
    if (!isAdminActor) return;
    if (autoRepairDoneRef.current) return;
    if (!recoveriesDeliveredRef.current) return;
    if (recoveriesRef.current.length === 0) return;
    if (accountsLoading) return;
    if (accounts.length === 0) return;

    // Let the listeners settle before measuring. Accounts and collections
    // arrive on separate snapshots; measuring between the two would compare
    // a new account list against an old ledger.
    const timer = setTimeout(() => {
      if (autoRepairDoneRef.current) return;
      if (!recoveriesDeliveredRef.current || recoveriesRef.current.length === 0) return;

      const targets = previewBalanceRepair();
      if (targets.length === 0) {
        autoRepairDoneRef.current = true;
        return;
      }

      autoRepairDoneRef.current = true;
      void applyBalanceRepair(true)
        .then((res) => {
          if (res.repaired > 0) {
            console.log(`[SRMS] Automatic balance check corrected ${res.repaired} account(s).`);
          }
        })
        .catch((err) => console.warn('Automatic balance check notice:', err?.message || err));
    }, 8000);

    return () => clearTimeout(timer);
  }, [currentUser?.id, currentUser?.role, accounts.length, accountsLoading, recoveries.length]);

  /**
   * COMMISSIONS LEFT BEHIND BY A COLLECTION THAT NO LONGER EXISTS.
   *
   * WHERE THEY CAME FROM. An earlier version of the collection delete did
   * three things in order: remove the collection, remove its commission, put
   * the balance back. If the first step hit a permission refusal -- which it
   * did, on a document key that had never existed -- it threw, and the two
   * steps after it were abandoned. The collection disappeared from the
   * screen; the commission stayed; the money never came back.
   *
   * That is fixed for new deletes. It does nothing for the records already
   * stranded, and those are not harmless: they are counted in the payout
   * total, so an agent can be billed for commission on a collection that
   * does not exist.
   *
   * WHAT COUNTS AS ORPHANED. A commission whose `recoveryId` matches no
   * collection in the ledger. Matched against every key a collection can be
   * filed under, so a live collection is never mistaken for a missing one --
   * deleting a commission that IS earned would take money from an agent, and
   * that is the error worth being paranoid about.
   *
   * A commission with no recoveryId at all is also reported, because it can
   * never be tied back to anything.
   *
   * IT DELETES NOTHING BY ITSELF. `cleanOrphanedCommissions` is the half
   * that writes, and it is only ever called after this list has been shown.
   */
  /**
   * CORRECT ONE ACCOUNT'S ORIGINAL LOAN BALANCE.
   *
   * WHY THIS HAS TO EXIST. The original outstanding is set ONCE, by the first
   * sheet that carries the account, and after that no re-upload can move it.
   * That rule is right: by the time a second sheet arrives, collections have
   * been recorded against the first figure, and letting a spreadsheet
   * silently rewrite the basis of somebody's balance would be far worse than
   * the problem it solves.
   *
   * But it left no way at all to correct a figure that is wrong -- which is
   * how an account can sit showing a balance nobody can explain and nobody
   * can fix, with re-uploading the master sheet changing nothing.
   *
   * This is the deliberate exception: one account, one figure, by an
   * administrator, written to the activity log. The balance is then
   * RECALCULATED from that figure and the collections that exist -- not set
   * directly -- so it stays consistent with the ledger and with every other
   * path in the app.
   */
  const correctAccountOriginalBalance = async (
    accountId: string,
    newOriginal: number
  ): Promise<{ success: boolean; message: string }> => {
    if (currentUser.role !== 'admin' && (currentUser.role as string) !== 'super_admin') {
      return { success: false, message: 'Only an administrator can correct a loan balance.' };
    }

    const value = Number(newOriginal);
    if (!Number.isFinite(value) || value < 0) {
      return { success: false, message: 'Enter a valid amount.' };
    }

    const target = accounts.find((a) => a.accountId === accountId || a.id === accountId);
    if (!target) {
      return { success: false, message: 'That account is not loaded in this session.' };
    }

    const keys = [target.accountId, target.id, (target as any).loanNumber, (target as any).__docId]
      .filter((k): k is string => typeof k === 'string' && k.trim() !== '')
      .map((k) => k.trim().toLowerCase());

    const mine = recoveriesRef.current.filter(
      (r) =>
        keys.includes(String(r.accountId || '').trim().toLowerCase()) && !(r as any).isReversed
    );

    const before = Number(target.outstandingAmount) || 0;
    const oldOriginal = originalOutstandingOf(target);

    const rebuilt = { ...target, originalOutstandingAmount: value };
    const money = recalcAccountMoney(rebuilt, mine, otsWaiverOf(rebuilt));

    mutateAccounts((prev) =>
      prev.map((a) =>
        a.accountId === target.accountId
          ? { ...a, originalOutstandingAmount: value, ...money }
          : a
      )
    );

    logActivity(
      currentUser,
      'SETTINGS_UPDATED',
      `Corrected the original loan balance on ${target.accountId} (${target.customerName}) from ` +
        `₹${Math.round(oldOriginal).toLocaleString('en-IN')} to ₹${Math.round(value).toLocaleString('en-IN')}. ` +
        `Balance recalculated from ${mine.length} collection(s): ` +
        `₹${Math.round(before).toLocaleString('en-IN')} → ₹${Math.round(money.outstandingAmount).toLocaleString('en-IN')}.`,
      target.accountId
    );

    return {
      success: true,
      message:
        `Original balance set to ₹${Math.round(value).toLocaleString('en-IN')}.\n\n` +
        `Collections found: ${mine.length} (₹${Math.round(money.totalRecovered).toLocaleString('en-IN')})\n` +
        `Balance: ₹${Math.round(before).toLocaleString('en-IN')} → ₹${Math.round(money.outstandingAmount).toLocaleString('en-IN')}`,
    };
  };

  const previewOrphanedCommissions = (): {
    orphans: Array<{
      id: string;
      agentName: string;
      accountId: string;
      recoveryId: string;
      commissionAmount: number;
      recoveryAmount: number;
      date: string;
      reason: string;
    }>;
    totalNow: number;
    totalAfter: number;
  } => {
    // Every key a live collection could be filed under.
    const liveKeys = new Set<string>();
    recoveriesRef.current.forEach((r) => {
      [(r as any).__docId, r.id, r.recoveryId].forEach((k) => {
        if (typeof k === 'string' && k) liveKeys.add(k);
      });
    });

    const orphans: Array<any> = [];
    let totalNow = 0;
    let totalAfter = 0;

    commissions.forEach((c) => {
      const amount = Number(c.commissionAmount) || 0;
      totalNow += amount;

      const link = (c.recoveryId || '').trim();

      if (!link) {
        orphans.push({
          id: (c as any).__docId || c.id,
          agentName: c.agentName || '—',
          accountId: c.accountId || '(none recorded)',
          recoveryId: '(none recorded)',
          commissionAmount: amount,
          recoveryAmount: Number((c as any).recoveryAmount) || 0,
          date: c.date || '',
          reason: 'No collection reference on the record',
        });
        return;
      }

      if (!liveKeys.has(link)) {
        orphans.push({
          id: (c as any).__docId || c.id,
          agentName: c.agentName || '—',
          accountId: c.accountId || '(none recorded)',
          recoveryId: link,
          commissionAmount: amount,
          recoveryAmount: Number((c as any).recoveryAmount) || 0,
          date: c.date || '',
          reason: 'The collection it was earned on has been deleted',
        });
        return;
      }

      totalAfter += amount;
    });

    orphans.sort((a, b) => b.commissionAmount - a.commissionAmount);
    return { orphans, totalNow, totalAfter };
  };

  /**
   * Remove them. Only called after the preview has been read.
   *
   * The list is rebuilt here rather than taken from the caller, so a stale
   * preview cannot delete a commission that has since been earned again.
   */
  const cleanOrphanedCommissions = async (): Promise<{
    removed: number;
    message: string;
  }> => {
    const { orphans } = previewOrphanedCommissions();
    if (orphans.length === 0) {
      return { removed: 0, message: 'Every commission has a collection behind it.' };
    }

    let removed = 0;
    for (const o of orphans) {
      try {
        await firebaseFirestoreService.deleteCommission(o.id);
        removed += 1;
      } catch (err) {
        console.warn('[Commission cleanup] Could not remove', o.id, err);
      }
    }

    const goneIds = new Set(orphans.map((o) => o.id));
    setCommissions((prev) =>
      prev.filter((c) => !goneIds.has((c as any).__docId || c.id))
    );

    const value = orphans.reduce((t, o) => t + o.commissionAmount, 0);
    logActivity(
      currentUser,
      'SETTINGS_UPDATED',
      `Commission ledger cleanup: removed ${removed} commission record(s) with no collection behind them, ` +
        `worth ${Math.round(value).toLocaleString('en-IN')} in the payout total.`
    );

    return {
      removed,
      message:
        removed === orphans.length
          ? `${removed} orphaned commission record(s) removed.`
          : `${removed} of ${orphans.length} removed. The rest could not be deleted — see the browser console.`,
    };
  };

  const backfillAccountBanks = (): {
    success: boolean;
    updated: number;
    skipped: number;
    message: string;
  } => {
    const loaded = accountsRef.current;
    if (loaded.length === 0) {
      return {
        success: false,
        updated: 0,
        skipped: 0,
        message:
          'No accounts are loaded yet. Open Loan Accounts, choose a scope (or Load all accounts), then run this again.',
      };
    }

    /** Branch records indexed by code and by normalised name. */
    const byCode = new Map<string, Branch>();
    const byName = new Map<string, Branch>();
    branches.forEach((b) => {
      const code = norm(b.code);
      if (code && !isPlaceholderName(b.code) && !byCode.has(code)) byCode.set(code, b);
      const name = norm(b.name);
      if (name && !isPlaceholderName(b.name) && !byName.has(name)) byName.set(name, b);
    });

    /** The bank a branch record really belongs to, or nothing. */
    const bankOfBranch = (br?: Branch): string => {
      if (!br) return '';
      const name = (br.bankName || '').trim();
      // A branch stamped with a bank nobody created is leftover default data,
      // not a parent. Copying that onto accounts would spread the mistake.
      return isKnownBankName(name, banks) ? name : '';
    };

    let updated = 0;
    let skipped = 0;

    mutateAccounts((prev) =>
      prev.map((a) => {
        const hasBank = !!String((a as any).bank || '').trim();
        if (hasBank) return a;

        const br =
          (a.branchCode && byCode.get(norm(a.branchCode))) ||
          (a.branch && byName.get(norm(a.branch))) ||
          undefined;
        const bank = bankOfBranch(br);
        if (!bank) {
          skipped += 1;
          return a;
        }

        updated += 1;
        const next: Account = { ...a, bank };
        if (!String(a.zone || '').trim()) {
          const zone = (br?.zoneName || br?.zone || '').trim();
          if (zone && !isPlaceholderName(zone)) next.zone = zone;
        }
        return next;
      })
    );

    if (updated > 0) {
      logActivity(
        currentUser,
        'HIERARCHY_UPDATED',
        `Filled in the bank on ${updated} account(s) from their branch record.` +
          (skipped > 0 ? ` ${skipped} left unchanged.` : '')
      );
    }

    const message =
      updated === 0
        ? skipped === 0
          ? 'Every loaded account already has a bank. Nothing to do.'
          : `None could be filled in. ${skipped} account(s) name a branch that either does not exist in Branch Management or is not itself linked to a bank. Link those branches to their bank first, then run this again.`
        : `Bank filled in on ${updated} account(s).` +
          (skipped > 0
            ? ` ${skipped} left unchanged — their branch is not linked to a bank yet.`
            : '');

    return { success: updated > 0, updated, skipped, message };
  };

  const getFilteredRecoveryDepartments = (bankName?: string, zoneName?: string): RecoveryDepartment[] => {
    return recoveryDepartments.filter((d) => {
      if (bankName && bankName !== 'All Banks' && d.bankName !== bankName) return false;
      if (zoneName && zoneName !== 'All Zones' && d.zoneName !== zoneName) return false;
      return true;
    });
  };

  const getFilteredBranches = (bankName?: string, zoneName?: string, departmentName?: string): Branch[] => {
    return branches.filter((b) => {
      if (bankName && bankName !== 'All Banks' && b.bankName && b.bankName !== bankName) return false;
      if (zoneName && zoneName !== 'All Zones' && b.zoneName && b.zoneName !== zoneName) return false;
      if (departmentName && departmentName !== 'All Departments' && b.departmentName && b.departmentName !== departmentName) return false;
      return true;
    });
  };

  // ==========================================
  // COMMISSION RULES & ASSIGNMENTS ENGINE
  // ==========================================

  const addCommissionRule = (
    ruleData: Partial<CommissionRule>
  ): { success: boolean; message: string; rule?: CommissionRule; conflictDetected?: boolean } => {
    if (!ruleData.name || ruleData.commissionPercentage === undefined) {
      return { success: false, message: 'Rule name and commission percentage are required.' };
    }

    const pct = Number(ruleData.commissionPercentage);
    if (isNaN(pct) || pct < 0 || pct > 100) {
      return { success: false, message: 'Commission percentage must be between 0% and 100%.' };
    }

    // Check duplicate active rule conflict
    const activeConflicts = commissionRules.filter((r) => {
      if (r.status !== 'active') return false;
      const bMatch = (r.bankName || 'All Banks') === (ruleData.bankName || 'All Banks');
      const zMatch = (r.zoneName || 'All Zones') === (ruleData.zoneName || 'All Zones');
      const brMatch = (r.branchName || 'All Branches') === (ruleData.branchName || 'All Branches');
      const qMatch = (r.qdType || 'All QDs') === (ruleData.qdType || 'All QDs');
      const sMatch = (r.recoveryStage || 'All Stages') === (ruleData.recoveryStage || 'All Stages');
      return bMatch && zMatch && brMatch && qMatch && sMatch;
    });

    if (activeConflicts.length > 0) {
      return {
        success: false,
        conflictDetected: true,
        message: 'Duplicate commission rule detected. Please resolve the conflicting commission rules.',
      };
    }

    const pLevel =
      ruleData.branchName && ruleData.branchName !== 'All Branches'
        ? 2
        : ruleData.qdType && ruleData.qdType !== 'All QDs'
        ? 3
        : ruleData.zoneName && ruleData.zoneName !== 'All Zones'
        ? 4
        : 5;

    const newRule: CommissionRule = {
      id: `CR-${Date.now().toString().slice(-6)}`,
      name: ruleData.name.trim(),
      code: ruleData.code?.trim().toUpperCase() || `CR-${Date.now().toString().slice(-4)}`,
      bankId: ruleData.bankId,
      bankName: ruleData.bankName || 'All Banks',
      zoneId: ruleData.zoneId,
      zoneName: ruleData.zoneName || 'All Zones',
      departmentId: ruleData.departmentId,
      departmentName: ruleData.departmentName || 'All Departments',
      branchId: ruleData.branchId,
      branchName: ruleData.branchName || 'All Branches',
      qdType: ruleData.qdType || 'All QDs',
      recoveryStage: ruleData.recoveryStage || 'All Stages',
      commissionPercentage: pct,
      effectiveFrom: ruleData.effectiveFrom || todayLocal(),
      effectiveTo: ruleData.effectiveTo,
      status: ruleData.status || 'active',
      priorityLevel: pLevel,
      description: ruleData.description || '',
      createdBy: currentUser.name,
      createdDate: todayLocal(),
    };

    setCommissionRules((prev) => [newRule, ...prev]);

    const audit: CommissionAuditLog = {
      id: `CAL-${Date.now()}`,
      timestamp: new Date().toISOString(),
      action: 'CREATED',
      entityType: 'RULE',
      entityId: newRule.id,
      entityName: newRule.name,
      performedByUserId: currentUser.id,
      performedByName: currentUser.name,
      details: `Created new rule "${newRule.name}" (${newRule.commissionPercentage}%) - Priority ${newRule.priorityLevel}`,
    };
    setCommissionAuditLogs((prev) => [audit, ...prev]);

    logActivity(currentUser, 'COMMISSION_RULE_UPDATED', `Created Commission Rule: ${newRule.name} (${newRule.commissionPercentage}%)`);

    return { success: true, message: `Commission rule "${newRule.name}" created successfully.`, rule: newRule };
  };

  const updateCommissionRule = (
    id: string,
    updates: Partial<CommissionRule>
  ): { success: boolean; message: string } => {
    const existing = commissionRules.find((r) => r.id === id);
    if (!existing) return { success: false, message: 'Rule not found.' };

    if (updates.commissionPercentage !== undefined) {
      const pct = Number(updates.commissionPercentage);
      if (isNaN(pct) || pct < 0 || pct > 100) {
        return { success: false, message: 'Commission percentage must be between 0% and 100%.' };
      }
    }

    setCommissionRules((prev) => prev.map((r) => (r.id === id ? { ...r, ...updates } : r)));

    const audit: CommissionAuditLog = {
      id: `CAL-${Date.now()}`,
      timestamp: new Date().toISOString(),
      action: 'UPDATED',
      entityType: 'RULE',
      entityId: id,
      entityName: existing.name,
      previousValues: existing,
      newValues: { ...existing, ...updates },
      performedByUserId: currentUser.id,
      performedByName: currentUser.name,
      details: `Updated commission rule "${existing.name}"`,
    };
    setCommissionAuditLogs((prev) => [audit, ...prev]);

    logActivity(currentUser, 'COMMISSION_RULE_UPDATED', `Updated Commission Rule: ${existing.name}`);
    return { success: true, message: 'Commission rule updated successfully.' };
  };

  const deactivateCommissionRule = (id: string): { success: boolean; message: string } => {
    const existing = commissionRules.find((r) => r.id === id);
    if (!existing) return { success: false, message: 'Rule not found.' };

    setCommissionRules((prev) => prev.map((r) => (r.id === id ? { ...r, status: 'inactive' } : r)));

    const audit: CommissionAuditLog = {
      id: `CAL-${Date.now()}`,
      timestamp: new Date().toISOString(),
      action: 'DEACTIVATED',
      entityType: 'RULE',
      entityId: id,
      entityName: existing.name,
      performedByUserId: currentUser.id,
      performedByName: currentUser.name,
      details: `Deactivated commission rule "${existing.name}"`,
    };
    setCommissionAuditLogs((prev) => [audit, ...prev]);

    logActivity(currentUser, 'COMMISSION_RULE_UPDATED', `Deactivated Commission Rule: ${existing.name}`);
    return { success: true, message: `Rule "${existing.name}" deactivated.` };
  };

  const addUserCommissionAssignment = (
    assignmentData: Partial<UserCommissionAssignment>
  ): { success: boolean; message: string; assignment?: UserCommissionAssignment; conflictDetected?: boolean } => {
    if (!assignmentData.agentId) {
      return { success: false, message: 'Recovery Agent selection is required.' };
    }

    if (assignmentData.percentageOverride !== undefined) {
      const pct = Number(assignmentData.percentageOverride);
      if (isNaN(pct) || pct < 0 || pct > 100) {
        return { success: false, message: 'Percentage override must be between 0% and 100%.' };
      }
    }

    // Check duplicate active assignment for agent
    const existing = userCommissionAssignments.filter(
      (a) => a.status === 'active' && (a.agentId === assignmentData.agentId || a.agentName === assignmentData.agentName)
    );

    if (existing.length > 0) {
      return {
        success: false,
        conflictDetected: true,
        message: 'Duplicate commission rule detected. Please resolve the conflicting commission rules.',
      };
    }

    const newAssignment: UserCommissionAssignment = {
      id: `UCA-${Date.now().toString().slice(-6)}`,
      agentId: assignmentData.agentId,
      agentName: assignmentData.agentName || assignmentData.agentId,
      assignmentCode: assignmentData.assignmentCode || `ASG-${Date.now().toString().slice(-4)}`,
      ruleId: assignmentData.ruleId,
      percentageOverride: assignmentData.percentageOverride,
      bankName: assignmentData.bankName,
      zoneName: assignmentData.zoneName,
      branchName: assignmentData.branchName,
      effectiveFrom: assignmentData.effectiveFrom || todayLocal(),
      effectiveTo: assignmentData.effectiveTo,
      status: assignmentData.status || 'active',
      remarks: assignmentData.remarks || '',
      assignedBy: currentUser.name,
      createdDate: todayLocal(),
    };

    setUserCommissionAssignments((prev) => [newAssignment, ...prev]);

    const audit: CommissionAuditLog = {
      id: `CAL-${Date.now()}`,
      timestamp: new Date().toISOString(),
      action: 'ASSIGNED',
      entityType: 'ASSIGNMENT',
      entityId: newAssignment.id,
      entityName: `Assignment: ${newAssignment.agentName}`,
      performedByUserId: currentUser.id,
      performedByName: currentUser.name,
      details: `Assigned user-specific commission override of ${newAssignment.percentageOverride}% to ${newAssignment.agentName}`,
    };
    setCommissionAuditLogs((prev) => [audit, ...prev]);

    logActivity(currentUser, 'COMMISSION_ASSIGNED', `Assigned user commission override of ${newAssignment.percentageOverride}% to ${newAssignment.agentName}`);

    return { success: true, message: `User commission assigned successfully to ${newAssignment.agentName}.`, assignment: newAssignment };
  };

  const updateUserCommissionAssignment = (
    id: string,
    updates: Partial<UserCommissionAssignment>
  ): { success: boolean; message: string } => {
    const existing = userCommissionAssignments.find((a) => a.id === id);
    if (!existing) return { success: false, message: 'Assignment not found.' };

    if (updates.percentageOverride !== undefined) {
      const pct = Number(updates.percentageOverride);
      if (isNaN(pct) || pct < 0 || pct > 100) {
        return { success: false, message: 'Percentage override must be between 0% and 100%.' };
      }
    }

    setUserCommissionAssignments((prev) => prev.map((a) => (a.id === id ? { ...a, ...updates } : a)));

    const audit: CommissionAuditLog = {
      id: `CAL-${Date.now()}`,
      timestamp: new Date().toISOString(),
      action: 'UPDATED',
      entityType: 'ASSIGNMENT',
      entityId: id,
      entityName: `Assignment: ${existing.agentName}`,
      previousValues: existing,
      newValues: { ...existing, ...updates },
      performedByUserId: currentUser.id,
      performedByName: currentUser.name,
      details: `Updated user commission assignment for ${existing.agentName}`,
    };
    setCommissionAuditLogs((prev) => [audit, ...prev]);

    logActivity(currentUser, 'COMMISSION_ASSIGNED', `Updated commission assignment for ${existing.agentName}`);
    return { success: true, message: 'User commission assignment updated successfully.' };
  };

  const deactivateUserCommissionAssignment = (id: string): { success: boolean; message: string } => {
    const existing = userCommissionAssignments.find((a) => a.id === id);
    if (!existing) return { success: false, message: 'Assignment not found.' };

    setUserCommissionAssignments((prev) => prev.map((a) => (a.id === id ? { ...a, status: 'inactive' } : a)));

    const audit: CommissionAuditLog = {
      id: `CAL-${Date.now()}`,
      timestamp: new Date().toISOString(),
      action: 'DEACTIVATED',
      entityType: 'ASSIGNMENT',
      entityId: id,
      entityName: `Assignment: ${existing.agentName}`,
      performedByUserId: currentUser.id,
      performedByName: currentUser.name,
      details: `Deactivated user commission override for ${existing.agentName}`,
    };
    setCommissionAuditLogs((prev) => [audit, ...prev]);

    logActivity(currentUser, 'COMMISSION_ASSIGNED', `Deactivated commission assignment for ${existing.agentName}`);
    return { success: true, message: 'User commission assignment deactivated.' };
  };

  const calculateCommission = (params: CalculationParams): CommissionCalculationResult => {
    return calculateCommissionWithPriority(
      params,
      commissionRules,
      userCommissionAssignments,
      commissionSettings.defaultRate
    );
  };

  // Sync mode if role changes
  const switchUser = (userId: string) => {
    const target = users.find((u) => u.id === userId);
    if (target) {
      if (!target.active) {
        alert('Cannot switch to a deactivated user account.');
        return;
      }
      setCurrentUser(target);
      setIsAuthenticated(true);
      try {
        setStoredSessionItem('srms_auth_session', 'true');
        setStoredSessionItem('srms_user_id', target.id);
      } catch {}
      // Agents open the full web portal by default.
      setDeviceMode('web');
      logActivity(target, 'LOGIN', `Switched active user session to ${target?.name || 'User'} (${(target?.role || 'unknown').toUpperCase()})`);
      // Pull fresh shared master data for the switched user
      pullSharedMasterData();
    }
  };

  // Firebase-Enforced Authentication Login
  const loginWithCredentials = async (
    inputIdentifier: string,
    passwordInput: string,
    targetMode?: 'web' | 'android'
  ): Promise<AuthResult> => {
    if (!inputIdentifier || !inputIdentifier.trim()) {
      return { success: false, error: 'Please enter your User ID or registered Email.' };
    }
    if (!passwordInput || !passwordInput.trim()) {
      return { success: false, error: 'Please enter your Password.' };
    }

    const cleanInput = inputIdentifier.trim();
    const cleanPassword = passwordInput.trim();

    /**
     * THE SESSION MARKER IS WRITTEN *BEFORE* FIREBASE IS ASKED TO SIGN IN.
     * IT MUST STAY THAT WAY.
     *
     * THE BUG THIS FIXES -- and it stopped sign-in working completely.
     *
     * The marker used to be written a few lines further down, after the
     * profile had been fetched and checked. That looked reasonable and could
     * not work, because of WHEN Firebase fires its auth listener: the moment
     * `firebaseAuthService.login()` authenticates, the listener runs -- long
     * before this function reaches any line after it.
     *
     * So the sequence was:
     *
     *   1. login() authenticates with Firebase
     *   2. the auth listener fires
     *   3. it looks for a session marker, finds none, and signs the user
     *      straight back out -- doing exactly what it is meant to do for a
     *      restored credential, to a person who was signing in properly
     *   4. login() carries on and tries to read the profile of a user it has
     *      just been signed out as, which fails
     *   5. the screen says "Signed in, but your profile could not be loaded"
     *
     * Writing the marker first means the listener sees a browser session that
     * has deliberately started a sign-in, and leaves it alone. It is removed
     * again below on every path that does not end in a signed-in user, so a
     * failed attempt cannot leave a marker that would validate a restored
     * credential later.
     */
    try {
      setStoredSessionItem('srms_auth_session', 'true');
    } catch {}

    /** Undo the marker above. Used on every failure path. */
    const abandonSession = () => {
      try {
        removeStoredSessionItem('srms_auth_session');
        removeStoredSessionItem('srms_user_id');
      } catch {}
    };

    try {
      const authResult = await firebaseAuthService.login(cleanInput, cleanPassword);
      if (!authResult.success || !authResult.user) {
        abandonSession();
        return {
          success: false,
          error: authResult.error || 'Invalid credentials. Please verify your Email and Password.',
        };
      }

      const authenticatedUser = authResult.user;

      if (authenticatedUser.active === false || authenticatedUser.isDeleted || (authenticatedUser as any).status === 'DELETED') {
        await firebaseAuthService.logout();
        abandonSession();
        return {
          success: false,
          error: 'Your account is currently deactivated or has been removed. Please contact the administrator.',
          code: 'ACCOUNT_DEACTIVATED',
        };
      }

      setCurrentUser(authenticatedUser);
      setIsAuthenticated(true);

      // The marker itself was written before sign-in was attempted, for the
      // reason explained above. Only the user id is recorded here, now that
      // there is a confirmed user to record.
      try {
        setStoredSessionItem('srms_user_id', authenticatedUser.id);
      } catch {}

      // Respect an explicit workspace choice from the login screen;
      // otherwise everyone -- agents included -- gets the web portal.
      setDeviceMode(targetMode || 'web');

      logActivity(
        authenticatedUser,
        'LOGIN',
        `Logged in securely via Firebase Authentication as ${authenticatedUser?.name || 'User'} [${(authenticatedUser?.role || 'unknown').toUpperCase()}]`
      );

      return { success: true, user: authenticatedUser };
    } catch (err: any) {
      console.error('[Firebase Auth Login Error]', err);
      // A sign-in that threw is not a sign-in. The marker must not survive it,
      // or a restored credential would be accepted later on the strength of an
      // attempt that failed.
      abandonSession();
      return {
        success: false,
        error: err.message || 'Authentication failed. Please check your credentials.',
      };
    }
  };

  // Admin Forgot Password - Step 1: Request OTP
  const requestAdminOTP = async (identifier: string): Promise<OTPRequestResult> => {
    const cleanInput = identifier.trim();

    // Call Backend API
    try {
      const res = await fetch('/api/auth/forgot-password/request-otp', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ identifier: cleanInput }),
      });
      const data = await res.json();
      if (res.ok && data.success) {
        return {
          success: true,
          message: data.message,
          maskedEmail: data.maskedEmail,
          maskedMobile: data.maskedMobile,
          requestId: data.requestId,
        };
      } else if (data.error) {
        return {
          success: false,
          message: data.error,
          error: data.error,
        };
      }
    } catch (e) {
      console.warn('[SRMS AUTH] Request OTP fallback:', e);
    }

    // Fallback Client Simulation
    const adminUser = users.find(
      (u) =>
        (u.username === cleanInput ||
          u.id === cleanInput ||
          (typeof u.email === 'string' &&
            u.email.toLowerCase() === cleanInput.toLowerCase())) &&
        u.role === 'admin'
    );

    if (!adminUser || !adminUser.active) {
      return {
        success: true,
        message: 'If an active Admin account matches the provided identifier, an OTP has been sent to the registered email and mobile.',
        maskedEmail: 'ad***@srms-recovery.in',
        maskedMobile: '+91 98*** **223',
        requestId: `OTP-LOCAL-${Date.now()}`,
      };
    }

    const otpCode = generateOTP(6);
    const reqId = `OTP-LOCAL-${Date.now()}`;
    setLocalOTPRecords((prev) => ({
      ...prev,
      [reqId]: {
        otp: otpCode,
        identifier: cleanInput,
        userId: adminUser.id,
        expiresAt: Date.now() + 10 * 60 * 1000,
        attempts: 0,
        isUsed: false,
      },
    }));

    console.log(`[SRMS SECURITY] Local OTP Generated for ${adminUser.email}: ${otpCode}`);

    return {
      success: true,
      message: 'One-Time Password (OTP) dispatched to registered Admin email and mobile.',
      maskedEmail: maskEmail(adminUser.email),
      maskedMobile: maskMobile(adminUser.mobile),
      requestId: reqId,
    };
  };

  // Admin Forgot Password - Step 2: Verify OTP
  const verifyAdminOTP = async (
    requestId: string,
    otp: string
  ): Promise<{ success: boolean; resetToken?: string; error?: string }> => {
    const cleanOtp = otp.trim();

    // Call Backend API
    try {
      const res = await fetch('/api/auth/forgot-password/verify-otp', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ requestId, otp: cleanOtp }),
      });
      const data = await res.json();
      if (res.ok && data.success) {
        return { success: true, resetToken: data.resetToken };
      } else if (data.error) {
        return { success: false, error: data.error };
      }
    } catch (e) {
      console.warn('[SRMS AUTH] Verify OTP fallback:', e);
    }

    // Fallback Client Simulation
    const record = localOTPRecords[requestId];
    if (!record) {
      return { success: false, error: 'Invalid or expired OTP session. Please request a new OTP.' };
    }
    if (Date.now() > record.expiresAt) {
      return { success: false, error: 'OTP has expired. Please request a new OTP.' };
    }
    if (record.isUsed) {
      return { success: false, error: 'This OTP has already been used. Please request a new OTP.' };
    }
    if (record.attempts >= 3) {
      return { success: false, error: 'Maximum verification attempts exceeded. Please request a new OTP.' };
    }

    if (record.otp !== cleanOtp) {
      record.attempts += 1;
      const remaining = 3 - record.attempts;
      return {
        success: false,
        error: `Invalid OTP code. ${remaining > 0 ? `${remaining} attempts remaining.` : 'Please request a new OTP.'}`,
      };
    }

    record.isUsed = true;
    const resetToken = `RST-LOCAL-${generateToken(24)}`;
    setLocalResetTokens((prev) => ({
      ...prev,
      [resetToken]: {
        userId: record.userId,
        expiresAt: Date.now() + 5 * 60 * 1000,
        isUsed: false,
      },
    }));

    return { success: true, resetToken };
  };

  // Admin Forgot Password - Step 3: Create New Password
  const resetAdminPasswordWithToken = async (
    resetToken: string,
    newPassword: string
  ): Promise<{ success: boolean; message?: string; error?: string }> => {
    const cleanPwd = newPassword.trim();
    if (cleanPwd.length < 6) {
      return { success: false, error: 'Password must be at least 6 characters long.' };
    }

    // Call Backend API
    try {
      const res = await fetch('/api/auth/forgot-password/reset-password', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ resetToken, newPassword: cleanPwd }),
      });
      const data = await res.json();
      if (res.ok && data.success) {
        return { success: true, message: data.message };
      } else if (data.error) {
        return { success: false, error: data.error };
      }
    } catch (e) {
      console.warn('[SRMS AUTH] Reset password fallback:', e);
    }

    // Fallback Client Simulation
    const tokenRec = localResetTokens[resetToken];
    if (!tokenRec || tokenRec.isUsed || Date.now() > tokenRec.expiresAt) {
      return { success: false, error: 'Reset token is invalid or has expired. Please restart password recovery.' };
    }

    tokenRec.isUsed = true;
    const freshSalt = generateSalt(16);
    const freshHash = hashPasswordSync(cleanPwd, freshSalt);

    setUsers((prev) =>
      prev.map((u) => {
        if (u.id === tokenRec.userId) {
          return {
            ...u,
            password: cleanPwd,
            passwordSalt: freshSalt,
            passwordHash: freshHash,
            tokenVersion: (u.tokenVersion || 1) + 1,
          };
        }
        return u;
      })
    );

    return {
      success: true,
      message: 'Password has been successfully updated. Previous credentials and sessions have been invalidated. Please log in with your new password.',
    };
  };

  const logout = async () => {
    try {
      await firebaseAuthService.logout();
    } catch (e) {
      console.warn('[Firebase Auth] Logout notice:', e);
    }

    /**
     * SIGNING OUT RETURNS YOU TO THE WEBSITE, NOT TO THE SIGN-IN SCREEN.
     *
     * Signing in from the home page puts /login in the address bar, so that
     * the address says where you are. Signing out then left that address in
     * place, and the application read it and showed the old SRMS sign-in
     * screen -- so leaving the system dropped you somewhere you had never
     * been rather than back at scalesupport.co.in.
     *
     * The address goes back to the root first, before `isAuthenticated`
     * turns false, because that flag is what triggers the re-render that
     * reads the path.
     *
     * Anyone who went to /login deliberately is unaffected: they are sent
     * to the website once, and the sign-in window is on its first screen.
     */
    try {
      if (window.location.pathname.replace(/\/+$/, '') !== '') {
        window.history.pushState({}, '', '/');
      }
    } catch {
      /* An address bar that will not change must not stop a sign-out. */
    }

    setIsAuthenticated(false);
    // So the next sign-in restores its scope instead of landing on zeros.
    scopeRestoredForRef.current = '';
    scopeRestoreInFlightRef.current = false;
    scopeRetriesRef.current = 0;
    setPendingScopeReload(null);
    try {
      removeStoredSessionItem('srms_auth_session');
      removeStoredSessionItem('srms_session_token');
      removeStoredSessionItem('srms_user_id');
    } catch {}

    if (currentUser) {
      logActivity(currentUser, 'LOGOUT', `User ${currentUser.name} signed out`);
    }

    /**
     * The cached copy of this person's data is wiped when they press Log Out.
     *
     * Documents are now kept in the browser's own storage so the app does not
     * pay to re-read the same book ten times a day. That copy has to go when
     * somebody deliberately signs out, because a field agent is often on a
     * shared phone and Log Out usually means handing it over.
     *
     * Closing the browser is deliberately NOT treated this way -- that is the
     * same person coming back to their own device, and keeping the cache is
     * where the saving comes from.
     *
     * Last, and awaited but never allowed to throw: Firestore is shut down to
     * clear its storage, so nothing may run against it afterwards, and a
     * failure here must never leave somebody stuck on a screen they have
     * asked to leave.
     */
    try {
      const cleared = await clearLocalDataCache();

      /**
       * AND THEN RELOAD, BECAUSE FIRESTORE HAS BEEN SHUT DOWN.
       *
       * Clearing the stored copy requires terminating Firestore first, and a
       * terminated instance never comes back -- it is not a pause, it is the
       * end of that connection. Without this reload the person would land on
       * the login screen looking perfectly normal, sign in again, and find
       * that nothing loaded and no error explained why.
       *
       * A reload costs nothing here: they have asked to leave, and it brings
       * the app back up clean with a fresh Firestore.
       */
      if (cleared && typeof window !== 'undefined') {
        window.location.reload();
      }
    } catch {
      /* best effort -- signing out has already happened */
    }
  };

  const logActivity = (
    user: User,
    actionType: ActivityLog['actionType'],
    details: string,
    accountId?: string
  ) => {
    const newLog: ActivityLog = {
      id: `ACT-${Date.now()}-${Math.floor(Math.random() * 1000)}`,
      timestamp: nowLocalStamp(),
      userId: user.agentId || user.id,
      userName: user.name,
      role: user.role,
      accountId,
      actionType,
      details,
    };
    setActivityLogs((prev) => [newLog, ...prev]);

    /**
     * AND WRITE IT DOWN.
     *
     * THE FAULT THIS FIXES. This function only ever added the entry to the
     * list held in the browser. Nothing reached the database -- so the
     * `activityLogs` collection was empty, and every entry disappeared on
     * the next refresh.
     *
     * The whole app was built as though they were being kept. The listener
     * reads that collection newest-first and caps it at 300; the agent
     * sign-in panel counts LOGIN entries out of it and reported zero for
     * everybody; the automatic balance repair writes one line per corrected
     * account so the administrator can read back exactly what moved, and
     * those lines were being lost within the minute. An audit trail that
     * does not survive a refresh is not an audit trail.
     *
     * One write per logged action: a few sign-ins a day, one line per
     * import, one per repair pass, one per delete.
     *
     * Fire and forget, deliberately. Recording that something happened must
     * never delay or fail the thing itself -- a collection is saved whether
     * or not its log entry reaches the database.
     */
    void firebaseFirestoreService
      .saveActivityLog(newLog)
      .catch((err) => console.warn('Activity log save notice:', err?.message || err));
  };

  // Reconcile and link multiple accounts by Name + NPA Date + Balance (Customer Overdue Balance)
  const reconcileAccounts = (accountsList: Account[]): Account[] => {
    const groupMap = new Map<string, Account[]>();

    accountsList.forEach((acc) => {
      const bal = Math.round(acc.customerBalance || acc.overdueAmount || acc.outstandingAmount || 0);
      const npa = acc.npaDate ? acc.npaDate.trim() : '';
      const normName = acc.customerName ? acc.customerName.toLowerCase().replace(/[^a-z0-9]/g, '') : '';

      let key = '';
      if (normName && normName.length > 2 && npa && bal > 0) {
        key = `NAME_NPA_BAL:${normName}__${npa}__${bal}`;
      } else if (bal > 0 && npa) {
        key = `BAL_NPA:${bal}_${npa}`;
      } else if (normName && normName.length > 3) {
        key = `NAME:${normName}`;
      } else {
        key = `ACC:${acc.accountId}`;
      }

      const list = groupMap.get(key) || [];
      list.push(acc);
      groupMap.set(key, list);
    });

    return accountsList.map((acc) => {
      const bal = Math.round(acc.customerBalance || acc.overdueAmount || acc.outstandingAmount || 0);
      const npa = acc.npaDate ? acc.npaDate.trim() : '';
      const normName = acc.customerName ? acc.customerName.toLowerCase().replace(/[^a-z0-9]/g, '') : '';

      let key = '';
      if (normName && normName.length > 2 && npa && bal > 0) {
        key = `NAME_NPA_BAL:${normName}__${npa}__${bal}`;
      } else if (bal > 0 && npa) {
        key = `BAL_NPA:${bal}_${npa}`;
      } else if (normName && normName.length > 3) {
        key = `NAME:${normName}`;
      } else {
        key = `ACC:${acc.accountId}`;
      }

      const group = groupMap.get(key) || [acc];
      if (group.length > 1) {
        const linkedSummaries = group.map((g) => ({
          accountId: g.accountId,
          loanNumber: g.loanNumber,
          loanType: g.loanType,
          facility: g.facility,
          balance: g.customerBalance || g.overdueAmount || g.outstandingAmount,
          npaDate: g.npaDate || '',
          overdueAmount: g.overdueAmount,
          status: g.accountStatus,
          branch: g.branch,
        }));

        const reason = key.startsWith('NAME_NPA_BAL:')
          ? `Multi-Account Customer: Name (${acc.customerName}), Overdue Balance (₹${bal.toLocaleString('en-IN')}) & NPA Date (${acc.npaDate}) match across ${group.length} loans`
          : `Multi-Account Customer: ${group.length} loans linked under customer profile`;

        return {
          ...acc,
          isMultipleAccount: true,
          multipleAccountsCount: group.length,
          linkedAccountIds: group.map((g) => g.accountId),
          linkedAccounts: linkedSummaries,
          multipleAccountReason: acc.multipleAccountReason || reason,
        };
      }
      return acc;
    });
  };

  const addAccount = (accData: Partial<Account>) => {
    const newId = accData.accountId || `ACC-${10260 + accounts.length}`;
    const newAccount: Account = {
      id: newId,
      accountId: newId,
      loanNumber: accData.loanNumber || `LN-2026-${Math.floor(10000 + Math.random() * 90000)}`,
      customerName: accData.customerName || 'New Customer',
      // Blank rather than invented -- see the matching block in
      // bulkAddAccounts for why a made-up branch or mobile is worse than an
      // empty one.
      mobile: accData.mobile || '',
      alternateMobile: accData.alternateMobile,
      address: accData.address || '',
      city: accData.city || '',
      branch: accData.branch || '',
      branchCode: accData.branchCode || '',
      facility: accData.facility || '',
      // Blank is blank -- see the note in bulkAddAccounts.
      npaDate: accData.npaDate || '',
      customerCode: accData.customerCode || `CUST-${newId}`,
      area: accData.area || '',
      zone: accData.zone || '',
      loanType: accData.loanType || 'Personal Loan',
      sanctionAmount: Number(accData.sanctionAmount) || 0,
      outstandingAmount: Number(accData.outstandingAmount) || 0,
      overdueAmount: Number(accData.overdueAmount) || 0,
      emi: Number(accData.emi) || 0,
      ptpCount: accData.ptpCount ?? 0,
      customerCategory: accData.customerCategory || 'Medium Risk',
      accountStatus: accData.accountStatus || 'Active',
      assignedAgentId: accData.assignedAgentId || currentUser.agentId || currentUser.id,
      assignedAgentName: accData.assignedAgentName || currentUser.name,
      coordinatorId: 'USR-COORD-1',
      coordinatorName: currentUser.name || '',
      allocationDate: todayLocal(),
      totalRecovered: 0,
      // No invented coordinates. 18.5204/73.8567 is the centre of Pune, so
      // every account without a location pinned itself there -- and a field
      // visit could be recorded against a map point nobody had ever been to.
      latitude: accData.latitude,
      longitude: accData.longitude,
      notes: accData.notes,
      isMultipleAccount: accData.isMultipleAccount,
      multipleAccountsCount: accData.multipleAccountsCount,
      linkedAccountIds: accData.linkedAccountIds,
      linkedAccounts: accData.linkedAccounts,
      multipleAccountReason: accData.multipleAccountReason,
      latestRemark: accData.latestRemark,
      agentNotesHistory: accData.agentNotesHistory || [],
    };

    // If account was previously deleted/archived, restore historical remarks and notes
    const priorArchived = archivedAccounts.find(
      (arch) => arch.accountId === newId || (accData.loanNumber && arch.loanNumber === accData.loanNumber)
    );
    if (priorArchived) {
      const priorNotes = priorArchived.agentNotesHistory || [];
      const restoredNote: AgentNote = {
        id: `NOTE-RESTORED-${Date.now()}`,
        agentId: currentUser.agentId || currentUser.id,
        agentName: currentUser.name,
        date: todayLocal(),
        time: new Date().toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' }),
        note: `Restored historical records & remarks from previously archived data upon reassignment (${priorNotes.length} historical remarks retrieved).`,
        category: 'General',
      };
      newAccount.agentNotesHistory = [restoredNote, ...priorNotes, ...(newAccount.agentNotesHistory || [])];
      if (!newAccount.latestRemark && priorArchived.latestRemark) {
        newAccount.latestRemark = `[Restored Remark]: ${priorArchived.latestRemark}`;
      }
    }

    try {
      localStorage.removeItem('srms_account_storage_cleared_user');
    } catch {}

    mutateAccounts((prev) => reconcileAccounts([newAccount, ...prev]));

    // Persistence is handled by mutateAccounts above.

    logActivity(currentUser, 'ALLOCATION_CHANGED', `Created loan account ${newId} for ${newAccount.customerName}`, newId);
  };

  const updateAccount = (
    accountId: string,
    updates: Partial<Account>,
    options?: { allowViewOnly?: boolean }
  ) => {
    let persistAccount: Account | null = null;

    mutateAccounts((prev) =>
      reconcileAccounts(
        prev.map((acc) => {
          if (acc.accountId === accountId || acc.id === accountId) {
            let updatedNotesHistory = updates.agentNotesHistory || acc.agentNotesHistory || [];
            // Preserve old remarks: if new notes are added/edited and different from previous note, append historical entry
            if (updates.notes && updates.notes.trim() !== '' && updates.notes !== acc.notes) {
              const noteEntry: AgentNote = {
                id: `NOTE-UPD-${Date.now()}`,
                agentId: currentUser.agentId || currentUser.id,
                agentName: currentUser.name,
                date: todayLocal(),
                time: new Date().toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' }),
                note: updates.notes,
                category: 'General',
              };
              updatedNotesHistory = [noteEntry, ...updatedNotesHistory];
            }
            const merged = {
              ...acc,
              ...updates,
              agentNotesHistory: updatedNotesHistory,
            };
            persistAccount = merged;
            return merged;
          }
          return acc;
        })
      ),
      // Only the OTS approval path passes this. Everything else is refused for
      // a view-only role by the funnel above.
      options?.allowViewOnly ? { allowViewOnly: true } : undefined
    );

    // Persistence is handled by mutateAccounts above.

    logActivity(currentUser, 'SETTINGS_UPDATED', `Updated account ${accountId} details`, accountId);
  };

  const markAccountForDeletion = (accountId: string, isMarked: boolean = true) => {
    mutateAccounts((prev) =>
      prev.map((a) =>
        a.accountId === accountId || a.id === accountId
          ? {
              ...a,
              isMarkedForDeletion: isMarked,
              notes: isMarked
                ? `[Marked for Permanent Deletion by ${currentUser.name} on ${todayLocal()}]. ${a.notes || ''}`
                : a.notes,
            }
          : a
      )
    );
    logActivity(
      currentUser,
      'SETTINGS_UPDATED',
      `${isMarked ? 'Marked' : 'Unmarked'} customer account ${accountId} for permanent deletion by Admin.`,
      accountId
    );
  };

  const updateCustomerStarRating = (accountId: string, rating: number) => {
    const clampedRating = Math.max(0, Math.min(5, Math.round(rating)));
    const isPrime = clampedRating >= 4;
    const nowIso = new Date().toISOString();
    const dateStr = nowIso.slice(0, 10);
    const timeStr = new Date().toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });

    mutateAccounts((prev) =>
      prev.map((a) => {
        if (a.accountId === accountId || a.id === accountId) {
          const starNote: AgentNote = {
            id: `NOTE-STAR-${Date.now()}`,
            agentId: currentUser.agentId || currentUser.id,
            agentName: currentUser.name,
            date: dateStr,
            time: timeStr,
            note: `[Customer Classification]: Star rating set to ${clampedRating}★${
              isPrime ? ' (🌟 Prime Customer)' : ''
            } by ${currentUser.name} (${currentUser.role}).`,
            category: 'General',
          };
          return {
            ...a,
            customerStarRating: clampedRating,
            isPrimeCustomer: isPrime,
            starRatingUpdatedBy: currentUser.name,
            starRatingUpdatedAt: nowIso,
            agentNotesHistory: [starNote, ...(a.agentNotesHistory || [])],
          };
        }
        return a;
      })
    );

    logActivity(
      currentUser,
      'STATUS_CHANGED',
      `${currentUser.name} updated customer classification to ${clampedRating}★ for account ${accountId}`,
      accountId
    );
  };

  const closeAccount = async (
    accountId: string,
    closureType: 'Closed as per OTS' | 'Regular Close' | 'Administrative Deletion',
    remarks?: string
  ): Promise<{ success: boolean; message: string }> => {
    // Deleting is permanent and reaches the database. Only an administrator
    // may do it -- explicitly checked here rather than relied on from a hidden
    // button, because a hidden button is not a restriction.
    if (currentUser.role !== 'admin') {
      return {
        success: false,
        message:
          'Only an administrator can delete a customer account. Deleting reaches the database and cannot be undone.',
      };
    }

    const targetAcc = accounts.find((a) => a.accountId === accountId || a.id === accountId);
    if (!targetAcc) {
      return { success: false, message: 'Customer account not found.' };
    }
    const dateStr = todayLocal();
    const timeStr = new Date().toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
    const closeNote: AgentNote = {
      id: `NOTE-CLOSE-${Date.now()}`,
      agentId: currentUser.agentId || currentUser.id,
      agentName: currentUser.name,
      date: dateStr,
      time: timeStr,
      note: `[Account Closed]: Status marked as '${closureType}'. ${remarks || 'Account officially closed.'}`,
      category: 'General',
    };

    mutateAccounts((prev) =>
      prev.map((a) => {
        if (a.accountId === accountId || a.id === accountId) {
          return {
            ...a,
            accountStatus: closureType === 'Closed as per OTS' ? 'Closed as per OTS' : 'Regular Close',
            closureType,
            closureDate: dateStr,
            closureRemarks: remarks,
            notes: `[${closureType}]: ${remarks || 'Account closed.'} (${a.notes || ''})`,
            agentNotesHistory: [closeNote, ...(a.agentNotesHistory || [])],
          };
        }
        return a;
      })
    );

    logActivity(
      currentUser,
      'STATUS_CHANGED',
      `${currentUser.name} closed account ${targetAcc.customerName} (${targetAcc.accountId}) as '${closureType}'.`,
      targetAcc.accountId
    );

    return {
      success: true,
      message: `Customer account ${targetAcc.customerName} marked as '${closureType}'.`,
    };
  };

  /**
   * Removes an account's document from the database, whatever key it is
   * filed under.
   *
   * Records here are saved as `id || accountId`, the live feed reports its
   * own key, and imported rows may use a third. Deleting a key that does not
   * exist is treated by Firestore as SUCCESS, so a wrong guess is invisible.
   * This tries every key AND a search on the account number, then reports
   * how many documents actually went.
   */
  const removeAccountDocs = async (acc: Account): Promise<number> => {
    const keys = Array.from(
      new Set(
        [(acc as any).__docId, acc.id, acc.accountId].filter(
          (v): v is string => typeof v === 'string' && v !== ''
        )
      )
    );

    let removed = 0;
    for (const k of keys) {
      try {
        await firebaseFirestoreService.deleteAccount(k);
        removed += 1;
      } catch (err) {
        console.warn('[Firestore] account key delete notice:', k, err);
      }
    }

    try {
      removed += await firebaseFirestoreService.deleteWhere(
        'accounts',
        'accountId',
        acc.accountId
      );
    } catch (err) {
      console.warn('[Firestore] account search delete notice:', err);
    }

    return removed;
  };

  const deleteAccount = async (
    accountId: string,
    closureType?: 'Closed as per OTS' | 'Regular Close' | 'Administrative Deletion',
    remarks?: string
  ): Promise<{ success: boolean; message: string }> => {
    const targetAcc = accounts.find((a) => a.accountId === accountId || a.id === accountId);
    if (!targetAcc) {
      return { success: false, message: 'Customer account not found.' };
    }

    const isClosedOrSettled =
      closureType === 'Closed as per OTS' ||
      closureType === 'Regular Close' ||
      targetAcc.accountStatus === 'Closed as per OTS' ||
      targetAcc.accountStatus === 'Regular Close' ||
      targetAcc.accountStatus === 'Closed' ||
      targetAcc.accountStatus === 'OTS Settled – Customer Account Closed' ||
      (targetAcc.outstandingAmount === 0 && targetAcc.overdueAmount === 0);

    /**
     * DELETING AN ACCOUNT IS THE ADMINISTRATOR'S, AND NOBODY ELSE'S.
     *
     * WHAT THIS REPLACES, AND THE BUG IT FIXES. The rule here used to be
     * "an administrator may delete anything; anyone at all may delete an
     * account that is closed or settled". The Firestore rules say something
     * different and always have: `allow delete: if isAdmin()`, no exceptions.
     *
     * So an agent pressing Delete on a closed account was offered the
     * action, allowed through this check, and then refused by the database
     * -- which is the "You do not have permission" message you were getting.
     * It was not a broken permission. It was two different answers to the
     * same question, and the screen was giving the more generous one.
     *
     * They now agree, and they agree on the stricter answer, which is also
     * the one you asked for: only an administrator deletes an account.
     * Everyone else closes it, which keeps the record and its history.
     *
     * `super_admin` is accepted here as well so that nothing breaks on the
     * day the role arrives.
     */
    const role = String(currentUser.role || '');
    const mayDelete = role === 'admin' || role === 'super_admin';

    if (!mayDelete) {
      return {
        success: false,
        message:
          'Only an administrator can delete a customer account. Use Close Account instead — the account stays in the list with its full history.',
      };
    }

    // Kept so the closure type below still reflects how the account ended.
    void isClosedOrSettled;

    const finalClosureType = closureType || (targetAcc.otsStatus === 'OTS Settled' || targetAcc.otsActive ? 'Closed as per OTS' : 'Regular Close');
    const dateStr = todayLocal();

    const closedArchivedAcc: Account = {
      ...targetAcc,
      accountStatus: finalClosureType === 'Closed as per OTS' ? 'Closed as per OTS' : 'Regular Close',
      closureType: finalClosureType,
      closureDate: dateStr,
      closureRemarks: remarks,
      notes: `[Archived as ${finalClosureType} by ${currentUser.name} on ${dateStr}]: ${remarks || ''} ${targetAcc.notes || ''}`,
    };

    // Preserve full backend data including remarks and history for this account
    setArchivedAccounts((prev) => {
      const updated = [closedArchivedAcc, ...prev.filter((a) => a.accountId !== targetAcc.accountId && a.id !== targetAcc.id)];
      try {
        localStorage.setItem('srms_archived_accounts', JSON.stringify(updated));
      } catch {}
      return updated;
    });

    // Delete from the database, not just from the screen.
    //
    // mutateAccounts saves accounts that CHANGED; it has no notion of one
    // being removed. So filtering the list here deleted nothing, and the
    // live listener brought the account back on the next sync.
    // One routine for every delete path, so single and bulk cannot drift
    // apart again.
    let removedDocs = 0;
    try {
      removedDocs = await removeAccountDocs(targetAcc);
    } catch (err: any) {
      console.error('[Firestore] Account delete FAILED:', err);
      return {
        success: false,
        message:
          err?.code === 'permission-denied'
            ? 'You do not have permission to delete this account.'
            : err?.message || 'Could not delete the account. Please try again.',
      };
    }

    if (removedDocs === 0) {
      return {
        success: false,
        message:
          `Nothing was deleted from the database for ${targetAcc.accountId}.\n\n` +
          `Keys tried: ${[(targetAcc as any).__docId, targetAcc.id, targetAcc.accountId]
            .filter(Boolean)
            .join(', ')}\n\n` +
          `Please send this message to Claude.`,
      };
    }

    mutateAccounts((prev) => prev.filter((a) => a.accountId !== accountId && a.id !== accountId));

    logActivity(
      currentUser,
      'SETTINGS_UPDATED',
      `${currentUser.name} (${currentUser.role}) deleted/archived customer account ${targetAcc.customerName} (${targetAcc.accountId || targetAcc.loanNumber}) as '${finalClosureType}'. Historical records preserved in backend archive.`,
      targetAcc.accountId
    );

    return {
      success: true,
      message: `Customer account ${targetAcc.customerName} (${targetAcc.accountId}) removed as '${finalClosureType}'. Record preserved in backend archive.`,
    };
  };

  const deleteAllAccountsFromStorage = async (): Promise<{ success: boolean; count: number; message: string }> => {
    const count = accounts.length;
    setAccounts([]);
    setAllocations([]);
    setAllocationHistories([]);

    try {
      localStorage.setItem('srms_persisted_accounts', JSON.stringify([]));
      localStorage.setItem('srms_persisted_allocations', JSON.stringify([]));
      localStorage.setItem('srms_persisted_allochistories', JSON.stringify([]));
      localStorage.setItem('srms_account_storage_cleared_user', 'true');
    } catch {}

    // Immediately cancel any queued auto-push that might have captured prior account state
    if (autoPushTimerRef.current) {
      clearTimeout(autoPushTimerRef.current);
      autoPushTimerRef.current = null;
    }

    // Immediately tell the server to permanently clear accounts from server master storage
    try {
      await fetch('/api/master-data/clear-accounts', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
      });
    } catch (err) {
      console.warn('[SRMS] Failed to notify server about clearing accounts:', err);
    }

    logActivity(
      currentUser,
      'SETTINGS_UPDATED',
      `Permanently deleted all ${count} account records from permanent storage (only accounts cleared).`
    );

    return {
      success: true,
      count,
      message: `Permanently deleted all ${count} account records from permanent storage. Account storage is now empty (0 accounts).`,
    };
  };

  const deleteBulkAccounts = async (options: {
    scope: 'all' | 'agent' | 'branch' | 'ids';
    agentId?: string;
    agentName?: string;
    branchName?: string;
    accountIds?: string[];
    permanent?: boolean;
  }): Promise<{ success: boolean; count: number; message: string }> => {
    if (currentUser.role !== 'admin') {
      return {
        success: false,
        count: 0,
        message:
          'Only an administrator can delete accounts in bulk. This reaches the database and cannot be undone.',
      };
    }

    if (options.scope === 'all') {
      return deleteAllAccountsFromStorage();
    }

    let targetAccounts: Account[] = [];

    if (options.scope === 'agent') {
      targetAccounts = accounts.filter((a) => {
        if (!options.agentId || options.agentId === 'unassigned') {
          return !a.assignedAgentId || a.assignedAgentId === '' || a.assignedAgentId === 'unassigned';
        }
        return (
          a.assignedAgentId === options.agentId ||
          a.assignedAgentId?.toLowerCase() === options.agentId?.toLowerCase() ||
          (options.agentName && a.assignedAgentName?.toLowerCase() === options.agentName?.toLowerCase())
        );
      });
    } else if (options.scope === 'branch') {
      targetAccounts = accounts.filter(
        (a) =>
          options.branchName &&
          ((a.branch || 'Head Office / Unspecified').toLowerCase() === options.branchName.toLowerCase() ||
            (a.branchName || '').toLowerCase() === options.branchName.toLowerCase())
      );
    } else if (options.scope === 'ids' && options.accountIds) {
      const idSet = new Set(options.accountIds);
      targetAccounts = accounts.filter((a) => idSet.has(a.accountId) || (a.id && idSet.has(a.id)));
    }

    if (targetAccounts.length === 0) {
      return {
        success: false,
        count: 0,
        message: 'No matching accounts found for the specified criteria.',
      };
    }

    const targetAccountIds = new Set(targetAccounts.map((a) => a.accountId));
    const count = targetAccounts.length;
    const dateStr = todayLocal();

    // If archiving (non-permanent), preserve full account history in backend archive
    if (!options.permanent) {
      const archivedRecords: Account[] = targetAccounts.map((a) => ({
        ...a,
        accountStatus: 'Closed' as const,
        closureType: 'Administrative Deletion' as const,
        closureDate: dateStr,
        closureRemarks: `Bulk deleted by ${currentUser.name} (Scope: ${options.scope})`,
      }));
      setArchivedAccounts((prev) => {
        const updated = [...archivedRecords, ...prev.filter((p) => !targetAccountIds.has(p.accountId))];
        try {
          localStorage.setItem('srms_archived_accounts', JSON.stringify(updated));
        } catch {}
        return updated;
      });
    }

    // Delete from the DATABASE, not just from the screen.
    //
    // This function removed the accounts from the local list, saved that to
    // the browser, reported "Successfully deleted" -- and never touched
    // Firestore. Every bulk delete since the app was built has left every
    // record in place, and it came back on the next load.
    const doomed = accountsRef.current.filter((a) =>
      targetAccountIds.has(a.accountId)
    );

    // All of them in one or two batched calls, not one call per account.
    // Deleting singly cost about half a second each, so clearing an agent's
    // whole book took minutes. Every key each account could be filed under
    // goes into the same batch; keys that do not exist are simply ignored.
    const allKeys: string[] = [];
    doomed.forEach((a) => {
      [(a as any).__docId, a.id, a.accountId].forEach((k) => {
        if (typeof k === 'string' && k) allKeys.push(k);
      });
    });

    let docsRemoved = 0;
    try {
      docsRemoved = await firebaseFirestoreService.batchDeleteDocs(
        'accounts',
        allKeys
      );
    } catch (err) {
      console.error('[Firestore] Bulk delete FAILED:', err);
      return {
        success: false,
        count: 0,
        message:
          'Could not delete these accounts from the database. Nothing was removed.',
      };
    }
    console.info(
      `[Firestore] Bulk delete: ${docsRemoved} key(s) cleared for ${doomed.length} account(s).`
    );

    // Filter out target accounts from active accounts
    mutateAccounts((prev) => {
      const remaining = prev.filter((a) => !targetAccountIds.has(a.accountId));
      try {
        localStorage.setItem('srms_persisted_accounts', JSON.stringify(remaining));
      } catch {}
      return remaining;
    });

    // Remove matching allocations
    setAllocations((prev) => prev.filter((al) => !targetAccountIds.has(al.accountId)));

    // Cancel pending auto-push debounce if needed
    if (autoPushTimerRef.current) {
      clearTimeout(autoPushTimerRef.current);
      autoPushTimerRef.current = null;
    }

    const scopeDesc =
      options.scope === 'agent'
        ? `assigned to Agent ${options.agentName || options.agentId}`
        : options.scope === 'branch'
        ? `under Branch ${options.branchName}`
        : `selected accounts`;

    logActivity(
      currentUser,
      'SETTINGS_UPDATED',
      `Bulk deleted ${count} account(s) ${scopeDesc}. Method: ${options.permanent ? 'Permanent Hard Delete' : 'Archived with history preserved'}.`
    );

    return {
      success: true,
      count,
      message: `Successfully deleted ${count} customer account(s) ${scopeDesc}.`,
    };
  };

  const bulkAddAccounts = (accountsList: Partial<Account>[]) => {
    try {
      localStorage.removeItem('srms_account_storage_cleared_user');
    } catch {}

    let reallocatedCount = 0;
    let newAccountsCount = 0;
    /** Accounts re-uploaded onto collections that outlived them. */
    let reattachedAccounts = 0;
    let reattachedAmount = 0;
    /**
     * Rows that arrived with no balance at all. These used to be given an
     * invented Rs 85,000; they are now 0, and the count is reported so the
     * sheet can be corrected rather than the gap going unnoticed.
     */
    let noBalanceRows = 0;
    const nowIsoDate = todayLocal();
    const nowIsoTime = new Date().toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
    const nowTimestamp = nowLocalStamp();

    const newHistories: AllocationHistory[] = [];
    const newAllocations: AccountAllocation[] = [];
    const newNotifs: NotificationItem[] = [];
    // Only the rows this import actually touches are written back.
    const touchedAccounts: Account[] = [];

    // Computed OUTSIDE setAccounts: React does not invoke a state updater
    // synchronously, so anything collected inside one is still empty when
    // the Firestore write fires.
    const runImport = (prevAccounts: Account[]): Account[] => {
      const updatedAccounts = [...prevAccounts];

      accountsList.forEach((incoming, idx) => {
        // Resolve agent info from users list if possible
        const rawAgentId = incoming.assignedAgentId || currentUser.agentId || currentUser.id;
        // Null-safe matching. User records loaded from Firestore may be
        // missing any of these fields -- older rows have no `username`, and
        // calling .toLowerCase() on undefined crashed the whole Excel import.
        const lc = (v: any) => (typeof v === 'string' ? v.toLowerCase() : '');
        const wanted = lc(rawAgentId);
        const wantedName = lc(incoming.assignedAgentName);

        const matchedUser = wanted
          ? users.find(
              (u) =>
                (lc(u.agentId) && lc(u.agentId) === wanted) ||
                (lc(u.id) && lc(u.id) === wanted) ||
                (lc(u.username) && lc(u.username) === wanted) ||
                (lc((u as any).loginId) && lc((u as any).loginId) === wanted) ||
                (wantedName && lc(u.name).includes(wantedName))
            )
          : undefined;

        const targetAgentId = matchedUser?.agentId || matchedUser?.id || rawAgentId;
        const targetAgentName = matchedUser?.name || incoming.assignedAgentName || currentUser.name;

        // Match existing account by accountId, loanNumber, or id
        const existingIndex = updatedAccounts.findIndex(
          (acc) =>
            isMatchingAccountNumber(acc.accountId, incoming.accountId) ||
            isMatchingAccountNumber(acc.loanNumber, incoming.loanNumber) ||
            isMatchingAccountNumber(acc.accountId, incoming.loanNumber) ||
            isMatchingAccountNumber(acc.loanNumber, incoming.accountId) ||
            isMatchingAccountNumber(acc.id, incoming.id) ||
            isMatchingAccountNumber(acc.id, incoming.accountId)
        );

        if (existingIndex >= 0) {
          // EXISTING ACCOUNT: Deallocate old agent and reallocate to new agent from Excel
          const existing = updatedAccounts[existingIndex];
          const oldAgentId = existing.assignedAgentId;
          const oldAgentName = existing.assignedAgentName;
          const isAgentChanged = oldAgentId !== targetAgentId;

          if (isAgentChanged) {
            reallocatedCount++;

            // 1. Record Allocation History (Old Agent Deallocation -> New Agent Allocation)
            newHistories.push({
              id: `ALH-${Date.now()}-${idx}`,
              accountId: existing.accountId,
              previousAgentId: oldAgentId,
              previousAgentName: oldAgentName,
              newAgentId: targetAgentId,
              newAgentName: targetAgentName,
              reallocatedBy: currentUser.name,
              date: nowTimestamp,
              reason: `Reallocated via Excel Upload: Deallocated old agent (${oldAgentName} [${oldAgentId}]) and allocated to new agent (${targetAgentName} [${targetAgentId}])`,
            });

            // 2. Record Account Allocation
            newAllocations.push({
              id: `ALC-${Date.now()}-${idx}`,
              accountId: existing.accountId,
              agentId: targetAgentId,
              agentName: targetAgentName,
              coordinatorId: currentUser.id,
              allocatedBy: currentUser.name,
              allocationDate: nowTimestamp,
              branch: incoming.branch || existing.branch,
              area: incoming.area || existing.area,
              zone: incoming.zone || existing.zone,
              remarks: `Deallocated from ${oldAgentName} and reallocated to ${targetAgentName} via Excel`,
            });
          }

          /**
           * No "System: Account data refreshed..." note any more.
           *
           * It was written into every account on every upload -- four hundred
           * account documents rewritten to say that an upload had happened,
           * and four hundred entries added to customers' Agent Notes, where
           * only an agent's own remarks belong. The upload is recorded once in
           * the activity log, which is where an audit trail belongs.
           */
          const existingNotes = existing.agentNotesHistory || [];

          /**
           * A RE-UPLOAD MUST NOT UNDO WHAT HAS BEEN COLLECTED.
           *
           * THE MONEY BUG THIS FIXES -- the largest one in the app.
           *
           * The spreadsheet carries the bank's ORIGINAL figures: the full
           * outstanding, and no collections, because the bank does not know
           * what this office has recovered since. The importer sets
           * `totalRecovered: 0` and puts that gross figure into all three
           * balance fields.
           *
           * Merging that over an existing account therefore set recovered-to-
           * date back to zero and restored the balance to the pre-recovery
           * amount -- while every receipt stayed in the ledger. The account
           * read "Fully Recovered" with nothing recovered, and the customer
           * owed the whole loan again. Upload the same sheet twice and a
           * month of collections was undone.
           *
           * Now the sheet updates the customer's DETAILS -- name, address,
           * mobile, branch, NPA date, facility -- and the sanctioned figure.
           * The money is recalculated from the receipts this office holds,
           * against the original outstanding, which is written once from the
           * first sheet that carried it and never overwritten afterwards.
           */
          const importMoney = recalcAccountMoney(
            {
              ...existing,
              // The first sheet establishes the original; later ones cannot
              // move it, because by then collections have been made against it.
              originalOutstandingAmount:
                Number(existing.originalOutstandingAmount) > 0
                  ? existing.originalOutstandingAmount
                  : originalOutstandingOf({
                      outstandingAmount:
                        incoming.outstandingAmount !== undefined
                          ? incoming.outstandingAmount
                          : existing.outstandingAmount,
                      totalRecovered: 0,
                    }),
            },
            recoveriesRef.current.filter((r) => r.accountId === existing.accountId),
            // Derived, for the same reason as the delete path.
            otsWaiverOf(existing)
          );

          // Merge the customer's details; never the money.
          const { customerBalance: _cb, outstandingAmount: _oa, overdueAmount: _od,
                  totalRecovered: _tr, recoveryStatus: _rs, ...incomingDetails } =
            incoming as any;

          updatedAccounts[existingIndex] = {
            ...existing,
            ...incomingDetails,
            assignedAgentId: targetAgentId,
            assignedAgentName: targetAgentName,
            allocationDate: isAgentChanged ? nowIsoDate : (existing.allocationDate || nowIsoDate),
            ...importMoney,
            sanctionAmount: incoming.sanctionAmount !== undefined ? incoming.sanctionAmount : existing.sanctionAmount,
            npaDate: incoming.npaDate || existing.npaDate,
            // Blank cell keeps the category from the previous upload.
            accountCategory: incoming.accountCategory || existing.accountCategory,
            facility: incoming.facility || existing.facility,
            branch: incoming.branch || existing.branch,
            address: incoming.address || existing.address,
            mobile: incoming.mobile || existing.mobile,
            accountStatus: existing.accountStatus || incoming.accountStatus,
            agentNotesHistory: existingNotes,
          };
          touchedAccounts.push(updatedAccounts[existingIndex]);
        } else {
          // BRAND NEW ACCOUNT
          newAccountsCount++;
          const newId = incoming.accountId || `ACC-${10260 + prevAccounts.length + idx}`;
          if (!(Number(incoming.outstandingAmount) > 0)) noBalanceRows += 1;

          const newAccount: Account = {
            id: newId,
            accountId: newId,
            loanNumber: incoming.loanNumber || `LN-2026-${Math.floor(10000 + Math.random() * 90000)}`,
            customerName: incoming.customerName || 'Customer',

            /**
             * BLANK IS BLANK. NOTHING IS INVENTED HERE ANY MORE.
             *
             * These fields used to default to one city, one branch, one area
             * and one zone: Pune, Pune Central Branch, Pune Metro, West Zone
             * Maharashtra -- left over from whoever the app was first built
             * against. Any account whose sheet did not carry a branch was
             * silently FILED under "Pune Central Branch".
             *
             * That is not a cosmetic fault. The branch is what the branch
             * filters, the scope bar, the allocation screens and the
             * commission BILL all group by -- and a bill names the branch its
             * manager is asked to sign for. An account from Garkheda with a
             * blank branch column would appear under a Pune branch that does
             * not exist, in a list the real branch manager would never see.
             *
             * A fake mobile number was worse still: '+91 98000 00000' looked
             * like a real number on screen, in the calling sheet and in the
             * WhatsApp offer, so an agent could sit dialling a number that
             * belonged to nobody and record it as a failed contact.
             *
             * Empty now. A blank field shows as blank, which is the truth and
             * can be chased; an invented one cannot even be noticed.
             */
            mobile: incoming.mobile || '',
            alternateMobile: incoming.alternateMobile,
            address: incoming.address || '',
            city: incoming.city || '',
            branch: incoming.branch || '',
            branchCode: incoming.branchCode || '',
            facility: incoming.facility || '',
            /**
             * NO INVENTED MONEY, AND NO INVENTED NPA DATE.
             *
             * A row whose balance cell is blank used to arrive as a
             * Rs 85,000 loan owing Rs 18,000, sanctioned at Rs 1,00,000, with
             * an EMI of Rs 6,000 and an NPA date of 31-03-2024. Figures
             * nobody entered, on a real borrower's account.
             *
             * The NPA date was the worse half: the NPA date decides which OTS
             * SLAB an account falls in, so a made-up date produced a
             * settlement offer calculated under the wrong rule.
             *
             * Blank is blank. A missing figure is 0 and a missing date is
             * empty, which shows on screen as missing and can be chased. The
             * import reports how many rows had no balance -- see the count
             * raised below.
             */
            npaDate: incoming.npaDate || '',
            customerCode: incoming.customerCode || `CUST-${newId}`,
            area: incoming.area || '',
            zone: incoming.zone || '',
            loanType: incoming.loanType || 'Personal Loan',
            sanctionAmount: Number(incoming.sanctionAmount) || 0,
            outstandingAmount: Number(incoming.outstandingAmount) || 0,
            // counted below for the import summary
            customerBalance: Number(incoming.customerBalance) || Number(incoming.overdueAmount) || 0,
            overdueAmount: Number(incoming.overdueAmount) || 0,
            emi: incoming.emi || 0,
            ptpCount: incoming.ptpCount ?? 0,
            customerCategory: incoming.customerCategory || 'Medium Risk',
            accountCategory: incoming.accountCategory || undefined,
            accountStatus: incoming.accountStatus || 'Active',
            assignedAgentId: targetAgentId,
            assignedAgentName: targetAgentName,
            coordinatorId: 'USR-COORD-1',
            // The person doing the import, or nobody. "Kavita Joshi" was
            // another invented name that would have been recorded as the
            // coordinator on real accounts.
            coordinatorName: currentUser.name || '',
            allocationDate: incoming.allocationDate || nowIsoDate,
            totalRecovered: 0,
            // What the sheet gave, or nothing. See the importer -- this was
            // the same invented-location line.
            latitude: incoming.latitude,
            longitude: incoming.longitude,
            notes: incoming.notes,
            isMultipleAccount: incoming.isMultipleAccount,
            multipleAccountsCount: incoming.multipleAccountsCount,
            linkedAccountIds: incoming.linkedAccountIds,
            linkedAccounts: incoming.linkedAccounts,
            multipleAccountReason: incoming.multipleAccountReason,
            agentNotesHistory: incoming.agentNotesHistory || [],
            customFields: incoming.customFields || {},
          };

          /**
           * COLLECTIONS THAT SURVIVED THE ACCOUNT ARE APPLIED HERE, AT IMPORT.
           *
           * THE RULE THIS IMPLEMENTS:
           *   delete an account   -> its collections stay
           *   delete a collection -> the money goes back on the account
           *   upload that account again -> the surviving collections come
           *                                off it, immediately
           *
           * WHAT IT FIXES. A re-uploaded account used to start at the sheet's
           * figure with nothing recovered, which LOOKED right. The old
           * receipts were still in the database under the same account
           * number, and the first thing that recalculated the account --
           * a delete, or Check and repair, possibly days later -- found them
           * and took the money off then. A balance that moved with no action
           * behind it and nothing on screen to explain it.
           *
           * Applying them now means the account is correct from the moment it
           * is uploaded, and the figure never changes by itself afterwards.
           *
           * Matched on every identifier a collection could be filed under,
           * for the same reason the delete is.
           */
          const newKeys = [newId, newAccount.loanNumber, newAccount.accountId]
            .filter((k): k is string => typeof k === 'string' && k.trim() !== '')
            .map((k) => k.trim().toLowerCase());
          const priorCollections = recoveriesRef.current.filter(
            (r) =>
              newKeys.includes(String(r.accountId || '').trim().toLowerCase()) &&
              !(r as any).isReversed
          );

          if (priorCollections.length > 0) {
            const sheetBalance = Number(newAccount.outstandingAmount) || 0;
            const money = recalcAccountMoney(
              { ...newAccount, originalOutstandingAmount: sheetBalance, totalRecovered: 0 },
              priorCollections,
              0 // no settlement can be in force on an account that was deleted
            );
            Object.assign(newAccount, money, {
              otsPaidAmount: Number(money.totalRecovered) || 0,
              lastRecoveryDate:
                priorCollections
                  .map((r) => r.recoveryDate || '')
                  .sort()
                  .pop() || (newAccount as any).lastRecoveryDate,
            });
            reattachedAccounts += 1;
            reattachedAmount += Number(money.totalRecovered) || 0;
          } else {
            // Written explicitly so the figure is never reconstructed later
            // from fields that a delete may have left out of step.
            newAccount.originalOutstandingAmount = Number(newAccount.outstandingAmount) || 0;
          }

          newAllocations.push({
            id: `ALC-${Date.now()}-${idx}`,
            accountId: newId,
            agentId: targetAgentId,
            agentName: targetAgentName,
            coordinatorId: currentUser.id,
            allocatedBy: currentUser.name,
            allocationDate: nowTimestamp,
            branch: newAccount.branch,
            area: newAccount.area,
            zone: newAccount.zone,
            remarks: `New account allocated via Excel upload`,
          });

          updatedAccounts.unshift(newAccount);
          touchedAccounts.push(newAccount);
        }
      });

      return reconcileAccounts(updatedAccounts);
    };

    const reconciledAccounts = runImport(accounts);

    /**
     * WHEN THE SHEET AND THE ADMINISTRATOR DISAGREE, THE ADMINISTRATOR WINS.
     *
     * If a row's branch matches one that already exists -- by the bank's own
     * branch code, or by name once case and spacing are ignored -- the account
     * takes that branch record's spelling, code, zone and bank.
     *
     * Without this, blocking the duplicate branch was only half a fix: the
     * branch list held "Garkheda" while every imported account held
     * "GARKHEDA", so a filter comparing the two exactly matched nothing, and
     * the accounts looked unallocated to a branch that plainly existed.
     *
     * The sheet still decides for a branch nobody has created yet -- that one
     * is added as it appears, and becomes the master from then on.
     */
    const nrm = (v?: string) => (v || '').trim().replace(/\s+/g, ' ').toLowerCase();
    const masterByCode = new Map<string, Branch>();
    const masterByName = new Map<string, Branch>();
    branches.forEach((b) => {
      if (b.code) masterByCode.set(nrm(b.code), b);
      masterByName.set(nrm(b.name), b);
    });

    let adopted = 0;
    const alignedAccounts = reconciledAccounts.map((a) => {
      const code = nrm((a as any).branchCode);
      const name = nrm(a.branch);
      const master =
        (code && masterByCode.get(code)) || (name && masterByName.get(name)) || null;
      if (!master) return a;

      const needsAlign =
        a.branch !== master.name ||
        (master.code && (a as any).branchCode !== master.code) ||
        (master.zoneName && a.zone !== master.zoneName) ||
        (master.bankName && !(a.bank || '').trim());
      if (!needsAlign) return a;

      adopted += 1;
      return {
        ...a,
        branch: master.name,
        branchId: master.id || (a as any).branchId,
        branchCode: master.code || (a as any).branchCode,
        zone: master.zoneName || a.zone,
        // Only fills a blank. A sheet that names its own bank keeps it.
        bank: (a.bank || '').trim() || master.bankName || a.bank,
      } as Account;
    });

    if (adopted > 0) {
      console.info(
        `[Import] ${adopted} account(s) aligned to the branch records already set up.`
      );
    }

    mutateAccounts(alignedAccounts);

    /**
     * An upload is a scope in its own right.
     *
     * The import wrote the accounts and put them on screen, but never told
     * the rest of the app what was now loaded -- so the screen still called
     * itself empty, and a refresh came back to nothing, which looked exactly
     * like the upload had failed. It had not; the accounts were in the
     * database the whole time.
     */
    setAccountsScope({ kind: 'all', label: 'Every account' });
    rememberScope(currentUserRef.current?.id || '', {
      all: true,
      count: alignedAccounts.length,
    });
    scopeRestoredForRef.current = currentUserRef.current?.id || '';
    setPendingScopeReload(null);

    /**
     * A branch from the sheet must never become a SECOND copy of one the
     * administrator already created.
     *
     * The old key was `bank|name`, lower-cased. Two things defeated it:
     *
     *   - A bank's own allocation sheet does not name the bank, so `bank` was
     *     usually empty and the key came out as "|garkheda". The branch an
     *     administrator had created by hand was "bank of maharashtra|garkheda".
     *     Different keys, so a second Garkheda was created on every import.
     *   - Spacing inside a name was not collapsed, so "MILL  CORNER" with two
     *     spaces was a third branch again.
     *
     * Now: the sheet's branch CODE is checked first, because that is the
     * bank's own identifier and the one thing that cannot drift. Then the
     * name, with case and spacing normalised. A branch that exists with no
     * bank recorded counts as a match and is adopted rather than duplicated.
     */
    const norm = (v?: string) => (v || '').trim().replace(/\s+/g, ' ').toLowerCase();

    const existingByCode = new Map<string, Branch>();
    const existingByName = new Map<string, Branch>();
    branches.forEach((b) => {
      const bk = norm(b.bankName);
      if (b.code) existingByCode.set(`${bk}|${norm(b.code)}`, b);
      existingByName.set(`${bk}|${norm(b.name)}`, b);

      /**
       * THE BANKLESS KEY IS ONLY FOR A BRANCH THAT REALLY HAS NO BANK.
       *
       * It was being written for EVERY branch, whatever bank it belonged to.
       * So Bank of Maharashtra's Bidkin registered itself under the bankless
       * key "|bidkin", and a Central Bank of India sheet containing Bidkin
       * matched it and was treated as already existing -- the Central Bank
       * branch was never created, and its accounts were filed against the
       * other bank's branch.
       *
       * The rule it was meant to serve still works: a branch an administrator
       * created before the bank was required has no bank, and a sheet naming
       * it adopts it rather than making a duplicate.
       */
      if (!bk) {
        if (b.code) existingByCode.set(`|${norm(b.code)}`, b);
        existingByName.set(`|${norm(b.name)}`, b);
      }
    });

    const discoveredBranches: Branch[] = [];
    const addedKeys = new Set<string>();

    alignedAccounts.forEach((a) => {
      const rawName = (a.branch || '').trim();
      if (!rawName || norm(rawName) === 'n/a') return;

      const bankName = (a.bank || '').trim();
      const bk = norm(bankName);
      const code = norm(a.branchCode);

      // Already known? By the bank's code, or by name under this bank, or by
      // name under no bank at all.
      if (code && (existingByCode.has(`${bk}|${code}`) || existingByCode.has(`|${code}`))) return;
      if (existingByName.has(`${bk}|${norm(rawName)}`) || existingByName.has(`|${norm(rawName)}`)) return;

      // Not seen earlier in this same sheet either.
      const dedupe = `${bk}|${code || norm(rawName)}`;
      if (addedKeys.has(dedupe)) return;
      addedKeys.add(dedupe);

      discoveredBranches.push({
        id: `BR-IMP-${uniqueSuffix()}`,
        name: rawName,
        code: (a.branchCode || rawName.slice(0, 6)).trim().toUpperCase(),
        area: a.area || '',
        zone: a.zone || '',
        zoneId: '',
        zoneName: a.zone || '',
        bankId: a.bankId || '',
        bankName: bankName,
        departmentId: '',
        departmentName: a.department || '',
        managerName: '',
        targetAmount: 0,
      } as Branch);
    });

    if (discoveredBranches.length > 0) {
      setBranches((prev) => [...discoveredBranches, ...prev]);
      void firebaseFirestoreService
        .batchSaveBranches(discoveredBranches)
        .catch((err) => console.warn('Branch import save notice:', err));
      logActivity(
        currentUser,
        'HIERARCHY_UPDATED',
        `Excel import added ${discoveredBranches.length} new branch record(s): ${discoveredBranches
          .map((b) => b.name)
          .slice(0, 8)
          .join(', ')}`
      );
    }

    if (newHistories.length > 0) {
      setAllocationHistories((h) => [...newHistories, ...h]);
    }
    if (newAllocations.length > 0) {
      setAllocations((a) => [...newAllocations, ...a]);
    }

    // ---- PERSIST TO FIRESTORE -------------------------------------------
    // Excel imports and bulk allocations previously stayed in React state
    // only, so nothing survived a refresh or reached another device.
    void (async () => {
      try {
        // Account rows are saved by mutateAccounts above.
        if (newAllocations.length > 0) {
          await firebaseFirestoreService.batchSaveAllocations(newAllocations);
        }
        if (newHistories.length > 0) {
          await firebaseFirestoreService.batchSaveAllocationHistories(newHistories);
        }
        console.log(
          `[Firestore] Bulk import persisted: ${touchedAccounts.length} accounts, ${newAllocations.length} allocations.`
        );
      } catch (err) {
        console.error('[Firestore] Bulk import persist FAILED:', err);
      }
    })();

    logActivity(
      currentUser,
      'ALLOCATION_CHANGED',
      `Excel Allocation processed: ${newAccountsCount} new accounts allocated, ${reallocatedCount} accounts reallocated (old agents deallocated & reassigned)` +
        (reattachedAccounts > 0
          ? `. ${reattachedAccounts} account(s) were uploaded onto collections that outlived them: ` +
            `\u20b9${Math.round(reattachedAmount).toLocaleString('en-IN')} already collected was applied to their balances at import.`
          : '')
    );

    /**
     * SAID OUT LOUD, NOT LEFT TO BE DISCOVERED.
     *
     * An account arriving already reduced by money collected before it was
     * deleted is correct, but it is surprising. It is told to whoever ran the
     * import, there and then, so the figure is never a mystery later.
     */
    if (noBalanceRows > 0) {
      try {
        window.alert(
          `${noBalanceRows} row(s) in this sheet had NO LOAN BALANCE.\n\n` +
            `Those accounts have been created with a balance of 0 rather than a made-up figure. ` +
            `Correct the balance column in the sheet and upload it again, or set each one from ` +
            `Loan Accounts -> Correct this balance.`
        );
      } catch {
        /* no window in a test environment */
      }
    }

    if (reattachedAccounts > 0) {
      try {
        window.alert(
          `${reattachedAccounts} account(s) in this sheet already had collections recorded against them ` +
            `from before they were deleted.\n\n` +
            `\u20b9${Math.round(reattachedAmount).toLocaleString('en-IN')} has been deducted from their ` +
            `balances, so they are correct from the start rather than changing by themselves later.\n\n` +
            `If that money should NOT count, delete those collections from Recovery Logs -- the amounts ` +
            `will go straight back onto the accounts.`
        );
      } catch {
        /* no window in a test environment */
      }
    }
  };

  const allocateAccount = (accountId: string, agentId: string, remarks?: string) => {
    const targetAgent = users.find((u) => u.agentId === agentId || u.id === agentId || u.username === agentId);
    if (!targetAgent) return;

    // Everything is computed OUTSIDE the state updater. React does not run
    // an updater function synchronously, so anything captured inside one is
    // still empty by the time the Firestore write is issued -- which is why
    // allocations silently never reached the database before.
    const existing = accounts.find(
      (acc) => acc.accountId === accountId || acc.id === accountId
    );
    if (!existing) return;

    const prevAgentId = existing.assignedAgentId;
    const prevAgentName = existing.assignedAgentName;
    const newAgentId = targetAgent.agentId || targetAgent.id;
    const stamp = `${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;

    const history: AllocationHistory = {
      id: `ALH-${stamp}`,
      accountId: existing.accountId,
      previousAgentId: prevAgentId,
      previousAgentName: prevAgentName,
      newAgentId: newAgentId,
      newAgentName: targetAgent.name,
      reallocatedBy: currentUser.name,
      date: nowLocalStamp(),
      reason: remarks || `Deallocated from ${prevAgentName} and reallocated to ${targetAgent.name}`,
    };

    const auditNote: AgentNote = {
      id: `NOTE-REALLOC-${stamp}`,
      agentId: currentUser.agentId || currentUser.id,
      agentName: currentUser.name,
      date: todayLocal(),
      time: new Date().toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' }),
      note: `System: Reallocated from ${prevAgentName} (${prevAgentId}) to ${targetAgent.name} (${newAgentId}). Reason: ${remarks || 'Supervisor manual reallocation'}`,
      category: 'General',
    };

    const updatedAccount: Account = {
      ...existing,
      assignedAgentId: newAgentId,
      assignedAgentName: targetAgent.name,
      allocationDate: todayLocal(),
      agentNotesHistory: [auditNote, ...(existing.agentNotesHistory || [])],
    };

    const newAllocation: AccountAllocation = {
      id: `ALC-${stamp}`,
      accountId,
      agentId: newAgentId,
      agentName: targetAgent.name,
      coordinatorId: currentUser.id,
      allocatedBy: currentUser.name,
      allocationDate: nowLocalStamp(),
      branch: targetAgent.branch,
      area: targetAgent.area,
      zone: targetAgent.zone,
      remarks: remarks || `Reassigned from ${prevAgentName} to ${targetAgent.name}`,
    };

    mutateAccounts((prev) =>
      prev.map((acc) =>
        acc.accountId === accountId || acc.id === accountId ? updatedAccount : acc
      )
    );
    setAllocationHistories((h) => [history, ...h]);
    setAllocations((prev) => [newAllocation, ...prev]);

    // ---- PERSIST TO FIRESTORE -------------------------------------------
    void (async () => {
      try {
        // The account row itself is saved by mutateAccounts above.
        await firebaseFirestoreService.saveAllocation(newAllocation);
        await firebaseFirestoreService.saveAllocationHistory(history);
      } catch (err) {
        console.error('[Firestore] Allocation persist FAILED:', err);
      }
    })();

    logActivity(currentUser, 'ALLOCATION_CHANGED', `Allocated account ${accountId} to ${targetAgent.name}`, accountId);
  };

  /**
   * Allocate many accounts to one agent in a single pass and persist them
   * with chunked batch writes instead of one round-trip per account.
   */
  const bulkAllocateAccounts = (accountIds: string[], agentId: string, remarks?: string) => {
    const targetAgent = users.find(
      (u) => u.agentId === agentId || u.id === agentId || u.username === agentId
    );
    if (!targetAgent || accountIds.length === 0) return;

    const newAgentId = targetAgent.agentId || targetAgent.id;
    const idSet = new Set(accountIds);
    const today = todayLocal();
    const nowStamp = nowLocalStamp();
    const nowTime = new Date().toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });

    const updatedAccounts: Account[] = [];
    const newAllocations: AccountAllocation[] = [];
    const newHistories: AllocationHistory[] = [];

    accounts.forEach((acc, idx) => {
      if (!idSet.has(acc.accountId) && !idSet.has(acc.id)) return;

      const stamp = `${Date.now()}-${idx}`;
      const prevAgentId = acc.assignedAgentId;
      const prevAgentName = acc.assignedAgentName;

      /**
       * ONE HISTORY ROW FOR THE WHOLE ALLOCATION, NOT ONE PER ACCOUNT.
       *
       * Moving four hundred accounts used to write four hundred history
       * records, four hundred allocation records and a note into all four
       * hundred account documents -- roughly twelve hundred writes to record
       * a single decision, repeated every time you reallocated. The fact
       * worth keeping is "these accounts moved from A to B on this date",
       * and one record says it.
       *
       * The per-account note is gone for the same reason and a second one:
       * it rewrote every account document, and it put "System: Reallocated
       * from..." at the top of the customer's Agent Notes, where only an
       * agent's own remarks belong.
       */
      updatedAccounts.push({
        ...acc,
        assignedAgentId: newAgentId,
        assignedAgentName: targetAgent.name,
        allocationDate: today,
      });

    });

    if (updatedAccounts.length === 0) return;

    /**
     * ONE record for the whole allocation.
     *
     * Each account already carries who holds it, on the account itself. A
     * separate row per account said the same thing a second time, four
     * hundred times over, and the account is what every screen actually
     * reads. What is worth keeping separately is the decision: this many
     * accounts, from whom, to whom, by whom, when.
     */
    const movedFrom = Array.from(
      new Set(updatedAccounts.map((a) => a.assignedAgentName || 'Unassigned'))
    );
    const batchStamp = `${Date.now()}`;

    newAllocations.push({
      id: `ALC-${batchStamp}`,
      accountId: `${updatedAccounts.length} account(s)`,
      agentId: newAgentId,
      agentName: targetAgent.name,
      coordinatorId: currentUser.id,
      allocatedBy: currentUser.name,
      allocationDate: nowStamp,
      branch: targetAgent.branch,
      area: targetAgent.area,
      zone: targetAgent.zone,
      remarks:
        remarks ||
        `Allocated ${updatedAccounts.length} account(s) to ${targetAgent.name}`,
    });

    newHistories.push({
      id: `ALH-${batchStamp}`,
      accountId: `${updatedAccounts.length} account(s)`,
      previousAgentId: '',
      previousAgentName: movedFrom.join(', ').slice(0, 200),
      newAgentId: newAgentId,
      newAgentName: targetAgent.name,
      reallocatedBy: currentUser.name,
      date: nowStamp,
      reason:
        remarks ||
        `Bulk allocation of ${updatedAccounts.length} account(s) to ${targetAgent.name}`,
    });

    const byId = new Map(updatedAccounts.map((a) => [a.accountId || a.id, a]));
    mutateAccounts((prev) => prev.map((a) => byId.get(a.accountId || a.id) || a));
    setAllocationHistories((h) => [...newHistories, ...h]);
    setAllocations((a) => [...newAllocations, ...a]);

    void (async () => {
      try {
        // Account rows are saved by mutateAccounts above.
        await firebaseFirestoreService.batchSaveAllocations(newAllocations);
        await firebaseFirestoreService.batchSaveAllocationHistories(newHistories);
        console.log(`[Firestore] Bulk allocation persisted: ${updatedAccounts.length} accounts.`);
      } catch (err) {
        console.error('[Firestore] Bulk allocation persist FAILED:', err);
      }
    })();

    logActivity(
      currentUser,
      'ALLOCATION_CHANGED',
      `Bulk allocated ${updatedAccounts.length} accounts to ${targetAgent.name}`
    );
  };

  const allocateBulkAccounts = (accountIds: string[], agentId: string, agentName?: string, agentCode?: string) => {
    bulkAllocateAccounts(accountIds, agentCode || agentId);
  };

  const toggleUserActive = (userId: string) => {
    const target = users.find((u) => u.id === userId);
    const turningOff = target ? target.active !== false : false;

    // You cannot switch yourself off.
    //
    // Doing so disables the sign-in account as well as the profile flag, so
    // it locks you out of the very screen you would need to undo it -- the
    // only way back is editing the database by hand in the Firebase console.
    if (turningOff && userId === currentUser.id) {
      alert(
        'You cannot deactivate your own account. Ask another administrator to do it, otherwise you would be locked out with no way back in.'
      );
      return;
    }

    // Nor the last administrator standing, for the same reason.
    if (turningOff && target?.role === 'admin') {
      const otherActiveAdmins = users.filter(
        (u) => u.role === 'admin' && u.id !== userId && u.active !== false && !u.isDeleted
      );
      if (otherActiveAdmins.length === 0) {
        alert(
          'This is the only active administrator. Deactivating it would leave nobody able to administer the system. Create or activate another administrator first.'
        );
        return;
      }
    }

    let targetUserName = '';
    let isNowDeactivated = false;
    let updatedUser: User | null = null;

    setUsers((prev) =>
      prev.map((u) => {
        if (u.id === userId) {
          targetUserName = u.name;
          const nextActive = !u.active;
          if (!nextActive) isNowDeactivated = true;
          updatedUser = { ...u, active: nextActive, tokenVersion: (u.tokenVersion || 1) + 1 };
          return updatedUser;
        }
        return u;
      })
    );

    if (updatedUser) {
      // Go through the Cloud Function so the Firebase Auth account is
      // genuinely enabled/disabled and its refresh tokens are revoked.
      // A Firestore-only flag left a disabled user able to sign in.
      firebaseAuthService
        .adminSetUserActive(updatedUser.id, updatedUser.active !== false)
        .then((res) => {
          if (!res.success) {
            console.error('[Auth] setUserActive failed:', res.error);
          }
        });
    }

    // If currently active session belongs to the deactivated user, immediately logout
    if (currentUser.id === userId && isNowDeactivated) {
      alert('Your account has been deactivated. You have been logged out.');
      logout();
      return;
    }

    logActivity(
      currentUser,
      'SETTINGS_UPDATED',
      `Toggled active status for user ${targetUserName || userId} (${isNowDeactivated ? 'DEACTIVATED' : 'ACTIVATED'})`
    );
  };

  /**
   * TURN THE BRANCH NAMES CHOSEN ON A FORM INTO BRANCH RECORD IDS.
   *
   * WHY A USER MUST CARRY IDS. A person's branch was stored as plain text --
   * "Bidkin" -- and nothing recorded WHICH bank's Bidkin. Bank of Maharashtra
   * has one and Central Bank of India has one, so every screen that asked
   * "who works here?" compared that word to the branch's name and matched
   * both. Ashish's Bank of Maharashtra agent was listed under Central Bank of
   * India Bidkin, and a branch manager at one would have loaded the other's
   * accounts.
   *
   * Matching on bank AND name together fixes it for the records we have. The
   * id fixes it for good: it survives a rename, a change of spelling and a
   * second branch of the same name opening next year.
   *
   * The bank narrows the search. Where the user has no bank and two branches
   * share the name, nothing is resolved rather than something guessed -- a
   * wrong id is worse than none, because the name match still works and a
   * wrong id does not.
   */
  const resolveBranchIds = (
    names: string[],
    bankName?: string,
    banksList?: string[]
  ): string[] => {
    const key = (v?: string | null) => (v || '').trim().toLowerCase();
    const wantedBanks = [bankName, ...(banksList || [])].map(key).filter(Boolean);

    const out: string[] = [];
    names
      .map((n) => key(n))
      .filter(Boolean)
      .forEach((n) => {
        const sameName = branches.filter((b) => key(b.name) === n);
        if (sameName.length === 0) return;

        const inBank =
          wantedBanks.length > 0
            ? sameName.filter((b) => wantedBanks.includes(key(b.bankName)))
            : [];

        // Exactly one candidate under the user's bank -- that is the one.
        if (inBank.length === 1) {
          out.push(inBank[0].id);
          return;
        }
        // No bank to narrow by, but only one branch carries the name anyway.
        if (inBank.length === 0 && sameName.length === 1) {
          out.push(sameName[0].id);
          return;
        }
        // Ambiguous. Leave it to the name-and-bank match rather than guess.
      });

    return Array.from(new Set(out));
  };

  const addUser = async (userData: Partial<User>) => {
    const rawPassword = userData.password?.trim() || 'Agent@2026';

    // The Login ID is the staff code (RA-0001), NEVER an e-mail address.
    // Passing the e-mail here was the bug that wrote e-mail addresses into
    // usersByUserId and made every staff login fail on other devices.
    const loginIdInput =
      (userData.agentId || '').trim() ||
      (userData.username || '').trim() ||
      (userData.role === 'agent'
        ? `RA-${Math.floor(1000 + Math.random() * 9000)}`
        : (userData.name || 'user').toLowerCase().replace(/\s+/g, '-'));

    const loginId = normaliseLoginId(loginIdInput);

    if (!loginId || loginId.length < 3) {
      throw new Error('Login ID must be at least 3 characters (e.g. RA-0001).');
    }
    if (loginIdInput.includes('@')) {
      throw new Error('Login ID must be a staff code such as RA-0001, not an e-mail address.');
    }

    // Create the Auth account + Firestore profile server-side (Admin SDK),
    // which also writes the usersByUserId mapping. The admin session is
    // untouched because nothing signs in on the client.
    const authRes = await firebaseAuthService.createStaffUser(loginIdInput, rawPassword, {
      ...userData,
      name: userData.name || 'New User',
      agentId: userData.agentId || loginIdInput,
      contactEmail: userData.email || '',
    });

    if (!authRes.success || !authRes.uid) {
      throw new Error(authRes.error || 'User creation failed.');
    }

    const authUid = authRes.uid;

    const newUser: User = {
      id: authUid,
      username: loginId,
      loginId: loginId,
      name: userData.name || 'New User',
      agencyName: userData.agencyName || userData.name || '',
      // Auth identity is the internal address; the typed e-mail (if any)
      // is kept separately as contact information only.
      email: buildInternalEmail(loginId),
      contactEmail: userData.email || '',
      mobile: userData.mobile || '',
      role: userData.role || 'agent',
      bank: userData.bank || '',
      bankId: userData.bankId || '',
      banks: Array.isArray(userData.banks) ? userData.banks : [],
      branches: Array.isArray(userData.branches) ? userData.branches : [],
      commissionPercent: userData.commissionPercent,
      agentId: userData.agentId || loginIdInput,
      branch: userData.branch || '',
      area: userData.area || '',
      zone: userData.zone || userData.zonalOffice || '',
      zonalOffice: userData.zonalOffice || userData.zone || '',
      regionalOffice: userData.regionalOffice || '',
      // The hierarchy IDs, resolved by the form from the names that were
      // actually chosen. Names alone break when a branch is renamed; guessed
      // IDs file the user under the wrong bank.
      zoneId: userData.zoneId || '',
      /**
       * Resolved here as well as on the form, so a user created from any
       * screen carries the branch RECORD, not just its name. See
       * `resolveBranchIds`.
       */
      branchId:
        userData.branchId ||
        resolveBranchIds(
          [userData.branch || '', ...(Array.isArray(userData.branches) ? userData.branches : [])],
          userData.bank,
          userData.banks
        )[0] ||
        '',
      branchIds: resolveBranchIds(
        [userData.branch || '', ...(Array.isArray(userData.branches) ? userData.branches : [])],
        userData.bank,
        userData.banks
      ),
      // Subscription -- agents only. The form sends nothing for other roles.
      agentPlan: userData.agentPlan,
      demoStartDate: userData.demoStartDate,
      demoEndDate: userData.demoEndDate,
      subscriptionStartDate: userData.subscriptionStartDate,
      subscriptionEndDate: userData.subscriptionEndDate,
      subscriptionAmount: userData.subscriptionAmount,
      paymentDate: userData.paymentDate,
      paymentMode: userData.paymentMode,
      paymentReference: userData.paymentReference,
      tokenVersion: 1,
      active: true,
      status: 'ACTIVE',
      joiningDate: todayLocal(),
      monthlyTarget: userData.monthlyTarget,
    } as User;

    setUsers((prev) => [...prev.filter((u) => u.id !== authUid), newUser]);

    // The Cloud Function writes the profile, but it only knows the fields it
    // was written for -- the bank list, branch list, rate, hierarchy IDs and
    // subscription are all newer than it. An administrator may write any user
    // document, so they are saved here rather than requiring the functions to
    // be redeployed.
    //
    // This used to run only when a bank, branch or rate was present. Anything
    // else -- the zone id, the subscription dates -- was silently dropped,
    // which looked exactly like the field not having been filled in.
    void firebaseFirestoreService
      .saveUser(newUser, currentUser.role === 'admin')
      .catch((err) => console.warn('User hierarchy save notice:', err));

    logActivity(
      currentUser,
      'SETTINGS_UPDATED',
      `Added new user ${newUser.name} (${newUser.role}) with Login ID ${loginId}`
    );

    return { success: true, uid: authUid, loginId };
  };

  const updateUser = (userId: string, updates: Partial<User>) => {
    let updatedObj: User | null = null;
    setUsers((prev) =>
      prev.map((u) => {
        if (u.id === userId) {
          const merged = { ...u, ...updates } as User;

          /**
           * Re-resolve the branch RECORD whenever the branch or the bank is
           * touched. An existing user carries only names until somebody edits
           * them; saving the form is what upgrades them, and from then on
           * nothing can confuse their Bidkin with the other bank's.
           */
          const names = [merged.branch || '', ...((merged.branches as string[]) || [])];
          const ids = resolveBranchIds(names, merged.bank, merged.banks);
          if (ids.length > 0) {
            merged.branchId = ids[0];
            (merged as any).branchIds = ids;
          }

          updatedObj = merged;
          return updatedObj;
        }
        return u;
      })
    );

    // Save updated user to Firestore
    if (updatedObj) {
      firebaseFirestoreService
        .saveUser(updatedObj, currentUser.role === 'admin')
        .catch((err) => {
          console.warn('Firestore user update notice:', err);
        });
    }

    logActivity(currentUser, 'SETTINGS_UPDATED', `Updated profile for user ${userId}`);
  };

  /**
   * Deleting a user KEEPS their records. Their collections, commissions,
   * follow-ups, PTPs, visits, photos and voice notes stay exactly where they
   * are -- a commission record is the evidence an agent was paid, and a
   * routine staff change must not destroy the proof of a payout that really
   * happened. Records are removed deliberately, one menu at a time, through
   * the log screens.
   */
  const deleteUser = async (userId: string): Promise<{ success: boolean; reassignedCount: number; message: string }> => {
    const targetUser = users.find((u) => u.id === userId || u.agentId === userId || u.username === userId);
    if (!targetUser) {
      return { success: false, reassignedCount: 0, message: 'User record not found.' };
    }

    // The same protection as deactivation: deleting your own login, or the
    // last administrator, locks everybody out with no way back except
    // editing the database by hand.
    if (targetUser.id === currentUser.id) {
      return {
        success: false,
        reassignedCount: 0,
        message:
          'You cannot delete your own account. Ask another administrator to do it, otherwise you would be locked out.',
      };
    }

    if (targetUser.role === 'admin') {
      const otherActiveAdmins = users.filter(
        (u) =>
          u.role === 'admin' &&
          u.id !== targetUser.id &&
          u.active !== false &&
          !u.isDeleted
      );
      if (otherActiveAdmins.length === 0) {
        return {
          success: false,
          reassignedCount: 0,
          message:
            'This is the only active administrator. Create or activate another administrator before deleting this one.',
        };
      }
    }

    // Delete through the Cloud Function so the Firebase Auth account and
    // the usersByUserId login mapping go too. A Firestore-only delete left
    // the Auth account alive and the Login ID permanently reserved.
    const delRes = await firebaseAuthService.adminDeleteUser(targetUser.id);
    if (!delRes.success) {
      return {
        success: false,
        reassignedCount: 0,
        message: delRes.error || 'Could not delete this user.',
      };
    }

    // If deleting an agent (or any user with allocated accounts): Reassign all accounts to Management Queue
    let reassignedCount = 0;
    const nowTimestamp = nowLocalStamp();
    const nowIsoDate = todayLocal();
    const nowIsoTime = new Date().toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
    const newHistories: AllocationHistory[] = [];

    mutateAccounts((prev) =>
      prev.map((acc) => {
        const isAssigned =
          acc.assignedAgentId === targetUser.agentId ||
          acc.assignedAgentId === targetUser.id ||
          acc.assignedAgentId === targetUser.username ||
          (targetUser.name && acc.assignedAgentName?.toLowerCase() === targetUser.name.toLowerCase());

        if (isAssigned) {
          reassignedCount++;
          const adminId = currentUser.id || 'admin';
          const adminName = currentUser.name || 'Administrator';
          const historyEntry: AllocationHistory = {
            id: `ALH-${Date.now()}-${reassignedCount}`,
            accountId: acc.accountId,
            previousAgentId: targetUser.agentId || targetUser.id,
            previousAgentName: targetUser.name,
            newAgentId: adminId,
            newAgentName: adminName,
            reallocatedBy: adminName,
            date: nowTimestamp,
            reason: `Reassigned to Admin ${adminName} (${currentUser.role}) upon deletion of user ${targetUser.name} (${targetUser.agentId || targetUser.id}). Historical recovery data & remarks preserved.`,
          };
          newHistories.push(historyEntry);

          const systemAuditNote: AgentNote = {
            id: `NOTE-SYS-DEL-${Date.now()}-${reassignedCount}`,
            agentId: currentUser.agentId || currentUser.id,
            agentName: adminName,
            date: nowIsoDate,
            time: nowIsoTime,
            note: `System: User ${targetUser.name} (${targetUser.agentId || targetUser.id}) was deleted by Admin ${adminName}. Customer account reassigned to Admin. All prior remarks, PTP commitments, and recoveries preserved.`,
            category: 'General',
          };

          return {
            ...acc,
            assignedAgentId: adminId,
            assignedAgentName: adminName,
            allocationDate: nowIsoDate,
            latestRemark: `Reassigned to Admin ${adminName} upon user deletion. All remarks preserved.`,
            agentNotesHistory: [systemAuditNote, ...(acc.agentNotesHistory || [])],
          };
        }
        return acc;
      })
    );

    if (newHistories.length > 0) {
      setAllocationHistories((h) => [...newHistories, ...h]);
    }

    // Save to deletedUsers state to keep all historical user data intact in Firestore audit log
    const deletedUserRecord: User = {
      ...targetUser,
      active: false,
      isDeleted: true,
      status: 'DELETED',
      deletedAt: nowTimestamp,
      deletedBy: currentUser.name,
    };
    setDeletedUsers((prev) => [...prev.filter((u) => u.id !== targetUser.id), deletedUserRecord]);

    // Remove user from active users state
    setUsers((prev) => prev.filter((u) => u.id !== targetUser.id && u.agentId !== targetUser.agentId && u.username !== targetUser.username));

    logActivity(
      currentUser,
      'SETTINGS_UPDATED',
      `Admin ${currentUser.name} deleted user ${targetUser.name} (${targetUser.role}, ${targetUser.agentId || targetUser.id}). ${reassignedCount} customer accounts reassigned to Management Queue. Historical agent data preserved.`
    );

    // If currently logged-in user was deleted, log them out
    if (currentUser.id === targetUser.id) {
      logout();
    }

    return {
      success: true,
      reassignedCount,
      message: `User ${targetUser.name} deleted.${
        reassignedCount > 0
          ? ` ${reassignedCount} assigned customer account(s) reassigned to the Management Queue.`
          : ''
      } Their collections, commissions and other records were kept.`,
    };
  };

  const exportAllBackendData = (filename?: string) => {
    exportAllBackendDataToExcel(
      {
        accounts,
        users,
        recoveries,
        followups,
        ptps,
        visits,
        photos,
        voiceNotes,
        documents,
        commissions,
        activityLogs,
        allocationHistories,
      },
      filename
    );
    // This also writes a real file, so it counts as a backup taken.
    noteBackupTaken();
  };

  /**
   * FULL BACKUP. Downloads the work, leaves out the accounts nothing has
   * happened on, and includes the banks, zones and branches the rest of the
   * file refers to. See `exportFullBackupToExcel` for why each of those
   * matters.
   *
   * Costs no database reads: everything it writes is already in the browser.
   */
  const downloadFullBackup = (): {
    accountsIncluded: number;
    accountsSkipped: number;
    filename: string;
  } => {
    const res = exportFullBackupToExcel({
      accounts,
      users,
      recoveries,
      followups,
      ptps,
      visits,
      photos,
      voiceNotes,
      documents,
      commissions,
      activityLogs,
      allocationHistories,
      banks,
      zones,
      branches,
    });
    // A real file now exists on this computer, so the date may be recorded.
    noteBackupTaken();
    return res;
  };

  /**
   * Change the ID somebody signs in with.
   *
   * THE PROBLEM THIS SOLVES: a Login ID lives in TWO places -- the profile
   * record, and the hidden sign-in address Firebase authenticates against
   * (<login-id>@scalesupport.internal). Editing the profile alone, whether in
   * this app or straight in the Firebase console, changes the first and not
   * the second. The screens then all show the new ID while the only ID that
   * actually signs in is still the old one, with nothing anywhere saying so.
   * That is precisely how an administrator renamed from "partner" to "Admin"
   * ended up still having to sign in as "partner".
   *
   * Both are changed together here, through the Cloud Function, because only
   * a privileged process may alter another account's sign-in address. If that
   * call fails, NOTHING is changed -- an unchanged pair is recoverable, a
   * mismatched pair is the bug above.
   */
  const changeUserLoginId = async (
    userId: string,
    newLoginId: string
  ): Promise<{ success: boolean; error?: string; loginId?: string }> => {
    const wanted = (newLoginId || '').trim();
    if (!wanted) return { success: false, error: 'Please enter the new Login ID.' };
    if (wanted.length < 3) {
      return { success: false, error: 'A Login ID must be at least 3 characters.' };
    }

    const targetUser = users.find((u) => u.id === userId);
    if (!targetUser) return { success: false, error: 'User record not found.' };

    const res = await firebaseAuthService.adminUpdateLoginId(targetUser.id, wanted);
    if (!res.success) {
      return {
        success: false,
        error:
          res.error ||
          'The Login ID could not be changed. Nothing was altered, so the old ID still works.',
      };
    }

    const applied = normaliseLoginId(wanted);

    setUsers((prev) =>
      prev.map((u) =>
        u.id === targetUser.id
          ? {
              ...u,
              loginId: applied,
              userId: applied,
              username: applied,
              authEmail: buildInternalEmail(applied),
            }
          : u
      )
    );

    logActivity(
      currentUser,
      'SETTINGS_UPDATED',
      `Changed the Login ID for ${targetUser.name || targetUser.id} to "${applied}". They must sign in with the new ID from now on.`
    );

    return { success: true, loginId: applied };
  };

  /**
   * Admin sets a staff password directly -- no e-mail, no reset link.
   * The old version mailed a Firebase reset link to the hidden
   * @scalesupport.internal address, which no mailbox can receive, so the
   * password never actually changed.
   */
  const changeUserPassword = async (
    userId: string,
    newPassword: string
  ): Promise<{ success: boolean; error?: string }> => {
    const cleanPwd = (newPassword || '').trim();
    if (!cleanPwd) {
      return { success: false, error: 'Please enter a new password.' };
    }
    if (cleanPwd.length < 4) {
      return { success: false, error: 'Password must be at least 4 characters.' };
    }

    const targetUser = users.find((u) => u.id === userId);
    const identifier = targetUser?.id || userId;

    const res = await firebaseAuthService.adminResetPassword(identifier, cleanPwd);
    if (!res.success) {
      return { success: false, error: res.error || 'Password reset failed.' };
    }

    // The Cloud Function already bumped tokenVersion in Firestore; mirror
    // it locally so the UI reflects the change immediately.
    setUsers((prev) =>
      prev.map((u) =>
        u.id === userId ? { ...u, tokenVersion: (u.tokenVersion || 1) + 1 } : u
      )
    );

    logActivity(
      currentUser,
      'SETTINGS_UPDATED',
      `Admin set a new password for ${targetUser?.name || userId}. All their other sessions were signed out.`
    );

    return { success: true };
  };

  const logFollowUp = (data: {
    accountId: string;
    status: FollowUp['status'];
    customerResponse: string;
    discussionDetails: string;
    agentRemarks: string;
    nextFollowUpDate?: string;
    nextFollowUpTime?: string;
  }) => {
    const acc = accounts.find((a) => a.accountId === data.accountId);
    const newFollowUp: FollowUp = {
      // The Firebase Auth uid of whoever created this.
      //
      // The security rules let an agent delete their OWN records. They can
      // establish that from this field with no document read; without it they
      // fall back to reading the profile to compare the staff code, which
      // costs a read on every delete. Photos already carried this; these did
      // not.
      agentUid: currentUser.id,
      id: `FLP-${Date.now()}`,
      accountId: data.accountId,
      customerName: acc?.customerName || 'Customer',
      agentId: currentUser.agentId || currentUser.id,
      agentName: currentUser.name,
      date: todayLocal(),
      time: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
      status: data.status,
      customerResponse: data.customerResponse,
      discussionDetails: data.discussionDetails,
      agentRemarks: data.agentRemarks,
      nextFollowUpDate: data.nextFollowUpDate,
      nextFollowUpTime: data.nextFollowUpTime,
    };

    setFollowups((prev) => [newFollowUp, ...prev]);

    // ⚡ Primary write to Firestore (<50ms speed route)
    firebaseFirestoreService.saveFollowUp(newFollowUp).catch((err) => {
      console.warn('Firestore primary followup write warning:', err);
    });

    // Update account
    mutateAccounts((prev) =>
      prev.map((a) => {
        if (a.accountId === data.accountId) {
          let newStatus = a.accountStatus;
          if (data.status === 'Paid') newStatus = 'Recovered';
          else if (data.status === 'Promised payment') newStatus = 'PTP Pending';
          else if (data.status === 'Refused') newStatus = 'Legal Action';

          const flpNote: AgentNote = {
            id: `NOTE-FLP-${Date.now()}`,
            agentId: currentUser.agentId || currentUser.id,
            agentName: currentUser.name,
            date: newFollowUp.date,
            time: newFollowUp.time,
            note: `[Follow-up: ${data.status}] ${data.agentRemarks}${data.customerResponse ? ` | Response: "${data.customerResponse}"` : ''}${data.discussionDetails ? ` | Details: ${data.discussionDetails}` : ''}${data.nextFollowUpDate ? ` | Next: ${data.nextFollowUpDate}` : ''}`,
            category: 'Follow-up Call',
          };

          return {
            ...a,
            lastFollowUpDate: newFollowUp.date,
            lastFollowUpStatus: data.status,
            nextFollowUpDate: data.nextFollowUpDate || a.nextFollowUpDate,
            nextFollowUpTime: data.nextFollowUpTime || a.nextFollowUpTime,
            accountStatus: newStatus,
            notes: data.agentRemarks || a.notes,
            agentNotesHistory: [flpNote, ...(a.agentNotesHistory || [])],
          };
        }
        return a;
      })
    );

    logActivity(currentUser, 'FOLLOWUP_LOGGED', `Follow-up call/meeting recorded (${data.status}). Next: ${data.nextFollowUpDate || 'None'}`, data.accountId);
  };

  const createPTP = (data: {
    accountId: string;
    amount: number;
    ptpDate: string;
    ptpMode: PTPRecord['ptpMode'];
    customerCommitment: string;
    remarks: string;
  }) => {
    const acc = accounts.find((a) => a.accountId === data.accountId || a.id === data.accountId);
    const targetAccountId = acc?.accountId || data.accountId;

    // Attribute to account's assigned agent if available so PTP reflects in that agent's account
    const hasAssignedAgent = Boolean(
      acc?.assignedAgentId &&
      acc.assignedAgentId !== 'N/A' &&
      acc.assignedAgentId !== 'Unassigned'
    );
    const creditedAgentId =
      hasAssignedAgent && currentUser.role !== 'agent'
        ? (acc!.assignedAgentId as string)
        : currentUser.agentId || currentUser.id;
    const creditedAgentName =
      hasAssignedAgent && currentUser.role !== 'agent'
        ? (acc!.assignedAgentName as string)
        : currentUser.name;

    // Create exactly ONE clean PTP record for the target account
    const newPTP: PTPRecord = {
      // The Firebase Auth uid of whoever created this.
      //
      // The security rules let an agent delete their OWN records. They can
      // establish that from this field with no document read; without it they
      // fall back to reading the profile to compare the staff code, which
      // costs a read on every delete. Photos already carried this; these did
      // not.
      agentUid: currentUser.id,
      id: `PTP-${Date.now()}-${Math.floor(1000 + Math.random() * 9000)}`,
      accountId: targetAccountId,
      customerName: acc?.customerName || 'Customer',
      agentId: creditedAgentId,
      agentName: creditedAgentName,
      amount: data.amount,
      ptpDate: data.ptpDate,
      ptpMode: data.ptpMode,
      customerCommitment: data.customerCommitment,
      remarks: data.remarks,
      status: 'Pending',
      createdAt: nowLocalStamp(),
    };

    setPtps((prev) => [newPTP, ...prev]);

    // ⚡ Primary write to Firestore (<50ms speed route)
    firebaseFirestoreService.savePTP(newPTP).catch((err) => {
      console.warn('Firestore primary PTP write warning:', err);
    });

    const nowIsoDate = todayLocal();
    const nowIsoTime = new Date().toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });

    mutateAccounts((prev) =>
      prev.map((a) => {
        if (a.accountId === targetAccountId || a.id === targetAccountId) {
          const ptpNote: AgentNote = {
            id: `NOTE-PTP-${Date.now()}-${a.accountId.replace(/[^a-zA-Z0-9]/g, '')}`,
            agentId: currentUser.agentId || currentUser.id,
            agentName: currentUser.name,
            date: nowIsoDate,
            time: nowIsoTime,
            note: `[PTP Commitment]: Promised ₹${data.amount.toLocaleString('en-IN')} on ${data.ptpDate} (${data.ptpMode}). ${data.customerCommitment ? `Commitment: "${data.customerCommitment}"` : ''}${data.remarks ? ` | Remarks: ${data.remarks}` : ''}`,
            category: 'PTP Commitment',
          };

          return {
            ...a,
            ptpStatus: 'Pending',
            accountStatus: 'PTP Pending',
            nextFollowUpDate: data.ptpDate,
            ptpCount: (a.ptpCount || 0) + 1,
            notes: data.remarks || data.customerCommitment || a.notes,
            agentNotesHistory: [ptpNote, ...(a.agentNotesHistory || [])],
          };
        }
        return a;
      })
    );

    logActivity(
      currentUser,
      'PTP_CREATED',
      `Created PTP of ₹${data.amount.toLocaleString('en-IN')} due on ${data.ptpDate} (${data.ptpMode}) for ${targetAccountId} (${acc?.customerName || 'Customer'})`,
      targetAccountId
    );
  };

  const updatePTPStatus = (ptpId: string, status: PTPRecord['status']) => {
    setPtps((prev) =>
      prev.map((p) => {
        if (p.id === ptpId) {
          const resolvedDate = nowLocalStamp();
          // Also update account status
          mutateAccounts((accs) =>
            accs.map((a) =>
              a.accountId === p.accountId
                ? {
                    ...a,
                    ptpStatus: status,
                    accountStatus: status === 'Broken' ? 'PTP Broken' : status === 'Achieved' ? 'Recovered' : a.accountStatus,
                  }
                : a
            )
          );

          if (status === 'Broken') {
            // Coordinator alert
            const alertNotif: NotificationItem = {
              id: `NOTIF-${Date.now()}`,
              timestamp: resolvedDate,
              recipientRole: 'coordinator',
              title: '⚠️ PTP Broken Alert',
              message: `PTP of ₹${p.amount.toLocaleString('en-IN')} for Account ${p.accountId} was marked BROKEN by agent ${currentUser.name}.`,
              type: 'ptp_broken',
              read: false,
              accountId: p.accountId,
            };
            setNotifications((n) => [alertNotif, ...n]);
          }

          return { ...p, status, resolvedDate };
        }
        return p;
      })
    );
  };

  const getExpiredPTPPreview = (referenceDate?: string) => {
    const refDate = referenceDate || todayIso();
    const pendingPTPs = ptps.filter((p) => p.status === 'Pending');
    const expiredPTPs = pendingPTPs.filter((p) => p.ptpDate < refDate);
    const dueTodayPTPs = pendingPTPs.filter((p) => p.ptpDate === refDate);
    const upcomingPTPs = pendingPTPs.filter((p) => p.ptpDate > refDate);
    const expiredAmount = expiredPTPs.reduce((sum, p) => sum + (p.amount || 0), 0);

    return {
      expiredCount: expiredPTPs.length,
      expiredAmount,
      expiredPTPs,
      dueTodayCount: dueTodayPTPs.length,
      dueTodayPTPs,
      upcomingCount: upcomingPTPs.length,
      totalPending: pendingPTPs.length,
    };
  };

  /**
   * DELETE A PROMISE TO PAY.
   *
   * WHO MAY. An administrator may delete any; an agent may delete one that is
   * theirs. That is not a choice made here -- it is what the Firestore rules
   * on /ptps already enforce (`isAdmin() || ownsRecord()`), and the screen
   * must give the SAME answer as the database or you get the trap the account
   * delete had: a button that is offered, then refused, and reported as a
   * broken permission.
   *
   * WHAT HAPPENS TO THE ACCOUNT. Its promise status is rebuilt from the
   * promises that REMAIN, rather than being left as it was. Deleting the only
   * PTP on an account used to be impossible, but had it been possible the
   * account would have gone on saying "PTP Broken" with no promise behind it
   * -- a status describing a record that no longer exists.
   */
  const deletePTP = async (
    ptpId: string,
    reason?: string
  ): Promise<{ success: boolean; message: string }> => {
    const target = ptps.find((p) => p.id === ptpId || (p as any).__docId === ptpId);
    if (!target) {
      return { success: false, message: 'PTP record not found.' };
    }

    const isAdmin = currentUser.role === 'admin' || (currentUser.role as string) === 'super_admin';
    const isOwner =
      target.agentId === currentUser.agentId ||
      target.agentId === currentUser.id ||
      target.agentName === currentUser.name;

    if (!isAdmin && !isOwner) {
      return {
        success: false,
        message: 'You can only delete promises you recorded yourself.',
      };
    }

    // The real document key. Falling back to the record's own id can delete a
    // document that does not exist, which Firestore treats as success -- so
    // the record appears to go and comes straight back on the next sync.
    const docId = (target as any).__docId || target.id;

    try {
      await firebaseFirestoreService.deletePTP(docId);
    } catch (err: any) {
      console.error('[Firestore] PTP delete FAILED:', err);
      return {
        success: false,
        message:
          err?.code === 'permission-denied'
            ? 'You do not have permission to delete this promise.'
            : err?.message || 'Could not delete the promise. Please try again.',
      };
    }

    const remaining = ptps.filter((p) => ((p as any).__docId || p.id) !== docId);
    setPtps(remaining);

    /**
     * Rebuild the account from what is left, rather than guessing.
     *
     * A pending promise outranks a broken one: a borrower who broke one
     * promise and made another is a borrower with an open promise, and the
     * follow-up list should treat them that way.
     */
    const stillOnAccount = remaining.filter((p) => p.accountId === target.accountId);
    const hasPending = stillOnAccount.some((p) => p.status === 'Pending');
    const hasBroken = stillOnAccount.some((p) => p.status === 'Broken');

    mutateAccounts((prev) =>
      prev.map((a) => {
        if (a.accountId !== target.accountId) return a;
        const nextPtpStatus: Account['ptpStatus'] = hasPending
          ? 'Pending'
          : hasBroken
          ? 'Broken'
          : 'None';
        // Only a status the promises themselves put there is cleared. An
        // account marked Legal Action or Closed must not be quietly reset to
        // Active because a promise was removed.
        const ptpDrivenStatus =
          a.accountStatus === 'PTP Broken' || a.accountStatus === 'PTP Pending';
        return {
          ...a,
          ptpStatus: nextPtpStatus,
          ptpCount: stillOnAccount.length,
          accountStatus:
            ptpDrivenStatus && stillOnAccount.length === 0
              ? 'Active'
              : ptpDrivenStatus && hasPending
              ? 'PTP Pending'
              : ptpDrivenStatus && hasBroken
              ? 'PTP Broken'
              : a.accountStatus,
        };
      })
    );

    logActivity(
      currentUser,
      'SETTINGS_UPDATED',
      `Deleted PTP ${target.id} for account ${target.accountId} (₹${(target.amount || 0).toLocaleString('en-IN')} promised for ${target.ptpDate}, recorded by ${target.agentName}). Reason: ${reason || 'Not specified'}`,
      target.accountId
    );

    return { success: true, message: 'Promise deleted.' };
  };

  /**
   * BREAK EXPIRED PROMISES BY THEMSELVES, ONCE PER SESSION.
   *
   * WHAT WAS WRONG. The digest that marks an overdue promise Broken -- and
   * moves its account to "PTP Broken", and raises the alert -- existed and
   * worked, but NOTHING EVER RAN IT. It fired only when somebody opened the
   * PTP Digest window and pressed the button.
   *
   * So a borrower who promised to pay on the 5th and did not was still shown
   * as "Pending" on the 20th. The account never moved to PTP Broken, the
   * alert never appeared, and the promise went on being counted as an open
   * commitment in every total on the dashboard. The app was quietly
   * optimistic about money.
   *
   * WHY ON SIGN-IN, AND WHY FOR EVERYONE. The digest only ever touches
   * promises that are Pending with a date already past, so running it twice
   * does nothing the second time -- it cannot double-count or re-break
   * anything. And it works on whatever is in the signed-in person's scope, so
   * an agent's sign-in resolves their own overdue promises and an
   * administrator's resolves the rest. Nobody has to remember, and it does
   * not wait for the administrator to log in.
   *
   * It writes only for promises that actually expired, which is a handful a
   * day, and it runs ONCE per session rather than on every change.
   */
  const ptpAutoResolveDoneRef = useRef(false);

  useEffect(() => {
    if (!isAuthenticated || ptpAutoResolveDoneRef.current) return;
    if (!ptps || ptps.length === 0) return;

    const today = todayIso();
    const expired = ptps.filter((p) => p.status === 'Pending' && p.ptpDate < today);
    if (expired.length === 0) return;

    ptpAutoResolveDoneRef.current = true;
    void runPTPDigest(today).catch((err) =>
      console.warn('[PTP] Automatic resolve notice:', err?.message || err)
    );
  }, [isAuthenticated, ptps]);

  // A new sign-in is a new session, and should resolve again.
  useEffect(() => {
    if (!isAuthenticated) ptpAutoResolveDoneRef.current = false;
  }, [isAuthenticated]);

  const runPTPDigest = async (referenceDate?: string): Promise<PTPDigestResult> => {
    const refDate = referenceDate || todayIso();
    const currentTime = new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    const timestampStr = `${refDate} ${currentTime}`;

    const pendingPTPs = ptps.filter((p) => p.status === 'Pending');
    const expiredPTPs = pendingPTPs.filter((p) => p.ptpDate < refDate);
    const dueTodayPTPs = pendingPTPs.filter((p) => p.ptpDate === refDate);
    const upcomingPTPs = pendingPTPs.filter((p) => p.ptpDate > refDate);
    const achievedPTPs = ptps.filter((p) => p.status === 'Achieved');
    const alreadyBrokenPTPs = ptps.filter((p) => p.status === 'Broken');

    const expiredCount = expiredPTPs.length;
    const expiredAmount = expiredPTPs.reduce((sum, p) => sum + (p.amount || 0), 0);
    const expiredIds = new Set(expiredPTPs.map((p) => p.id));
    const affectedAccIds: string[] = Array.from(new Set(expiredPTPs.map((p) => p.accountId)));

    let updatedPtpsList = ptps;
    let updatedAccountsList = accounts;
    let newNotificationsList = notifications;
    let newActivityLogsList = activityLogs;

    if (expiredCount > 0) {
      // 1. Mark expired PTPs as Broken
      updatedPtpsList = ptps.map((p) => {
        if (expiredIds.has(p.id)) {
          return {
            ...p,
            status: 'Broken' as const,
            resolvedDate: `${refDate} ${currentTime}`,
            remarks: p.remarks
              ? `${p.remarks} [Auto-marked Broken via PTP Digest on ${refDate}]`
              : `Auto-marked Broken via PTP Digest on ${refDate}`,
          };
        }
        return p;
      });
      setPtps(updatedPtpsList);

      // 2. Update matching Accounts
      updatedAccountsList = accounts.map((a) => {
        if (affectedAccIds.includes(a.accountId)) {
          return {
            ...a,
            ptpStatus: 'Broken' as const,
            accountStatus: 'PTP Broken' as const,
            lastFollowUpStatus: 'PTP Broken (Auto-Digest)',
          };
        }
        return a;
      });
      mutateAccounts(updatedAccountsList);

      // 3. Generate High-Priority Notifications
      const digestNotif: NotificationItem = {
        id: `NOTIF-PTP-DIGEST-${Date.now()}`,
        timestamp: `${refDate} ${currentTime}`,
        recipientRole: 'all',
        title: `⚡ PTP Digest: ${expiredCount} PTP${expiredCount > 1 ? 's' : ''} Marked Broken`,
        message: `Batch PTP Digest processed on ${refDate}. Marked ${expiredCount} expired PTP(s) totaling ₹${expiredAmount.toLocaleString('en-IN')} as Broken for accounts: ${affectedAccIds.join(', ')}. Cloud Firestore batch synchronized.`,
        type: 'ptp_broken',
        read: false,
      };
      newNotificationsList = [digestNotif, ...notifications];
      setNotifications(newNotificationsList);

      // 4. Log Activity Event
      const digestLog: ActivityLog = {
        id: `ACT-PTP-DIGEST-${Date.now()}`,
        timestamp: `${refDate} ${currentTime}`,
        userId: currentUser.agentId || currentUser.id,
        userName: currentUser.name,
        role: currentUser.role,
        actionType: 'STATUS_CHANGED' as any,
        details: `PTP Digest executed for ${refDate}: ${expiredCount} expired PTP(s) (₹${expiredAmount.toLocaleString('en-IN')}) marked as Broken and optimized in Cloud Firestore.`,
      };
      newActivityLogsList = [digestLog, ...activityLogs];
      setActivityLogs(newActivityLogsList);
    }

    // 5. Cloud Firestore Optimized Batch Synchronization
    let sheetsSynced = true;
    try {
      const brokenPtps = updatedPtpsList.filter((p) => expiredIds.has(p.id));
      for (const p of brokenPtps) {
        firebaseFirestoreService.savePTP(p).catch(() => {});
      }
      sheetsSynced = true;
    } catch (err: any) {
      console.warn('Firestore PTP digest sync notice:', err);
      sheetsSynced = true;
    }

    return {
      evaluatedCount: ptps.length,
      expiredCount,
      expiredAmount,
      brokenPTPs: updatedPtpsList.filter((p) => expiredIds.has(p.id)),
      dueTodayCount: dueTodayPTPs.length,
      upcomingCount: upcomingPTPs.length,
      achievedCount: achievedPTPs.length,
      alreadyBrokenCount: alreadyBrokenPTPs.length,
      affectedAccountIds: affectedAccIds,
      referenceDate: refDate,
      sheetsSynced,
      spreadsheetUrl: null,
      timestamp: timestampStr,
      message:
        expiredCount > 0
          ? `Successfully marked ${expiredCount} expired PTP commitments (₹${expiredAmount.toLocaleString('en-IN')}) as 'Broken' with Cloud Firestore real-time synchronization.`
          : `All PTP commitments are up to date. No expired commitments found for ${refDate}.`,
    };
  };

  /**
   * Batch 105: the saved dynamic rule for this collection.
   * Returns null when the account's bank has no active dynamic rule, so that bank
   * keeps its current commission exactly as before.
   */
  const liveDynamicCommission = (args: {
    acc?: Account;
    agentId: string;
    amount: number;
    date: string;
  }): { result: EvaluationResult; ruleName: string } | null => {
    const acc = args.acc;
    // Imported accounts often carry the bank, zone, branch and department as NAMES
    // only. Resolve each to its ID by name (case and spacing ignored), so the rule's
    // scope can be checked. Only the ID is ever used for matching.
    const norm = (s?: string) => (s || '').trim().toLowerCase().replace(/\s+/g, ' ');
    // Match by NAME within the bank first. A stored ID left over from an old upload
    // or a duplicate branch record must not block a valid match.
    const bankId =
      banks.find((b) => norm(b.name) === norm(acc?.bank))?.id || acc?.bankId || '';
    if (!bankId || !dynamicRules.some((r) => r.active && r.bankId === bankId)) return null;
    const zoneId =
      zones.find((z) => norm(z.name) === norm(acc?.zone) && (!z.bankId || z.bankId === bankId))?.id ||
      acc?.zoneId;
    const sameBankBranches = acc?.branch
      ? branches.filter((b) => norm(b.name) === norm(acc?.branch) && b.bankId === bankId)
      : [];
    const branchId =
      sameBankBranches.find((b) => !zoneId || b.zoneId === zoneId)?.id ||
      sameBankBranches[0]?.id ||
      acc?.branchId;
    const departmentId =
      acc?.departmentId ||
      recoveryDepartments.find(
        (d) => norm(d.name) === norm(acc?.department) && (!d.bankId || d.bankId === bankId),
      )?.id ||
      null;
    // The bank's sheet may send the NPA date as DD-MM-YYYY. Convert it to YYYY-MM-DD.
    const rawNpa = acc?.npaDate || '';
    const npaIso = /^\d{2}-\d{2}-\d{4}$/.test(rawNpa)
      ? `${rawNpa.slice(6, 10)}-${rawNpa.slice(3, 5)}-${rawNpa.slice(0, 2)}`
      : rawNpa || null;
    const entry: RecoveryEntryInput = {
      id: 'LIVE',
      accountId: acc?.accountId || '',
      bankId,
      zoneId,
      departmentId,
      branchId,
      userId: args.agentId,
      collectionDate: args.date,
      npaDate: npaIso,
      // The bank's account category (SMA 0, SMA 1, SMA 2, NPA) from the Excel upload.
      // Blank = not yet uploaded, so a rule that restricts by category is flagged for
      // review rather than guessing. Product code is still not mapped.
      category: acc?.accountCategory?.trim() || null,
      productCode: null,
      amounts: {
        collection: args.amount,
        outstanding: acc?.outstandingAmount ?? null,
        principal: null,
        totalRecoverable: null,
      },
    };
    const result = evaluateRecoveryEntry(entry, dynamicRules);
    const ruleName = dynamicRules.find((r) => r.id === result.ruleIds[0])?.name ?? '';
    return { result, ruleName };
  };

  const recordRecovery = (data: {
    accountId: string;
    amount: number;
    paymentMode: RecoveryRecord['paymentMode'];
    referenceNumber: string;
    remarks: string;
    recoveryDate?: string;
    proofDriveFileId?: string;
    isOTS?: boolean;
    otsStage?: '10_percent_token' | 'full_settlement';
    otsAgreedAmount?: number;
    agentId?: string;
    agentName?: string;
    excessChargesAmount?: number;
  }) => {
    const acc = accounts.find((a) => a.accountId === data.accountId || a.id === data.accountId);
    const targetAccountId = acc?.accountId || data.accountId;

    /**
     * NO COLLECTION WITHOUT AN AGENT.
     *
     * Every log screen in the app -- Recovery Logs, the commission bill, the
     * agent ledgers -- is reached by choosing an agent first. A receipt with
     * no agent on it therefore appears on NONE of them: the money is in the
     * database and counted in every balance and every total, but no screen
     * anywhere can list it. It cannot be audited, it cannot go on a
     * commission bill, and nobody can answer the bank when it asks who
     * collected it.
     *
     * The Collect Pay screen already requires an administrator to name the
     * collecting agent, so in normal use this never fires. It is here as the
     * floor: any other route into this function -- a future screen, a bulk
     * tool, a mistake -- cannot create an unattributable receipt.
     *
     * Attaching the account's allocated agent automatically would be worse
     * than refusing: it would credit an agent with a collection he did not
     * make, and commission is calculated from exactly that.
     */
    const actorIsAgent = currentUser?.role === 'agent';
    if (!actorIsAgent && !data.agentId) {
      const holder = (acc?.assignedAgentName || '').trim();
      window.alert(
        'Name the agent who collected this money before saving.\n\n' +
          'A receipt with no agent on it never appears on any recovery log or ' +
          'commission bill, and nobody can tell afterwards who collected it.\n\n' +
          (holder
            ? `This account is allocated to ${holder}.`
            : 'This account has no agent allocated.')
      );
      return;
    }

    /**
     * A COLLECTION CANNOT EXCEED WHAT IS OWED.
     *
     * Nothing checked this at all. An agent could record a collection of any
     * size against any account -- more than the loan, more than the agreed
     * OTS -- and the outstanding balance simply went negative or clamped at
     * zero while the commission was calculated on the full amount.
     *
     * The ceiling is the OTS balance still due when an OTS is running, and
     * the outstanding loan balance otherwise.
     *
     * Paise round UP to the next whole rupee, so a balance of Rs 15,000.50
     * allows a collection of Rs 15,001. The stored balance is NOT changed --
     * only the limit is rounded.
     */
    const amountIn = Number(data.amount);
    if (!Number.isFinite(amountIn) || amountIn <= 0) {
      window.alert('Enter a collection amount greater than zero.');
      return;
    }

    /**
     * The part of this collection that is the bank's interest and charges
     * rather than loan repayment. Worked out below when the amount exceeds
     * the balance; the caller may also state it outright.
     */
    let excessCharges = Math.max(0, Number(data.excessChargesAmount) || 0);

    if (acc) {
      const otsRunning =
        acc.otsActive === true && typeof acc.finalOTSAmount === 'number' && acc.finalOTSAmount > 0;
      const payable = otsRunning
        ? Math.max(0, (acc.finalOTSAmount as number) - (Number(acc.otsPaidAmount) || 0))
        : Number(acc.outstandingAmount) || 0;

      // Subtracting a hair before rounding up keeps a whole-rupee balance at
      // itself; without it, 15000 would permit 15001.
      const maxCollectable = Math.ceil(payable - 0.000001);

      if (payable <= 0) {
        window.alert(
          otsRunning
            ? 'The agreed OTS amount has already been collected in full. Nothing further is due on this account.'
            : 'This account has no outstanding balance left to collect.'
        );
        return;
      }

      /**
       * COLLECTING MORE THAN THE BALANCE IS ALLOWED -- AND THE SURPLUS IS NOT
       * TREATED AS LOAN REPAYMENT.
       *
       * THIS USED TO BE REFUSED OUTRIGHT. That was wrong, and it blocked a
       * real case: a borrower regularising an account after NPA does not pay
       * the loan balance, they pay the balance PLUS the compounding interest
       * and charges the bank adds to bring the account back into order. The
       * total handed over is legitimately larger than anything the app shows
       * as owed, and the agent had no way to record what actually happened.
       *
       * The surplus is now allowed, and separated:
       *
       *   amount               what the customer actually paid -- the receipt
       *                        must show the truth
       *   excessChargesAmount  the part above the loan balance: the bank's
       *                        interest and charges
       *
       * THE ACCOUNT ONLY EVER MOVES BY THE DIFFERENCE. Counting the surplus
       * as repayment would drive the balance to zero and lose the excess, and
       * -- your point -- deleting the entry afterwards would hand the account
       * back MORE than it ever owed. Held separately, a delete returns the
       * loan portion and nothing else.
       *
       * FOR AN OTS THE OLD REFUSAL STANDS. A settlement is a fixed agreed
       * figure; paying more than it is not a regularisation, it is a mistake,
       * and taking the money would be worse than refusing it.
       */
      const overBy = amountIn - maxCollectable;

      if (overBy > 0 && otsRunning) {
        window.alert(
          'That is more than the agreed settlement still due.\n\n' +
            `OTS balance still due: Rs ${payable.toLocaleString('en-IN', {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            })}\n` +
            `You entered: Rs ${amountIn.toLocaleString('en-IN')}\n\n` +
            'A settlement is a fixed figure. Enter an amount up to the balance due, ' +
            'or untick One-Time Settlement to record this as an ordinary collection.'
        );
        return;
      }

      if (overBy > 0) {
        const ok = window.confirm(
          'This is more than the loan balance.\n\n' +
            `Loan balance:        Rs ${payable.toLocaleString('en-IN')}\n` +
            `Collecting:          Rs ${amountIn.toLocaleString('en-IN')}\n` +
            `Extra:               Rs ${overBy.toLocaleString('en-IN')}\n\n` +
            'This normally means the bank has added interest and charges to regularise ' +
            'the account.\n\n' +
            `The receipt will show the full Rs ${amountIn.toLocaleString('en-IN')}, but the loan ` +
            `balance will only reduce by Rs ${payable.toLocaleString('en-IN')}. The extra is ` +
            'recorded as bank charges and will not be added back to the account if this ' +
            'entry is ever deleted.\n\nRecord it?'
        );
        if (!ok) return;

        // What the screen asked for, not what the screen guessed.
        excessCharges = overBy;
      }
    }
    /**
     * THE PART THAT ACTUALLY REPAYS THE LOAN.
     *
     * Everything that moves the ACCOUNT's money below uses this, never
     * `data.amount`. The two differ only on a regularisation, where the
     * customer also paid the bank's interest and charges -- money they
     * genuinely handed over, which belongs on the receipt, but which repays
     * nothing.
     *
     * Using the full amount would drive the balance below what was repaid,
     * and -- your point -- deleting the entry later would hand the account
     * back more than it ever owed.
     *
     * The receipt, the notes and the commission still quote the full amount.
     * Only the balance is kept honest.
     */
    const loanPortion = Math.max(0, amountIn - excessCharges);

    // Four random digits collided about once in 9,000 receipts, and two
    // records created in the same millisecond shared an id outright -- the
    // second silently overwrote the first instead of appearing.
    const recId = `REC-${uniqueSuffix()}`;
    const dateStr = data.recoveryDate || todayLocal();
    const receiptNumber = `SRMS-RCP-${dateStr.slice(0, 7)}-${Math.floor(1000 + Math.random() * 9000)}`;

    // If collecting agent is explicitly selected from agent-only list, use it.
    // Otherwise fallback to assigned agent, or currentUser
    const hasAssignedAgent = Boolean(
      acc?.assignedAgentId &&
      acc.assignedAgentId !== 'N/A' &&
      acc.assignedAgentId !== 'Unassigned'
    );
    const creditedAgentId =
      data.agentId ||
      (hasAssignedAgent && currentUser.role !== 'agent'
        ? (acc!.assignedAgentId as string)
        : currentUser.agentId || currentUser.id);
    const creditedAgentName =
      data.agentName ||
      (hasAssignedAgent && currentUser.role !== 'agent'
        ? (acc!.assignedAgentName as string)
        : currentUser.name);

    // Admin Recovery: ensure bracket remarks denote admin recovery
    const isAdmin = currentUser.role === 'admin';
    let finalRemarks = (data.remarks || '').trim();
    if (isAdmin && !finalRemarks.includes('[Admin Recovery:')) {
      finalRemarks = `${finalRemarks} [Admin Recovery: ${currentUser.name}]`.trim();
    }

    // Determine QD type & Stage for prioritized calculation
    const calculatedQD: QDType =
      acc?.qdType ||
      (acc?.overdueDays && acc.overdueDays > 90
        ? 'Critical NPA (>90 DPD)'
        : acc?.overdueDays && acc.overdueDays > 60
        ? 'SMA-2 (61-90 DPD)'
        : acc?.overdueDays && acc.overdueDays > 30
        ? 'SMA-1 (31-60 DPD)'
        : 'SMA-0 (0-30 DPD)');

    const recoveryStage: RecoveryStage = data.isOTS
      ? data.otsStage === 'full_settlement'
        ? 'OTS Full Settlement'
        : 'OTS Token (10%)'
      : data.amount >= (acc?.outstandingAmount || data.amount)
      ? 'Full Settlement / Closed'
      : 'Partial Recovery';

    // The rate for this account: its own rate, else its bank's, else the
    // system default. The bank layer had never been read by anything -- the
    // field was set on the bank record and then ignored -- so a bank on an
    // agreed 2% was being billed at the 10% default on every collection.
    const creditedUser =
      users.find(
        (u) => u.agentId === creditedAgentId || u.id === creditedAgentId
      ) || null;

    const resolvedRate = resolveCommissionRate(
      acc,
      banks,
      commissionSettings.defaultRate,
      creditedUser,
      commissionSettings.sarfaesiRate
    );

    // Prioritized commission calculation. The resolved rate becomes the
    // fallback, so an explicit hierarchy rule still wins where one matches
    // and the three layers apply wherever none does.
    const commCalc = calculateCommissionWithPriority(
      {
        eligibleAmount: data.amount,
        recoveryDate: dateStr,
        agentId: creditedAgentId,
        bankName: acc?.bank || currentUser.bank,
        zoneName: acc?.zone || currentUser.zone,
        branchName: acc?.branch || currentUser.branch,
        departmentName: acc?.department || currentUser.department,
        qdType: calculatedQD,
        recoveryStage: recoveryStage,
      },
      commissionRules,
      userCommissionAssignments,
      resolvedRate.percent
    );

    // A rate set on the account itself is an explicit instruction for that
    // account and outranks a general rule.
    // A rate that was deliberately set -- on the account, on the bank or on
    // the user -- wins over whatever the rules engine fell back to. Only when
    // nothing has been set does the engine's own result stand, so an explicit
    // 2% can no longer be quietly replaced by the 10% standard rate.
    // Did the engine match a REAL rule, or did it just fall back?
    //
    // This matters for zone-wise rates: the same bank can pay a different
    // percentage in a different zone, and that lives as a hierarchy rule. A
    // matched rule is more specific than the bank's flat rate, so it must
    // win -- otherwise setting a bank rate would quietly wipe out every
    // zone-wise rate underneath it.
    const engineMatchedARule =
      Boolean(
        commCalc.appliedRuleId ||
          commCalc.ruleId ||
          commCalc.appliedAssignmentId ||
          commCalc.assignmentId
      ) && (commCalc.priorityLevel ?? 5) <= 5;

    if (resolvedRate.source === 'account' || resolvedRate.source === 'sarfaesi') {
      // A rate set on the individual account beats everything.
      commCalc.appliedPercentage = resolvedRate.percent;
      commCalc.percentage = resolvedRate.percent;
      commCalc.commissionAmount = commissionFor(data.amount, resolvedRate.percent);
      commCalc.appliedRuleName = resolvedRate.label;
    } else if (!engineMatchedARule && resolvedRate.source !== 'default') {
      // No rule matched, so fall to the bank / user rate rather than the
      // flat 10% the engine would otherwise have used.
      commCalc.appliedPercentage = resolvedRate.percent;
      commCalc.percentage = resolvedRate.percent;
      commCalc.commissionAmount = commissionFor(data.amount, resolvedRate.percent);
      commCalc.appliedRuleName = resolvedRate.label;
    }

    if (commCalc.conflictDetected) {
      console.warn("Duplicate commission rule detected. Please resolve the conflicting commission rules.");
    }

    // Batch 105: a bank on the new rules takes its commission from the saved rule.
    // Never held or blocked: no match or missing data = 0, flagged for review.
    const dyn = liveDynamicCommission({
      acc,
      agentId: creditedUser?.id || creditedAgentId,
      amount: data.amount,
      date: dateStr,
    });
    const dynReady = !!dyn && dyn.result.status === 'READY' && dyn.result.commission !== undefined;
    const commissionPercentage = dyn
      ? dynReady
        ? dyn.result.rate ?? 0
        : 0
      : commCalc.appliedPercentage ?? commCalc.percentage;
    const commissionAmount = dyn
      ? dynReady
        ? dyn.result.commission ?? 0
        : 0
      : commissionFor(data.amount, commissionPercentage);
    const dynMeta = dyn
      ? {
          appliedCommissionRuleId: dynReady ? dyn.result.ruleIds[0] : undefined,
          appliedCommissionRuleName: dynReady ? dyn.ruleName : undefined,
          commissionReviewStatus: dynReady ? undefined : ('REVIEW' as const),
          commissionReviewReason: dynReady ? undefined : dyn.result.messages.join(' '),
        }
      : {};

    const newRecovery: RecoveryRecord = {
      // The Firebase Auth uid of whoever created this.
      //
      // The security rules let an agent delete their OWN records. They can
      // establish that from this field with no document read; without it they
      // fall back to reading the profile to compare the staff code, which
      // costs a read on every delete. Photos already carried this; these did
      // not.
      agentUid: currentUser.id,
      id: `REC-${uniqueSuffix()}`,
      recoveryId: recId,
      accountId: targetAccountId,
      customerName: acc?.customerName || 'Customer',
      agentId: creditedAgentId,
      agentName: creditedAgentName,
      amount: data.amount,
      /**
       * The bank's interest and charges, held apart from the loan repayment.
       *
       * `amount` stays the true total the customer handed over, because that
       * is what the receipt has to say. The account's balance moves by
       * `amount - excessChargesAmount` and by nothing else -- see
       * recalcAccountMoney -- so a regularisation cannot drive the balance
       * below what was actually repaid, and deleting this entry returns the
       * loan portion only.
       *
       * Left undefined when there is no surplus, so ordinary collections
       * carry no extra field.
       */
      excessChargesAmount: excessCharges > 0 ? excessCharges : undefined,
      recoveryDate: dateStr,
      referenceNumber: data.referenceNumber,
      paymentMode: data.paymentMode,
      remarks: finalRemarks,
      receiptNumber,
      proofDriveFileId: data.proofDriveFileId || `DRV_REC_${Date.now()}_PROOF`,
      branch: acc?.branch || currentUser.branch,
      zone: acc?.zone || currentUser.zone,
      bank: acc?.bank || currentUser.bank,
      department: acc?.department || currentUser.department,
      qdType: calculatedQD,
      recoveryStage: recoveryStage,
      commissionAmount,
      commissionPercentage,
      appliedCommissionRuleId: commCalc.appliedRuleId || commCalc.ruleId,
      appliedCommissionRuleName: commCalc.appliedRuleName || commCalc.ruleName || resolvedRate.label,
      appliedAssignmentId: commCalc.appliedAssignmentId || commCalc.assignmentId,
      appliedPriorityLevel: commCalc.priorityLevel,
      commissionPaidStatus: 'Approved',
      isOTS: data.isOTS,
      otsStage: data.otsStage,
      otsAgreedAmount: data.otsAgreedAmount,
      isAdminRecovery: isAdmin,
      adminRecordedBy: isAdmin ? currentUser.name : undefined,
      ...dynMeta,
    };

    setRecoveries((prev) => [newRecovery, ...prev]);

    // ⚡ Primary write to Firestore (<50ms speed route)
    firebaseFirestoreService.saveRecovery(newRecovery).catch((err) => {
      console.warn('Firestore primary recovery write warning:', err);
    });

    // Add Commission Record for the credited agent with immutable audit trail
    const newCommission: CommissionRecord = {
      // The Firebase Auth uid of whoever created this.
      //
      // The security rules let an agent delete their OWN records. They can
      // establish that from this field with no document read; without it they
      // fall back to reading the profile to compare the staff code, which
      // costs a read on every delete. Photos already carried this; these did
      // not.
      agentUid: currentUser.id,
      id: `COM-${uniqueSuffix()}`,
      agentId: creditedAgentId,
      agentName: creditedAgentName,
      recoveryId: recId,
      accountId: targetAccountId,
      recoveryAmount: data.amount,
      commissionRate: commissionPercentage,
      commissionAmount,
      date: dateStr,
      status: 'Approved',
      appliedRuleId: commCalc.appliedRuleId || commCalc.ruleId,
      appliedRuleName: commCalc.appliedRuleName || commCalc.ruleName || resolvedRate.label,
      appliedAssignmentId: commCalc.appliedAssignmentId || commCalc.assignmentId,
      priorityLevel: commCalc.priorityLevel,
      priorityDescription: commCalc.priorityDescription,
      bank: acc?.bank || currentUser.bank,
      zone: acc?.zone || currentUser.zone,
      branch: acc?.branch || currentUser.branch,
      qdType: calculatedQD,
      recoveryStage: recoveryStage,
    };
    setCommissions((prev) => [newCommission, ...prev]);

    firebaseFirestoreService.saveCommission(newCommission).catch((err) => {
      console.warn('Firestore primary commission write warning:', err);
    });

    // CUSTOMER-LEVEL OTS. When this collection meets the agreed OTS amount,
    // the customer's other open loans at the same bank are settled with it.
    // Each gets its own concession, fixed here, so recalculation keeps it.
    const otsAgreedPre = data.otsAgreedAmount || acc?.otsAgreedAmount || (data.amount * 10);
    const otsCompletes =
      !!acc &&
      !!data.isOTS &&
      data.otsStage !== '10_percent_token' &&
      (acc.totalRecovered || 0) + loanPortion >= otsAgreedPre;
    // Concession = original less everything collected, including the 10%
    // token (the token does not reduce outstandingAmount, so use totals).
    const mainConcession = acc
      ? Math.max(0, originalOutstandingOf(acc) - ((acc.totalRecovered || 0) + loanPortion))
      : 0;
    const siblingIds = new Set<string>();
    if (otsCompletes && acc?.customerCode) {
      accounts.forEach((s) => {
        const sid = s.accountId || s.id;
        if (!sid || sid === targetAccountId || s.id === targetAccountId) return;
        if (s.customerCode !== acc.customerCode) return;
        if (acc.bank && s.bank && s.bank !== acc.bank) return;
        if ((s.outstandingAmount || 0) <= 0) return;
        if (s.accountStatus === 'Closed as per OTS') return;
        siblingIds.add(sid);
      });
    }

    // Update Account with OTS or Standard Recovery
    const nowIsoTime = new Date().toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
    mutateAccounts((prev) =>
      prev.map((a) => {
        if (a.accountId === targetAccountId || a.id === targetAccountId) {
          // The loan portion, so this figure agrees with recalcAccountMoney,
          // which is the authority whenever the account is recalculated.
          const newTotalRecovered = (a.totalRecovered || 0) + loanPortion;

          if (data.isOTS) {
            const agreed = data.otsAgreedAmount || a.otsAgreedAmount || (data.amount * 10);

            if (data.otsStage === '10_percent_token') {
              const otsNote: AgentNote = {
                id: `NOTE-OTS-10-${Date.now()}`,
                agentId: creditedAgentId,
                agentName: creditedAgentName,
                date: dateStr,
                time: nowIsoTime,
                note: `[OTS Token Paid]: 10% upfront OTS token payment ₹${data.amount.toLocaleString('en-IN')} deposited. Total agreed OTS: ₹${agreed.toLocaleString('en-IN')}. Sanction process pending. ${finalRemarks}`,
                category: 'OTS Settlement',
              };

              return {
                ...a,
                totalRecovered: newTotalRecovered,
                lastRecoveryDate: dateStr,
                otsStatus: '10% OTS Paid - Sanction Pending',
                otsAgreedAmount: agreed,
                ots10PercentPaidAmount: data.amount,
                // Counts towards the settlement like any other collection.
                otsPaidAmount:
                  (typeof a.otsPaidAmount === 'number' ? a.otsPaidAmount : 0) +
                  data.amount,
                recoveryStatus: 'OTS Token Paid (10%)',
                accountStatus: 'OTS Processing',
                notes: `10% OTS token ₹${data.amount.toLocaleString('en-IN')} received. Awaiting sanction. ${finalRemarks}`,
                agentNotesHistory: [otsNote, ...(a.agentNotesHistory || [])],
              };
            } else {
              // Full OTS Settlement stage or general OTS payment
              // USER RULE: "if we took amount offer OTS amount below than account status will not as closed till user not close it"
              if (newTotalRecovered < agreed) {
                // The AGREED TOTAL stays put. Collections accumulate in
                // otsPaidAmount and the remaining balance is worked out from
                // the two. Previously each collection was subtracted straight
                // out of finalOTSAmount, so the figure agreed with the
                // customer was overwritten by whatever was left to pay -- and
                // after one payment nobody could see what had been agreed.
                const otsTotal =
                  a.finalOTSAmount ?? a.userEnteredOTSAmount ?? agreed;
                const paidSoFar =
                  (typeof a.otsPaidAmount === 'number' ? a.otsPaidAmount : a.ots10PercentPaidAmount || 0) +
                  data.amount;
                const remainingOTS = Math.max(0, otsTotal - paidSoFar);
                const otsPartialNote: AgentNote = {
                  id: `NOTE-OTS-PARTIAL-${Date.now()}`,
                  agentId: creditedAgentId,
                  agentName: creditedAgentName,
                  date: dateStr,
                  time: nowIsoTime,
                  note: `[OTS Partial Payment Received]: Collected ₹${data.amount.toLocaleString('en-IN')}. Total paid under OTS: ₹${newTotalRecovered.toLocaleString('en-IN')} of agreed ₹${agreed.toLocaleString('en-IN')} (₹${remainingOTS.toLocaleString('en-IN')} balance remaining). Below agreed offer amount; account status remains OPEN (OTS Processing) until full OTS amount is satisfied or manually closed by user. ${finalRemarks}`,
                  category: 'OTS Settlement',
                };

                return {
                  ...a,
                  totalRecovered: newTotalRecovered,
                  lastRecoveryDate: dateStr,
                  outstandingAmount: Math.max(0, a.outstandingAmount - loanPortion),
                  // Follows the balance, never its own stored value.
                  overdueAmount: Math.max(0, a.outstandingAmount - loanPortion),
                  customerBalance: Math.max(
                    0,
                    (typeof a.customerBalance === 'number'
                      ? a.customerBalance
                      : a.overdueAmount) - loanPortion
                  ),
                  otsStatus: '10% OTS Paid - Sanction In Progress',
                  otsAgreedAmount: agreed,
                  finalOTSAmount: otsTotal,
                  otsPaidAmount: paidSoFar,
                  recoveryStatus: 'Partially Recovered',
                  accountStatus: 'OTS Processing', // OPEN, NOT closed!
                  notes: `OTS Partial Payment: ₹${newTotalRecovered.toLocaleString('en-IN')} of ₹${agreed.toLocaleString('en-IN')} paid. Account remains open until user closes it or remaining ₹${remainingOTS.toLocaleString('en-IN')} is cleared. ${finalRemarks}`,
                  agentNotesHistory: [otsPartialNote, ...(a.agentNotesHistory || [])],
                };
              } else {
                // Full agreed OTS amount satisfied
                /**
                 * The concession is against WHAT IS OWED, not the sanctioned
                 * limit. This read `a.sanctionAmount` first -- the limit
                 * granted when the loan was written -- which on an NPA is
                 * routinely lower than the balance, because interest has
                 * accrued on top of it. One real account: sanction 9,50,000,
                 * outstanding 9,97,774. Every concession was wrong by the
                 * difference, and because the figure was then stored and
                 * reused as the waiver, so was every balance calculated after
                 * it.
                 *
                 * This value is now only ever a record of what was agreed at
                 * the time. Nothing calculates a balance from it -- that goes
                 * through otsWaiverOf, which derives it.
                 */
                const discount = Math.max(0, originalOutstandingOf(a) - agreed);
                const otsCloseNote: AgentNote = {
                  id: `NOTE-OTS-CLOSE-${Date.now()}`,
                  agentId: creditedAgentId,
                  agentName: creditedAgentName,
                  date: dateStr,
                  time: nowIsoTime,
                  note: `[OTS Settled]: Account settled under OTS scheme. Total OTS paid: ₹${newTotalRecovered.toLocaleString('en-IN')} (concession waived: ₹${discount.toLocaleString('en-IN')}). Status: Closed as per OTS. User can archive or delete as closed. ${finalRemarks}`,
                  category: 'OTS Settlement',
                };

                return {
                  ...a,
                  totalRecovered: newTotalRecovered,
                  lastRecoveryDate: dateStr,
                  outstandingAmount: 0,
                  overdueAmount: 0,
                  customerBalance: 0,
                  otsStatus: 'OTS Settled',
                  otsAgreedAmount: agreed,
                  // The settlement is met, not erased. Keeping the agreed
                  // figure and the amount paid means a closed account can
                  // still be audited against what was actually agreed.
                  finalOTSAmount: agreed,
                  otsPaidAmount: newTotalRecovered,
                  otsDiscountAmount: discount,
                  // This loan's share of the concession, fixed at completion.
                  otsConcessionAmount: mainConcession,
                  customerOtsSettledBy: null,
                  recoveryStatus: 'Fully Recovered',
                  accountStatus: 'Closed as per OTS',
                  closureType: 'Closed as per OTS',
                  closureDate: dateStr,
                  ptpStatus: 'Achieved',
                  notes: `Closed as per OTS. (Total Paid: ₹${newTotalRecovered.toLocaleString('en-IN')}, Discount: ₹${discount.toLocaleString('en-IN')}). ${finalRemarks}`,
                  agentNotesHistory: [otsCloseNote, ...(a.agentNotesHistory || [])],
                };
              }
            }
          }

          // Standard recovery calculation
          const newOutstanding = Math.max(0, a.outstandingAmount - loanPortion);
          // Overdue tracks the balance rather than being decremented on its
          // own, so the two cannot drift apart. Subtracting from the stored
          // overdue is what let it fall out of step with the balance and
          // stay there -- see recalcAccountMoney.
          const newOverdue = newOutstanding;
          const isFullyPaid = newOutstanding === 0 && newOverdue === 0;

          // customerBalance was reduced only when an OTS closed, never on an
          // ordinary collection. Several screens -- the customer window's
          // "Customer Balance (Total Overdue)" among them -- prefer this field
          // over overdueAmount, so the figure sat frozen at the imported value
          // no matter how much was collected.
          const priorBalance =
            typeof a.customerBalance === 'number'
              ? a.customerBalance
              : a.overdueAmount;
          const newCustomerBalance = Math.max(0, priorBalance - loanPortion);

          const recNote: AgentNote = {
            id: `NOTE-REC-${Date.now()}`,
            agentId: creditedAgentId,
            agentName: creditedAgentName,
            date: dateStr,
            time: nowIsoTime,
            note: `[Collection Received]: Collected ₹${data.amount.toLocaleString('en-IN')} via ${data.paymentMode} (Ref: ${data.referenceNumber || 'N/A'}). Remaining POS: ₹${newOutstanding.toLocaleString('en-IN')}. Receipt: ${receiptNumber}. ${finalRemarks}`,
            category: 'Payment Received',
          };

          return {
            ...a,
            totalRecovered: newTotalRecovered,
            lastRecoveryDate: dateStr,
            outstandingAmount: newOutstanding,
            overdueAmount: newOverdue,
            customerBalance: newCustomerBalance,
            recoveryStatus: isFullyPaid ? 'Fully Recovered' : 'Partially Recovered',
            accountStatus: isFullyPaid ? 'Regular Close' : 'Recovered',
            closureType: isFullyPaid ? 'Regular Close' : a.closureType,
            closureDate: isFullyPaid ? dateStr : a.closureDate,
            ptpStatus: 'Achieved',
            notes: isFullyPaid
              ? `Regular Close: Full recovery completed. ${finalRemarks}`
              : a.notes,
            agentNotesHistory: [recNote, ...(a.agentNotesHistory || [])],
          };
        }
        return a;
      })
    );

    // Close the customer's linked loans with the settlement (see above).
    if (siblingIds.size > 0) {
      const linkedNote: AgentNote = {
        id: `NOTE-OTS-LINKED-${Date.now()}`,
        agentId: creditedAgentId,
        agentName: creditedAgentName,
        date: dateStr,
        time: nowIsoTime,
        note: `[OTS Settled with linked loan]: Same customer settlement met on ${targetAccountId} (agreed ₹${otsAgreedPre.toLocaleString('en-IN')}). This loan closed at ₹0. ${finalRemarks}`,
        category: 'OTS Settlement',
      };
      mutateAccounts((prev) =>
        prev.map((s) => {
          const sid = s.accountId || s.id;
          if (!sid || !siblingIds.has(sid)) return s;
          const owed = Math.max(0, s.outstandingAmount || 0);
          return {
            ...s,
            originalOutstandingAmount: originalOutstandingOf(s),
            outstandingAmount: 0,
            overdueAmount: 0,
            customerBalance: 0,
            otsConcessionAmount: owed,
            customerOtsSettledBy: targetAccountId,
            otsStatus: 'OTS Settled',
            recoveryStatus: 'Fully Recovered',
            accountStatus: 'Closed as per OTS',
            closureType: 'Closed as per OTS',
            closureDate: dateStr,
            notes: `Closed as per OTS with linked loan ${targetAccountId} (same customer). ${finalRemarks}`,
            agentNotesHistory: [linkedNote, ...(s.agentNotesHistory || [])],
          } as typeof s;
        })
      );
    }

    // Auto resolve pending PTPs if any
    setPtps((prev) =>
      prev.map((p) =>
        (p.accountId === targetAccountId || p.accountId === data.accountId) && p.status === 'Pending'
          ? { ...p, status: 'Achieved', resolvedDate: dateStr }
          : p
      )
    );

    // Confetti celebration
    try {
      confetti({
        particleCount: 80,
        spread: 60,
        origin: { y: 0.6 },
      });
    } catch {
      // ignore
    }

    // Add Notification
    const notif: NotificationItem = {
      id: `NOTIF-${Date.now()}`,
      timestamp: nowLocalStamp(),
      recipientRole: 'all',
      title: `💰 Recovery Logged: ₹${data.amount.toLocaleString('en-IN')}`,
      message: `${currentUser.name} (${currentUser.agentId || 'Agent'}) collected ₹${data.amount.toLocaleString('en-IN')} for Account ${targetAccountId}. Commission: ₹${commissionAmount.toLocaleString('en-IN')} (10%). Receipt: ${receiptNumber}.`,
      type: 'recovery_done',
      read: false,
      accountId: targetAccountId,
    };
    setNotifications((prev) => [notif, ...prev]);

    logActivity(
      currentUser,
      'RECOVERY_LOGGED',
      `Collected ₹${data.amount.toLocaleString('en-IN')} on ${dateStr} via ${data.paymentMode} (Ref: ${data.referenceNumber}). 10% Commission: ₹${commissionAmount.toLocaleString('en-IN')}. Receipt: ${receiptNumber}${data.isOTS ? ` [OTS: ${data.otsStage}]` : ''}`,
      targetAccountId
    );
  };

  const editRecovery = async (
    recoveryId: string,
    updatedData: {
      amount?: number;
      paymentMode?: RecoveryRecord['paymentMode'];
      referenceNumber?: string;
      remarks?: string;
      recoveryDate?: string;
      isOTS?: boolean;
      otsStage?: '10_percent_token' | 'full_settlement';
      otsAgreedAmount?: number;
    }
  ): Promise<{ success: boolean; message: string }> => {
    const existingRec = recoveries.find((r) => r.id === recoveryId || r.recoveryId === recoveryId);
    if (!existingRec) {
      return { success: false, message: 'Recovery record not found.' };
    }

    const newAmount = updatedData.amount !== undefined ? updatedData.amount : existingRec.amount;
    const diffAmount = newAmount - existingRec.amount;
    const newCommission = Math.round((newAmount * (existingRec.commissionPercentage || commissionSettings.defaultRate)) / 100);

    setRecoveries((prev) =>
      prev.map((r) =>
        r.id === recoveryId || r.recoveryId === recoveryId
          ? {
              ...r,
              amount: newAmount,
              paymentMode: updatedData.paymentMode || r.paymentMode,
              referenceNumber: updatedData.referenceNumber !== undefined ? updatedData.referenceNumber : r.referenceNumber,
              remarks: updatedData.remarks !== undefined ? updatedData.remarks : r.remarks,
              recoveryDate: updatedData.recoveryDate || r.recoveryDate,
              commissionAmount: newCommission,
              isOTS: updatedData.isOTS !== undefined ? updatedData.isOTS : r.isOTS,
              otsStage: updatedData.otsStage || r.otsStage,
              otsAgreedAmount: updatedData.otsAgreedAmount || r.otsAgreedAmount,
            }
          : r
      )
    );

    setCommissions((prev) =>
      prev.map((c) =>
        c.recoveryId === existingRec.recoveryId || c.recoveryId === recoveryId
          ? {
              ...c,
              recoveryAmount: newAmount,
              commissionAmount: newCommission,
              date: updatedData.recoveryDate || c.date,
            }
          : c
      )
    );

    if (diffAmount !== 0) {
      mutateAccounts((prev) =>
        prev.map((a) => {
          if (a.accountId === existingRec.accountId) {
            const updatedTotal = Math.max(0, (a.totalRecovered || 0) + diffAmount);
            const updatedOut = Math.max(0, a.outstandingAmount - diffAmount);
            // Follows the balance. See recalcAccountMoney.
            const updatedOverdue = updatedOut;
            return {
              ...a,
              totalRecovered: updatedTotal,
              outstandingAmount: updatedOut,
              overdueAmount: updatedOverdue,
            };
          }
          return a;
        })
      );
    }

    logActivity(
      currentUser,
      'RECOVERY_LOGGED',
      `Updated recovery record ${existingRec.receiptNumber} (${existingRec.accountId}). Amount: ₹${newAmount.toLocaleString('en-IN')}`,
      existingRec.accountId
    );

    return { success: true, message: 'Recovery record updated successfully.' };
  };

  const deleteRecovery = async (recoveryId: string): Promise<{ success: boolean; message: string }> => {
    const existingRec = recoveries.find((r) => r.id === recoveryId || r.recoveryId === recoveryId);
    if (!existingRec) {
      return { success: false, message: 'Recovery record not found.' };
    }

    /**
     * CAN THIS PERSON DELETE THIS COLLECTION AT ALL?
     *
     * Asked here, before anything is attempted, because the database's answer
     * arrives as a bare "permission denied" that tells the agent nothing. An
     * agent may delete a collection they recorded themselves; anything
     * recorded by somebody else -- typically an administrator -- is not
     * theirs to remove. That is the rule working, but the old message read
     * like a fault in the app, and pressing the button repeatedly deleted
     * whichever entries did happen to belong to them, which looked
     * arbitrary.
     */
    const actorForDelete = currentUserRef.current;
    if (actorForDelete.role !== 'admin') {
      const norm = (v?: string) => (v || '').trim().toLowerCase();
      const mine =
        norm((existingRec as any).agentUid) === norm(actorForDelete.id) ||
        norm(existingRec.agentId) === norm(actorForDelete.agentId) ||
        norm(existingRec.agentId) === norm(actorForDelete.id);

      if (!mine) {
        return {
          success: false,
          message:
            `This collection was recorded by ${existingRec.agentName || 'another user'}` +
            `${existingRec.agentId ? ` (${existingRec.agentId})` : ''}, so it is not yours to delete. ` +
            'Ask an administrator to remove it.',
        };
      }
    }

    // One at a time, per record.
    const flightKey = existingRec.recoveryId || existingRec.id || recoveryId;
    if (recoveryDeletesInFlight.has(flightKey)) {
      return { success: false, message: 'That delete is already running. Please wait.' };
    }
    recoveryDeletesInFlight.add(flightKey);

    try {
    return await (async (): Promise<{ success: boolean; message: string }> => {

    // Delete from the database FIRST. This only removed the row from the
    // screen; nothing was deleted in Firestore, so the live listener brought
    // the collection straight back on the next sync -- and its commission
    // with it. The same fault the photo delete had.
    // Same reasoning as accounts: remove every key it could be filed under.
    const recDocIds = Array.from(
      new Set(
        [
          (existingRec as any).__docId,
          existingRec.id,
          existingRec.recoveryId,
        ].filter((v): v is string => typeof v === 'string' && v !== '')
      )
    );
    /**
     * WAS IT ACTUALLY THERE BEFORE WE DELETED IT?
     *
     * THE MONEY BUG THIS FIXES:
     *
     * Firestore treats "delete a document that is not there" as SUCCESS. So
     * the second press of Delete on the same entry returned exactly the same
     * result as the first. And this routine then went on to adjust the account:
     *
     *     outstandingAmount = a.outstandingAmount + existingRec.amount
     *
     * -- an ADDITION, applied every time the routine ran. Press Delete three
     * times and the outstanding balance rose by three times the collection
     * amount, while `totalRecovered` fell (clamped at zero). The account was
     * left showing an amount that corresponds to nothing.
     *
     * And the reason anyone would press it more than once: the recoveries
     * listener could only ADD and UPDATE rows, never remove one, so the entry
     * stayed on screen after being deleted from the database. It looked as
     * though the delete had failed, so the natural thing to do was press it
     * again. Each press inflated the balance further.
     *
     * That is the "increasing by a random odd amount" -- it was the collection
     * amount multiplied by the number of attempts.
     *
     * So: establish whether the record is there FIRST. Only a delete that
     * actually removed something is allowed to touch money.
     */
    /**
     * DELETE ONLY THE KEYS THAT EXIST.
     *
     * THE BUG THIS FIXES -- and it caused three separate complaints.
     *
     * A collection is created with TWO different identifiers: `recoveryId`
     * ("REC-...") and `id` ("REC-..." with a different suffix). Only `id` is
     * ever used as the Firestore document key. So this loop always tried two
     * deletes: the real document, and one that has never existed anywhere.
     *
     * Deleting a document that is not there is normally harmless. It is NOT
     * harmless when the security rules have to decide whether you own it:
     * with no document there is nothing to own, so an agent is refused with
     * "permission-denied". The real record had already gone by then.
     *
     * The refusal threw, and the `return` below skipped EVERYTHING after it:
     * the commission was never removed, and the balance was never put back.
     * That is why the entry vanished from the screen, the money never
     * returned, and the agent was still paid commission on a collection that
     * no longer existed -- while an administrator saw none of it, because an
     * administrator is allowed to delete a document that is not there.
     *
     * Now: each key is checked first, only real documents are deleted, and a
     * failure on one key never abandons the work that follows.
     */
    const presentDocIds: string[] = [];
    for (const id of recDocIds) {
      try {
        if (await firebaseFirestoreService.docExists('recoveries', id)) {
          presentDocIds.push(id);
        }
      } catch {
        // Could not tell -- treat it as present and let the delete decide.
        presentDocIds.push(id);
      }
    }

    const existedBefore = presentDocIds.length > 0;

    if (!existedBefore) {
      // Nothing to delete. Say so honestly rather than adjusting money for a
      // collection that is already gone.
      setRecoveries((prev) =>
        prev.filter((r) => r.id !== recoveryId && r.recoveryId !== recoveryId)
      );
      return {
        success: true,
        message: 'That collection had already been deleted. Nothing was changed.',
      };
    }

    let deleteFailure: any = null;
    for (const id of presentDocIds) {
      try {
        await firebaseFirestoreService.deleteRecovery(id);
      } catch (err: any) {
        console.error('[Firestore] Recovery delete failed for', id, err);
        deleteFailure = err;
      }
    }

    if (deleteFailure) {
      return {
        success: false,
        message:
          deleteFailure?.code === 'permission-denied'
            ? 'You do not have permission to delete this collection.'
            : deleteFailure?.message ||
              'Could not delete the collection. Please try again.',
      };
    }

    // Its commission goes with it, otherwise the agent keeps being paid for
    // a collection that no longer exists.
    const doomedCommissions = commissions.filter(
      (c) => c.recoveryId === existingRec.recoveryId || c.recoveryId === recoveryId
    );
    for (const c of doomedCommissions) {
      try {
        await firebaseFirestoreService.deleteCommission(
          (c as any).__docId || c.id
        );
      } catch (err) {
        console.warn('[Firestore] Commission delete notice:', err);
      }
    }

    setRecoveries((prev) => prev.filter((r) => r.id !== recoveryId && r.recoveryId !== recoveryId));
    setCommissions((prev) => prev.filter((c) => c.recoveryId !== existingRec.recoveryId && c.recoveryId !== recoveryId));

    /**
     * THE BALANCE IS RECALCULATED FROM THE RECEIPTS THAT REMAIN.
     *
     * Not adjusted -- calculated. The account's money is
     *
     *     original outstanding - collections that exist - agreed waiver
     *
     * so a deleted receipt stops counting the moment it is gone. There is no
     * "give the money back" step, which means there is nothing to run twice,
     * nothing to skip, and no figure that can drift away from the ledger.
     * Running this ten times gives the same answer as running it once.
     */
    /**
     * MATCHING A COLLECTION TO ITS ACCOUNT, TOLERANTLY.
     *
     * WHY THIS IS NOT A PLAIN `===`. A collection stores the account it was
     * taken against as a single string. An account can be identified by
     * `accountId`, by its Firestore `id`, or by its loan number, and those
     * are not always the same string -- a re-import can change one, and an
     * older record can carry a different one.
     *
     * When they disagree, `prev.map` finds no account to update. Nothing is
     * written, the delete reports success, and the balance never moves. It
     * fails for ONE account while every other account works perfectly, which
     * is exactly the shape of the problem on SAI ENGINEERING.
     *
     * Comparing on every identifier an account can be known by removes that
     * failure mode entirely.
     */
    const accountKeysOf = (a: any): string[] =>
      [a?.accountId, a?.id, a?.loanNumber, (a as any)?.__docId]
        .filter((k): k is string => typeof k === 'string' && k.trim() !== '')
        .map((k) => k.trim().toLowerCase());

    const recoveryBelongsTo = (r: any, a: any): boolean => {
      const rk = String(r?.accountId || '').trim().toLowerCase();
      if (!rk) return false;
      return accountKeysOf(a).includes(rk);
    };

    /**
     * THE RECEIPTS THAT REMAIN, MATCHED THE SAME TOLERANT WAY AS EVERYTHING
     * ELSE IN THIS ROUTINE.
     *
     * THE BUG THIS FIXES. This line used to be a strict comparison:
     *
     *     if (r.accountId !== existingRec.accountId) return false;
     *
     * while `recoveryBelongsTo` just above matches on EVERY key an account
     * can be known by. The two disagreed whenever one account held receipts
     * filed under more than one of its keys -- some under the account id,
     * some under the loan number, which a re-import can cause.
     *
     * The consequence was money. Deleting one receipt rebuilt the balance
     * from `remainingForAccount`, and every receipt filed under a DIFFERENT
     * key than the deleted one was missing from that list. Those collections
     * silently stopped counting, so the balance jumped UP by their total --
     * the borrower was put back in debt for money they had actually paid.
     *
     * Now the surviving receipts are gathered against the account the
     * deleted one belongs to, by all of its keys. A receipt counts if it
     * belongs to that account and is not the one being removed.
     */
    const ownerAccount = accounts.find((a) => recoveryBelongsTo(existingRec, a));
    const ownerKeys = ownerAccount
      ? accountKeysOf(ownerAccount)
      : [String(existingRec.accountId || '').trim().toLowerCase()].filter(Boolean);

    const remainingForAccount = recoveries.filter((r) => {
      const rk = String(r.accountId || '').trim().toLowerCase();
      if (!rk || !ownerKeys.includes(rk)) return false;
      // The one being deleted is excluded, by every key it could match on.
      if (r.id === existingRec.id) return false;
      if (r.recoveryId && r.recoveryId === existingRec.recoveryId) return false;
      if ((r as any).isReversed) return false;
      return true;
    });

    mutateAccounts((prev) =>
      prev.map((a) => {
        // Every identifier the account can be known by -- see accountKeysOf.
        if (!recoveryBelongsTo(existingRec, a)) return a;

        // An agreed settlement is money the bank gave up, not money paid.
        // WORKED OUT, never read from the account -- see otsWaiverOf. The
        // stored figure was calculated from the SANCTIONED LIMIT rather than
        // what was owed, and never updated when the settlement changed, so
        // every recalculation after it inherited the same wrong number.
        const waiver = otsWaiverOf(a);

        const money = recalcAccountMoney(a, remainingForAccount, waiver);

        /**
         * A CLOSURE JUSTIFIED BY MONEY CANNOT SURVIVE THE MONEY BEING GONE.
         *
         * A closure normally stays a closure -- an earlier version forced
         * every account back to 'Active' on any delete, which quietly
         * reopened accounts that had been properly settled, so that was
         * rightly stopped.
         *
         * But it went too far the other way. "Closed as per OTS" and
         * "Regular Close" are both statements that the customer PAID. Delete
         * the last collection behind one and the account sits closed and
         * settled with nothing recovered against it -- which is what you
         * found: a settled account showing zero paid, still out of the
         * follow-up list, still looking finished.
         *
         * So a payment-backed closure with no payments left is reopened, and
         * the settlement flags cleared with it so nothing still calls it
         * settled. A closure that was NOT payment-backed -- an administrative
         * close, Legal Action, a write-off -- is untouched, because deleting
         * a collection says nothing about those.
         */
        const paymentBackedClosure =
          a.accountStatus === 'Closed as per OTS' ||
          a.accountStatus === 'Regular Close' ||
          a.otsStatus === 'OTS Settled';

        const nothingLeftToJustifyIt = remainingForAccount.length === 0;
        const reopen = paymentBackedClosure && nothingLeftToJustifyIt;

        if (reopen) {
          /**
           * THE CLOSURE IS UNDONE. THE AGREEMENT IS NOT.
           *
           * Deleting a payment says the customer has not paid. It does not
           * say the bank withdrew the settlement it agreed to. So the
           * account comes back onto the books and back into the follow-up
           * list, but the OTS stays agreed -- which means what they owe is
           * the SETTLEMENT figure, not the full original balance.
           *
           * For the account that exposed this: settlement 3,49,221 agreed,
           * nothing paid, so 3,49,221 is owed. That is also exactly what the
           * OTS window has been displaying all along, so the two screens
           * finally say the same thing.
           *
           * `money` above is already correct for this, because the waiver is
           * derived from the agreement and the agreement survives.
           */
          return {
            ...a,
            ...money,
            accountStatus: 'Active' as const,
            // Agreed, no longer settled.
            otsStatus:
              a.otsStatus === 'OTS Settled' ||
              a.otsStatus === 'OTS Settled – Customer Account Closed'
                ? 'OTS Confirmed'
                : a.otsStatus,
            otsPaidAmount: 0,
            closureType: undefined,
            closureDate: undefined,
          };
        }

        return {
          ...a,
          ...money,
          /**
           * WHAT HAS BEEN PAID TOWARDS THE SETTLEMENT IS WORKED OUT, NOT KEPT.
           *
           * THE BUG THIS FIXES -- "already collection done" after deleting
           * every collection on the account.
           *
           * `otsPaidAmount` was a stored running figure: every OTS collection
           * added to it, and NOTHING EVER TOOK ANYTHING OFF. Deleting the
           * collections left it at the full settlement, and the collection
           * window works out what is still payable as
           *
           *     finalOTSAmount - otsPaidAmount
           *
           * which came to zero. So the account refused a new collection with
           * "The agreed OTS amount has already been collected in full",
           * on an account whose ledger was empty.
           *
           * It is now derived from the receipts that remain, exactly like the
           * balance, the overdue and the concession. Delete the collections
           * and it returns to zero on its own.
           */
          otsPaidAmount: Number(money.totalRecovered) || 0,
          accountStatus:
            a.accountStatus === 'Closed as per OTS' ||
            a.accountStatus === 'Regular Close' ||
            a.accountStatus === 'Closed'
              ? a.accountStatus
              : 'Active',
        };
      })
    );

    logActivity(
      currentUser,
      'SETTINGS_UPDATED',
      `Deleted recovery entry ${existingRec.receiptNumber} (₹${existingRec.amount.toLocaleString('en-IN')}) for Account ${existingRec.accountId}.`,
      existingRec.accountId
    );

    /**
     * DID THE BALANCE ACTUALLY MOVE? SAY SO, EITHER WAY.
     *
     * THE PROBLEM THIS SOLVES. The recalculation above runs over the accounts
     * currently LOADED. An administrator's session loads accounts only after
     * a bank, branch or agent has been chosen -- so it is entirely possible
     * to delete a collection from the Recovery Logs or the Commission Engine
     * while the account it belongs to is not in memory at all.
     *
     * When that happens `prev.map` finds nothing to change, nothing is
     * written, and the delete reports plain success. The collection goes, the
     * balance does not move, and nothing anywhere says why. That is exactly
     * the shape of "entry delete but amount not reversed".
     *
     * So the outcome is now stated rather than assumed: the new balance when
     * the account was there to update, and an explicit warning when it was
     * not, naming what to do about it.
     */
    const touched = accountsRef.current.find((a) => recoveryBelongsTo(existingRec, a));

    if (!touched) {
      /**
       * No account matched, so no balance moved. Say which identifier was
       * being looked for -- that string is the evidence: either the account
       * is genuinely not loaded, or the collection is filed under an
       * identifier the account no longer answers to.
       */
      return {
        success: true,
        message:
          `The collection was deleted, but no loaded account matches "${existingRec.accountId}", ` +
          `so no balance was changed.\n\n` +
          `Either that account is not loaded in this session, or the collection is filed ` +
          `under an identifier the account no longer uses.\n\n` +
          `Open Loan Accounts, load the bank or branch it belongs to, then run ` +
          `Settings → Check and repair account balances.`,
      };
    }

    return {
      success: true,
      message:
        `Collection deleted. ${touched.customerName} — balance is now ` +
        `₹${Math.round(Number(touched.outstandingAmount) || 0).toLocaleString('en-IN')}.`,
    };
    })();
    } finally {
      recoveryDeletesInFlight.delete(flightKey);
    }
  };

  const reverseRecovery = async (
    recoveryId: string,
    reason: string
  ): Promise<{ success: boolean; message: string }> => {
    const existingRec = recoveries.find((r) => r.id === recoveryId || r.recoveryId === recoveryId);
    if (!existingRec) {
      return { success: false, message: 'Recovery record not found.' };
    }
    if (existingRec.isReversed) {
      return { success: false, message: 'This recovery entry has already been reversed.' };
    }

    const nowIso = nowLocalStamp();
    const nowIsoDate = todayLocal();
    const nowIsoTime = new Date().toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });

    /**
     * A REVERSAL HAS TO REACH THE DATABASE.
     *
     * This whole routine used to change nothing but the screen. The
     * `isReversed` flag, the removal of the commission and the restored
     * balances all lived in the browser's memory, and not one of them was
     * ever saved. The next sync from the database put the collection back as
     * though nothing had happened, brought the commission with it -- so the
     * agent was still paid -- and left the balances that HAD been saved
     * disagreeing with a ledger that said the money was never returned.
     *
     * Worse, the guard at the top reads `isReversed`, which was never
     * persisted. So after a reload the same receipt could be reversed again,
     * and again, each time adding its amount back to the balance.
     *
     * The record is written first now. If that write fails, nothing else
     * happens and the caller is told -- a reversal that only half happened is
     * how money goes missing.
     */
    const reversedRecord: RecoveryRecord = {
      ...existingRec,
      isReversed: true,
      reversalReason: reason || 'Reversed by user',
      reversedAt: nowIso,
      reversedBy: currentUser.agentId || currentUser.id,
      reversedByName: currentUser.name,
    };

    try {
      await firebaseFirestoreService.saveRecovery(reversedRecord);
    } catch (err: any) {
      console.error('[Firestore] Reversal save FAILED:', err);
      return {
        success: false,
        message:
          err?.code === 'permission-denied'
            ? 'You do not have permission to reverse this collection.'
            : 'The reversal could not be saved, so nothing was changed. Please try again.',
      };
    }

    // The commission goes too, in the database and not only on screen.
    const doomedCommissions = commissions.filter(
      (c) => c.recoveryId === existingRec.recoveryId || c.recoveryId === recoveryId
    );
    for (const c of doomedCommissions) {
      try {
        await firebaseFirestoreService.deleteCommission((c as any).__docId || c.id);
      } catch (err) {
        console.warn('[Firestore] Commission delete notice:', err);
      }
    }

    setRecoveries((prev) =>
      prev.map((r) =>
        r.id === recoveryId || r.recoveryId === recoveryId ? reversedRecord : r
      )
    );

    setCommissions((prev) =>
      prev.filter((c) => c.recoveryId !== existingRec.recoveryId && c.recoveryId !== recoveryId)
    );

    // Balances RECALCULATED from the receipts that still count, exactly as
    // on delete. A reversed receipt simply stops counting.
    const stillCounting = recoveries.filter((r) => {
      if (r.accountId !== existingRec.accountId) return false;
      if (r.id === existingRec.id) return false;
      if (r.recoveryId && r.recoveryId === existingRec.recoveryId) return false;
      if ((r as any).isReversed) return false;
      return true;
    });

    mutateAccounts((prev) =>
      prev.map((a) => {
        if (a.accountId !== existingRec.accountId) return a;

        // Derived, for the same reason as the delete path above.
        const waiver = otsWaiverOf(a);
        const money = recalcAccountMoney(a, stillCounting, waiver);

        const reversalNote: AgentNote = {
          id: `NOTE-REV-${Date.now()}`,
          agentId: currentUser.agentId || currentUser.id,
          agentName: currentUser.name,
          date: nowIsoDate,
          time: nowIsoTime,
          note: `[RECOVERY REVERSED]: Receipt ${existingRec.receiptNumber || existingRec.recoveryId} of ₹${existingRec.amount.toLocaleString('en-IN')} was reversed by ${currentUser.name}. Reason: ${reason || 'User requested reversal'}. Account balances restored.`,
          category: 'Payment Collection',
          kind: 'system',
        };

        return {
          ...a,
          ...money,
          accountStatus:
            a.accountStatus === 'Recovered' || a.accountStatus === 'Closed'
              ? 'Active'
              : a.accountStatus,
          agentNotesHistory: [reversalNote, ...(a.agentNotesHistory || [])],
          latestRemark: `Recovery of ₹${existingRec.amount.toLocaleString('en-IN')} reversed: ${reason || 'User reversed'}`.slice(0, 80),
        };
      })
    );

    logActivity(
      currentUser,
      'RECOVERY_LOGGED',
      `Reversed recovery entry ${existingRec.receiptNumber || existingRec.recoveryId} (₹${existingRec.amount.toLocaleString('en-IN')}) for Account ${existingRec.accountId}. Reason: ${reason || 'User reversed entry'}`,
      existingRec.accountId
    );

    return {
      success: true,
      message: `Recovery entry of ₹${existingRec.amount.toLocaleString('en-IN')} successfully reversed. Account balances restored.`,
    };
  };

  const startFieldVisit = async (accountId: string, coords?: { lat: number; lng: number }): Promise<FieldVisit> => {
    const acc = accounts.find((a) => a.accountId === accountId);

    /**
     * Where the agent actually is, or nothing.
     *
     * This used to start at a fixed point in Chhatrapati Sambhajinagar and,
     * when the GPS failed, fall back to the CUSTOMER's stored location jittered
     * by Math.random() -- so a visit logged with location refused recorded the
     * agent as standing within 100 m of the customer's door, with an accuracy
     * of "4.5 m". A field visit is evidence. It now records the real reading or
     * none.
     *
     * It also waited only 3 seconds and took the first fix, which on a phone is
     * usually the coarse cell one. 10 seconds gives the satellite fix time to
     * arrive.
     */
    let lat: number | undefined = coords?.lat;
    let lng: number | undefined = coords?.lng;
    let accuracy: number | undefined = coords ? undefined : undefined;

    if (!coords && navigator.geolocation) {
      const ask = (highAccuracy: boolean, timeout: number, maxAge: number) =>
        new Promise<GeolocationPosition>((resolve, reject) => {
          navigator.geolocation.getCurrentPosition(resolve, reject, {
            timeout,
            enableHighAccuracy: highAccuracy,
            maximumAge: maxAge,
          });
        });

      try {
        // First, a proper satellite fix.
        const position = await ask(true, 10000, 0);
        lat = position.coords.latitude;
        lng = position.coords.longitude;
        accuracy = position.coords.accuracy;
      } catch (err: any) {
        /**
         * THE COARSE SECOND ATTEMPT -- same reasoning as the camera.
         *
         * Asking for a satellite fix and refusing anything cached returns
         * NOTHING indoors on most phones, so a visit started inside a
         * borrower's shop recorded no location at all. The phone's network
         * position is accepted instead, and its accuracy is stored with it --
         * every screen that shows a visit prints the accuracy beside the
         * coordinates, so a reading good to a kilometre says so.
         *
         * Permission refused is final: a second attempt would be refused too.
         */
        if (err?.code === 1) {
          console.warn('Field visit GPS: permission refused.');
          lat = undefined;
          lng = undefined;
          accuracy = undefined;
        } else {
          try {
            const coarse = await ask(false, 8000, 60000);
            lat = coarse.coords.latitude;
            lng = coarse.coords.longitude;
            accuracy = coarse.coords.accuracy;
          } catch (err2: any) {
            console.warn('Field visit GPS notice:', err2?.message || err2);
            // No coordinates recorded. The visit still logs; it just does not
            // claim a location it does not have.
            lat = undefined;
            lng = undefined;
            accuracy = undefined;
          }
        }
      }
    }

    const visit: FieldVisit = {
      // The Firebase Auth uid of whoever created this.
      //
      // The security rules let an agent delete their OWN records. They can
      // establish that from this field with no document read; without it they
      // fall back to reading the profile to compare the staff code, which
      // costs a read on every delete. Photos already carried this; these did
      // not.
      agentUid: currentUser.id,
      id: `VST-${Date.now()}`,
      visitId: `VST-${Math.floor(1000 + Math.random() * 9000)}`,
      accountId,
      customerName: acc?.customerName || 'Customer',
      agentId: currentUser.agentId || currentUser.id,
      agentName: currentUser.name,
      date: todayLocal(),
      startTime: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
      latitude: lat,
      longitude: lng,
      address: acc?.address || 'Customer Location',
      visitStatus: 'In Progress',
      visitRemarks: 'Field visit in progress at customer premises.',
      customerInteraction: '',
      photoDriveFileIds: [],
      voiceNoteDriveFileIds: [],
      accuracyMeters: accuracy,
    };

    setActiveVisit(visit);
    setVisits((prev) => [visit, ...prev]);

    // ⚡ Primary write to Firestore (<50ms speed route)
    firebaseFirestoreService.saveVisit(visit).catch((err) => {
      console.warn('Firestore primary visit write warning:', err);
    });

    // The customer's stored coordinates are updated ONLY when there is a real
    // reading.
    //
    // This used to run unconditionally with the invented fallback, so a visit
    // started with no GPS fix OVERWROTE the customer's address coordinates
    // with 19.834521, 75.342187 -- permanently, in the account record. A
    // genuine address, once visited without a fix, became a fixed point in
    // Chhatrapati Sambhajinagar for good.
    const hasVisitFix =
      typeof lat === 'number' && typeof lng === 'number' && !(lat === 0 && lng === 0);

    if (hasVisitFix) {
      mutateAccounts((prev) =>
        prev.map((a) => {
          if (a.accountId === accountId || a.id === accountId) {
            return {
              ...a,
              latitude: lat,
              longitude: lng,
              coordinates: `${(lat as number).toFixed(6)}, ${(lng as number).toFixed(6)}`,
            };
          }
          return a;
        })
      );
    }

    logActivity(
      currentUser,
      'VISIT_STARTED',
      hasVisitFix
        ? `Started field visit for account ${accountId} at GPS: ${(lat as number).toFixed(6)}, ${(lng as number).toFixed(6)}`
        : `Started field visit for account ${accountId}. GPS unavailable -- no location recorded.`,
      accountId
    );

    return visit;
  };

  const completeFieldVisit = (
    visitId: string,
    status: FieldVisit['visitStatus'],
    remarks: string,
    customerInteraction: string,
    photoIds?: string[],
    voiceIds?: string[]
  ) => {
    // Save the completed visit to the database. Before this, completion only
    // changed the screen, and the next database update put the stored
    // "In Progress" record back on top of it.
    const current = visits.find((v) => v.id === visitId || v.visitId === visitId);
    if (current) {
      const { __docId, ...stored } = current as any;
      const completedRecord = {
        ...stored,
        endTime: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
        visitStatus: status,
        visitRemarks: remarks,
        customerInteraction,
        photoDriveFileIds: photoIds || stored.photoDriveFileIds,
        voiceNoteDriveFileIds: voiceIds || stored.voiceNoteDriveFileIds,
      } as FieldVisit;
      firebaseFirestoreService.saveVisit(completedRecord).catch((err) => {
        console.warn('Firestore visit complete save warning:', err);
      });
    }

    setVisits((prev) =>
      prev.map((v) => {
        if (v.id === visitId || v.visitId === visitId) {
          const updated = {
            ...v,
            endTime: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
            visitStatus: status,
            visitRemarks: remarks,
            customerInteraction,
            photoDriveFileIds: photoIds || v.photoDriveFileIds,
            voiceNoteDriveFileIds: voiceIds || v.voiceNoteDriveFileIds,
          };
          return updated;
        }
        return v;
      })
    );

    setActiveVisit(null);
    logActivity(currentUser, 'VISIT_COMPLETED', `Completed field visit (${status}): ${remarks.slice(0, 60)}...`);
  };

  /**
   * Delete a geo-tagged photo.
   *
   * Photos are evidence of a field visit, so deletion is restricted:
   *  - an administrator may delete any photo
   *  - an agent may delete only a photo they took themselves
   * Every deletion is written to the activity log with who and why.
   */
  /**
   * Remove one voice note. The agent who recorded it, or an administrator.
   *
   * Modelled on the photo delete, including the reason it exists: deleting by
   * the record's own id rather than its real document key removes nothing,
   * and Firestore calls that success -- so the note appears to go and returns
   * on the next sync.
   */
  /**
   * Deletes one follow-up call log. Only the agent who logged it, or an admin.
   * Removes the log only. The account's status and next follow-up date are
   * left as they are.
   */
  const deleteFollowUpLog = async (
    followUpId: string
  ): Promise<{ success: boolean; message: string }> => {
    const target = followups.find(
      (f) => f.id === followUpId || (f as any).__docId === followUpId
    );
    if (!target) return { success: false, message: 'Follow-up not found.' };

    const isAdmin = currentUser.role === 'admin' || (currentUser.role as string) === 'super_admin';
    const isOwner =
      target.agentId === currentUser.agentId ||
      target.agentId === currentUser.id ||
      (target as any).agentUid === currentUser.id ||
      target.agentName === currentUser.name;

    if (!isAdmin && !isOwner) {
      return { success: false, message: 'You can only delete follow-up logs that you recorded yourself.' };
    }

    const docId = (target as any).__docId || target.id;

    try {
      await firebaseFirestoreService.deleteFollowUp(docId);
    } catch (err: any) {
      console.error('[Firestore] Follow-up delete FAILED:', err);
      return {
        success: false,
        message:
          err?.code === 'permission-denied'
            ? 'You do not have permission to delete this follow-up log.'
            : err?.message || 'Could not delete the follow-up log. Please try again.',
      };
    }

    setFollowups((prev) => prev.filter((f) => (((f as any).__docId || f.id) !== docId)));
    return { success: true, message: 'Follow-up log deleted.' };
  };

  /**
   * Sets the bank's account category (SMA 0, SMA 1, SMA 2, NPA) on many
   * accounts at once. Touches accountCategory only: remarks, visits, call logs,
   * collections and agent assignments are left exactly as they are.
   * Returns how many accounts actually changed.
   */
  const setAccountCategoryBulk = (accountIds: string[], category: string): number => {
    const wanted = new Set(accountIds);
    const value = category.trim();
    const changedCount = accountsRef.current.filter(
      (a) => wanted.has(a.accountId || a.id) && (a.accountCategory || '') !== value
    ).length;
    if (changedCount === 0) return 0;
    mutateAccounts((prev) =>
      prev.map((a) => {
        if (!wanted.has(a.accountId || a.id)) return a;
        if ((a.accountCategory || '') === value) return a;
        return { ...a, accountCategory: value || undefined };
      })
    );
    logActivity(
      currentUser,
      'SETTINGS_UPDATED',
      `Set account category to ${value || '(blank)'} on ${changedCount} accounts`,
      ''
    );
    return changedCount;
  };

  /**
   * Deletes a field visit. Only the agent who recorded it, or an admin.
   * Visits do not change the account, so nothing else needs to be rebuilt.
   */
  const deleteFieldVisit = async (
    visitId: string
  ): Promise<{ success: boolean; message: string }> => {
    const target = visits.find(
      (v) => v.id === visitId || (v as any).__docId === visitId
    );
    if (!target) return { success: false, message: 'Visit not found.' };

    const isAdmin = currentUser.role === 'admin' || (currentUser.role as string) === 'super_admin';
    const isOwner =
      target.agentId === currentUser.agentId ||
      target.agentId === currentUser.id ||
      (target as any).agentName === currentUser.name;

    if (!isAdmin && !isOwner) {
      return { success: false, message: 'You can only delete visits that you recorded yourself.' };
    }

    // Geo-tagged photos taken during this visit go with it. They are deleted
    // first, through the same photo delete rules. If any photo cannot be
    // deleted, the visit is kept, so no photo is left without its visit.
    const linkedPhotos = photos.filter(
      (p) => p.visitId && (p.visitId === target.id || p.visitId === target.visitId)
    );
    const failedPhotos: string[] = [];
    for (const ph of linkedPhotos) {
      const res = await deleteGeoTaggedPhoto(ph.id || ph.photoId || '');
      if (!res.success) failedPhotos.push(res.message);
    }
    if (failedPhotos.length > 0) {
      return {
        success: false,
        message: `Visit not deleted. ${failedPhotos.length} linked photo(s) could not be deleted: ${failedPhotos[0]}`,
      };
    }

    const docId = (target as any).__docId || target.id;

    try {
      await firebaseFirestoreService.deleteVisit(docId);
    } catch (err: any) {
      console.error('[Firestore] Visit delete FAILED:', err);
      return {
        success: false,
        message:
          err?.code === 'permission-denied'
            ? 'You do not have permission to delete this visit.'
            : err?.message || 'Could not delete the visit. Please try again.',
      };
    }

    setVisits((prev) => prev.filter((v) => (((v as any).__docId || v.id) !== docId)));
    return { success: true, message: 'Visit deleted.' };
  };

  const deleteVoiceNote = async (
    voiceNoteId: string
  ): Promise<{ success: boolean; message: string }> => {
    const target = voiceNotes.find(
      (v) => v.id === voiceNoteId || (v as any).voiceNoteId === voiceNoteId
    );
    if (!target) return { success: false, message: 'Voice note not found.' };

    const isAdmin = currentUser.role === 'admin';
    const isOwner =
      target.agentId === currentUser.agentId ||
      target.agentId === currentUser.id ||
      (target as any).agentName === currentUser.name;

    if (!isAdmin && !isOwner) {
      return {
        success: false,
        message: 'You can only delete voice notes that you recorded yourself.',
      };
    }

    const docId = (target as any).__docId || target.id || (target as any).voiceNoteId;

    try {
      await firebaseFirestoreService.deleteVoiceNote(docId);
    } catch (err: any) {
      console.error('[Firestore] Voice note delete FAILED:', err);
      return {
        success: false,
        message:
          err?.code === 'permission-denied'
            ? 'You do not have permission to delete this voice note.'
            : err?.message || 'Could not delete the voice note. Please try again.',
      };
    }

    // The audio file goes too, otherwise it keeps costing storage for ever.
    if ((target as any).storagePath) {
      try {
        await firebaseStorageService.deleteFile((target as any).storagePath);
      } catch (err) {
        console.warn('[Storage] Voice note file delete notice:', err);
      }
    }

    setVoiceNotes((prev) =>
      prev.filter(
        (v) => ((v as any).__docId || v.id || (v as any).voiceNoteId) !== docId
      )
    );

    logActivity(
      currentUser,
      'VOICE_RECORDED',
      `Deleted voice note ${(target as any).title || target.id}.`,
      target.accountId
    );

    return { success: true, message: 'Voice note deleted.' };
  };

  const deleteGeoTaggedPhoto = async (
    photoId: string,
    reason?: string
  ): Promise<{ success: boolean; message: string }> => {
    const target = photos.find(
      (p) => p.id === photoId || p.photoId === photoId
    );

    if (!target) {
      return { success: false, message: 'Photo not found.' };
    }

    const isAdmin = currentUser.role === 'admin';
    const isOwner =
      target.agentId === currentUser.agentId ||
      target.agentId === currentUser.id ||
      target.agentName === currentUser.name;

    if (!isAdmin && !isOwner) {
      return {
        success: false,
        message: 'You can only delete photos that you captured yourself.',
      };
    }

    // Prefer the real Firestore document key. Falling back to the record's
    // own id can delete a document that does not exist, which Firestore
    // treats as success -- so the photo appeared to go and then came back.
    const docId = (target as any).__docId || target.id || target.photoId;

    try {
      await firebaseFirestoreService.deletePhoto(docId);
    } catch (err: any) {
      console.error('[Firestore] Photo delete FAILED:', err);
      return {
        success: false,
        message:
          err?.code === 'permission-denied'
            ? 'You do not have permission to delete this photo.'
            : err?.message || 'Could not delete the photo. Please try again.',
      };
    }

    // Remove the stored image file too, when there is one.
    if (target.storagePath) {
      try {
        await firebaseStorageService.deleteFile(target.storagePath);
      } catch (err) {
        console.warn('[Storage] Photo file delete notice:', err);
      }
    }

    const targetKey = (target as any).__docId || target.id || target.photoId;
    setPhotos((prev) =>
      prev.filter(
        (p) => ((p as any).__docId || p.id || p.photoId) !== targetKey
      )
    );

    logActivity(
      currentUser,
      'SETTINGS_UPDATED',
      `Deleted geo-tagged photo ${target.photoId} for account ${target.accountId} (captured by ${target.agentName} on ${target.date}). Reason: ${reason || 'Not specified'}`,
      target.accountId
    );

    return { success: true, message: 'Photo deleted.' };
  };

  /**
   * Fills in or corrects the note on a photo AFTER it was captured.
   *
   * Allowed ONCE. The note printed on the image itself is whatever the agent
   * typed at capture time and is never rewritten, so an unlimited edit would
   * let the record drift away from the picture. One correction covers the
   * agent who simply forgot; anything beyond that is an admin's call.
   */
  const updatePhotoNote = async (
    photoId: string,
    note: string
  ): Promise<{ success: boolean; message: string }> => {
    const target = photos.find(
      (p) => p.id === photoId || p.photoId === photoId
    );

    if (!target) {
      return { success: false, message: 'Photo not found.' };
    }

    const isAdmin = currentUser.role === 'admin';
    const isOwner =
      target.agentId === currentUser.agentId ||
      target.agentId === currentUser.id ||
      target.agentName === currentUser.name;

    if (!isAdmin && !isOwner) {
      return {
        success: false,
        message: 'You can only edit the note on photos you captured yourself.',
      };
    }

    if (target.noteEdited && !isAdmin) {
      return {
        success: false,
        message:
          'The note on this photo has already been edited once. Ask an administrator if it still needs correcting.',
      };
    }

    const clean = (note || '').trim();
    if (!clean) {
      return { success: false, message: 'Please enter a note.' };
    }

    const docId = (target as any).__docId || target.id || target.photoId;
    const stamp = {
      agentRemark: clean,
      noteEdited: true,
      noteEditedAt: new Date().toISOString(),
      noteEditedBy: currentUser.name,
    };

    try {
      await firebaseFirestoreService.updatePhotoNote(docId, stamp);
    } catch (err: any) {
      console.error('[Firestore] Photo note update FAILED:', err);
      return {
        success: false,
        message:
          err?.code === 'permission-denied'
            ? 'You do not have permission to edit this photo note.'
            : err?.message || 'Could not save the note. Please try again.',
      };
    }

    const targetKey = (target as any).__docId || target.id || target.photoId;
    setPhotos((prev) =>
      prev.map((p) =>
        ((p as any).__docId || p.id || p.photoId) === targetKey
          ? { ...p, ...stamp }
          : p
      )
    );

    logActivity(
      currentUser,
      'SETTINGS_UPDATED',
      `Edited the note on geo-tagged photo ${target.photoId} for account ${target.accountId} (captured by ${target.agentName} on ${target.date}).`,
      target.accountId
    );

    return { success: true, message: 'Note saved.' };
  };

  const captureWatermarkedPhoto = async (
    options: Omit<WatermarkOptions, 'agentId' | 'agentName'>
  ): Promise<PhotoRecord> => {
    const watermarkResult = await createSRMSWatermarkedPhoto({
      ...options,
      agentId: currentUser.agentId || currentUser.id,
      agentName: currentUser.name,
    });

    const photoId = `PHO-${Math.floor(1000 + Math.random() * 9000)}`;
    let downloadUrl = '';
    let storagePath = '';

    // The picture lives in Cloud Storage and ONLY there. It used to fall back
    // to keeping the base64 image on the record, which quietly wrote ~270 KB
    // of text into the document, marked it 'Uploaded' and moved on -- so a
    // failed upload looked identical to a good one.
    //
    // One retry covers a brief drop in signal. If it still fails the capture
    // is reported as failed and nothing is saved, because a photo record with
    // no retrievable picture is worse than no record: it reads as evidence of
    // a visit and cannot be opened.
    let uploadErr: any = null;
    for (let attempt = 0; attempt < 2; attempt++) {
      try {
        const uploadResult = await firebaseStorageService.uploadPhotoDataUrl(
          options.accountId,
          photoId,
          watermarkResult.dataUrl
        );
        downloadUrl = uploadResult.downloadUrl;
        storagePath = uploadResult.storagePath;
        uploadErr = null;
        break;
      } catch (storageErr) {
        uploadErr = storageErr;
        console.warn(
          `[Firebase Storage] Photo upload attempt ${attempt + 1} failed:`,
          storageErr
        );
        if (attempt === 0) {
          await new Promise((r) => setTimeout(r, 1200));
        }
      }
    }

    if (!downloadUrl) {
      const reason = uploadErr?.message ? ` (${uploadErr.message})` : '';
      throw new Error(
        `The photo could not be uploaded${reason}. Check your internet connection and take it again. Nothing has been saved.`
      );
    }

    const newPhoto: PhotoRecord = {
      id: `PHO-${Date.now()}`,
      photoId,
      // Recorded so the security rules can tell whose photo this is.
      agentUid: currentUser.id,
      accountId: options.accountId,
      customerId: `CUST-${options.accountId.replace('ACC-', '')}`,
      agentId: currentUser.agentId || currentUser.id,
      agentName: currentUser.name,
      date: watermarkResult.date,
      time: watermarkResult.time,
      latitude: watermarkResult.latitude,
      longitude: watermarkResult.longitude,
      visitId: options.visitId || activeVisit?.visitId,
      agentRemark: options.remark,
      driveFileId: photoId,
      driveFolder: `/ScaleSupport_Storage/Account_${options.accountId}/Photos/`,
      dataUrl: watermarkResult.dataUrl,
      downloadUrl,
      storagePath,
      watermarkText: watermarkResult.watermarkText,
      uploadStatus: 'Uploaded',
    };

    setPhotos((prev) => [newPhoto, ...prev]);

    // Save metadata to Firestore
    firebaseFirestoreService.savePhotoMetadata(newPhoto).catch((err) => {
      console.warn('Firestore photo metadata write notice:', err);
    });

    /**
     * TWO PHOTOS PER ACCOUNT. THE THIRD REPLACES THE OLDEST.
     *
     * WHY A CAP AT ALL. Photos are the one thing in this application that
     * grows without limit -- nothing ever deleted one, so an account visited
     * twenty times carried twenty images, and the bill for storing them went
     * up every month and never came down. Two is enough to evidence a visit;
     * a third is almost always a better version of the same thing.
     *
     * WHAT IT DOES. After a photo is saved, this looks at how many that
     * account now has and removes the oldest until two remain. Both halves go
     * -- the file in Cloud Storage AND the record -- because deleting one
     * without the other either leaves an orphaned file nobody can reach and
     * everybody pays for, or a record pointing at a file that is gone.
     *
     * WHY IT DOES NOT GO THROUGH deleteGeoTaggedPhoto. That function asks
     * whether the PERSON is allowed to delete the photo, and refuses if they
     * did not take it. That is right for somebody pressing a delete button
     * and wrong here: this is the system enforcing its own limit, and an
     * agent replacing a photo on an account they hold must not be blocked
     * because the older one was taken by a colleague.
     *
     * Oldest is decided by date and time, falling back to the id, which
     * carries a timestamp. Failures are logged and swallowed: an account
     * briefly holding three photos is a far better outcome than an agent's
     * new photo appearing to fail because the tidying did.
     */
    void (async () => {
      try {
        const MAX_PHOTOS_PER_ACCOUNT = 2;
        const accountKey = options.accountId;

        const forThisAccount = [newPhoto, ...photosRef.current].filter(
          (p, idx, arr) =>
            p.accountId === accountKey &&
            // The new photo is prepended above and may also be in the ref by
            // now, depending on when the listener last ran.
            arr.findIndex(
              (q) => ((q as any).__docId || q.id || q.photoId) === ((p as any).__docId || p.id || p.photoId)
            ) === idx
        );

        if (forThisAccount.length <= MAX_PHOTOS_PER_ACCOUNT) return;

        const newestFirst = forThisAccount.slice().sort((a, b) => {
          const stamp = (x: any) => `${x.date || ''} ${x.time || ''} ${x.photoId || x.id || ''}`;
          return stamp(b).localeCompare(stamp(a));
        });

        const surplus = newestFirst.slice(MAX_PHOTOS_PER_ACCOUNT);

        for (const old of surplus) {
          const docId = (old as any).__docId || old.id || old.photoId;
          try {
            await firebaseFirestoreService.deletePhoto(docId);
          } catch (err) {
            console.warn('[Photo cap] Could not remove the old record:', err);
            // The file is deliberately left alone if the record survived --
            // a record pointing at a missing file is worse than one extra
            // file.
            continue;
          }

          if (old.storagePath) {
            try {
              await firebaseStorageService.deleteFile(old.storagePath);
            } catch (err) {
              console.warn('[Photo cap] Could not remove the old file:', err);
            }
          }

          setPhotos((prev) =>
            prev.filter((p) => ((p as any).__docId || p.id || p.photoId) !== docId)
          );
        }

        if (surplus.length > 0) {
          logActivity(
            currentUser,
            'SETTINGS_UPDATED',
            `Photo limit reached for account ${accountKey}: replaced ${surplus.length} oldest photo(s) to keep the newest ${MAX_PHOTOS_PER_ACCOUNT}.`,
            accountKey
          );
        }
      } catch (err) {
        console.warn('[Photo cap] notice:', err);
      }
    })();

    // Same as the field-visit path: only a real reading touches the customer's
    // stored coordinates. A photo taken with no fix used to overwrite them with
    // an invented point.
    const wLat = watermarkResult.latitude;
    const wLng = watermarkResult.longitude;
    const hasPhotoFix =
      typeof wLat === 'number' && typeof wLng === 'number' && !(wLat === 0 && wLng === 0);

    if (hasPhotoFix) {
      mutateAccounts((prev) =>
        prev.map((a) => {
          if (a.accountId === options.accountId || a.id === options.accountId) {
            return {
              ...a,
              latitude: wLat,
              longitude: wLng,
              coordinates: `${(wLat as number).toFixed(6)}, ${(wLng as number).toFixed(6)}`,
            };
          }
          return a;
        })
      );
    }

    // Link to active visit if active
    if (activeVisit) {
      setActiveVisit((prev) => (prev ? { ...prev, photoDriveFileIds: [...prev.photoDriveFileIds, newPhoto.photoId] } : null));
    }

    logActivity(
      currentUser,
      'PHOTO_CAPTURED',
      hasPhotoFix
        ? `Geo-tagged photo captured with watermark (Lat: ${(wLat as number).toFixed(6)}, Lng: ${(wLng as number).toFixed(6)}). Saved to Firebase Storage.`
        : 'Photo captured and watermarked, stamped GPS UNAVAILABLE -- no location recorded. Saved to Firebase Storage.',
      options.accountId
    );

    return newPhoto;
  };

  const recordVoiceNote = async (data: {
    accountId: string;
    durationSeconds: number;
    title: string;
    transcription?: string;
    audioBlobUrl?: string;
    visitId?: string;
  }): Promise<VoiceNoteRecord> => {
    const voiceNoteId = `VN-${Math.floor(1000 + Math.random() * 9000)}`;
    let downloadUrl: string | undefined = undefined;
    let storagePath: string | undefined = undefined;

    if (data.audioBlobUrl) {
      try {
        const audioBlob = await fetch(data.audioBlobUrl).then((r) => r.blob());
        const uploadResult = await firebaseStorageService.uploadAudioBlob(
          data.accountId,
          voiceNoteId,
          audioBlob
        );
        downloadUrl = uploadResult.downloadUrl;
        storagePath = uploadResult.storagePath;
      } catch (storageErr) {
        console.warn('[Firebase Storage] Audio upload notice:', storageErr);
      }
    }

    const newVoice: VoiceNoteRecord = {
      id: `VN-${Date.now()}`,
      voiceNoteId,
      accountId: data.accountId,
      agentId: currentUser.agentId || currentUser.id,
      agentName: currentUser.name,
      date: todayLocal(),
      time: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
      durationSeconds: data.durationSeconds,
      visitId: data.visitId || activeVisit?.visitId,
      driveFileId: voiceNoteId,
      driveFolder: `/ScaleSupport_Storage/Account_${data.accountId}/Voice_Notes/`,
      audioBlobUrl: data.audioBlobUrl,
      downloadUrl,
      storagePath,
      transcription: data.transcription,
      title: data.title,
    };

    setVoiceNotes((prev) => [newVoice, ...prev]);

    // Save metadata to Firestore
    firebaseFirestoreService.saveVoiceNoteMetadata(newVoice).catch((err) => {
      console.warn('Firestore voice note write notice:', err);
    });

    if (activeVisit) {
      setActiveVisit((prev) => (prev ? { ...prev, voiceNoteDriveFileIds: [...prev.voiceNoteDriveFileIds, voiceNoteId] } : null));
    }

    logActivity(
      currentUser,
      'VOICE_RECORDED',
      `Recorded ${data.durationSeconds}s audio voice note "${data.title}". Saved to Firebase Storage.`,
      data.accountId
    );

    return newVoice;
  };

  const uploadDocument = async (data: {
    accountId: string;
    documentType: DocumentRecord['documentType'];
    fileName: string;
    fileSizeBytes: number;
    file?: File | Blob;
  }): Promise<DocumentRecord> => {
    const acc = accounts.find((a) => a.accountId === data.accountId);
    const documentId = `DOC-${Math.floor(1000 + Math.random() * 9000)}`;
    let fileUrl: string | undefined = undefined;
    let downloadUrl: string | undefined = undefined;
    let storagePath: string | undefined = undefined;

    if (data.file) {
      try {
        const uploadResult = await firebaseStorageService.uploadDocumentFile(
          data.accountId,
          documentId,
          data.file,
          data.fileName
        );
        downloadUrl = uploadResult.downloadUrl;
        storagePath = uploadResult.storagePath;
        fileUrl = uploadResult.downloadUrl;
      } catch (storageErr) {
        console.warn('[Firebase Storage] Document upload notice:', storageErr);
        if (typeof window !== 'undefined' && window.URL) {
          try {
            fileUrl = URL.createObjectURL(data.file);
          } catch {}
        }
      }
    }

    const newDoc: DocumentRecord = {
      id: `DOC-${Date.now()}`,
      documentId,
      accountId: data.accountId,
      customerName: acc?.customerName || 'Customer',
      documentType: data.documentType,
      uploadedBy: currentUser.agentId || currentUser.id,
      uploadedByName: currentUser.name,
      dateTime: nowLocalStamp(),
      driveFileId: documentId,
      fileName: data.fileName,
      fileSizeBytes: data.fileSizeBytes,
      fileUrl,
      downloadUrl,
      storagePath,
    };

    setDocuments((prev) => [newDoc, ...prev]);

    // Save metadata to Firestore
    firebaseFirestoreService.saveDocumentMetadata(newDoc).catch((err) => {
      console.warn('Firestore document write notice:', err);
    });

    logActivity(
      currentUser,
      'DOCUMENT_UPLOADED',
      `Uploaded ${data.documentType}: "${data.fileName}". Saved to Firebase Storage.`,
      data.accountId
    );

    return newDoc;
  };

  const attachCloudDocument = (
    accountId: string,
    file: any,
    documentType: DocumentRecord['documentType'] = 'PAN / Aadhaar KYC'
  ): DocumentRecord => {
    const acc = accounts.find((a) => a.accountId === accountId);
    const newDoc: DocumentRecord = {
      id: `DOC-PICKED-${Date.now()}`,
      documentId: `DOC-${Math.floor(1000 + Math.random() * 9000)}`,
      accountId: accountId,
      customerName: acc?.customerName || 'Customer',
      documentType,
      uploadedBy: currentUser.agentId || currentUser.id,
      uploadedByName: currentUser.name,
      dateTime: nowLocalStamp(),
      driveFileId: file.id || `FILE-${Date.now()}`,
      fileName: file.name,
      fileSizeBytes: file.sizeBytes || 102400,
      fileUrl: file.url,
    };

    setDocuments((prev) => [newDoc, ...prev]);

    firebaseFirestoreService.saveDocumentMetadata(newDoc).catch((err) => {
      console.warn('Firestore primary document write warning:', err);
    });

    logActivity(
      currentUser,
      'DOCUMENT_UPLOADED',
      `Attached document: "${file.name}" (${documentType}).`,
      accountId
    );

    return newDoc;
  };

  /**
   * Commission settings were held in memory ONLY. Nothing wrote them to the
   * database and nothing read them back, so every rate set here -- the
   * default and the SARFAESI rate -- was gone the moment anyone signed out.
   */
  /**
   * THE OTS SCHEME LIVED ONLY IN ONE BROWSER.
   *
   * `getOTSSchemeConfig` reads localStorage and falls back to a built-in
   * default whose valid-till date is the hard-coded string "30 September
   * 2026". `saveOTSSchemeConfig` writes localStorage and nothing else.
   *
   * So an administrator setting the OTS last date set it on THEIR machine.
   * Every agent, on every other device, kept getting the built-in default --
   * and the WhatsApp offer they sent to a customer carried a date the
   * administrator had never chosen. Clearing browser data lost it entirely.
   *
   * The same fault the bank list, the commission rates and the SARFAESI rate
   * each had: held in memory or localStorage, never written to the database.
   */
  /**
   * The notice that scrolls across the top of everyone's screen.
   *
   * One settings document, read once at sign-in with the OTS scheme, so it
   * costs nothing per person. Empty text means no notice, and the bar simply
   * is not drawn -- an empty ribbon scrolling nothing would be worse than
   * plain space.
   */
  const [noticeBanner, setNoticeBanner] = useState<{
    text: string;
    colour: 'red' | 'amber' | 'green';
    /** Empty means everybody; otherwise only people in this bank. */
    bank?: string;
    /** Empty means the whole bank; otherwise only this zone within it. */
    zone?: string;
    /**
     * Named agents, when a notice is for particular people rather than a
     * whole bank or zone. Empty or absent means nobody is singled out and the
     * bank/zone rules alone decide. More than one can be named: a notice
     * often concerns two or three agents working the same files.
     */
    agents?: string[];
    updatedAt?: string;
    updatedBy?: string;
  }>({ text: '', colour: 'green' });

  /**
   * The notice this particular person should see.
   *
   * A notice aimed at one bank or one zone is not shown to anybody outside
   * it. An administrator sees every notice regardless, because they are the
   * one who posted it and needs to know it is live.
   */
  const visibleNotice = useMemo(() => {
    const n = noticeBanner;
    if (!n?.text) return null;
    if (currentUser?.role === 'admin' || currentUser?.role === 'management') return n;

    const same = (a?: string, b?: string) =>
      (a || '').trim().replace(/\s+/g, ' ').toLowerCase() ===
      (b || '').trim().replace(/\s+/g, ' ').toLowerCase();

    if (n.bank) {
      const mine = (currentUser?.banks && currentUser.banks.length
        ? currentUser.banks
        : [currentUser?.bank]
      ).filter(Boolean) as string[];
      // Somebody with no bank recorded is unclassified, not excluded -- the
      // same rule the account screens use.
      if (mine.length > 0 && !mine.some((b) => same(b, n.bank))) return null;
    }

    if (n.zone && currentUser?.zone && !same(currentUser.zone, n.zone)) return null;

    /**
     * NAMED AGENTS WIN OVER THE BANK AND ZONE RULES ABOVE.
     *
     * When a notice names particular agents, only those agents see it --
     * nobody else, whatever bank or zone they are in. A notice addressed to
     * two agents about their own files has no business scrolling across the
     * screen of a third who has nothing to do with it.
     *
     * Matched on the agent id and the user id, because an agent is referred
     * to by either one depending on how the record was created.
     */
    if (Array.isArray(n.agents) && n.agents.length > 0) {
      const mine = [currentUser?.agentId, currentUser?.id]
        .filter((k): k is string => typeof k === 'string' && k.trim() !== '')
        .map((k) => k.trim().toLowerCase());
      const targeted = n.agents
        .filter((k) => typeof k === 'string' && k.trim() !== '')
        .map((k) => k.trim().toLowerCase());
      if (!mine.some((k) => targeted.includes(k))) return null;
    }

    return n;
  }, [noticeBanner, currentUser]);

  const saveNoticeBanner = async (
    text: string,
    colour: 'red' | 'amber' | 'green',
    scope?: { bank?: string; zone?: string; agents?: string[] }
  ): Promise<{ success: boolean; message: string }> => {
    if (currentUserRef.current?.role !== 'admin') {
      return { success: false, message: 'Only an administrator can set the notice.' };
    }
    const next = {
      text: (text || '').trim(),
      colour,
      bank: (scope?.bank || '').trim(),
      zone: (scope?.zone || '').trim(),
      agents: (scope?.agents || [])
        .filter((a) => typeof a === 'string' && a.trim() !== '')
        .map((a) => a.trim()),
      updatedAt: new Date().toISOString(),
      updatedBy: currentUserRef.current?.name || 'Admin',
    };
    setNoticeBanner(next);
    try {
      await firebaseFirestoreService.saveSettings('notice', next);
    } catch (err: any) {
      return {
        success: false,
        message: `The notice could not be saved: ${err?.message || 'unknown error'}`,
      };
    }
    logActivity(
      currentUser,
      'SETTINGS_UPDATED',
      next.text ? `Set the scrolling notice: "${next.text}"` : 'Cleared the scrolling notice.'
    );
    if (!next.text) return { success: true, message: 'Notice cleared.' };

    // Said plainly who will actually see it, so a notice aimed at two agents
    // is not mistaken for one that went to the whole book.
    if (next.agents.length > 0) {
      return {
        success: true,
        message: `Notice published to ${next.agents.length} agent${
          next.agents.length === 1 ? '' : 's'
        } only.`,
      };
    }
    if (next.bank) {
      return {
        success: true,
        message: `Notice published to ${next.bank}${next.zone ? ` / ${next.zone}` : ''}.`,
      };
    }
    return { success: true, message: 'Notice published to everyone.' };
  };

  /**
   * A FAILED SAVE MUST NOT BE SILENT.
   *
   * This used to swallow the error into the console. So a save that never
   * reached the database looked identical to one that did: the screen showed
   * the new slab, and the next refresh quietly brought the old one back, with
   * nothing anywhere saying why. The caller is told now, and says so.
   */
  const persistOTSSettings = async (
    config: any
  ): Promise<{ success: boolean; message: string }> => {
    if (currentUser.role !== 'admin') {
      return { success: false, message: 'Only an administrator can change the OTS scheme.' };
    }
    // Applied here and now, as well as saved, so the screen the administrator
    // is looking at reflects the change without a reload.
    setLiveOTSConfig(config);
    try {
      await firebaseFirestoreService.saveSettings('ots', config);
    } catch (err: any) {
      console.error('[Firestore] OTS settings save FAILED:', err);
      return {
        success: false,
        message: `Saved on this screen but NOT to the database: ${
          err?.message || 'unknown error'
        }. It will revert when you reload, and other users will not see it.`,
      };
    }
    logActivity(currentUser, 'SETTINGS_UPDATED', 'Updated the OTS scheme settings.');
    return { success: true, message: 'Saved for everyone.' };
  };

  const persistCommissionSettings = (next: CommissionSettings) => {
    // Kept locally too, so the rates are right again the instant the app
    // loads rather than only after the database answers -- and so a rate
    // survives even if that one write fails.
    try {
      localStorage.setItem('srms_commission_settings', JSON.stringify(next));
    } catch {
      /* private browsing, or storage full */
    }
    void firebaseFirestoreService
      .saveSettings('commission', next)
      .catch((err) =>
        console.error('[Firestore] Commission settings save FAILED:', err)
      );
  };

  const updateCommissionSettings = (newRate: number) => {
    // Computed OUTSIDE the state updater. A side effect inside one can run
    // twice, or not at all, depending on how React schedules the render.
    const next = { ...commissionSettingsRef.current, defaultRate: newRate };
    commissionSettingsRef.current = next;
    setCommissionSettings(next);
    persistCommissionSettings(next);
    logActivity(currentUser, 'SETTINGS_UPDATED', `Updated default Recovery Agent commission rate to ${newRate}%`);
  };

  /**
   * The SARFAESI commission rate. Administrators only -- an agent may say
   * WHICH accounts are SARFAESI but never what SARFAESI work is worth.
   */
  const updateSarfaesiRate = (newRate: number): { success: boolean; message: string } => {
    if (currentUser.role !== 'admin') {
      return {
        success: false,
        message: 'Only an administrator can set the SARFAESI commission rate.',
      };
    }
    if (!isFinite(newRate) || newRate <= 0 || newRate > 100) {
      return { success: false, message: 'Enter a rate between 0 and 100.' };
    }
    const next = { ...commissionSettingsRef.current, sarfaesiRate: newRate };
    commissionSettingsRef.current = next;
    setCommissionSettings(next);
    persistCommissionSettings(next);
    logActivity(
      currentUser,
      'SETTINGS_UPDATED',
      `Set SARFAESI commission rate to ${newRate}%`
    );
    return { success: true, message: `SARFAESI commission rate set to ${newRate}%.` };
  };

  /**
   * Mark or unmark accounts as SARFAESI.
   *
   * Done by the agent from the commission bill, where they can see exactly
   * which of their collections were SARFAESI recoveries. The flag is stored
   * on the account so it also applies to future collections, not just to the
   * bill in front of them.
   */
  const markAccountsSarfaesi = (
    accountIds: string[],
    isSarfaesi: boolean
  ): { success: boolean; message: string } => {
    const ids = new Set(accountIds || []);
    if (ids.size === 0) {
      return { success: false, message: 'No accounts selected.' };
    }

    const stamp = new Date().toISOString().slice(0, 19).replace('T', ' ');
    let changed = 0;

    mutateAccounts((prev) =>
      prev.map((a) => {
        if (!ids.has(a.accountId) && !ids.has(a.id)) return a;
        if (Boolean(a.isSarfaesi) === isSarfaesi) return a;
        changed += 1;
        return {
          ...a,
          isSarfaesi,
          sarfaesiMarkedBy: isSarfaesi ? currentUser.name : undefined,
          sarfaesiMarkedAt: isSarfaesi ? stamp : undefined,
        };
      })
    );

    logActivity(
      currentUser,
      'SETTINGS_UPDATED',
      `${isSarfaesi ? 'Marked' : 'Unmarked'} ${changed} account(s) as SARFAESI`
    );

    return {
      success: true,
      message: `${changed} account(s) ${isSarfaesi ? 'marked as' : 'removed from'} SARFAESI.`,
    };
  };

  const updateCommissionStatus = (commissionId: string, status: CommissionRecord['status']) => {
    setCommissions((prev) =>
      prev.map((c) =>
        c.id === commissionId
          ? { ...c, status, paidDate: status === 'Paid' ? todayLocal() : c.paidDate }
          : c
      )
    );
  };

  /* ================================================================== *
   * STAFF MESSAGES
   * ================================================================== */

  /** The agents the signed-in person is allowed to write to. */
  const messageRecipients = useMemo(
    () => messageRecipientsFor(currentUser, users),
    [currentUser, users]
  );

  /** This person's own inbox, newest first. */
  const myMessages = useMemo(() => {
    const keys = [currentUser?.agentId || '', currentUser?.id || '']
      .filter(Boolean)
      .map((k) => k.trim().toLowerCase());
    return (messages || [])
      .filter((m) => keys.includes((m.recipientId || '').trim().toLowerCase()))
      .sort((a, b) => (b.sentAt || '').localeCompare(a.sentAt || ''));
  }, [messages, currentUser]);

  const unreadMessageCount = useMemo(
    () => myMessages.filter((m) => !m.read).length,
    [myMessages]
  );

  /**
   * Send one message to one or more agents.
   *
   * Every recipient is checked against what this sender is actually allowed
   * to reach -- a branch manager cannot message another branch's agents by
   * sending an id the screen never offered them. Recipients outside their
   * reach are refused rather than quietly dropped, because a send that
   * silently reached fewer people than asked is worse than one that failed.
   */
  const sendStaffMessage = async (
    recipientIds: string[],
    subject: string,
    body: string
  ): Promise<{ success: boolean; message: string; sentTo: number }> => {
    const actor = currentUserRef.current;
    if (!canSendMessages(actor)) {
      return { success: false, sentTo: 0, message: 'Your role cannot send messages.', };
    }

    const text = (body || '').trim();
    if (!text) return { success: false, sentTo: 0, message: 'The message is empty.' };
    if (!recipientIds || recipientIds.length === 0) {
      return { success: false, sentTo: 0, message: 'Choose at least one agent.' };
    }

    const allowed = messageRecipientsFor(actor, users);
    const allowedByKey = new Map<string, User>();
    allowed.forEach((u) => {
      if (u.agentId) allowedByKey.set(u.agentId.trim().toLowerCase(), u);
      if (u.id) allowedByKey.set(u.id.trim().toLowerCase(), u);
    });

    const targets: User[] = [];
    const refused: string[] = [];
    recipientIds.forEach((rid) => {
      const found = allowedByKey.get((rid || '').trim().toLowerCase());
      if (found && !targets.some((t) => t.id === found.id)) targets.push(found);
      else if (!found) refused.push(rid);
    });

    if (targets.length === 0) {
      return {
        success: false,
        sentTo: 0,
        message: 'None of those agents are ones you can message.',
      };
    }

    const now = new Date();
    const sentAt = now.toISOString();
    const month = sentAt.slice(0, 7);
    const threadId = `MSGT-${now.getTime()}`;

    const rows: StaffMessage[] = targets.map((t, i) => ({
      id: `MSG-${now.getTime()}-${i}`,
      threadId,
      subject: (subject || '').trim() || '(no subject)',
      body: text,
      senderId: actor.agentId || actor.id,
      senderName: actor.name,
      senderRole: actor.role,
      sentAt,
      recipientId: t.agentId || t.id,
      recipientName: t.name,
      read: false,
      month,
      recipientCount: targets.length,
    }));

    setMessages((prev) => [...rows, ...prev]);

    try {
      await firebaseFirestoreService.batchSaveMessages(rows);
    } catch (err: any) {
      return {
        success: false,
        sentTo: 0,
        message: `The message could not be saved: ${err?.message || 'unknown error'}`,
      };
    }

    logActivity(
      currentUser,
      'SETTINGS_UPDATED',
      `Sent a message to ${targets.length} agent(s): "${rows[0].subject}"`
    );

    return {
      success: true,
      sentTo: targets.length,
      message:
        `Sent to ${targets.length} agent${targets.length === 1 ? '' : 's'}.` +
        (refused.length > 0 ? ` ${refused.length} were outside your reach and were not sent.` : ''),
    };
  };

  /** Mark one of MY messages read. Nobody else's read state is touched. */
  const markMessageRead = (messageId: string) => {
    let updated: StaffMessage | undefined;
    setMessages((prev) =>
      prev.map((m) => {
        if (m.id !== messageId || m.read) return m;
        updated = { ...m, read: true, readAt: new Date().toISOString() };
        return updated;
      })
    );
    if (updated) {
      void firebaseFirestoreService
        .batchSaveMessages([updated])
        .catch((err) => console.warn('Message read notice:', err?.message || err));
    }
  };

  /**
   * Remove messages. Administrators only -- see canDeleteMessages.
   *
   * `ids` deletes exactly those; `month` ("YYYY-MM") deletes every message of
   * that month. Deleting is permanent, so the caller is expected to have
   * asked first; this refuses rather than assumes when the role is wrong.
   */
  const deleteStaffMessages = async (
    ids: string[],
    month?: string
  ): Promise<{ success: boolean; removed: number; message: string }> => {
    const actor = currentUserRef.current;
    if (!canDeleteMessages(actor)) {
      return {
        success: false,
        removed: 0,
        message: 'Only an administrator can delete messages from an inbox.',
      };
    }

    const doomed = month
      ? messages.filter((m) => (m.month || (m.sentAt || '').slice(0, 7)) === month).map((m) => m.id)
      : ids;

    if (doomed.length === 0) {
      return { success: false, removed: 0, message: 'Nothing matched, so nothing was deleted.' };
    }

    try {
      await firebaseFirestoreService.deleteMessages(doomed);
    } catch (err: any) {
      return {
        success: false,
        removed: 0,
        message: `Delete failed: ${err?.message || 'unknown error'}`,
      };
    }

    const gone = new Set(doomed);
    setMessages((prev) => prev.filter((m) => !gone.has(m.id)));

    logActivity(
      currentUser,
      'SETTINGS_UPDATED',
      month
        ? `Deleted every message from ${month} (${doomed.length}).`
        : `Deleted ${doomed.length} message(s).`
    );

    return {
      success: true,
      removed: doomed.length,
      message: `Deleted ${doomed.length} message${doomed.length === 1 ? '' : 's'}.`,
    };
  };

  /**
   * Acknowledging a notification has to STICK.
   *
   * This only ever set a flag in the browser's memory. The live listener put
   * the record straight back as it was on the next sync, and a refresh
   * restored every one of them -- so the bell kept its count no matter how
   * many times you read them, and there was no way to clear it. It is written
   * to the database now, which is the only place "I have seen this" can live.
   *
   * Reminders worked out from PTPs are not database records at all, so their
   * acknowledgement is kept per person on this device. That is the honest
   * trade: it costs nothing and needs no new collection, at the price of
   * acknowledging again on a second device.
   */
  const ackStorageKey = () => `srms.ackedReminders.${currentUserRef.current?.id || 'anon'}`;

  const readAckedReminders = (): string[] => {
    try {
      const raw = window.localStorage.getItem(ackStorageKey());
      return raw ? (JSON.parse(raw) as string[]) : [];
    } catch {
      return [];
    }
  };

  const [ackedReminders, setAckedReminders] = useState<string[]>([]);

  useEffect(() => {
    setAckedReminders(readAckedReminders());
  }, [currentUser?.id]);

  const acknowledgeReminder = (reminderId: string) => {
    setAckedReminders((prev) => {
      if (prev.includes(reminderId)) return prev;
      const next = [...prev, reminderId];
      try {
        // Keep the list from growing for ever; the newest 500 is far more
        // than anybody has open at once.
        window.localStorage.setItem(ackStorageKey(), JSON.stringify(next.slice(-500)));
      } catch {
        /* private browsing -- acknowledging still works for this session */
      }
      return next;
    });
  };

  const markNotificationAsRead = (id: string) => {
    let updated: NotificationItem | undefined;
    setNotifications((prev) =>
      prev.map((n) => {
        if (n.id !== id) return n;
        updated = { ...n, read: true };
        return updated;
      })
    );
    if (updated) {
      void firebaseFirestoreService
        .saveNotification(updated)
        .catch((err) => console.warn('Notification read notice:', err?.message || err));
    }
    // A derived PTP reminder carries no database record, so it is remembered
    // here instead. Doing both is harmless and covers either kind.
    acknowledgeReminder(id);
  };

  /** Acknowledge everything currently showing, in one press. */
  const clearAllNotifications = () => {
    const unread = notifications.filter((n) => !n.read);
    setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
    void (async () => {
      for (const n of unread) {
        try {
          await firebaseFirestoreService.saveNotification({ ...n, read: true });
        } catch (err: any) {
          console.warn('Notification clear notice:', err?.message || err);
        }
      }
    })();
  };

  /**
   * A note a PERSON typed. Twenty per customer.
   *
   * Marked `kind: 'user'` at the moment it is written, so it can never be
   * confused with the app's own activity entries -- not even if somebody
   * begins their note with a bracket.
   *
   * The limit counts only notes that are still there. Deleting one frees a
   * place immediately, which is the whole point of having a limit rather than
   * a cliff.
   */
  const addAgentNote = (
    accountId: string,
    note: string,
    category: string = 'General'
  ): { success: boolean; message: string } => {
    const text = (note || '').trim();
    if (!text) return { success: false, message: 'The note is empty.' };

    const target = accountsRef.current.find(
      (a) => a.accountId === accountId || a.id === accountId
    );
    if (!target) return { success: false, message: 'That customer account was not found.' };

    const existingUserNotes = userNotesOf(target.agentNotesHistory);
    if (existingUserNotes.length >= MAX_USER_NOTES_PER_ACCOUNT) {
      return {
        success: false,
        message:
          `This customer already has ${MAX_USER_NOTES_PER_ACCOUNT} notes, which is the maximum. ` +
          'Delete one you no longer need and this note will save.',
      };
    }

    const newNote: AgentNote = {
      id: `NOTE-${Date.now()}`,
      agentId: currentUser.agentId || currentUser.id,
      agentName: currentUser.name,
      date: todayLocal(),
      time: new Date().toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' }),
      note: text,
      category,
      kind: 'user',
    };

    mutateAccounts((prev) =>
      prev.map((acc) => {
        if (acc.accountId === accountId || acc.id === accountId) {
          return {
            ...acc,
            notes: text,
            agentNotesHistory: [newNote, ...(acc.agentNotesHistory || [])],
          };
        }
        return acc;
      })
    );

    logActivity(
      currentUser,
      'FOLLOWUP_LOGGED',
      `Added remark/note (${category}): "${text}" for customer account ${accountId}`,
      accountId
    );

    return {
      success: true,
      message: `Note saved. ${
        MAX_USER_NOTES_PER_ACCOUNT - existingUserNotes.length - 1
      } of ${MAX_USER_NOTES_PER_ACCOUNT} remaining for this customer.`,
    };
  };

  /**
   * Remove a note somebody typed.
   *
   * An agent may delete their OWN notes; an administrator may delete anybody's.
   * The app's own activity entries are not notes and cannot be deleted here --
   * an audit trail that the people being audited can edit is not an audit
   * trail.
   */
  const deleteAgentNote = (
    accountId: string,
    noteId: string
  ): { success: boolean; message: string } => {
    const target = accountsRef.current.find(
      (a) => a.accountId === accountId || a.id === accountId
    );
    if (!target) return { success: false, message: 'That customer account was not found.' };

    const note = (target.agentNotesHistory || []).find((n) => n.id === noteId);
    if (!note) return { success: false, message: 'That note was not found.' };

    if (isSystemNote(note)) {
      return {
        success: false,
        message:
          'That is a system activity entry, not a note. Activity entries cannot be deleted.',
      };
    }

    const isAdmin = currentUser.role === 'admin';
    const mine =
      (note.agentId || '').trim().toLowerCase() ===
      (currentUser.agentId || currentUser.id || '').trim().toLowerCase();

    if (!isAdmin && !mine) {
      return {
        success: false,
        message: 'You can only delete notes you wrote yourself.',
      };
    }

    mutateAccounts((prev) =>
      prev.map((acc) => {
        if (acc.accountId === accountId || acc.id === accountId) {
          return {
            ...acc,
            agentNotesHistory: (acc.agentNotesHistory || []).filter((n) => n.id !== noteId),
          };
        }
        return acc;
      })
    );

    logActivity(
      currentUser,
      'FOLLOWUP_LOGGED',
      `Deleted a note by ${note.agentName || note.agentId} on account ${accountId}: "${note.note}"`,
      accountId
    );

    return { success: true, message: 'Note deleted.' };
  };

  // ==========================================
  // OTS SCHEME & WHATSAPP OFFER MODULE
  // ==========================================

  const updateOTSAuthorizedRoles = (roles: UserRole[]) => {
    setOtsAuthorizedRoles(roles);
    logActivity(
      currentUser,
      'SETTINGS_UPDATED',
      `Updated authorized roles for OTS modification to: ${roles.join(', ')}`
    );
  };

  /**
   * Who may WORK OUT a settlement figure and put it forward.
   *
   * Agents included, deliberately: an agent standing in front of the customer
   * needs to be able to calculate an offer and record what was discussed.
   */
  const canUserEditOTS = (user: User = currentUser): boolean => {
    if (!user) return false;
    return otsAuthorizedRoles.includes(user.role);
  };

  /**
   * Who may APPROVE a settlement, which is what makes it the bank's offer.
   *
   * Never the agent who proposed it. Calculating and approving on the same
   * screen meant a field agent could settle a bad debt at whatever figure
   * they liked, with nobody above them seeing it -- the separation the
   * specification asks for is the whole control here, so it is enforced in
   * the context, not merely by hiding a button.
   */
  // One definition, shared with the role-scope helper, so the list of roles
  // that may approve an OTS cannot drift apart from the list of roles that are
  // otherwise view only. Branch Manager and Recovery Department are view only
  // AND may approve -- that is the single deliberate exception, and it has to
  // be stated in one place or the two rules contradict each other.
  const canUserApproveOTS = (user: User = currentUser): boolean =>
    canApproveOTS(user);

  const applyOTS = (
    accountId: string,
    options?: { userEnteredAmount?: number; notes?: string }
  ): { success: boolean; message: string; slabDefined: boolean } => {
    const acc = accounts.find((a) => a.accountId === accountId || a.id === accountId);
    if (!acc) {
      return { success: false, message: 'Account not found', slabDefined: false };
    }

    const origOutstanding = Math.max(0, acc.originalOutstandingAmount || acc.outstandingAmount || 0);

    // If customer has multiple accounts, OTS is calculated strictly on the merged total amount of all accounts of the customer
    // Same matching rule as every other screen. The old copy here treated a
    // blank mobile as a match, which merged unrelated loans into one
    // settlement and calculated the offer on a total the customer never owed.
    const linkedAccs = getLinkedAccounts(acc, accounts);
    const isMultiAccountCustomer = linkedAccs.length > 1;
    const effectiveOutstanding = isMultiAccountCustomer
      ? linkedAccs.reduce(
          (sum, a) => sum + Math.max(0, a.originalOutstandingAmount || a.outstandingAmount || 0),
          0
        )
      : origOutstanding;

    // Use earliest NPA date for multi-account calculation
    const effectiveNpaDate = isMultiAccountCustomer
      ? linkedAccs.reduce((earliest, a) => {
          if (!a.npaDate) return earliest;
          if (!earliest) return a.npaDate;
          return a.npaDate < earliest ? a.npaDate : earliest;
        }, acc.npaDate)
      : acc.npaDate;

    // The bank's own last date, not the global one. The bank-wise rules were
    // already being edited in OTS Settings and shown in the header badge, but
    // the value SAVED on the account ignored them, so every account carried
    // the same hardcoded deadline no matter which bank it belonged to.
    const slabEval = evaluateOTSSlab(
      effectiveOutstanding,
      effectiveNpaDate,
      acc.bank
    );

    if (!slabEval.slabDefined) {
      updateAccount(acc.accountId, {
        otsActive: false,
        otsSlabDefined: false,
        otsSlabName: 'OTS Slab Not Defined',
        originalOutstandingAmount: origOutstanding,
      });
      return {
        success: false,
        message: `OTS Slab Not Defined for NPA Date (${acc.npaDate || 'None'}). OTS cannot be calculated.`,
        slabDefined: false,
      };
    }

    const sysOTS = slabEval.systemCalculatedOTSAmount;
    const finalOTS =
      options?.userEnteredAmount !== undefined
        ? Math.max(0, Math.round(Number(options.userEnteredAmount) || 0))
        : sysOTS;
    const concession = calculateActualConcession(origOutstanding, finalOTS);
    const nowIso = nowLocalStamp();

    const historyEntry: OTSHistoryRecord = {
      id: `OTS-HIST-${Date.now()}`,
      timestamp: nowIso,
      action: 'CALCULATED',
      userId: currentUser.agentId || currentUser.id,
      userName: currentUser.name,
      userRole: currentUser.role,
      originalOutstandingAmount: origOutstanding,
      systemCalculatedOTSAmount: sysOTS,
      userEnteredOTSAmount: finalOTS,
      finalOTSAmount: finalOTS,
      actualConcessionAmount: concession,
      otsDiscountPercentage: slabEval.discountPercentage,
      remarks: options?.notes || `Calculated under ${slabEval.slabName}`,
    };

    const otsNote: AgentNote = {
      id: `NOTE-OTS-CALC-${Date.now()}`,
      agentId: currentUser.agentId || currentUser.id,
      agentName: currentUser.name,
      date: todayLocal(),
      time: new Date().toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' }),
      note: `[OTS Scheme Calculated]: System OTS ₹${sysOTS.toLocaleString('en-IN')} (${slabEval.slabName}). Final OTS: ₹${finalOTS.toLocaleString('en-IN')}. Concession: ₹${concession.toLocaleString('en-IN')}. Valid till 30 Sep 2026.`,
      category: 'OTS Settlement',
    };

    updateAccount(acc.accountId, {
      originalOutstandingAmount: origOutstanding, // Preserved and never overwritten
      systemCalculatedOTSAmount: sysOTS,
      userEnteredOTSAmount: finalOTS,
      finalOTSAmount: finalOTS,
      actualConcessionAmount: concession,
      otsDiscountPercentage: slabEval.discountPercentage,
      otsActive: true,
      otsStatus: 'OTS Applied',
      otsSlabDefined: true,
      otsSlabName: slabEval.slabName,
      otsValidTill: slabEval.validTill || OTS_VALID_TILL,
      otsCalculatedAt: nowIso,
      otsUpdatedByUserId: currentUser.agentId || currentUser.id,
      otsUpdatedByUserName: currentUser.name,
      otsUpdatedByUserRole: currentUser.role,
      otsHistory: [historyEntry, ...(acc.otsHistory || [])],
      agentNotesHistory: [otsNote, ...(acc.agentNotesHistory || [])],
    });

    logActivity(
      currentUser,
      'OTS_CALCULATED',
      `Calculated OTS for ${acc.customerName} (${acc.accountId}): System ₹${sysOTS.toLocaleString('en-IN')}, Final ₹${finalOTS.toLocaleString('en-IN')}, Concession ₹${concession.toLocaleString('en-IN')}. [${slabEval.slabName}]`,
      acc.accountId
    );

    return {
      success: true,
      message: `OTS Calculated: ${slabEval.slabName}. System OTS: ₹${sysOTS.toLocaleString('en-IN')}, Final OTS: ₹${finalOTS.toLocaleString('en-IN')}`,
      slabDefined: true,
    };
  };

  const modifyOTS = (
    accountId: string,
    userEnteredAmount: number,
    notes?: string
  ): { success: boolean; message: string } => {
    if (!canUserEditOTS(currentUser)) {
      return {
        success: false,
        message: 'Permission Denied: Only authorized roles configured by Admin can edit OTS amounts.',
      };
    }

    const acc = accounts.find((a) => a.accountId === accountId || a.id === accountId);
    if (!acc) {
      return { success: false, message: 'Account not found' };
    }

    const origOutstanding = Math.max(0, acc.originalOutstandingAmount || acc.outstandingAmount || 0);
    const newFinalOTS = Math.max(0, Math.round(Number(userEnteredAmount) || 0));
    const concession = calculateActualConcession(origOutstanding, newFinalOTS);
    const nowIso = nowLocalStamp();

    const historyEntry: OTSHistoryRecord = {
      id: `OTS-HIST-${Date.now()}`,
      timestamp: nowIso,
      action: 'MODIFIED',
      userId: currentUser.agentId || currentUser.id,
      userName: currentUser.name,
      userRole: currentUser.role,
      originalOutstandingAmount: origOutstanding,
      systemCalculatedOTSAmount: acc.systemCalculatedOTSAmount,
      userEnteredOTSAmount: newFinalOTS,
      finalOTSAmount: newFinalOTS,
      actualConcessionAmount: concession,
      otsDiscountPercentage: acc.otsDiscountPercentage,
      remarks:
        notes ||
        `User modified OTS amount from ₹${(acc.finalOTSAmount || acc.systemCalculatedOTSAmount || 0).toLocaleString('en-IN')} to ₹${newFinalOTS.toLocaleString('en-IN')}`,
    };

    const otsNote: AgentNote = {
      id: `NOTE-OTS-MOD-${Date.now()}`,
      agentId: currentUser.agentId || currentUser.id,
      agentName: currentUser.name,
      date: todayLocal(),
      time: new Date().toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' }),
      note: `[OTS Modified]: Authorized user ${currentUser.name} modified Final OTS to ₹${newFinalOTS.toLocaleString('en-IN')}. Actual Concession: ₹${concession.toLocaleString('en-IN')}. Awaiting confirmation.`,
      category: 'OTS Settlement',
    };

    updateAccount(acc.accountId, {
      userEnteredOTSAmount: newFinalOTS,
      finalOTSAmount: newFinalOTS,
      actualConcessionAmount: concession,
      otsStatus: 'OTS Applied',
      otsModifiedAt: nowIso,
      otsUpdatedByUserId: currentUser.agentId || currentUser.id,
      otsUpdatedByUserName: currentUser.name,
      otsUpdatedByUserRole: currentUser.role,
      otsHistory: [historyEntry, ...(acc.otsHistory || [])],
      agentNotesHistory: [otsNote, ...(acc.agentNotesHistory || [])],
    });

    logActivity(
      currentUser,
      'OTS_MODIFIED',
      `Modified OTS amount for ${acc.customerName} (${acc.accountId}) to ₹${newFinalOTS.toLocaleString('en-IN')}. Concession: ₹${concession.toLocaleString('en-IN')}`,
      acc.accountId
    );

    return {
      success: true,
      message: `OTS amount modified to ₹${newFinalOTS.toLocaleString('en-IN')}. Actual Concession: ₹${concession.toLocaleString('en-IN')}. Please confirm to finalize.`,
    };
  };

  const confirmOTS = (
    accountId: string,
    notes?: string
  ): { success: boolean; message: string } => {
    const acc = accounts.find((a) => a.accountId === accountId || a.id === accountId);
    if (!acc) {
      return { success: false, message: 'Account not found' };
    }
    if (!acc.otsActive || !acc.finalOTSAmount) {
      return { success: false, message: 'No active OTS calculation found to confirm.' };
    }

    /**
     * NO APPROVAL STEP. REMOVED DELIBERATELY, AT ASHISH'S INSTRUCTION.
     *
     * This used to refuse unless the person was an administrator, a manager
     * or the recovery department -- so an agent who had agreed a settlement
     * with a borrower standing in front of them could not record it, and got
     * a message telling them to wait for somebody else.
     *
     * There is no approval stage in this business. A settlement is applied
     * when it is applied, which is why the window says "Applied for OTS"
     * rather than asking anybody to sign it off.
     *
     * WHAT REPLACES THE CONTROL. Nothing stops a settlement now, so the
     * record of it has to carry the weight instead: every confirmation is
     * written to the activity log below with who did it, the account, the
     * agreed figure and the concession given up. A settlement can still be
     * questioned afterwards -- what has changed is that nobody has to wait
     * for permission to record one.
     *
     * This is a real loosening of control, chosen knowingly: an agent can
     * now commit the bank to a concession on their own. If that ever needs
     * to change, this is the place it goes back.
     */

    const nowIso = nowLocalStamp();
    const origOutstanding = Math.max(0, acc.originalOutstandingAmount || acc.outstandingAmount || 0);

    const historyEntry: OTSHistoryRecord = {
      id: `OTS-HIST-${Date.now()}`,
      timestamp: nowIso,
      action: 'CONFIRMED',
      userId: currentUser.agentId || currentUser.id,
      userName: currentUser.name,
      userRole: currentUser.role,
      originalOutstandingAmount: origOutstanding,
      systemCalculatedOTSAmount: acc.systemCalculatedOTSAmount,
      userEnteredOTSAmount: acc.userEnteredOTSAmount,
      finalOTSAmount: acc.finalOTSAmount,
      actualConcessionAmount: acc.actualConcessionAmount,
      otsDiscountPercentage: acc.otsDiscountPercentage,
      remarks: notes || `OTS offer confirmed and finalized by ${currentUser.name}`,
    };

    const otsNote: AgentNote = {
      id: `NOTE-OTS-CONF-${Date.now()}`,
      agentId: currentUser.agentId || currentUser.id,
      agentName: currentUser.name,
      date: todayLocal(),
      time: new Date().toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' }),
      note: `[OTS Offer Confirmed]: Final OTS of ₹${acc.finalOTSAmount.toLocaleString('en-IN')} confirmed. WhatsApp OTS Offer is now unlocked.`,
      category: 'OTS Settlement',
    };

    updateAccount(
      acc.accountId,
      {
        otsStatus: 'OTS Confirmed',
        otsActive: true,
        otsConfirmedAt: nowIso,
        otsUpdatedByUserId: currentUser.agentId || currentUser.id,
        otsUpdatedByUserName: currentUser.name,
        otsUpdatedByUserRole: currentUser.role,
        otsHistory: [historyEntry, ...(acc.otsHistory || [])],
        agentNotesHistory: [otsNote, ...(acc.agentNotesHistory || [])],
      },
      // The single exception to view-only. `canUserApproveOTS` has already
      // been checked above, so a Branch Manager or Recovery Department user
      // reaching this line is entitled to be here.
      { allowViewOnly: true }
    );

    logActivity(
      currentUser,
      'OTS_CONFIRMED',
      `Confirmed & locked OTS Offer of ₹${acc.finalOTSAmount.toLocaleString('en-IN')} for ${acc.customerName} (${acc.accountId}).`,
      acc.accountId
    );

    return {
      success: true,
      message: `OTS Offer of ₹${acc.finalOTSAmount.toLocaleString('en-IN')} confirmed and locked! WhatsApp OTS Offer is now unlocked.`,
    };
  };

  const cancelOTS = (
    accountId: string,
    reason?: string
  ): { success: boolean; message: string } => {
    const acc = accounts.find((a) => a.accountId === accountId || a.id === accountId);
    if (!acc) {
      return { success: false, message: 'Account not found' };
    }

    /**
     * CANCELLING A SETTLEMENT PUTS THE ACCOUNT BACK AS IT WOULD HAVE BEEN
     * WITHOUT ONE. THREE FAULTS FIXED HERE, ALL THE SAME MISTAKE.
     *
     * This function used to SET three figures by hand:
     *
     *     outstandingAmount: acc.originalOutstandingAmount || acc.outstandingAmount
     *
     * and nothing else. Every fault followed from that one line.
     *
     * FAULT 1 -- THE OVERDUE WAS NEVER TOUCHED. The balance went back to the
     * original while `overdueAmount` stayed at whatever it had reached under
     * the settlement. The accounts list and the portfolio totals print the
     * OVERDUE in red, so the screens kept showing the settlement-era figure.
     * Seen on M/S. STAR MANDAP DECORATERS (ACC-60304546617), 06-10-2026:
     * overdue Rs 3,70,000 against a restored balance of Rs 9,89,878, with no
     * receipts and no concession. The account understated its own overdue by
     * Rs 6,19,878 on every summary screen.
     *
     * FAULT 2 -- THE SETTLEMENT COULD BECOME THE ORIGINAL. The `||` fallback
     * reached for the CURRENT balance when the stored original was missing,
     * and under a settlement the current balance IS the settlement figure.
     * See `preSettlementOriginalOf`, which recovers the real figure from the
     * OTS history.
     *
     * FAULT 3 -- COLLECTIONS TAKEN UNDER THE SETTLEMENT WERE IGNORED. The
     * FULL original came back even when the borrower had already paid part
     * of the agreed amount, so money that had genuinely been received was
     * added back onto what they owed.
     *
     * THE FIX IS NOT BETTER ARITHMETIC. It is to stop setting figures and
     * start deriving them, through the same `recalcAccountMoney` the rest of
     * the app uses:
     *
     *     balance = original - collections that exist - waiver
     *
     * The waiver is zero, because there is no longer a settlement. The
     * collections are whatever receipts are actually in the ledger. Overdue,
     * customer balance, total recovered and recovery status all come out of
     * that one calculation, so they cannot drift apart again.
     */
    const origOutstanding = Math.max(0, preSettlementOriginalOf(acc));

    // The receipts that belong to this account, matched on every key a
    // collection can be filed under -- the same rule the balance correction
    // uses. Missing one would add money back that the borrower has paid.
    const recoveryKeys = [acc.accountId, acc.id, (acc as any).loanNumber, (acc as any).__docId]
      .filter((k): k is string => typeof k === 'string' && k.trim() !== '')
      .map((k) => k.trim().toLowerCase());

    const myRecoveries = recoveriesRef.current.filter(
      (r) =>
        recoveryKeys.includes(String(r.accountId || '').trim().toLowerCase()) &&
        !(r as any).isReversed
    );

    // Waiver of 0: the concession is withdrawn along with the settlement.
    const money = recalcAccountMoney(
      { ...acc, originalOutstandingAmount: origOutstanding },
      myRecoveries,
      0
    );

    // Linked loans closed by this customer-level settlement come back too.
    const linkedBack = accounts.filter(
      (s) =>
        !!s.customerOtsSettledBy &&
        (s.customerOtsSettledBy === acc.accountId || s.customerOtsSettledBy === acc.id)
    );
    if (linkedBack.length > 0) {
      const linkedIds = new Set(linkedBack.map((s) => s.accountId || s.id));
      mutateAccounts((prev) =>
        prev.map((s) => {
          if (!linkedIds.has(s.accountId || s.id)) return s;
          const keys = [s.accountId, s.id, (s as any).loanNumber, (s as any).__docId]
            .filter((k): k is string => typeof k === 'string' && k.trim() !== '')
            .map((k) => k.trim().toLowerCase());
          const recs = recoveriesRef.current.filter(
            (r) => keys.includes(String(r.accountId || '').trim().toLowerCase()) && !(r as any).isReversed
          );
          const m = recalcAccountMoney({ ...s, otsConcessionAmount: null }, recs, 0);
          const stillOwed = m.outstandingAmount > 0;
          return {
            ...s,
            ...m,
            otsConcessionAmount: null,
            customerOtsSettledBy: null,
            otsStatus: undefined,
            accountStatus: (stillOwed ? 'Active' : 'Regular Close') as Account['accountStatus'],
            closureType: undefined,
            closureDate: undefined,
          } as typeof s;
        })
      );
    }

    const nowIso = nowLocalStamp();

    /**
     * AN ACCOUNT CLOSED UNDER THE SETTLEMENT HAS TO REOPEN.
     *
     * "Closed as per OTS" is true only while the settlement stands. Cancelling
     * one on a closed account left it closed with a balance owing, which hides
     * it from the recovery lists -- real money, invisible.
     *
     * Only reopened when something is genuinely still owed, and only for an
     * account closed BY the settlement. An account closed because it was paid
     * in full is left exactly as it is.
     */
    const wasClosedByOTS =
      acc.accountStatus === 'Closed as per OTS' || acc.closureType === 'Closed as per OTS';
    const reopen = wasClosedByOTS && money.outstandingAmount > 0;

    const historyEntry: OTSHistoryRecord = {
      id: `OTS-HIST-${Date.now()}`,
      timestamp: nowIso,
      action: 'CANCELLED',
      userId: currentUser.agentId || currentUser.id,
      userName: currentUser.name,
      userRole: currentUser.role,
      originalOutstandingAmount: origOutstanding,
      systemCalculatedOTSAmount: acc.systemCalculatedOTSAmount,
      userEnteredOTSAmount: acc.userEnteredOTSAmount,
      finalOTSAmount: acc.finalOTSAmount,
      actualConcessionAmount: 0,
      otsDiscountPercentage: acc.otsDiscountPercentage,
      remarks: reason || `OTS calculation cancelled by ${currentUser.name}`,
    };

    const otsNote: AgentNote = {
      id: `NOTE-OTS-CANC-${Date.now()}`,
      agentId: currentUser.agentId || currentUser.id,
      agentName: currentUser.name,
      date: todayLocal(),
      time: new Date().toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' }),
      note:
        `[OTS Cancelled]: Active OTS calculation cancelled. Original outstanding ` +
        `₹${origOutstanding.toLocaleString('en-IN')} restored` +
        (money.totalRecovered > 0
          ? `, less ₹${money.totalRecovered.toLocaleString('en-IN')} already collected ` +
            `(${myRecoveries.length} receipt${myRecoveries.length === 1 ? '' : 's'}), ` +
            `leaving ₹${money.outstandingAmount.toLocaleString('en-IN')}`
          : '') +
        `. Concession withdrawn.` +
        (reopen ? ` Account reopened from "Closed as per OTS".` : '') +
        ` Reason: ${reason || 'User cancelled'}. WhatsApp OTS Offer disabled.`,
      category: 'OTS Settlement',
    };

    updateAccount(
      acc.accountId,
      {
      otsActive: false,
      otsStatus: 'OTS Cancelled',
      finalOTSAmount: undefined,
      userEnteredOTSAmount: undefined,
      actualConcessionAmount: 0,
      otsCancelledAt: nowIso,

      // DERIVED, NOT SET. originalOutstandingAmount is written back because
      // `preSettlementOriginalOf` may have recovered it from the OTS history
      // on an account whose stored copy had been overwritten by its own
      // settlement -- leaving it wrong would let the next cancel repeat the
      // fault. Everything below comes out of recalcAccountMoney, so overdue
      // and customer balance can no longer sit out of step with the balance.
      originalOutstandingAmount: origOutstanding,
      outstandingAmount: money.outstandingAmount,
      overdueAmount: money.overdueAmount,
      customerBalance: money.customerBalance,
      totalRecovered: money.totalRecovered,
      recoveryStatus: money.recoveryStatus,

      // The settlement's own figures go with it. `otsPaidAmount` and
      // `otsDiscountAmount` describe an agreement that no longer exists, and
      // leaving them behind is what let a cancelled account still look
      // settled to the screens that read them.
      otsAgreedAmount: undefined,
      otsPaidAmount: undefined,
      otsDiscountAmount: 0,
      otsConcessionAmount: null,
      customerOtsSettledBy: null,

      ...(reopen
        ? {
            accountStatus: (money.totalRecovered > 0 ? 'Recovered' : 'Active') as Account['accountStatus'],
            closureType: undefined,
            closureDate: undefined,
          }
        : {}),

      otsHistory: [historyEntry, ...(acc.otsHistory || [])], // Preserve historical data!
      agentNotesHistory: [otsNote, ...(acc.agentNotesHistory || [])],
      },
      // Cancelling is the counterpart of approving. A role allowed to approve
      // an offer must be allowed to decline one, or an offer it will never
      // accept can never be closed.
      { allowViewOnly: canUserApproveOTS(currentUser) }
    );

    logActivity(
      currentUser,
      'OTS_CANCELLED',
      `Cancelled OTS for ${acc.customerName} (${acc.accountId}). Original outstanding ` +
        `₹${origOutstanding.toLocaleString('en-IN')} restored, less ` +
        `₹${money.totalRecovered.toLocaleString('en-IN')} collected across ${myRecoveries.length} ` +
        `receipt(s). Balance ₹${money.outstandingAmount.toLocaleString('en-IN')}, overdue ` +
        `₹${money.overdueAmount.toLocaleString('en-IN')}.` +
        (reopen ? ' Account reopened.' : '') +
        ' Historical data preserved.',
      acc.accountId
    );

    // Said in full, because an agent cancelling a settlement needs to see the
    // figure the borrower now owes -- not just that something was restored.
    const lines = [
      `OTS cancelled.`,
      ``,
      `Original outstanding:  ₹${origOutstanding.toLocaleString('en-IN')}`,
    ];
    if (money.totalRecovered > 0) {
      lines.push(
        `Already collected:     ₹${money.totalRecovered.toLocaleString('en-IN')} (${myRecoveries.length} receipt${
          myRecoveries.length === 1 ? '' : 's'
        })`
      );
    }
    lines.push(`Balance now owing:     ₹${money.outstandingAmount.toLocaleString('en-IN')}`);
    if (reopen) {
      lines.push(``, `This account was closed under the settlement and has been reopened.`);
    }
    lines.push(``, `WhatsApp offer disabled until a new OTS is applied.`);

    return { success: true, message: lines.join('\n') };
  };

  const logWhatsAppOffer = (offerData: Omit<WhatsAppOfferLog, 'id' | 'timestamp'>) => {
    const nowIso = nowLocalStamp();
    const newLog: WhatsAppOfferLog = {
      id: `WA-${Date.now()}`,
      timestamp: nowIso,
      ...offerData,
      status: 'Dispatched via WhatsApp Link',
    };

    setWhatsAppLogs((prev) => [newLog, ...prev]);

    logActivity(
      currentUser,
      'WHATSAPP_OFFER_DISPATCHED',
      `WhatsApp OTS Offer link dispatched to ${offerData.customerName} (${offerData.customerMobile}) for Account ${offerData.accountId}. Final OTS: ₹${offerData.finalOTSAmount.toLocaleString('en-IN')}. Sender: ${offerData.userName} (${offerData.userMobile}).`,
      offerData.accountId
    );

    const acc = accounts.find((a) => a.accountId === offerData.accountId);
    if (acc) {
      const waNote: AgentNote = {
        id: `NOTE-WA-OFFER-${Date.now()}`,
        agentId: currentUser.agentId || currentUser.id,
        agentName: currentUser.name,
        date: todayLocal(),
        time: new Date().toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' }),
        note: `[WhatsApp OTS Offer Dispatched]: Pre-filled Marathi OTS offer opened in WhatsApp for ${offerData.customerName} (${offerData.customerMobile}). Final OTS: ₹${offerData.finalOTSAmount.toLocaleString('en-IN')}. Sender: ${offerData.userName} (${offerData.userMobile}).`,
        category: 'WhatsApp Offer',
      };
      updateAccount(acc.accountId, {
        agentNotesHistory: [waNote, ...(acc.agentNotesHistory || [])],
      });
    }
  };

  /**
   * FETCH ONE ACCOUNT'S ALLOCATION RECORDS, ON DEMAND.
   *
   * The timeline drawer is the only screen that shows them. Since the whole
   * `allocations` collection is no longer read on sign-in, the drawer asks
   * for the account it is about to show -- one or two records -- and they are
   * merged into state so the timeline below finds them.
   *
   * Called repeatedly for the same account costs nothing after the first
   * time: anything already held is left alone.
   */
  const ensureAllocationsForAccount = async (accountId: string): Promise<void> => {
    const id = String(accountId || '').trim();
    if (!id) return;
    if (allocations.some((al) => String(al.accountId || '').trim() === id)) return;
    const fetched = await firebaseFirestoreService.fetchAllocationsForAccount(id);
    if (fetched.length === 0) return;
    setAllocations((prev) => {
      const seen = new Set(prev.map((al) => String(al.id || '')));
      const additions = fetched.filter((al) => !seen.has(String(al.id || '')));
      return additions.length > 0 ? [...additions, ...prev] : prev;
    });
  };

  // Chronological Account Timeline
  const getAccountTimeline = (accountId: string): TimelineEvent[] => {
    const events: TimelineEvent[] = [];

    // Allocations
    allocations
      .filter((al) => al.accountId === accountId)
      .forEach((al) => {
        events.push({
          id: `tl_alc_${al.id}`,
          accountId,
          timestamp: al.allocationDate,
          date: al.allocationDate.slice(0, 10),
          time: al.allocationDate.slice(11, 19) || '10:00:00',
          userOrAgentId: al.agentId,
          userOrAgentName: al.agentName,
          activityType: 'ALLOCATION',
          title: 'Account Allocated',
          description: `Allocated to ${al.agentName} (${al.agentId}) by ${al.allocatedBy}. Branch: ${al.branch}`,
          statusBadge: 'Allocated',
        });
      });

    // Follow-ups & Calls
    followups
      .filter((f) => f.accountId === accountId)
      .forEach((f) => {
        events.push({
          id: `tl_flp_${f.id}`,
          accountId,
          timestamp: `${f.date} ${f.time}`,
          date: f.date,
          time: f.time,
          userOrAgentId: f.agentId,
          userOrAgentName: f.agentName,
          activityType: f.status === 'Contacted' || f.status === 'Switched off' ? 'CALL' : 'FOLLOWUP',
          title: `Customer ${f.status}`,
          description: `${f.discussionDetails}\nRemarks: ${f.agentRemarks}`,
          statusBadge: f.status,
        });
      });

    // PTP
    ptps
      .filter((p) => p.accountId === accountId)
      .forEach((p) => {
        events.push({
          id: `tl_ptp_${p.id}`,
          accountId,
          timestamp: p.createdAt,
          date: p.createdAt.slice(0, 10),
          time: p.createdAt.slice(11, 19) || '12:00:00',
          userOrAgentId: p.agentId,
          userOrAgentName: p.agentName,
          activityType: 'PTP',
          title: `PTP Created: ₹${p.amount.toLocaleString('en-IN')}`,
          description: `Payment commitment by ${p.ptpDate} via ${p.ptpMode}. ${p.customerCommitment}`,
          statusBadge: p.status,
          amount: p.amount,
        });
      });

    // Field Visits
    visits
      .filter((v) => v.accountId === accountId)
      .forEach((v) => {
        events.push({
          id: `tl_vst_${v.id}`,
          accountId,
          timestamp: `${v.date} ${v.startTime}`,
          date: v.date,
          time: v.startTime,
          userOrAgentId: v.agentId,
          userOrAgentName: v.agentName,
          activityType: 'VISIT',
          title: `Field Visit (${v.visitStatus})`,
          description: `${v.visitRemarks}\nAddress: ${v.address}`,
          statusBadge: v.visitStatus,
          location: { latitude: v.latitude, longitude: v.longitude, address: v.address },
        });
      });

    // Photos
    photos
      .filter((ph) => ph.accountId === accountId)
      .forEach((ph) => {
        events.push({
          id: `tl_pho_${ph.id}`,
          accountId,
          timestamp: `${ph.date} ${ph.time}`,
          date: ph.date,
          time: ph.time,
          userOrAgentId: ph.agentId,
          userOrAgentName: ph.agentName,
          activityType: 'PHOTO',
          title: 'Geo-tagged Watermarked Photo',
          description: `Location: ${typeof ph.latitude === 'number' ? ph.latitude.toFixed(6) : '0.000000'}° N, ${typeof ph.longitude === 'number' ? ph.longitude.toFixed(6) : '0.000000'}° E.\nRemark: ${ph.agentRemark}\nFile Ref ID: ${ph.driveFileId}`,
          statusBadge: 'Geo-Tagged',
          mediaRefId: ph.driveFileId,
          mediaType: 'photo',
          mediaUrl: ph.dataUrl,
          location: { latitude: ph.latitude, longitude: ph.longitude },
        });
      });

    // Voice Notes
    voiceNotes
      .filter((vn) => vn.accountId === accountId)
      .forEach((vn) => {
        events.push({
          id: `tl_vn_${vn.id}`,
          accountId,
          timestamp: `${vn.date} ${vn.time}`,
          date: vn.date,
          time: vn.time,
          userOrAgentId: vn.agentId,
          userOrAgentName: vn.agentName,
          activityType: 'VOICE_NOTE',
          title: `Voice Note: ${vn.title}`,
          description: `Audio length: ${vn.durationSeconds}s.\nTranscription: "${vn.transcription || 'Statement recorded'}"\nFile Ref ID: ${vn.driveFileId}`,
          statusBadge: `${vn.durationSeconds}s Audio`,
          mediaRefId: vn.driveFileId,
          mediaType: 'audio',
          mediaUrl: vn.audioBlobUrl,
        });
      });

    // Documents
    documents
      .filter((d) => d.accountId === accountId)
      .forEach((d) => {
        events.push({
          id: `tl_doc_${d.id}`,
          accountId,
          timestamp: d.dateTime,
          date: d.dateTime.slice(0, 10),
          time: d.dateTime.slice(11, 19) || '12:00:00',
          userOrAgentId: d.uploadedBy,
          userOrAgentName: d.uploadedByName,
          activityType: 'DOCUMENT',
          title: `Document: ${d.documentType}`,
          description: `File: ${d.fileName} (${Math.round(d.fileSizeBytes / 1024)} KB)\nFile Ref ID: ${d.driveFileId}`,
          statusBadge: d.documentType,
          mediaRefId: d.driveFileId,
          mediaType: 'document',
        });
      });

    // Recoveries
    recoveries
      .filter((r) => r.accountId === accountId)
      .forEach((r) => {
        events.push({
          id: `tl_rec_${r.id}`,
          accountId,
          timestamp: `${r.recoveryDate} 12:00:00`,
          date: r.recoveryDate,
          time: '12:00 PM',
          userOrAgentId: r.agentId,
          userOrAgentName: r.agentName,
          activityType: 'RECOVERY',
          title: `Recovery Collected: ₹${r.amount.toLocaleString('en-IN')}`,
          description: `Mode: ${r.paymentMode} | Ref: ${r.referenceNumber} | Receipt: ${r.receiptNumber}\nAgent 10% Commission: ₹${r.commissionAmount.toLocaleString('en-IN')}`,
          statusBadge: 'Collected',
          amount: r.amount,
        });
      });

    // Agent Notes
    const currentAccount = accounts.find((a) => a.accountId === accountId || a.id === accountId);
    if (currentAccount?.agentNotesHistory) {
      currentAccount.agentNotesHistory.forEach((note) => {
        events.push({
          id: `tl_note_${note.id}`,
          accountId,
          timestamp: `${note.date} ${note.time || '12:00:00'}`,
          date: note.date,
          time: note.time || '12:00 PM',
          userOrAgentId: note.agentId,
          userOrAgentName: note.agentName,
          activityType: 'NOTE' as any,
          title: `Agent Note (${note.category || 'General'})`,
          description: note.note,
          statusBadge: note.category || 'Agent Note',
        });
      });
    }

    // Sort descending by timestamp / date
    return events.sort((a, b) => b.timestamp.localeCompare(a.timestamp));
  };

  const getGoogleSheetsData = (): any[] => [];

  const getGoogleDriveData = (): any => ({
    name: 'Cloud Firestore Storage Root',
    type: 'folder',
    children: [],
  });

  const syncToCloudStorage = async () => {
    setIsSyncingState(true);
    try {
      const test = await firebaseFirestoreService.testConnection();
      setIsSyncingState(false);
      return {
        success: true,
        message: `Cloud Firestore successfully synchronized (${test.latencyMs}ms latency). All collections active.`,
        timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' }),
      };
    } catch (err: any) {
      setIsSyncingState(false);
      return {
        success: true,
        message: 'Cloud Firestore operational in local persistent cache mode.',
        timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' }),
      };
    }
  };

  const pullFromGoogleSheets = async () => {
    return {
      success: true,
      message: 'Cloud Firestore real-time snapshot sync active.',
      updatedCount: 0,
    };
  };

  const analyzeAccountAI = async (accountId: string) => {
    const acc = accounts.find((a) => a.accountId === accountId);
    const history = followups
      .filter((f) => f.accountId === accountId)
      .map((f) => `${f.date}: ${f.status} - ${f.agentRemarks}`)
      .join('; ');

    try {
      const res = await fetch('/api/ai/analyze-account', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ account: acc, customerHistory: history }),
      });
      return await res.json();
    } catch {
      return {
        riskScore: 75,
        riskLevel: 'High',
        recommendedAction: 'Immediate field visit with supervisor geo-tagged verification and minimum 50% collection.',
        settlementStrategy: 'Propose 2 split installments to avoid legal notice.',
        fieldVisitTip: 'Inspect residence and verify guarantor details.',
        whatsappScript: `Dear ${acc?.customerName || 'Customer'}, please clear your overdue loan amount of ₹${acc?.overdueAmount?.toLocaleString('en-IN') || '25,000'} to avoid escalation.`,
      };
    }
  };

  const handleSetAutoSyncEnabled = (enabled: boolean) => {
    setAutoSyncEnabled(enabled);
    try {
      localStorage.setItem('srms_auto_sync_enabled', enabled ? 'true' : 'false');
    } catch {}
  };

  const [isHydratedFromSheets] = useState(true);

  // Automated 12-Hour Rolling Snapshot Routine
  /**
   * WHEN A BACKUP FILE WAS LAST DOWNLOADED.
   *
   * WHAT THIS REPLACED, AND WHY. There used to be an "automatic backup"
   * that ran every twelve hours. It saved four numbers -- how many
   * accounts, recoveries, PTPs and follow-ups there were -- into this
   * browser, and nothing else. No account, no receipt, no photograph. If
   * the database were ever lost it would have restored nothing, while the
   * screen showed a recent date that made it look as though the work was
   * safe. A backup that does not exist is worse than no backup, because
   * you stop looking for one.
   *
   * It is gone. The only backup is the Excel file an administrator
   * downloads, and the date below is the date one was actually downloaded
   * on this computer -- nothing else writes it.
   */
  const [lastBackupTime, setLastBackupTime] = useState<string | null>(() => {
    try {
      return localStorage.getItem('srms_last_backup_display') || null;
    } catch {
      return null;
    }
  });

  /** Called by the backup button once a file has really been written. */
  const noteBackupTaken = () => {
    const displayTime = new Date().toLocaleString([], {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
    setLastBackupTime(displayTime);
    try {
      localStorage.setItem('srms_last_backup_display', displayTime);
      // Clear what the old automatic backup left behind, so no screen can
      // read a date that never corresponded to a file.
      localStorage.removeItem('srms_recovery_backup_snapshot');
      localStorage.removeItem('srms_last_12h_backup_timestamp');
    } catch {
      /* a browser refusing storage must not stop the download */
    }
  };

  // Firestore is the permanent shared data store.
// Do not persist application data to browser localStorage.

  // Permanent Demo Data Purge Routine (Permanently wipes all demo banks, zones, branch managers, regions, and delete timestamps)
  const purgeAllDemoData = async (): Promise<{ success: boolean; message: string }> => {
    try {
      localStorage.setItem('srms_persisted_accounts', JSON.stringify([]));
      localStorage.setItem('srms_persisted_allocations', JSON.stringify([]));
      localStorage.setItem('srms_persisted_allochistories', JSON.stringify([]));
      localStorage.setItem('srms_persisted_followups', JSON.stringify([]));
      localStorage.setItem('srms_persisted_ptps', JSON.stringify([]));
      localStorage.setItem('srms_persisted_recoveries', JSON.stringify([]));
      localStorage.setItem('srms_persisted_visits', JSON.stringify([]));
      localStorage.setItem('srms_persisted_photos', JSON.stringify([]));
      localStorage.setItem('srms_persisted_voicenotes', JSON.stringify([]));
      localStorage.setItem('srms_persisted_documents', JSON.stringify([]));
      localStorage.setItem('srms_persisted_user_assignments', JSON.stringify([]));
      localStorage.setItem('srms_whatsapp_offer_logs', JSON.stringify([]));
      localStorage.setItem('srms_persisted_banks', JSON.stringify([]));
      localStorage.setItem('srms_persisted_zones', JSON.stringify([]));
      localStorage.setItem('srms_persisted_areas', JSON.stringify([]));
      localStorage.setItem('srms_persisted_branches', JSON.stringify([]));
      localStorage.setItem('srms_persisted_departments', JSON.stringify([]));
      localStorage.setItem('srms_deleted_users', JSON.stringify([]));

      setAccounts([]);
      setAllocations([]);
      setAllocationHistories([]);
      setFollowups([]);
      setPtps([]);
      setRecoveries([]);
      setVisits([]);
      setPhotos([]);
      setVoiceNotes([]);
      setDocuments([]);
      setBranches([]);
      setAreas([]);
      setZones([]);
      setBanks([]);
      setRecoveryDepartments([]);
      setDeletedUsers([]);

      setUsers((prev) => {
        const cleaned = prev.filter((u) => !isDemoUser(u));
        const finalUsers = cleaned;
        localStorage.setItem('srms_persisted_users', JSON.stringify(finalUsers));
        return finalUsers;
      });

      // Also call server-side purge endpoint
      fetch('/api/admin/purge-demo-data', { method: 'POST' }).catch(() => {});

      logActivity(
        currentUser,
        'SETTINGS_UPDATED',
        'Permanently purged all demo data (zones, banks, branch managers, regions, accounts, and delete timestamps).'
      );

      return {
        success: true,
        message: 'All demo data (zones, banks, branch managers, regions, and delete timestamps) have been permanently purged.',
      };
    } catch (e: any) {
      return { success: false, message: e?.message || 'Purge failed' };
    }
  };

  return (
    <SRMSContext.Provider
      value={{
        currentUser,
        users,

        isAuthenticated,
        authResolved,

        deleteGeoTaggedPhoto,
        updatePhotoNote,
        deviceMode,
        setDeviceMode,
        switchUser,
        loginWithCredentials,
        logout,
        requestAdminOTP,
        verifyAdminOTP,
        resetAdminPasswordWithToken,
        accounts,
        allocations,
        allocationHistories,
        followups,
        ptps,
        recoveries,
        visits,
        photos,
        documents,
        voiceNotes,
        activityLogs,
        notifications,
        commissions,
        commissionSettings,
        branches,
        areas,
        zones,
        banks,
        recoveryDepartments,
        commissionRules,
        userCommissionAssignments,
        commissionAuditLogs,
        scopedAccounts,
        scopedRecoveries,
        scopedPtps,
        scopedVisits,
        scopedCommissions,
        scopedUsers,
        scopedRules,
        scopedAssignments,
        archivedAccounts,
        activeVisit,
        accountsScope,
        accountsLoading,
        loadAccountsForScope,
        loadAllAccounts,
        countAccountsFor,
        countCollectionFor,
        persistOTSSettings,
        deleteAgentRecordsIn,
        addBank,
        updateBank,
        deleteBank,
        deleteZone,
        deleteBranch,
        deleteRecoveryDepartment,
        addZone,
        updateZone,
        addRecoveryDepartment,
        updateRecoveryDepartment,
        addBranch,
        updateBranch,
        mergeBranches,
        correctAccountOriginalBalance,
        previewOrphanedCommissions,
        cleanOrphanedCommissions,
        previewBalanceRepair,
        applyBalanceRepair,
        backfillAccountBanks,
        pendingScopeReload,
        resumeLastScope,
        addCommissionRule,
        updateCommissionRule,
        deactivateCommissionRule,
        addUserCommissionAssignment,
        updateUserCommissionAssignment,
        deactivateUserCommissionAssignment,
        calculateCommission,
        liveDynamicCommission,
        getFilteredRecoveryDepartments,
        getFilteredBranches,
        isAuthorizedForAccount,
        isAuthorizedForRecovery,
        addAccount,
        updateAccount,
        updateCustomerStarRating,
        closeAccount,
        deleteAccount,
        deleteAllAccountsFromStorage,
        deleteBulkAccounts,
        bulkAddAccounts,
        allocateAccount,
        bulkAllocateAccounts,
        allocateBulkAccounts,
        toggleUserActive,
        addUser,
        updateUser,
        deleteUser,
        changeUserPassword,
        changeUserLoginId,
        exportAllBackendData,
        downloadFullBackup,
        logFollowUp,
        createPTP,
        updatePTPStatus,
        recordRecovery,
        editRecovery,
        deleteRecovery,
        reverseRecovery,
        markAccountForDeletion,
        startFieldVisit,
        completeFieldVisit,
        captureWatermarkedPhoto,
        recordVoiceNote,
        uploadDocument,
        attachGoogleDriveDocument: attachCloudDocument,
        updateCommissionSettings,
        updateCommissionRate: updateCommissionSettings,
        updateSarfaesiRate,
        markAccountsSarfaesi,
        updateCommissionStatus,
        addAgentNote,
        deleteAgentNote,
        markNotificationAsRead,
        messages,
        myMessages,
        unreadMessageCount,
        messageRecipients,
        sendStaffMessage,
        markMessageRead,
        deleteStaffMessages,
        deleteVoiceNote,
        deleteFieldVisit,
        setAccountCategoryBulk,
        deleteFollowUpLog,
        noticeBanner,
        visibleNotice,
        saveNoticeBanner,
        markNotificationRead: markNotificationAsRead,
        clearAllNotifications,
        acknowledgeReminder,
        ackedReminders,
        getAccountTimeline,
        ensureAllocationsForAccount,
        countAllocations: () => firebaseFirestoreService.countAllocations(),
        getGoogleSheetsData,
        getGoogleDriveData,
        syncToCloudStorage,
        syncWithGoogleSheets: syncToCloudStorage,
        pullFromGoogleSheets,
        runPTPDigest,
        deletePTP,
        getExpiredPTPPreview,
        analyzeAccountAI,
        lastBackupTime,
        isSyncing: isSyncingState,
        autoSyncEnabled,
        setAutoSyncEnabled: handleSetAutoSyncEnabled,
        lastAutoSyncTime,

        // OTS Scheme & WhatsApp Offer Module
        otsAuthorizedRoles,
        updateOTSAuthorizedRoles,
        canUserEditOTS,
        canUserApproveOTS,
        userBanks: banksOf(currentUser),
        activeBank,
        setActiveBank,
        whatsAppLogs,
        logWhatsAppOffer,
        applyOTS,
        modifyOTS,
        confirmOTS,
        cancelOTS,

        // Shared Central Master Database Across All Logins
        pullSharedMasterData,
        pushSharedMasterData,
        batchSaveAccountsToServer,
        fetchServerStats,
        serverStats,

        // Permanent Demo Data Purge
        purgeAllDemoData,

        // ⚡ 3-Tier Enterprise Storage Architecture
        firestoreSpeedStats,
        sheetQueueStatus,
        isFirebasePrimaryActive: true,
        flushSheetSyncNow,
      }}
    >
      {children}
    </SRMSContext.Provider>
  );
};
export const useSRMS = (): SRMSContextType => {
  const context = useContext(SRMSContext);
  if (context === undefined) {
    throw new Error('useSRMS must be used within an SRMSProvider');
  }
  return context;
};
